import { REPORT_VERSION_NAMING_KEY, getReportVersionNaming, normalizeReportVersionNaming } from './reportVersioning';
import React, { useCallback, useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getDashboardNavItems, MANAGEMENT_VIEW_KEYS } from './dashboardNavigation';
import Reports from './Reports';
import ProjectSetup from './ProjectSetup';
import UKProjectMap from './UKProjectMap';
import Team from './Team';
import Clients from './Clients';
import BusinessDevelopment from './BusinessDevelopment';
import FeeProposals from './FeeProposals';
import FeasibilityHub from './FeasibilityHub';
import Financials from './Financials';
import AnalyticsHub from './AnalyticsHub';
import ActionsHub from './ActionsHub';
import { buildActionRepository } from './actionRepository';
import ConfirmLiveProjectsDialog from './ConfirmLiveProjectsDialog';
import { LIVE_PROJECT_CHASER_DEFAULTS, normalizeLiveProjectChaserThresholds } from './liveProjectChaserPreferences';
import { AnalyticsPageHero, AnalyticsPanelSection, glassSubCardSx } from './analyticsSurface';
import IssueReports from './IssueReports';
import InvoiceLineChart from './InvoiceLineChart';
import { computeMonthAndYearInvoiceTotals, computeProgrammeDurationMonths } from './financialSchedule';
import KpiMetricsCard from './KpiMetricsCard';
import Calendar from './Calendar';
import { API_BASE_URL, fetchJson, tryPaths } from './api';
import LoginPage from './LoginPage';
import GmailIntegrationSettings from './GmailIntegrationSettings';
import { NotificationBell } from './notifications';
import AccountMenu from './AccountMenu';
import { getDarkMode, setDarkMode as setDarkModePreference } from './darkMode';

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    AppBar,
    Avatar,
    Alert,
    Button,
    Box,
    Checkbox,
    CircularProgress,
    Collapse,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Chip,
    Radio,
    RadioGroup,
    Slider as MuiSlider,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FlagIcon from '@mui/icons-material/Flag';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ConstructionIcon from '@mui/icons-material/Construction';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChatAssistantDrawer from './ChatAssistantDrawer';
import { MonsurvVoicePanel, MonsurvVoiceSettings, VoiceNavLauncher } from './MonsurvVoice';
import ActivityFeed from './ActivityFeed';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

function getUserFacingAuthError(errorValue, fallback) {
    const raw = String(errorValue?.message || fallback || '').trim();
    const withoutApiDetail = raw.replace(/\s*\(API:\s*[^)]*\)\s*$/i, '').trim();
    const normalized = withoutApiDetail.toLowerCase();

    if (!withoutApiDetail) return fallback;
    if (normalized === 'username is required' || normalized === 'email is required') {
        return 'Please enter your email address.';
    }
    if (normalized === 'password is required') {
        return 'Please enter your password.';
    }
    if (normalized === 'login failed' || normalized === 'invalid username or password') {
        return 'The email address or password is incorrect.';
    }
    if (normalized === 'invalid code') {
        return 'That verification code is not correct. Check your authenticator app and try again.';
    }
    if (normalized === 'mfa verification failed') {
        return 'We could not verify that code. Check your authenticator app and try again.';
    }
    if (normalized === 'mfa required but no challenge token returned') {
        return 'We could not start verification. Please try signing in again.';
    }
    if (normalized.includes('table missing') || normalized.includes('migration')) {
        return 'Sign-in is temporarily unavailable. Please try again later.';
    }
    return withoutApiDetail;
}

const TRUSTED_MFA_DEVICES_KEY = 'monsurv_mfa_trusted_devices_v1';
const TRUSTED_MFA_DEVICE_ID_KEY = 'monsurv_mfa_device_id_v1';
const DEFAULT_SESSION_POLICY = {
    require_mfa_for_admins: false,
    session_timeout_minutes: 720,
    idle_timeout_minutes: 240,
    password_min_length: 12
};

function resolveStaticCompanyName(user) {
    const email = String(user?.email || user?.username || '').trim().toLowerCase();
    const domain = email.includes('@') ? email.split('@')[1] : '';
    const tenantSlug = String(user?.tenant_slug || '').trim().toLowerCase();
    const explicit = String(user?.company || user?.company_name || '').trim();

    if (domain === 'monsurv.com' || domain.endsWith('.monsurv.com')) return 'MonTech Ltd';
    if (
        domain === 'iesisconsult.com' || domain.endsWith('.iesisconsult.com')
        || domain === 'iesisgroup.com' || domain.endsWith('.iesisgroup.com')
    ) return 'Iesis Consult Ltd';
    if (tenantSlug === 'montech') return 'MonTech Ltd';
    if (tenantSlug === 'iesis') return 'Iesis Consult Ltd';
    return explicit || 'Monsurv Ltd';
}

function normalizeTrustedDeviceUsername(value) {
    return String(value || '').trim().toLowerCase();
}

function tryGetDashboardTenantSlugFromToken(token) {
    try {
        const parts = String(token || '').split('.');
        if (parts.length < 2) return null;
        const raw = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = raw.padEnd(Math.ceil(raw.length / 4) * 4, '=');
        const json = atob(padded);
        const payload = JSON.parse(json);
        const tenantSlug = String(payload?.tenant_slug || '').trim().toLowerCase();
        return tenantSlug || null;
    } catch {
        return null;
    }
}

function loadTrustedMfaDeviceMap() {
    try {
        const raw = localStorage.getItem(TRUSTED_MFA_DEVICES_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function saveTrustedMfaDeviceMap(nextMap) {
    try {
        localStorage.setItem(TRUSTED_MFA_DEVICES_KEY, JSON.stringify(nextMap || {}));
    } catch {
        // ignore
    }
}

function getTrustedMfaDeviceToken(username) {
    const key = normalizeTrustedDeviceUsername(username);
    if (!key) return '';
    const map = loadTrustedMfaDeviceMap();
    return String(map?.[key] || '').trim();
}

function setTrustedMfaDeviceToken(username, token) {
    const key = normalizeTrustedDeviceUsername(username);
    if (!key) return;
    const map = loadTrustedMfaDeviceMap();
    if (String(token || '').trim()) map[key] = String(token).trim();
    else delete map[key];
    saveTrustedMfaDeviceMap(map);
}

function getOrCreateTrustedMfaDeviceId() {
    try {
        const existing = String(localStorage.getItem(TRUSTED_MFA_DEVICE_ID_KEY) || '').trim();
        if (existing) return existing;
        const next = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
            ? crypto.randomUUID()
            : `device-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem(TRUSTED_MFA_DEVICE_ID_KEY, next);
        return next;
    } catch {
        return `device-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
}

function formatTimeoutMinutes(minutes) {
    const value = Number(minutes);
    if (!Number.isFinite(value) || value <= 0) return 'Disabled';
    if (value % 60 === 0) {
        const hours = value / 60;
        return `${hours} hour${hours === 1 ? '' : 's'}`;
    }
    return `${value} minute${value === 1 ? '' : 's'}`;
}

function getIdleWarningLeadMs(timeoutMs) {
    if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return 0;
    return Math.min(2 * 60 * 1000, Math.max(30 * 1000, Math.floor(timeoutMs / 2)));
}

const APPENDIX_DOCS_KEY = 'monsurv_appendix_docs';
const OVERVIEW_DISPLAY_KEY = 'monsurv_overview_display';
const TEAM_CACHE_KEY = 'monsurv_team';
const CLIENTS_CACHE_KEY = 'monsurv_clients';
const TEAM_UPDATED_EVENT = 'monsurv:team-updated';
const CLIENTS_UPDATED_EVENT = 'monsurv:clients-updated';
const TEAM_API_PATHS = ['/team', '/api/team', '/team_members', '/api/team_members'];
const FUNDERS_API_PATHS = ['/funders', '/api/funders', '/clients', '/api/clients'];

function getDashboardAuthHeader() {
    try {
        const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

function normalizeTeamCacheMember(member) {
    if (!member || typeof member !== 'object') return null;
    return {
        id: member.id ?? member.member_id ?? member.team_member_id ?? member.email ?? member.name,
        name: String(member.name ?? member.full_name ?? '').trim(),
        position: String(member.position ?? '').trim(),
        email: String(member.email ?? '').trim(),
        phone: String(member.phone ?? '').trim(),
        role: String(member.role ?? member.access_role ?? '').trim().toLowerCase() || 'standard',
        profile_photo_url: member.profile_photo_url ?? member.profilePhotoUrl ?? member.profile_photo ?? null,
    };
}

function normalizeClientCacheItem(client) {
    if (!client || typeof client !== 'object') return null;
    return {
        id: client.id ?? client.funder_id ?? client.client_id,
        companyName: String(client.companyName ?? client.company_name ?? client.name ?? '').trim(),
        headOfficeAddress: String(client.headOfficeAddress ?? client.head_office_address ?? '').trim(),
        leadContact: String(client.leadContact ?? client.lead_contact ?? client.contact ?? '').trim(),
        phone: String(client.phone ?? '').trim(),
        email: String(client.email ?? '').trim(),
        logo_url: client.logo_url ?? client.logoUrl ?? null,
        contacts: Array.isArray(client.contacts)
            ? client.contacts
                .map((contact) => ({
                    name: String(contact?.name ?? contact?.contact_name ?? '').trim(),
                    phone: String(contact?.phone ?? contact?.contact_phone ?? '').trim(),
                    email: String(contact?.email ?? contact?.contact_email ?? '').trim(),
                }))
                .filter((contact) => contact.name || contact.phone || contact.email)
            : []
    };
}

function loadOverviewDisplayFromStorage() {
    const defaults = {
        activeProjects: true,
        monthlyFees: true,
        feeForecast: true,
        reportsDueWeek: true,
        avgCostPerReport: false,
        awsForecastCost: false,
        priorityTask: false,
        reportsIssuedThisMonth: false,
        openIssueReports: false
    };
    try {
        const raw = localStorage.getItem(OVERVIEW_DISPLAY_KEY);
        if (!raw) return defaults;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return defaults;
        return { ...defaults, ...parsed };
    } catch {
        return defaults;
    }
}

function loadAppendixDocsFromStorage() {
    try {
        const raw = localStorage.getItem(APPENDIX_DOCS_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map((s) => String(s || '').trim()).filter(Boolean);
    } catch {
        // ignore
    }
    return [];
}

function formatCurrency(val) {
    try {
        const n = Number(val);
        if (Number.isNaN(n)) return '';
        // Dashboard headline KPIs should not show pence.
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(n);
    } catch (e) {
        return String(val ?? '');
    }
}

function startOfLocalDay(value) {
    const d = value instanceof Date ? new Date(value.getTime()) : new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
}

function formatOverviewDueDate(value) {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return 'No due date';
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short'
    }).format(date);
}

function formatOverviewReportType(item) {
    if (item?.action_type === 'upload_report') return 'Upload Report PDF';
    if (item?.action_type === 'confirm_live_projects') return 'Project Status';
    const type = String(item?.type || '').trim().toLowerCase();
    if (item?.source === 'manual' || type === 'manual') return 'Personal Action';
    if (type === 'interim') {
        const n = item?.interim_number != null ? Number(item.interim_number) : null;
        return Number.isFinite(n) ? `Interim Report ${n}` : 'Interim Report';
    }
    return 'Initial Report';
}

function buildOverviewPortalUrl(item) {
    if (item?.source === 'manual') return `${window.location.origin}/?view=actions_hub`;
    const isReportUploadAction = item?.action_type === 'upload_report';
    const reference = String(isReportUploadAction ? item?.report_reference : item?.reference || '').trim();
    if (!reference) return '';

    const params = new URLSearchParams();
    params.set('portal', 'surveyor');
    params.set('reference', reference);
    const reportInstance = String(item?.target_report_instance || '').trim().toLowerCase();
    const interimMatch = reportInstance.match(/^interim:(\d+)$/);
    if (isReportUploadAction && interimMatch) {
        params.set('page', 'interim');
        params.set('interim', interimMatch[1]);
    } else if (String(item?.type || '').trim().toLowerCase() === 'interim') {
        params.set('page', 'interim');
        params.set('interim', String(Number(item?.interim_number) || 1));
    } else {
        params.set('page', 'initial');
    }

    return `${window.location.origin}/?${params.toString()}`;
}

function createOfficeMapDraftId() {
    return `office_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function createOfficeMapDraftOffice(index = 0, partial = {}) {
    const value = partial && typeof partial === 'object' ? partial : {};
    return {
        id: String(value.id || createOfficeMapDraftId()).trim() || createOfficeMapDraftId(),
        office_location: String(value.office_location || ''),
        address: String(value.address || ''),
        pin_color: String(value.pin_color || '#0f3b68').trim() || '#0f3b68'
    };
}

function normalizeOfficeMapFormSettings(raw) {
    const value = raw && typeof raw === 'object' ? raw : {};
    const legacyOffices = (value.office_location || value.address || value.pin_color)
        ? [value]
        : [];
    const source = Array.isArray(value.offices) ? value.offices : legacyOffices;
    const offices = source.map((office, index) => createOfficeMapDraftOffice(index, office));
    const safeOffices = offices;
    const primaryOfficeId = safeOffices.some((office) => office.id === value.primary_office_id)
        ? String(value.primary_office_id)
        : (safeOffices[0]?.id || '');
    const primaryOffice = safeOffices.find((office) => office.id === primaryOfficeId) || safeOffices[0] || null;

    return {
        offices: safeOffices,
        primary_office_id: primaryOfficeId,
        office_location: String(primaryOffice?.office_location || ''),
        address: String(primaryOffice?.address || ''),
        pin_color: String(primaryOffice?.pin_color || '#0f3b68').trim() || '#0f3b68',
        brand_upload_url: String(value.brand_upload_url || '').trim()
    };
}

const OFFICE_MAP_SETTINGS_UPDATED_EVENT = 'monsurv:office-map-settings-updated';
const OFFICE_MAP_LOCAL_CACHE_KEY_BASE = 'monsurv_office_map_settings_cache_v1';

function readOfficeMapSettingsCache(cacheKey = OFFICE_MAP_LOCAL_CACHE_KEY_BASE) {
    try {
        const raw = localStorage.getItem(cacheKey);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function writeOfficeMapSettingsCache(settings, cacheKey = OFFICE_MAP_LOCAL_CACHE_KEY_BASE) {
    try {
        localStorage.setItem(cacheKey, JSON.stringify({
            ...(settings || {}),
            _saved_at: new Date().toISOString()
        }));
    } catch {
        // ignore
    }
}

function choosePreferredOfficeMapSettings(serverSettings, cachedSettings) {
    const server = normalizeOfficeMapFormSettings(serverSettings || null);
    const cached = cachedSettings ? normalizeOfficeMapFormSettings(cachedSettings) : null;
    if (!cached) return server;
    const serverCount = Array.isArray(server?.offices) ? server.offices.length : 0;
    const cachedCount = Array.isArray(cached?.offices) ? cached.offices.length : 0;
    if (cachedCount > serverCount) return { ...server, ...cached, brand_upload_url: cached.brand_upload_url || server.brand_upload_url || '' };
    return server;
}

function expandHexColor(value) {
    const trimmed = String(value || '').trim();
    const shortHexMatch = trimmed.match(/^#([0-9a-fA-F]{3})$/);
    if (!shortHexMatch) return trimmed;
    const [r, g, b] = shortHexMatch[1].split('');
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
}

function getColorPickerValue(value, fallback = '#0f3b68') {
    const normalizedFallback = expandHexColor(fallback) || '#0f3b68';
    const trimmed = expandHexColor(value);
    return /^#([0-9a-fA-F]{6})$/.test(trimmed) ? trimmed.toLowerCase() : normalizedFallback;
}

function getColorPreviewValue(value, fallback = '#0f3b68') {
    const trimmed = String(value || '').trim();
    if (trimmed && typeof window !== 'undefined' && window.CSS && typeof window.CSS.supports === 'function' && window.CSS.supports('color', trimmed)) {
        return trimmed;
    }
    return getColorPickerValue(fallback, '#0f3b68');
}

function ColorSettingField({
    label,
    value,
    onChange,
    disabled,
    sx,
    fallbackColor = '#0f3b68'
}) {
    const pickerValue = getColorPickerValue(value, fallbackColor);
    const previewValue = getColorPreviewValue(value, fallbackColor);

    return (
        <Box
            sx={{
                ...sx,
                px: 1.75,
                py: 1.25,
                borderRadius: 999,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha('#ffffff', 0.72),
                boxShadow: '0 12px 28px rgba(15, 23, 42, 0.06)'
            }}
        >
            <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between">
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {label}
                </Typography>
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        p: 0.375,
                        borderRadius: '50%',
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        flexShrink: 0,
                        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.10)'
                    }}
                >
                    <Box
                        component="input"
                        type="color"
                        aria-label={`${label} colour picker`}
                        value={pickerValue}
                        onChange={(e) => onChange(e.target.value)}
                        disabled={disabled}
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'block',
                            border: 0,
                            p: 0,
                            borderRadius: '50%',
                            bgcolor: 'transparent',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            '&::-webkit-color-swatch-wrapper': { p: 0 },
                            '&::-webkit-color-swatch': { border: 'none', borderRadius: '50%' },
                            '&::-moz-color-swatch': { border: 'none', borderRadius: '50%' }
                        }}
                    />
                </Box>
            </Stack>
            <Box
                sx={{
                    width: 1,
                    height: 0,
                    overflow: 'hidden',
                    opacity: 0,
                    pointerEvents: 'none',
                    bgcolor: previewValue
                }}
            />
        </Box>
    );
}

export default function Dashboard({ onAuthChange } = {}) {
    const theme = useTheme();
    const isMobileNav = useMediaQuery(theme.breakpoints.down('md'));
    const isPhoneHeader = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);
    const [voiceOpen, setVoiceOpen] = useState(false);
    const [voiceActive, setVoiceActive] = useState(false);
    const [chatAnchorEl, setChatAnchorEl] = useState(null);
    const openedSettingsSectionRef = useRef('');

    const initialRoute = (() => {
        try {
            const params = new URLSearchParams(window.location.search || '');
            const rawView = String(params.get('view') || '').trim().toLowerCase();
            const rawTab = String(params.get('tab') || params.get('analytics_tab') || '').trim().toLowerCase();

            const analyticsTab = (rawView === 'workload' || rawTab === 'workload')
                ? 'workload'
                : ((rawView === 'active_projects' || rawTab === 'active_projects') ? 'active_projects' : 'portfolio');
            const view = (rawView === 'workload' || rawView === 'active_projects') ? 'analytics' : (rawView || 'overview');
            return { view, analyticsTab };
        } catch {
            return { view: 'overview', analyticsTab: 'portfolio' };
        }
    })();

    const portalMode = useMemo(() => {
        const readParams = (search) => {
            try {
                const params = new URLSearchParams(search || '');
                const portal = String(params.get('portal') || '').trim().toLowerCase();
                if (portal) return portal;
                const mode = String(params.get('mode') || '').trim().toLowerCase();
                if (mode) return mode;
                const role = String(params.get('role') || '').trim().toLowerCase();
                if (role) return role;
            } catch {
                // ignore
            }
            return '';
        };

        const fromSearch = readParams(window.location.search || '');
        if (fromSearch) return fromSearch;

        const hash = String(window.location.hash || '');
        const hashQueryIndex = hash.indexOf('?');
        if (hashQueryIndex >= 0) {
            const fromHash = readParams(hash.slice(hashQueryIndex + 1));
            if (fromHash) return fromHash;
        }

        return '';
    }, []);

    const [view, setView] = useState(initialRoute.view);
    const [analyticsTab, setAnalyticsTab] = useState(initialRoute.analyticsTab);

    useEffect(() => {
        // Back-compat: map legacy deep links (?view=workload) to Analytics w/ Workload tab.
        try {
            const url = new URL(window.location.href);
            const params = url.searchParams;
            const rawView = String(params.get('view') || '').trim().toLowerCase();
            if (rawView !== 'workload') return;

            params.set('view', 'analytics');
            params.set('tab', 'workload');

            const nextSearch = params.toString();
            const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}${url.hash || ''}`;
            window.history.replaceState({}, document.title, nextUrl);
        } catch {
            // ignore
        }
    }, []);
    const [reportsNavNonce, setReportsNavNonce] = useState(0);
    const [authUser, setAuthUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    const buildPremiumMetricCardSx = (accent) => ({
        p: { xs: 1.15, sm: 1.35 },
        minWidth: 0,
        width: '100%',
        minHeight: { xs: 84, sm: 92 },
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#f8fbff', 0.78)} 100%)`,
        boxShadow: '0 16px 32px rgba(15, 23, 42, 0.08)',
        border: `1px solid ${alpha('#ffffff', 0.7)}`,
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 14,
            right: 14,
            height: 3,
            borderRadius: '0 0 6px 6px',
            background: accent
        }
    });

    const premiumMetricLabelSx = {
        color: 'text.secondary',
        fontSize: { xs: '0.62rem', sm: '0.68rem' },
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: '0.07em',
        lineHeight: 1.2,
        pr: 0,
        width: '100%',
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };

    const premiumMetricValueSx = {
        mt: 0.5,
        fontWeight: 900,
        fontSize: { xs: '1.22rem', sm: '1.48rem', md: '1.82rem' },
        lineHeight: 1.02,
        letterSpacing: '-0.03em',
        width: '100%',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: 'text.primary'
    };

    const premiumPanelSx = {
        p: 0,
        borderRadius: 5,
        overflow: 'hidden',
        background: `linear-gradient(180deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8fbff', 0.78)} 100%)`,
        boxShadow: '0 24px 54px rgba(15, 23, 42, 0.10)',
        border: `1px solid ${alpha('#ffffff', 0.7)}`,
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)'
    };

    const premiumPanelHeaderSx = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: { xs: 1.75, sm: 2.5 },
        pt: { xs: 1.5, sm: 1.9 },
        pb: { xs: 0.9, sm: 1.05 },
        background: `linear-gradient(180deg, ${alpha('#ffffff', 0.46)} 0%, ${alpha('#f3f7fb', 0.18)} 100%)`
    };

    const premiumPanelTitleSx = {
        fontWeight: 800,
        fontSize: { xs: '0.98rem', sm: '1.04rem' },
        letterSpacing: '-0.01em',
        textAlign: 'center'
    };

    const premiumPanelBodySx = {
        p: { xs: 1.75, sm: 2.25 },
        minWidth: 0
    };

    const metricAccents = {
        activeProjects: 'linear-gradient(90deg, rgba(36, 50, 66, 0.95), rgba(59, 130, 246, 0.62))',
        monthlyFees: 'linear-gradient(90deg, rgba(21, 94, 117, 0.88), rgba(14, 165, 233, 0.58))',
        feeForecast: 'linear-gradient(90deg, rgba(22, 101, 52, 0.86), rgba(34, 197, 94, 0.56))',
        reportsDueWeek: 'linear-gradient(90deg, rgba(153, 27, 27, 0.92), rgba(244, 63, 94, 0.56))',
        avgCostPerReport: 'linear-gradient(90deg, rgba(91, 33, 182, 0.86), rgba(139, 92, 246, 0.54))',
        awsForecastCost: 'linear-gradient(90deg, rgba(180, 83, 9, 0.9), rgba(245, 158, 11, 0.56))',
        priorityTask: 'linear-gradient(90deg, rgba(15, 118, 110, 0.9), rgba(45, 212, 191, 0.54))',
        reportsIssuedThisMonth: 'linear-gradient(90deg, rgba(30, 41, 59, 0.92), rgba(99, 102, 241, 0.58))',
        openIssueReports: 'linear-gradient(90deg, rgba(127, 29, 29, 0.92), rgba(248, 113, 113, 0.54))'
    };
    useLayoutEffect(() => {
        if (typeof onAuthChange !== 'function') return;
        onAuthChange({
            isAuthenticated: Boolean(authUser),
            authResolved: !authLoading
        });
    }, [authUser, authLoading, onAuthChange]);
    const [authError, setAuthError] = useState('');
    const [mustChangePassword, setMustChangePassword] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [mfaRequired, setMfaRequired] = useState(false);
    const [mfaToken, setMfaToken] = useState('');
    const [mfaLoginUser, setMfaLoginUser] = useState(null);
    const [pwForm, setPwForm] = useState({ temporaryPassword: '', newPassword: '', confirmNewPassword: '' });
    const [pwError, setPwError] = useState('');
    const [pwSaving, setPwSaving] = useState(false);
    const [projectsCount, setProjectsCount] = useState(0);
    const [sessionPolicy, setSessionPolicy] = useState(DEFAULT_SESSION_POLICY);
    const [sessionPolicyLoading, setSessionPolicyLoading] = useState(false);
    const [idleWarningOpen, setIdleWarningOpen] = useState(false);
    const [idleWarningSeconds, setIdleWarningSeconds] = useState(0);
    const resetInactivityTimerRef = useRef(() => { });

    const tenantStorageScope = useMemo(() => {
        const fromUser = String(authUser?.tenant_slug || '').trim().toLowerCase();
        if (fromUser) return fromUser;
        try {
            const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
            return tryGetDashboardTenantSlugFromToken(token) || 'iesis';
        } catch {
            return 'iesis';
        }
    }, [authUser?.tenant_slug]);

    const completedProjectsStorageKey = useMemo(
        () => `monsurv_completed_projects:${tenantStorageScope}`,
        [tenantStorageScope]
    );
    const developerPortalUrl = useMemo(() => {
        const isDev = String(process.env.NODE_ENV || '').trim().toLowerCase() !== 'production';
        let hostBasedProdDefault = 'https://developer.monsurv.com';
        try {
            const host = String(window?.location?.hostname || '').trim().toLowerCase();
            if (host.endsWith('.co.uk')) hostBasedProdDefault = 'https://developer.monsurv.co.uk';
        } catch {
            // no-op
        }
        const prodUrl = String(process.env.REACT_APP_DEVELOPER_PORTAL_URL || '').trim() || hostBasedProdDefault;
        const devUrl = String(process.env.REACT_APP_DEV_DEVELOPER_PORTAL_URL || '').trim() || 'http://127.0.0.1:5173';
        return isDev ? devUrl : prodUrl;
    }, []);
    const canOpenDeveloperSettings = tenantStorageScope === 'montech';

    function handleOpenDeveloperSettings() {
        try {
            window.open(developerPortalUrl, '_blank', 'noopener,noreferrer');
        } catch {
            window.location.assign(developerPortalUrl);
        }
    }

    useEffect(() => {
        if (view !== 'settings') return;

        let section = '';
        try {
            const params = new URLSearchParams(window.location.search || '');
            section = String(params.get('section') || '').trim();
        } catch {
            section = '';
        }

        if (!section) return;
        if (openedSettingsSectionRef.current === section) return;
        openedSettingsSectionRef.current = section;

        setTimeout(() => {
            try {
                window.dispatchEvent(new CustomEvent('monsurv_open_settings_section', { detail: { id: section } }));
            } catch {
                // ignore
            }

            setTimeout(() => {
                try {
                    const el = document.getElementById(`settings-section-${section}`);
                    if (el && typeof el.scrollIntoView === 'function') {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                } catch {
                    // ignore
                }
            }, 60);
        }, 50);
    }, [view]);
    const [completedByRef, setCompletedByRef] = useState(() => {
        try {
            const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
            const slug = tryGetDashboardTenantSlugFromToken(token) || 'iesis';
            const raw = localStorage.getItem(`monsurv_completed_projects:${slug}`);
            const parsed = raw ? JSON.parse(raw) : {};
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    });
    const [projectLocations, setProjectLocations] = useState([]);
    const [currentMonthFees, setCurrentMonthFees] = useState(null);
    const [feeForecastYear, setFeeForecastYear] = useState(null);
    const [overviewDisplay, setOverviewDisplay] = useState(() => loadOverviewDisplayFromStorage());
    const [reportsDueWeek, setReportsDueWeek] = useState({ total: null, initial: 0, interim: 0 });
    const [reportsDueLoading, setReportsDueLoading] = useState(false);
    const [priorityTask, setPriorityTask] = useState(null);
    const [priorityTaskLoading, setPriorityTaskLoading] = useState(false);
    const [avgCostPerReport, setAvgCostPerReport] = useState(null);
    const [avgCostLoading, setAvgCostLoading] = useState(false);
    const [awsForecastCost, setAwsForecastCost] = useState(null);
    const [awsForecastLabel, setAwsForecastLabel] = useState('');
    const [awsForecastLoading, setAwsForecastLoading] = useState(false);
    const [reportsIssuedMonth, setReportsIssuedMonth] = useState({ total: null, initial: 0, interim: 0 });
    const [reportsIssuedLoading, setReportsIssuedLoading] = useState(false);
    const [openIssueReportsCount, setOpenIssueReportsCount] = useState(null);
    const [openIssueReportsLoading, setOpenIssueReportsLoading] = useState(false);
    const [overviewInsightsLoading, setOverviewInsightsLoading] = useState(false);
    const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
    const [priorityActions, setPriorityActions] = useState([]);
    const [priorityActionUsers, setPriorityActionUsers] = useState([]);
    const [priorityActionRefresh, setPriorityActionRefresh] = useState(0);
    const [priorityActionMenu, setPriorityActionMenu] = useState({ open: false, x: 0, y: 0, item: null });
    const [priorityActionDialog, setPriorityActionDialog] = useState({ open: false, type: '', item: null });
    const [priorityActionTarget, setPriorityActionTarget] = useState('');
    const [priorityActionDescription, setPriorityActionDescription] = useState('');
    const [priorityActionSendEmail, setPriorityActionSendEmail] = useState(false);
    const [priorityActionDelegationReason, setPriorityActionDelegationReason] = useState('');
    const [priorityActionWarning, setPriorityActionWarning] = useState('');
    const [priorityActionSubmitting, setPriorityActionSubmitting] = useState(false);
    const [confirmLiveAction, setConfirmLiveAction] = useState(null);
    const [priorityActionError, setPriorityActionError] = useState('');
    const [workloadItems, setWorkloadItems] = useState([]);
    const [overviewDetail, setOverviewDetail] = useState({ open: false, title: '', items: [] });
    const [workloadSummary, setWorkloadSummary] = useState({ overdue: 0, dueSoon: 0, onTrack: 0, late: 0, focusCount: 0 });
    const [issueOpen, setIssueOpen] = useState(false);
    const [issueTitle, setIssueTitle] = useState('');
    const [issueDescription, setIssueDescription] = useState('');
    const [issuePriority, setIssuePriority] = useState('medium');
    const [issueImage, setIssueImage] = useState(null);
    const [issueSubmitting, setIssueSubmitting] = useState(false);
    const [issueError, setIssueError] = useState('');
    const [issueSuccess, setIssueSuccess] = useState('');

    function loadTeamPosition(displayName) {
        try {
            const raw = localStorage.getItem('monsurv_team');
            const team = raw ? JSON.parse(raw) : [];
            const list = Array.isArray(team) ? team : [];
            const name = String(displayName || '').trim().toLowerCase();
            const match = list.find((m) => String(m?.name || '').trim().toLowerCase() === name);
            return match?.position || '';
        } catch {
            return '';
        }
    }

    function loadTeamProfilePhotoUrl({ displayName, username, email }) {
        try {
            const raw = localStorage.getItem('monsurv_team');
            const team = raw ? JSON.parse(raw) : [];
            const list = Array.isArray(team) ? team : [];

            const uname = String(username || '').trim().toLowerCase();
            const mail = String(email || '').trim().toLowerCase();
            const name = String(displayName || '').trim().toLowerCase();

            const byEmail = list.find((m) => {
                const mEmail = String(m?.email || '').trim().toLowerCase();
                if (!mEmail) return false;
                return (uname && mEmail === uname) || (mail && mEmail === mail);
            });
            if (byEmail?.profile_photo_url) return String(byEmail.profile_photo_url).trim();

            const byName = name ? list.find((m) => String(m?.name || '').trim().toLowerCase() === name) : null;
            if (byName?.profile_photo_url) return String(byName.profile_photo_url).trim();
            return '';
        } catch {
            return '';
        }
    }

    const [teamCacheNonce, setTeamCacheNonce] = useState(0);
    const sharedDirectoryHydrationInFlightRef = useRef(false);

    useEffect(() => {
        function onTeamUpdated() {
            setTeamCacheNonce((n) => n + 1);
        }
        try {
            window.addEventListener('monsurv:team-updated', onTeamUpdated);
            return () => window.removeEventListener('monsurv:team-updated', onTeamUpdated);
        } catch {
            return undefined;
        }
    }, []);

    useEffect(() => {
        if (!authUser) return;

        let cancelled = false;

        async function hydrateSharedDirectories() {
            if (sharedDirectoryHydrationInFlightRef.current) return;
            sharedDirectoryHydrationInFlightRef.current = true;

            try {
                try {
                    const teamJson = await tryPaths(
                        TEAM_API_PATHS,
                        (path) => `${API_BASE_URL}${path}`,
                        { method: 'GET', headers: { ...getDashboardAuthHeader() } }
                    );
                    const rawTeam = Array.isArray(teamJson) ? teamJson : (Array.isArray(teamJson?.data) ? teamJson.data : []);
                    const teamList = rawTeam.map(normalizeTeamCacheMember).filter(Boolean);
                    if (!cancelled) {
                        try {
                            localStorage.setItem(TEAM_CACHE_KEY, JSON.stringify(teamList));
                            window.dispatchEvent(new CustomEvent(TEAM_UPDATED_EVENT, { detail: teamList }));
                        } catch {
                            // ignore local cache/event failures
                        }
                    }
                } catch {
                    // keep existing cache when team endpoint is unavailable
                }

                try {
                    const clientsJson = await tryPaths(
                        FUNDERS_API_PATHS,
                        (path) => `${API_BASE_URL}${path}`,
                        { method: 'GET', headers: { ...getDashboardAuthHeader() } }
                    );
                    const rawClients = Array.isArray(clientsJson) ? clientsJson : (Array.isArray(clientsJson?.data) ? clientsJson.data : []);
                    const clientsList = rawClients.map(normalizeClientCacheItem).filter(Boolean);
                    if (!cancelled) {
                        try {
                            localStorage.setItem(CLIENTS_CACHE_KEY, JSON.stringify(clientsList));
                            window.dispatchEvent(new CustomEvent(CLIENTS_UPDATED_EVENT, { detail: clientsList }));
                        } catch {
                            // ignore local cache/event failures
                        }
                    }
                } catch {
                    // keep existing cache when clients endpoint is unavailable
                }
            } finally {
                sharedDirectoryHydrationInFlightRef.current = false;
            }
        }

        hydrateSharedDirectories();
        const refreshTimer = window.setInterval(hydrateSharedDirectories, 2 * 60 * 1000);

        function onWindowFocus() {
            hydrateSharedDirectories();
        }

        window.addEventListener('focus', onWindowFocus);
        return () => {
            cancelled = true;
            window.clearInterval(refreshTimer);
            window.removeEventListener('focus', onWindowFocus);
        };
    }, [authUser]);

    const authDisplayName = authUser?.display_name || authUser?.displayName || authUser?.username || '';
    const authCompany = resolveStaticCompanyName(authUser);
    const authPosition = useMemo(() => {
        return loadTeamPosition(authDisplayName) || authUser?.position || '';
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authDisplayName, authUser?.position, teamCacheNonce]);

    const authTeamPhotoUrl = useMemo(() => {
        return loadTeamProfilePhotoUrl({
            displayName: authDisplayName,
            username: authUser?.username,
            email: authUser?.email
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authDisplayName, authUser?.username, authUser?.email, teamCacheNonce]);

    const authPhotoSrc = useMemo(() => {
        const raw = String(authUser?.profile_photo_url || authTeamPhotoUrl || '').trim();
        if (!raw) return '';
        if (/^https?:\/\//i.test(raw)) return raw;
        return `${API_BASE_URL}${raw.startsWith('/') ? raw : `/${raw}`}`;
    }, [authUser?.profile_photo_url, authTeamPhotoUrl]);

    const authInitials = useMemo(() => {
        const parts = String(authDisplayName || '').trim().split(/\s+/).filter(Boolean);
        if (!parts.length) return 'U';
        const a = parts[0]?.[0] || '';
        const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] || '') : (parts[0]?.[1] || '');
        const out = `${a}${b}`.trim().toUpperCase();
        return out || 'U';
    }, [authDisplayName]);

    const authRole = String(authUser?.role || '').toLowerCase();
    const isDeveloperPortal = portalMode === 'developer';
    const isFunderPortal = portalMode === 'funder';
    const isAdmin = authRole === 'admin' && !isDeveloperPortal;
    const isFinance = authRole === 'finance' && !isDeveloperPortal;
    const isSurveyor = (authRole === 'surveyor' || authRole === 'standard') && !isDeveloperPortal;
    const isDeveloper = authRole === 'developer' || isDeveloperPortal;
    const isFunder = authRole === 'funder' || isFunderPortal;

    const overviewMetricCardCount = [
        overviewDisplay.activeProjects,
        overviewDisplay.monthlyFees,
        overviewDisplay.feeForecast,
        overviewDisplay.reportsDueWeek,
        overviewDisplay.avgCostPerReport,
        overviewDisplay.awsForecastCost,
        overviewDisplay.priorityTask,
        overviewDisplay.reportsIssuedThisMonth,
        overviewDisplay.openIssueReports && isAdmin
    ].filter(Boolean).length;

    const overviewMetricDesktopColumns = Math.max(1, Math.min(overviewMetricCardCount, 4));

    function handleReportIssue() {
        setIssueError('');
        setIssueSuccess('');
        setIssueOpen(true);
    }

    function openPriorityActionMenu(event, item) {
        event.preventDefault();
        setPriorityActionMenu({ open: true, x: event.clientX, y: event.clientY, item });
    }

    function closePriorityActionMenu() {
        setPriorityActionMenu((current) => ({ ...current, open: false }));
    }

    function openPriorityActionDialog(type) {
        const item = priorityActionMenu.item;
        closePriorityActionMenu();
        if (!item) return;
        setPriorityActionError('');
        setPriorityActionTarget('');
        setPriorityActionDescription('');
        setPriorityActionSendEmail(false);
        setPriorityActionDelegationReason('');
        setPriorityActionWarning('');
        setPriorityActionDialog({ open: true, type, item });
    }

    function closePriorityActionDialog() {
        if (priorityActionSubmitting) return;
        setPriorityActionDialog({ open: false, type: '', item: null });
        setPriorityActionError('');
        setPriorityActionTarget('');
        setPriorityActionDescription('');
        setPriorityActionSendEmail(false);
        setPriorityActionDelegationReason('');
    }

    async function submitPriorityAction(typeOverride, itemOverride, extra = {}) {
        const item = itemOverride || priorityActionDialog.item;
        const type = typeOverride || priorityActionDialog.type;
        if (priorityActionSubmitting || !item) return;
        if (type === 'delegate' && !priorityActionTarget) {
            setPriorityActionError('Choose a user to delegate this task to.');
            return;
        }
        if (type === 'delegate' && priorityActionSendEmail && !String(priorityActionDelegationReason || '').trim()) {
            setPriorityActionError('Enter a reason for delegating this task.');
            return;
        }
        if (type === 'dismiss' && !String(priorityActionDescription || '').trim()) {
            setPriorityActionError('Enter a reason for dismissing this task.');
            return;
        }

        setPriorityActionSubmitting(true);
        setPriorityActionError('');
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            const result = await fetchJson(`${API_BASE_URL}/api/dashboard/priority-actions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    action: type === 'priority' ? 'set_priority' : type,
                    reference: item.reference,
                    report_instance: item.report_instance,
                    target_user_key: type === 'delegate' ? priorityActionTarget : undefined,
                    send_email: type === 'delegate' ? priorityActionSendEmail : undefined,
                    delegation_reason: type === 'delegate' && priorityActionSendEmail ? priorityActionDelegationReason : undefined,
                    description: type === 'dismiss' ? priorityActionDescription : undefined,
                    report_uploaded: type === 'complete' ? Boolean(extra.report_uploaded) : undefined
                })
            });
            if (result?.delegation_email?.warning) {
                setPriorityActionWarning(`Task delegated, but the email could not be sent: ${result.delegation_email.warning}`);
            }
            setPriorityActionDialog({ open: false, type: '', item: null });
            setPriorityActionRefresh((value) => value + 1);
        } catch (error) {
            setPriorityActionError(error?.message || 'Unable to update this priority action.');
        } finally {
            setPriorityActionSubmitting(false);
        }
    }

    async function submitIssueReport() {
        if (issueSubmitting) return;
        const title = String(issueTitle || '').trim();
        if (!title) {
            setIssueError('Title is required.');
            return;
        }
        const description = String(issueDescription || '').trim();
        if (!description) {
            setIssueError('Description is required.');
            return;
        }

        setIssueSubmitting(true);
        setIssueError('');
        setIssueSuccess('');

        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            if (!token) throw new Error('Not authenticated');

            const fd = new FormData();
            fd.append('title', title);
            fd.append('description', description);
            fd.append('priority', String(issuePriority || 'medium'));
            fd.append('page', `${String(view || '').toUpperCase()} — ${window.location.pathname}`);
            if (issueImage) fd.append('image', issueImage);

            const resp = await fetch(`${API_BASE_URL}/issue-report`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: fd
            });
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(json?.error || 'Failed to submit issue report');

            try {
                window.dispatchEvent(new CustomEvent('monsurv:issue-reports-updated'));
            } catch {
                // ignore
            }

            if (json && json.emailSent === false) {
                setIssueSuccess('Issue report submitted (admin email delivery failed — admins can still view it in Issue Reports).');
            } else {
                setIssueSuccess('Issue report submitted.');
            }
            setIssueTitle('');
            setIssueDescription('');
            setIssuePriority('medium');
            setIssueImage(null);
        } catch (e) {
            setIssueError(e?.message || 'Failed to submit issue report');
        } finally {
            setIssueSubmitting(false);
        }
    }

    async function bootstrapAuth() {
        setAuthLoading(true);
        setAuthError('');
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            if (!token) {
                setAuthUser(null);
                setMustChangePassword(false);
                setShowChangePassword(false);
                return;
            }
            const me = await fetchJson(`${API_BASE_URL}/auth/me`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` }
            });
            let mergedUser = me?.user || null;
            try {
                const capabilitiesJson = await fetchJson(`${API_BASE_URL}/auth/capabilities`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (mergedUser && capabilitiesJson && typeof capabilitiesJson === 'object') {
                    mergedUser = {
                        ...mergedUser,
                        tenant_slug: String(capabilitiesJson?.tenant_slug || mergedUser?.tenant_slug || '').trim().toLowerCase() || mergedUser?.tenant_slug,
                        tenant_capabilities: Array.isArray(capabilitiesJson?.capabilities)
                            ? capabilitiesJson.capabilities
                            : (Array.isArray(mergedUser?.tenant_capabilities) ? mergedUser.tenant_capabilities : []),
                        feature_visibility: capabilitiesJson?.features && typeof capabilitiesJson.features === 'object'
                            ? capabilitiesJson.features
                            : (mergedUser?.feature_visibility || {})
                    };
                }
            } catch {
                // keep /auth/me payload as fallback
            }

            setAuthUser(mergedUser);
            const must = Boolean(me?.mustChangePassword);
            setMustChangePassword(must);
            setShowChangePassword(must);

            // Prime per-user preferences cache so other modules (e.g., notifications) can apply settings.
            try {
                const prefsJson = await fetchJson(`${API_BASE_URL}/auth/preferences`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` }
                });
                const prefs = prefsJson?.preferences || prefsJson?.settings || prefsJson || {};
                try {
                    localStorage.setItem('monsurv_user_preferences_v1', JSON.stringify(prefs || {}));
                } catch { }
                try {
                    window.dispatchEvent(new CustomEvent('monsurv:user-preferences-updated', { detail: prefs || {} }));
                } catch { }
            } catch {
                // ignore
            }
        } catch (e) {
            try { localStorage.removeItem('monsurv_auth_token'); } catch { }
            setAuthUser(null);
            setMustChangePassword(false);
            setShowChangePassword(false);
        } finally {
            setAuthLoading(false);
        }
    }

    useEffect(() => {
        bootstrapAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keepalive heartbeat so other surveyors can see who is online.
    useEffect(() => {
        if (!authUser) return;

        let cancelled = false;
        const ping = async () => {
            try {
                const token = localStorage.getItem('monsurv_auth_token') || '';
                if (!token) return;
                await fetchJson(`${API_BASE_URL}/auth/ping`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch {
                // ignore
            }
        };

        ping();
        const timer = setInterval(() => {
            if (!cancelled) ping();
        }, 30_000);

        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [authUser]);

    // Allow other shells (e.g. Information Portal) to request opening the Change Password modal.
    useEffect(() => {
        if (!authUser) return;
        try {
            const flag = String(localStorage.getItem('monsurv_trigger_change_password') || '').trim();
            if (flag === '1') {
                localStorage.removeItem('monsurv_trigger_change_password');
                setShowChangePassword(true);
                setView('settings');
            }
        } catch {
            // ignore
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser]);

    // Allow other shells (e.g. Information Portal) to request opening the Report Issue modal.
    useEffect(() => {
        if (!authUser) return;
        try {
            const flag = String(localStorage.getItem('monsurv_trigger_report_issue') || '').trim();
            if (flag === '1') {
                localStorage.removeItem('monsurv_trigger_report_issue');
                handleReportIssue();
            }
        } catch {
            // ignore
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser]);

    useEffect(() => {
        if (!authUser) {
            setReportsDueWeek({ total: null, initial: 0, interim: 0 });
            setPriorityTask(null);
            return;
        }

        let cancelled = false;

        function startOfWeek(date) {
            const d = new Date(date);
            const day = (d.getDay() + 6) % 7; // Monday = 0
            d.setHours(0, 0, 0, 0);
            d.setDate(d.getDate() - day);
            return d;
        }

        function computeDaysRemaining(it, now) {
            if (it?.days_remaining != null && Number.isFinite(Number(it.days_remaining))) {
                return Number(it.days_remaining);
            }
            const due = it?.due_at ? new Date(it.due_at) : null;
            if (!due || Number.isNaN(due.getTime())) return Number.POSITIVE_INFINITY;
            return Math.ceil((due.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
        }

        function buildPriorityLabel(it) {
            if (!it) return null;
            const ref = String(it.reference || '').trim();
            if (!ref) return null;
            const type = String(it.type || '').toLowerCase();
            if (type === 'interim') return `${ref} - Interim ${it.interim_number ?? ''}`.trim();
            return `${ref} - Initial`;
        }

        async function loadReportsDueWeek() {
            setReportsDueLoading(true);
            setPriorityTaskLoading(true);
            try {
                const token = localStorage.getItem('monsurv_auth_token') || '';
                const json = await fetchJson(`${API_BASE_URL}/api/sla/tracking?days=30`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` }
                });
                const items = Array.isArray(json?.items) ? json.items : [];
                const start = startOfWeek(new Date());
                const end = new Date(start);
                end.setDate(end.getDate() + 6);
                end.setHours(23, 59, 59, 999);

                let initial = 0;
                let interim = 0;

                items.forEach((it) => {
                    const due = it?.due_at ? new Date(it.due_at) : null;
                    if (!due || Number.isNaN(due.getTime())) return;
                    if (due < start || due > end) return;
                    if (String(it?.type || '').toLowerCase() === 'interim') interim += 1;
                    else initial += 1;
                });

                if (!cancelled) setReportsDueWeek({ total: initial + interim, initial, interim });

                const now = new Date();
                const openItems = items.filter((it) => !it?.completed_at);
                const displayName = String(authDisplayName || '').trim().toLowerCase();
                const assigned = displayName
                    ? openItems.filter((it) => String(it?.lead_surveyor || '').trim().toLowerCase() === displayName)
                    : [];
                const pool = assigned.length ? assigned : openItems;
                pool.sort((a, b) => {
                    const da = computeDaysRemaining(a, now);
                    const db = computeDaysRemaining(b, now);
                    if (da !== db) return da - db;
                    const ar = String(a?.reference || '');
                    const br = String(b?.reference || '');
                    return ar.localeCompare(br);
                });

                if (!cancelled) setPriorityTask(buildPriorityLabel(pool[0]) || null);
            } catch {
                if (!cancelled) {
                    setReportsDueWeek({ total: null, initial: 0, interim: 0 });
                    setPriorityTask(null);
                }
            } finally {
                if (!cancelled) {
                    setReportsDueLoading(false);
                    setPriorityTaskLoading(false);
                }
            }
        }

        loadReportsDueWeek();
        return () => {
            cancelled = true;
        };
    }, [authUser, authDisplayName]);

    useEffect(() => {
        if (!authUser || !overviewDisplay.avgCostPerReport) {
            setAvgCostPerReport(null);
            return;
        }

        let cancelled = false;

        function toDateOnly(d) {
            const dt = d instanceof Date ? d : new Date(d);
            if (Number.isNaN(dt.getTime())) return '';
            return dt.toISOString().slice(0, 10);
        }

        async function loadAverageCost() {
            setAvgCostLoading(true);
            try {
                const now = new Date();
                const start = new Date(now.getFullYear(), now.getMonth(), 1);
                const token = localStorage.getItem('monsurv_auth_token') || '';
                const url = `${API_BASE_URL}/analytics/summary?from=${toDateOnly(start)}&to=${toDateOnly(now)}`;
                const json = await fetchJson(url, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` }
                });
                const avg = Number(json?.avg_cost_per_ai_event ?? 0);
                if (!cancelled) setAvgCostPerReport(Number.isFinite(avg) ? avg : null);
            } catch {
                if (!cancelled) setAvgCostPerReport(null);
            } finally {
                if (!cancelled) setAvgCostLoading(false);
            }
        }

        loadAverageCost();
        return () => {
            cancelled = true;
        };
    }, [authUser, overviewDisplay.avgCostPerReport]);

    useEffect(() => {
        if (!authUser || !overviewDisplay.awsForecastCost || (!isAdmin && !isFinance)) {
            setAwsForecastCost(null);
            setAwsForecastLabel('');
            return;
        }

        let cancelled = false;

        async function loadAwsForecast() {
            setAwsForecastLoading(true);
            try {
                const now = new Date();
                const token = localStorage.getItem('monsurv_auth_token') || '';
                const json = await fetchJson(`${API_BASE_URL}/aws/costs/summary`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` }
                });
                const mtd = Number(json?.month_to_date_total ?? 0);
                const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
                const daysElapsed = Math.max(1, now.getDate());
                const forecast = Number.isFinite(mtd) ? (mtd / daysElapsed) * daysInMonth : null;
                if (!cancelled) {
                    setAwsForecastCost(Number.isFinite(forecast) ? forecast : null);
                    setAwsForecastLabel(new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(now));
                }
            } catch {
                if (!cancelled) {
                    setAwsForecastCost(null);
                    setAwsForecastLabel('');
                }
            } finally {
                if (!cancelled) setAwsForecastLoading(false);
            }
        }

        loadAwsForecast();
        return () => {
            cancelled = true;
        };
    }, [authUser, overviewDisplay.awsForecastCost, isAdmin, isFinance]);

    useEffect(() => {
        if (!authUser || !overviewDisplay.reportsIssuedThisMonth) {
            setReportsIssuedMonth({ total: null, initial: 0, interim: 0 });
            return;
        }

        let cancelled = false;

        function toDateOnly(d) {
            const dt = d instanceof Date ? d : new Date(d);
            if (Number.isNaN(dt.getTime())) return '';
            return dt.toISOString().slice(0, 10);
        }

        async function loadReportsIssuedThisMonth() {
            setReportsIssuedLoading(true);
            try {
                const now = new Date();
                const start = new Date(now.getFullYear(), now.getMonth(), 1);
                const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                end.setHours(23, 59, 59, 999);
                const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
                const token = localStorage.getItem('monsurv_auth_token') || '';
                const url = `${API_BASE_URL}/api/sla/tracking?start=${toDateOnly(start)}&days=${daysInMonth}`;
                const json = await fetchJson(url, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` }
                });
                const items = Array.isArray(json?.items) ? json.items : [];

                let initial = 0;
                let interim = 0;

                items.forEach((it) => {
                    const completed = it?.completed_at ? new Date(it.completed_at) : null;
                    if (!completed || Number.isNaN(completed.getTime())) return;
                    if (completed < start || completed > end) return;
                    if (String(it?.type || '').toLowerCase() === 'interim') interim += 1;
                    else initial += 1;
                });

                if (!cancelled) setReportsIssuedMonth({ total: initial + interim, initial, interim });
            } catch {
                if (!cancelled) setReportsIssuedMonth({ total: null, initial: 0, interim: 0 });
            } finally {
                if (!cancelled) setReportsIssuedLoading(false);
            }
        }

        loadReportsIssuedThisMonth();
        return () => {
            cancelled = true;
        };
    }, [authUser, overviewDisplay.reportsIssuedThisMonth]);

    useEffect(() => {
        if (!authUser) {
            setUpcomingDeadlines([]);
            setPriorityActions([]);
            setWorkloadItems([]);
            setWorkloadSummary({ overdue: 0, dueSoon: 0, onTrack: 0, late: 0, focusCount: 0 });
            setOverviewInsightsLoading(false);
            return;
        }

        let cancelled = false;

        function computeDueDays(item, now) {
            const due = item?.due_at ? new Date(item.due_at) : null;
            if (!due || Number.isNaN(due.getTime())) return Number.POSITIVE_INFINITY;
            const dueDay = startOfLocalDay(due);
            const nowDay = startOfLocalDay(now);
            return Math.round((dueDay.getTime() - nowDay.getTime()) / 86400000);
        }

        async function loadOverviewInsights() {
            setOverviewInsightsLoading(true);
            try {
                const token = localStorage.getItem('monsurv_auth_token') || '';
                const requestOptions = {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` }
                };
                const json = await fetchJson(`${API_BASE_URL}/api/sla/tracking?days=45`, requestOptions);
                let actionState = { actions: [], users: [] };
                try {
                    actionState = await fetchJson(`${API_BASE_URL}/api/dashboard/priority-actions`, requestOptions);
                } catch {
                    // Keep the SLA overview available while action storage is unavailable.
                }

                const items = Array.isArray(json?.items) ? json.items : [];
                const actionByKey = new Map((Array.isArray(actionState?.actions) ? actionState.actions : []).map((action) => [action.key, action]));
                const actionItems = items
                    .map((item) => {
                        const key = `${String(item?.reference || '').trim()}::${String(item?.report_instance || '').trim()}`;
                        const action = actionByKey.get(key) || {};
                        return {
                            ...item,
                            priority_flag: Boolean(action.priority),
                            workflow_completed: Boolean(action.completed),
                            delegated_by: action.delegated_to_me ? String(action.delegated_by || '').trim() : ''
                        };
                    })
                    .filter((item) => {
                        const key = `${String(item?.reference || '').trim()}::${String(item?.report_instance || '').trim()}`;
                        const action = actionByKey.get(key) || {};
                        return !action.dismissed && !(action.delegated_from_me && !action.delegated_to_me);
                    });
                const actionRepository = buildActionRepository({ slaItems: items, actionState, authUser });
                const openItems = actionItems.filter((item) => !item?.completed_at && !item?.workflow_completed);
                const displayName = String(authDisplayName || '').trim().toLowerCase();
                const assignedItems = displayName
                    ? openItems.filter((item) => String(item?.lead_surveyor || '').trim().toLowerCase() === displayName)
                    : [];
                const delegatedItems = openItems.filter((item) => String(item?.delegated_by || '').trim() !== '');
                const focusItems = assignedItems.length
                    ? [...new Map([...assignedItems, ...delegatedItems].map((item) => [`${item.reference}::${item.report_instance}`, item])).values()]
                    : openItems;
                const now = new Date();

                const upcoming = focusItems
                    .filter((item) => item?.due_at)
                    .sort((a, b) => {
                        const ad = new Date(a.due_at).getTime();
                        const bd = new Date(b.due_at).getTime();
                        return ad - bd;
                    })
                    .slice(0, 5);

                const priorityItems = actionRepository.myActions
                    .filter((item) => {
                        const status = String(item?.status || '').toLowerCase();
                        const dueDays = computeDueDays(item, now);
                        return status !== 'completed' && (!item?.due_at || (Number.isFinite(dueDays) && dueDays <= 7));
                    })
                    .sort((a, b) => computeDueDays(a, now) - computeDueDays(b, now));

                const summary = {
                    overdue: openItems.filter((item) => String(item?.status || '').toLowerCase() === 'overdue').length,
                    dueSoon: openItems.filter((item) => String(item?.status || '').toLowerCase() === 'due_soon').length,
                    onTrack: openItems.filter((item) => String(item?.status || '').toLowerCase() === 'on_track').length,
                    late: openItems.filter((item) => String(item?.status || '').toLowerCase() === 'late').length,
                    focusCount: focusItems.length
                };

                if (!cancelled) {
                    setPriorityActionUsers(Array.isArray(actionState?.users) ? actionState.users : []);
                    setUpcomingDeadlines(upcoming);
                    setPriorityActions(priorityItems);
                    setWorkloadItems(openItems);
                    setWorkloadSummary(summary);
                }
            } catch {
                if (!cancelled) {
                    setUpcomingDeadlines([]);
                    setPriorityActions([]);
                    setWorkloadItems([]);
                    setWorkloadSummary({ overdue: 0, dueSoon: 0, onTrack: 0, late: 0, focusCount: 0 });
                }
            } finally {
                if (!cancelled) setOverviewInsightsLoading(false);
            }
        }

        loadOverviewInsights();
        return () => {
            cancelled = true;
        };
    }, [authUser, authDisplayName, isAdmin, openIssueReportsCount, priorityActionRefresh]);

    useEffect(() => {
        if (!authUser || !overviewDisplay.openIssueReports || !isAdmin) {
            setOpenIssueReportsCount(null);
            setOpenIssueReportsLoading(false);
            return;
        }

        let cancelled = false;

        async function loadOpenIssuesCount() {
            setOpenIssueReportsLoading(true);
            try {
                const token = localStorage.getItem('monsurv_auth_token') || '';
                if (!token) throw new Error('Not authenticated');

                const json = await fetchJson(`${API_BASE_URL}/admin/issue-reports/count?status=open`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` }
                });
                const n = Number(json?.count);
                if (!cancelled) setOpenIssueReportsCount(Number.isFinite(n) ? n : 0);
            } catch {
                if (!cancelled) setOpenIssueReportsCount(null);
            } finally {
                if (!cancelled) setOpenIssueReportsLoading(false);
            }
        }

        loadOpenIssuesCount();
        return () => {
            cancelled = true;
        };
    }, [authUser, overviewDisplay.openIssueReports, isAdmin]);

    // Keep completed-project state in sync so counts match the Projects/Instructions "Existing List".
    useEffect(() => {
        function refreshCompleted() {
            try {
                const raw = localStorage.getItem(completedProjectsStorageKey);
                const parsed = raw ? JSON.parse(raw) : {};
                setCompletedByRef(parsed && typeof parsed === 'object' ? parsed : {});
            } catch {
                setCompletedByRef({});
            }
        }

        refreshCompleted();
        window.addEventListener('focus', refreshCompleted);
        window.addEventListener('storage', (e) => {
            if (e && e.key === completedProjectsStorageKey) refreshCompleted();
        });
        return () => {
            window.removeEventListener('focus', refreshCompleted);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completedProjectsStorageKey]);

    // Active Projects count should reflect the SQL-backed Existing List (not stale localStorage).
    useEffect(() => {
        if (!authUser) {
            setProjectsCount(0);
            return;
        }

        let cancelled = false;

        async function loadProjectsCount() {
            try {
                const data = await fetchJson(`${API_BASE_URL}/key_project_data`, { method: 'GET' });
                const rows = Array.isArray(data) ? data : [];
                const active = rows.filter((p) => {
                    const ref = String(p?.reference || '').trim();
                    if (!ref) return false;
                    return !completedByRef?.[ref];
                });
                if (!cancelled) setProjectsCount(active.length);
            } catch {
                if (!cancelled) setProjectsCount(0);
            }
        }

        loadProjectsCount();
        return () => {
            cancelled = true;
        };
    }, [authUser, completedByRef]);

    async function handleLogin({ username, password }) {
        setAuthError('');
        setAuthLoading(true);
        try {
            try { localStorage.removeItem('monsurv_auth_token'); } catch { }
            const trustedDeviceId = getOrCreateTrustedMfaDeviceId();
            const trustedDeviceToken = getTrustedMfaDeviceToken(username);
            const res = await fetchJson(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password, trustedDeviceToken, trustedDeviceId })
            });

            if (res?.mfaRequired) {
                const t = String(res?.mfaToken || '').trim();
                if (!t) throw new Error('MFA required but no challenge token returned');
                setMfaRequired(true);
                setMfaToken(t);
                setMfaLoginUser(res?.user || null);
                setAuthUser(null);
                setMustChangePassword(false);
                setShowChangePassword(false);
                return;
            }

            const token = String(res?.token || '').trim();
            if (!token) throw new Error('Login failed');
            try { localStorage.setItem('monsurv_auth_token', token); } catch { }
            // Hydrate from /auth/me so header/sidebar have email + profile photo etc.
            await bootstrapAuth();
            setPwForm({ temporaryPassword: '', newPassword: '', confirmNewPassword: '' });
            setPwError('');
            setView('overview');
        } catch (e) {
            try { localStorage.removeItem('monsurv_auth_token'); } catch { }
            setAuthUser(null);
            setMustChangePassword(false);
            setShowChangePassword(false);
            setMfaRequired(false);
            setMfaToken('');
            setMfaLoginUser(null);
            setAuthError(getUserFacingAuthError(e, 'We could not complete sign-in. Please try again.'));
        } finally {
            setAuthLoading(false);
        }
    }

    async function handleMfaComplete({ code, trustDevice }) {
        setAuthError('');
        setAuthLoading(true);
        try {
            const trustedDeviceId = getOrCreateTrustedMfaDeviceId();
            const res = await fetchJson(`${API_BASE_URL}/auth/mfa/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mfaToken, code, trustDevice: Boolean(trustDevice), trustedDeviceId })
            });

            const token = String(res?.token || '').trim();
            if (!token) throw new Error('MFA verification failed');
            try { localStorage.setItem('monsurv_auth_token', token); } catch { }
            if (trustDevice && mfaLoginUser?.username) {
                setTrustedMfaDeviceToken(mfaLoginUser.username, String(res?.trustedDeviceToken || '').trim());
            }

            setMfaRequired(false);
            setMfaToken('');
            setMfaLoginUser(null);

            // Hydrate from /auth/me so header/sidebar have email + profile photo etc.
            await bootstrapAuth();
            setPwForm({ temporaryPassword: '', newPassword: '', confirmNewPassword: '' });
            setPwError('');
            setView('overview');
        } catch (e) {
            setAuthError(getUserFacingAuthError(e, 'We could not verify that code. Check your authenticator app and try again.'));
        } finally {
            setAuthLoading(false);
        }
    }

    function handleMfaCancel() {
        setMfaRequired(false);
        setMfaToken('');
        setMfaLoginUser(null);
    }

    async function submitPasswordChange(e) {
        e.preventDefault();
        setPwError('');
        setPwSaving(true);
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            if (!token) throw new Error('Not authenticated');

            await fetchJson(`${API_BASE_URL}/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    temporaryPassword: pwForm.temporaryPassword,
                    newPassword: pwForm.newPassword,
                    confirmNewPassword: pwForm.confirmNewPassword
                })
            });

            setMustChangePassword(false);
            setShowChangePassword(false);
            setPwForm({ temporaryPassword: '', newPassword: '', confirmNewPassword: '' });
        } catch (e2) {
            setPwError(e2?.message || 'Unable to change password');
        } finally {
            setPwSaving(false);
        }
    }

    async function signOut(options = {}) {
        const shouldKeepAuthError = Boolean(options?.keepAuthError);
        const nextAuthError = typeof options?.nextAuthError === 'string' ? options.nextAuthError : '';
        let revocationWarning = '';
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            if (String(token).trim()) {
                const response = await fetch(`${API_BASE_URL}/auth/logout`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    signal: AbortSignal.timeout(10000)
                }).catch(() => null);
                const result = response?.ok ? await response.json().catch(() => null) : null;
                if (!result || result.voice_access_revoked === false || result.voice_termination_confirmed === false) {
                    revocationWarning = 'Signed out locally. Remote voice shutdown could not be confirmed.';
                }
            }
        } catch {
            // ignore
        }
        try { localStorage.removeItem('monsurv_auth_token'); } catch { }
        setIdleWarningOpen(false);
        setIdleWarningSeconds(0);
        setAuthUser(null);
        setAuthError(shouldKeepAuthError ? nextAuthError : revocationWarning);
        setMustChangePassword(false);
        setShowChangePassword(false);
        setView('overview');
    }

    async function loadCurrentSessionPolicy({ silent } = {}) {
        if (!authUser) {
            setSessionPolicy(DEFAULT_SESSION_POLICY);
            return DEFAULT_SESSION_POLICY;
        }

        setSessionPolicyLoading(true);
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            const json = await fetchJson(`${API_BASE_URL}/auth/session-policy`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` }
            });
            const settings = json?.settings || {};
            const next = {
                require_mfa_for_admins: Boolean(settings.require_mfa_for_admins),
                session_timeout_minutes: settings.session_timeout_minutes == null ? DEFAULT_SESSION_POLICY.session_timeout_minutes : Number(settings.session_timeout_minutes),
                idle_timeout_minutes: settings.idle_timeout_minutes == null ? DEFAULT_SESSION_POLICY.idle_timeout_minutes : Number(settings.idle_timeout_minutes),
                password_min_length: settings.password_min_length == null ? DEFAULT_SESSION_POLICY.password_min_length : Number(settings.password_min_length)
            };
            setSessionPolicy(next);
            return next;
        } catch (e) {
            if (!silent) console.warn('loadCurrentSessionPolicy failed:', e);
            setSessionPolicy(DEFAULT_SESSION_POLICY);
            return DEFAULT_SESSION_POLICY;
        } finally {
            setSessionPolicyLoading(false);
        }
    }

    useEffect(() => {
        if (!authUser) {
            setSessionPolicy(DEFAULT_SESSION_POLICY);
            setIdleWarningOpen(false);
            setIdleWarningSeconds(0);
            return undefined;
        }

        let cancelled = false;

        (async () => {
            const next = await loadCurrentSessionPolicy({ silent: true });
            if (cancelled) return;
            setSessionPolicy(next);
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser]);

    useEffect(() => {
        if (!authUser) return undefined;

        let warningTimeoutId = null;
        let signOutTimeoutId = null;
        let countdownIntervalId = null;

        const clearTimers = () => {
            if (warningTimeoutId) window.clearTimeout(warningTimeoutId);
            if (signOutTimeoutId) window.clearTimeout(signOutTimeoutId);
            if (countdownIntervalId) window.clearInterval(countdownIntervalId);
            warningTimeoutId = null;
            signOutTimeoutId = null;
            countdownIntervalId = null;
        };

        const readTimeoutMs = (policyOverride) => {
            const policy = policyOverride && typeof policyOverride === 'object' ? policyOverride : sessionPolicy;
            const rawTimeout = Number(policy?.idle_timeout_minutes ?? DEFAULT_SESSION_POLICY.idle_timeout_minutes);
            if (!Number.isFinite(rawTimeout) || rawTimeout <= 0) return 0;
            return rawTimeout * 60 * 1000;
        };

        const armTimeout = (policyOverride) => {
            const timeoutMs = readTimeoutMs(policyOverride);
            clearTimers();
            setIdleWarningOpen(false);
            setIdleWarningSeconds(0);
            if (!timeoutMs) return;

            const signOutAtMs = Date.now() + timeoutMs;
            const warningLeadMs = getIdleWarningLeadMs(timeoutMs);
            const warningDelayMs = Math.max(0, timeoutMs - warningLeadMs);

            warningTimeoutId = window.setTimeout(() => {
                const syncCountdown = () => {
                    const remainingSeconds = Math.max(0, Math.ceil((signOutAtMs - Date.now()) / 1000));
                    setIdleWarningSeconds(remainingSeconds);
                    if (remainingSeconds <= 0 && countdownIntervalId) {
                        window.clearInterval(countdownIntervalId);
                        countdownIntervalId = null;
                    }
                };

                setIdleWarningOpen(true);
                syncCountdown();
                countdownIntervalId = window.setInterval(syncCountdown, 1000);
            }, warningDelayMs);

            signOutTimeoutId = window.setTimeout(() => {
                clearTimers();
                setIdleWarningOpen(false);
                setIdleWarningSeconds(0);
                signOut({ keepAuthError: true, nextAuthError: 'You were signed out due to inactivity.' });
            }, timeoutMs);
        };

        resetInactivityTimerRef.current = (policyOverride) => {
            armTimeout(policyOverride);
        };

        const handleActivity = () => {
            armTimeout();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') armTimeout();
        };

        const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
        events.forEach((eventName) => window.addEventListener(eventName, handleActivity, true));
        document.addEventListener('visibilitychange', handleVisibilityChange);
        armTimeout();

        return () => {
            clearTimers();
            events.forEach((eventName) => window.removeEventListener(eventName, handleActivity, true));
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            resetInactivityTimerRef.current = () => { };
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser, sessionPolicy?.idle_timeout_minutes]);

    function keepSessionAlive() {
        setIdleWarningOpen(false);
        setIdleWarningSeconds(0);
        resetInactivityTimerRef.current();
    }

    function openSettingsSection(sectionId) {
        const id = String(sectionId || '').trim();
        if (!id) {
            setView('settings');
            return;
        }
        try {
            localStorage.setItem(`monsurv_settings_section_${id}_open`, '1');
        } catch {
            // ignore
        }
        setView('settings');
        setTimeout(() => {
            try {
                window.dispatchEvent(new CustomEvent('monsurv_open_settings_section', { detail: { id } }));
            } catch {
                // ignore
            }
            try {
                const el = document.getElementById(`settings-section-${id}`);
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            } catch {
                // ignore
            }
        }, 60);
    }

    // Fetch key_project_data addresses for the map
    useEffect(() => {
        if (!authUser) {
            setProjectLocations([]);
            return;
        }
        let cancelled = false;

        (async () => {
            try {
                const data = await fetchJson(`${API_BASE_URL}/key_project_data`, { method: 'GET' });
                const rows = Array.isArray(data) ? data : [];
                const locations = rows.map((p) => ({
                    reference: p.reference,
                    name: p.project_name,
                    address: p.site_address,
                    lat: p.site_latitude ?? p.site_lat ?? p.latitude ?? p.lat ?? null,
                    lng: p.site_longitude ?? p.site_lng ?? p.longitude ?? p.lng ?? p.lon ?? null
                }));
                if (!cancelled) setProjectLocations(locations);
            } catch {
                if (!cancelled) setProjectLocations([]);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [authUser]);

    // Compute total fees for the current month using Financials schedule rules.
    useEffect(() => {
        if (!authUser) {
            setCurrentMonthFees(null);
            setFeeForecastYear(null);
            return;
        }
        let cancelled = false;

        async function loadMonthFees() {
            try {
                const raw = await fetchJson(`${API_BASE_URL}/key_project_data`, { method: 'GET' });
                const baseProjects = Array.isArray(raw) ? raw : [];

                const projects = baseProjects
                    .map((p) => ({
                        reference: p.reference ?? '',
                        instructionDate: p.instruction_date ?? null,
                        initialReportFee: p.initial_report_fee ?? null,
                        interimReportFee: p.interim_report_fee ?? null,
                        startOnSiteDate: p.works_start_month ?? null,
                        durationMonths: p.project_duration ?? null
                    }))
                    .filter((p) => String(p.reference || '').trim() !== '');

                const enriched = await Promise.all(projects.map(async (p) => {
                    const ref = String(p.reference || '').trim();
                    try {
                        const irUrl = `${API_BASE_URL}/project/${encodeURIComponent(ref)}/initial-report?mode=surveyor`;
                        const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
                        const headers = token ? { Authorization: `Bearer ${token}` } : {};
                        const ir = await fetchJson(irUrl, { method: 'GET', headers });
                        const programmeRow = ir?.tables?.ini_section_8_programme?.[0] || null;
                        if (!programmeRow) return p;
                        return {
                            ...p,
                            startOnSiteDate: programmeRow?.start_date || p.startOnSiteDate,
                            durationMonths: (computeProgrammeDurationMonths(programmeRow) ?? p.durationMonths)
                        };
                    } catch (e) {
                        return p;
                    }
                }));

                const totals = computeMonthAndYearInvoiceTotals(enriched, new Date());

                if (!cancelled) {
                    setCurrentMonthFees(totals.currentMonthTotal);
                    setFeeForecastYear(totals.yearTotal);
                }
            } catch (e) {
                if (!cancelled) {
                    setCurrentMonthFees(null);
                    setFeeForecastYear(null);
                }
            }
        }

        loadMonthFees();
        return () => {
            cancelled = true;
        };
    }, [authUser]);

    // NOTE: Removed monsurv_projects localStorage counting for Active Projects.

    useEffect(() => {
        // Legacy cache contained project snapshots (including lead_surveyor) and can cause confusing stale UI.
        // Surveyor assignment should now be driven solely by the Instructions "Choose Surveyor to Assign" system.
        try { localStorage.removeItem('monsurv_projects'); } catch { }
    }, []);

    const drawerExpandedWidth = 280;
    const drawerCollapsedWidth = 72;
    const drawerMobileWidth = '50vw';
    const [isNavPinned, setIsNavPinned] = useState(() => {
        try {
            return localStorage.getItem('monsurv_sidebar_pinned') === '1';
        } catch {
            return false;
        }
    });
    const [isNavHover, setIsNavHover] = useState(false);
    const [isManagementNavOpen, setIsManagementNavOpen] = useState(() => MANAGEMENT_VIEW_KEYS.includes(initialRoute.view));
    const isNavExpanded = Boolean(isNavPinned || (!isMobileNav && isNavHover));
    const navLayoutWidth = isNavExpanded ? drawerExpandedWidth : drawerCollapsedWidth;
    const isManagementView = MANAGEMENT_VIEW_KEYS.includes(view);

    useEffect(() => {
        // Keep global shell elements (e.g. footer) aligned with the nav Drawer.
        // Mirrors the AppBar Toolbar `pl` logic so content doesn't sit under the sidebar.
        const root = document?.documentElement;
        if (!root || !root.style) return;

        const basePadding = 24; // matches .legal-footer horizontal padding
        const pl = authUser && !isMobileNav ? (navLayoutWidth + 16) : basePadding;
        root.style.setProperty('--ms-shell-pl', `${pl}px`);

        return () => {
            try {
                root.style.setProperty('--ms-shell-pl', `${basePadding}px`);
            } catch {
                // ignore
            }
        };
    }, [authUser, isMobileNav, navLayoutWidth]);

    useEffect(() => {
        // Hover expansion is desktop-only; clear hover state on mobile layout.
        if (isMobileNav) setIsNavHover(false);
    }, [isMobileNav]);

    useEffect(() => {
        try {
            localStorage.setItem('monsurv_sidebar_pinned', isNavPinned ? '1' : '0');
        } catch {
            // ignore
        }
    }, [isNavPinned]);

    useEffect(() => {
        try {
            localStorage.setItem(OVERVIEW_DISPLAY_KEY, JSON.stringify(overviewDisplay));
        } catch {
            // ignore
        }
    }, [overviewDisplay]);
    const navItems = useMemo(() => (
        getDashboardNavItems({
            role: authRole,
            portalMode,
            tenantSlug: authUser?.tenant_slug,
            tenantCapabilities: authUser?.tenant_capabilities,
            featureVisibility: authUser?.feature_visibility
        })
    ), [authRole, portalMode, authUser?.tenant_slug, authUser?.tenant_capabilities, authUser?.feature_visibility]);

    useEffect(() => {
        if (!authUser || !navItems.length) return;
        const isAllowed = navItems.some((item) => item.key === view);
        if (!isAllowed) setView(navItems[0].key);
    }, [authUser, navItems, view]);

    useEffect(() => {
        if (isManagementView) setIsManagementNavOpen(true);
        else setIsManagementNavOpen(false);
    }, [isManagementView]);

    const managementNavItems = useMemo(() => (
        navItems.filter((item) => MANAGEMENT_VIEW_KEYS.includes(item.key))
    ), [navItems]);

    const topLevelNavItems = useMemo(() => (
        navItems.filter((item) => !MANAGEMENT_VIEW_KEYS.includes(item.key))
    ), [navItems]);

    const settingsNavIndex = topLevelNavItems.findIndex((item) => item.key === 'settings');
    const navItemsBeforeSettings = settingsNavIndex >= 0 ? topLevelNavItems.slice(0, settingsNavIndex) : topLevelNavItems;
    const navItemsFromSettings = settingsNavIndex >= 0 ? topLevelNavItems.slice(settingsNavIndex) : [];

    const viewTitle = useMemo(() => {
        if (!authUser) return 'Monsurv';
        return navItems.find((i) => i.key === view)?.label || 'Dashboard';
    }, [authUser, navItems, view]);

    function clearReportsDeepLinkParamsFromUrl() {
        // Reports supports deep-link params (from notifications). If they remain in the URL,
        // re-entering Projects can incorrectly reopen the previously-deep-linked project.
        try {
            const url = new URL(window.location.href);
            const params = url.searchParams;

            const keysToClear = [
                'ref',
                'reference',
                'reportLogId',
                'report_log_id',
                'openChanges',
                'open_changes'
            ];

            let changed = false;
            for (const k of keysToClear) {
                if (params.has(k)) {
                    params.delete(k);
                    changed = true;
                }
            }

            if (!changed) return;

            const nextSearch = params.toString();
            const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}${url.hash || ''}`;
            window.history.replaceState({}, document.title, nextUrl);
        } catch {
            // ignore
        }
    }

    function handleNavClick(item) {
        if (typeof item === 'string') {
            const fromKey = navItems.find((navItem) => navItem?.key === item) || { key: item };
            return handleNavClick(fromKey);
        }

        if (item?.external && item?.externalUrl) {
            try {
                window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
            } catch {
                window.location.assign(item.externalUrl);
            }
            if (isMobileNav) setMobileNavOpen(false);
            return;
        }

        const nextView = item?.key;
        if (!nextView) return;
        if (nextView === 'reports') {
            clearReportsDeepLinkParamsFromUrl();
            // Force a remount so "Projects" always shows the list view by default.
            setReportsNavNonce((n) => n + 1);
        }
        setIsManagementNavOpen(MANAGEMENT_VIEW_KEYS.includes(nextView));
        setView(nextView);
        if (isMobileNav) setMobileNavOpen(false);
    }

    function renderNavItem(item, options = {}) {
        const { nested = false } = options;
        const selected = view === item.key;
        return (
            <ListItemButton
                key={item.key}
                selected={selected}
                onClick={() => handleNavClick(item)}
                sx={{
                    px: isNavExpanded ? (nested ? 2.15 : 1.25) : 0.75,
                    py: nested ? 0.85 : 1.1,
                    ml: isNavExpanded && nested ? 1.25 : 0,
                    borderRadius: 2,
                    justifyContent: isNavExpanded ? 'flex-start' : 'center',
                    alignItems: 'center',
                    width: '100%',
                    '&.Mui-selected': {
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.22),
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.20 : 0.26) },
                    },
                }}
                aria-label={item.label}
            >
                <ListItemIcon
                    sx={{
                        minWidth: 36,
                        width: 36,
                        flexShrink: 0,
                        mr: isNavExpanded ? 1 : 0,
                        color: selected ? theme.palette.primary.main : 'inherit',
                        display: 'flex',
                        justifyContent: 'center',
                        transition: 'margin-right 180ms ease'
                    }}
                >
                    {item.icon}
                </ListItemIcon>
                <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                        noWrap: true,
                        fontWeight: selected ? 800 : 650,
                        fontSize: nested ? 13 : 14,
                        color: selected ? 'inherit' : alpha('#e6eef8', theme.palette.mode === 'dark' ? 0.85 : 0.82)
                    }}
                    sx={{
                        opacity: isNavExpanded ? 1 : 0,
                        maxWidth: isNavExpanded ? 220 : 0,
                        minWidth: 0,
                        transform: isNavExpanded ? 'translateX(0)' : 'translateX(-6px)',
                        overflow: 'hidden',
                        transition: 'opacity 150ms ease, max-width 180ms ease, transform 180ms ease'
                    }}
                />
            </ListItemButton>
        );
    }

    const drawer = (
        <Box
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            onMouseEnter={() => setIsNavHover(true)}
            onMouseLeave={() => setIsNavHover(false)}
        >
            <Box sx={{ px: isNavExpanded ? 2 : 1.25, pt: 2, pb: 1.5, transition: 'padding 180ms ease' }}>
                <Paper
                    sx={{
                        p: isNavExpanded ? 2 : 1.25,
                        borderRadius: 3,
                        bgcolor: alpha('#0b1220', theme.palette.mode === 'dark' ? 0.35 : 0.25),
                        borderColor: alpha('#e6eef8', 0.10),
                        transition: 'padding 180ms ease'
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <Typography
                                variant="subtitle2"
                                noWrap
                                sx={{
                                    color: alpha('#e6eef8', 0.7),
                                    letterSpacing: 1.1,
                                    opacity: isNavExpanded ? 1 : 0,
                                    transform: isNavExpanded ? 'translateX(0)' : 'translateX(-6px)',
                                    transition: 'opacity 150ms ease, transform 180ms ease',
                                    pointerEvents: isNavExpanded ? 'auto' : 'none'
                                }}
                            >
                                Monsurv Dashboard
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => setIsNavPinned((v) => !v)}
                            aria-label={isNavPinned ? 'Unpin sidebar' : 'Pin sidebar'}
                            sx={{ color: '#fff' }}
                        >
                            {isNavPinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
                        </IconButton>
                    </Stack>

                    <Stack
                        direction="column"
                        spacing={1.2}
                        alignItems="center"
                        sx={{ mt: isNavExpanded ? 1.5 : 0.5, transition: 'margin-top 180ms ease' }}
                    >
                        <Avatar
                            src={authPhotoSrc || undefined}
                            alt=""
                            sx={{
                                width: 56,
                                height: 56,
                                bgcolor: alpha(theme.palette.primary.main, 0.75),
                                fontWeight: 900,
                            }}
                        >
                            {authInitials}
                        </Avatar>

                        {isNavExpanded ? (
                            <>
                                <Typography
                                    variant="subtitle1"
                                    noWrap
                                    sx={{ fontWeight: 900, lineHeight: 1.1, color: '#ffffff', maxWidth: '100%' }}
                                >
                                    Hi, {authDisplayName || 'User'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    noWrap
                                    sx={{ color: alpha('#e6eef8', 0.7), maxWidth: '100%' }}
                                >
                                    {authCompany}
                                </Typography>
                            </>
                        ) : null}
                    </Stack>
                </Paper>
            </Box>
            <Divider />
            <Box
                sx={{
                    px: isNavExpanded ? 1.25 : 0.75,
                    py: 1.25,
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarGutter: 'stable',
                    transition: 'padding 180ms ease',
                    '&::-webkit-scrollbar': { width: 0, height: 0 }
                }}
            >
                <List disablePadding sx={{ display: 'grid', gap: 0.5 }}>
                    {navItemsBeforeSettings.map((item) => renderNavItem(item))}

                    {managementNavItems.length ? (
                        <>
                            <ListItemButton
                                selected={isManagementView}
                                onClick={() => {
                                    if (isManagementView) return;
                                    setIsManagementNavOpen((open) => !open);
                                }}
                                sx={{
                                    px: isNavExpanded ? 1.25 : 0.75,
                                    py: 1.1,
                                    borderRadius: 2,
                                    justifyContent: isNavExpanded ? 'flex-start' : 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    '&.Mui-selected': {
                                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.22),
                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.20 : 0.26) },
                                    },
                                }}
                                aria-label="Management"
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 36,
                                        width: 36,
                                        flexShrink: 0,
                                        mr: isNavExpanded ? 1 : 0,
                                        color: isManagementView ? theme.palette.primary.main : 'inherit',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        transition: 'margin-right 180ms ease'
                                    }}
                                >
                                    <BusinessCenterIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Management"
                                    primaryTypographyProps={{
                                        noWrap: true,
                                        fontWeight: isManagementView ? 800 : 650,
                                        fontSize: 14,
                                        color: isManagementView ? 'inherit' : alpha('#e6eef8', theme.palette.mode === 'dark' ? 0.85 : 0.82)
                                    }}
                                    sx={{
                                        opacity: isNavExpanded ? 1 : 0,
                                        maxWidth: isNavExpanded ? 180 : 0,
                                        minWidth: 0,
                                        transform: isNavExpanded ? 'translateX(0)' : 'translateX(-6px)',
                                        overflow: 'hidden',
                                        transition: 'opacity 150ms ease, max-width 180ms ease, transform 180ms ease'
                                    }}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: isNavExpanded ? 20 : 0,
                                        opacity: isNavExpanded ? 1 : 0,
                                        overflow: 'hidden',
                                        transition: 'opacity 150ms ease, width 180ms ease, transform 180ms ease',
                                        transform: isManagementNavOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                        color: alpha('#e6eef8', 0.78)
                                    }}
                                >
                                    <ExpandMoreIcon fontSize="small" />
                                </Box>
                            </ListItemButton>

                            <Collapse in={isManagementNavOpen && isNavExpanded} timeout="auto" unmountOnExit>
                                <List disablePadding sx={{ display: 'grid', gap: 0.35, pt: 0.25 }}>
                                    {managementNavItems.map((item) => renderNavItem(item, { nested: true }))}
                                </List>
                            </Collapse>
                        </>
                    ) : null}

                    {navItemsFromSettings.map((item) => renderNavItem(item))}
                </List>
            </Box>
            {['admin', 'surveyor'].includes(String(authUser?.role || '').toLowerCase()) && <Box sx={{
                px: isNavExpanded ? 2 : 1.25, py: 1.5, display: 'flex', justifyContent: 'center'
            }}>
                <VoiceNavLauncher expanded={isNavExpanded} active={voiceActive} open={voiceOpen} onClick={() => {
                    setVoiceOpen(true);
                    if (isMobileNav) setMobileNavOpen(false);
                }} />
            </Box>}
            <Divider />
            <Box sx={{ p: isNavExpanded ? 2 : 1.25, display: 'flex', justifyContent: 'center', transition: 'padding 180ms ease' }}>
                <Paper
                    sx={{
                        p: 1.5,
                        bgcolor: alpha('#0b1220', theme.palette.mode === 'dark' ? 0.35 : 0.25),
                        borderColor: alpha('#e6eef8', 0.10),
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: isNavExpanded ? '100%' : 56,
                        transition: 'width 180ms ease'
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            overflow: 'hidden',
                            maxHeight: isNavExpanded ? 28 : 0,
                            opacity: isNavExpanded ? 1 : 0,
                            mb: isNavExpanded ? 1 : 0,
                            transition: 'max-height 180ms ease, opacity 150ms ease, margin-bottom 180ms ease'
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ justifyContent: 'flex-start' }}>
                            <SmartToyIcon fontSize="small" style={{ color: '#ffffff' }} />
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 900, color: '#ffffff' }}>
                                Ask MonsurvAI
                            </Typography>
                        </Stack>
                    </Box>
                    <Button
                        fullWidth={isNavExpanded}
                        variant="contained"
                        onClick={(e) => {
                            setChatAnchorEl(e.currentTarget);
                            setChatOpen(true);
                        }}
                        sx={{
                            fontWeight: 900,
                            bgcolor: alpha('#ffffff', 0.14),
                            color: '#fff',
                            '&:hover': { bgcolor: alpha('#ffffff', 0.18) },
                            minWidth: 0,
                            px: isNavExpanded ? 2 : 0,
                            py: isNavExpanded ? 1 : 0,
                            borderRadius: isNavExpanded ? 999 : 2,
                            width: isNavExpanded ? '100%' : 40,
                            height: isNavExpanded ? 'auto' : 40,
                            display: 'flex',
                            justifyContent: 'center',
                            alignSelf: isNavExpanded ? 'stretch' : 'center',
                            lineHeight: 1,
                            transition: 'width 180ms ease, height 180ms ease, padding 180ms ease, border-radius 180ms ease, background-color 150ms ease',
                            '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0 }
                        }}
                    >
                        <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    opacity: isNavExpanded ? 0 : 1,
                                    transition: 'opacity 150ms ease',
                                    pointerEvents: 'none'
                                }}
                            >
                                <SmartToyIcon fontSize="small" />
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    opacity: isNavExpanded ? 1 : 0,
                                    transition: 'opacity 150ms ease',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                Start Chat
                            </Box>
                        </Box>
                    </Button>
                    <Button
                        fullWidth={isNavExpanded}
                        variant="outlined"
                        onClick={handleOpenDeveloperSettings}
                        sx={{
                            mt: 1,
                            fontWeight: 800,
                            color: '#fff',
                            borderColor: alpha('#ffffff', 0.4),
                            '&:hover': { borderColor: alpha('#ffffff', 0.7), bgcolor: alpha('#ffffff', 0.08) },
                            minWidth: 0,
                            px: isNavExpanded ? 2 : 0,
                            py: isNavExpanded ? 1 : 0,
                            borderRadius: isNavExpanded ? 999 : 2,
                            width: isNavExpanded ? '100%' : 40,
                            height: isNavExpanded ? 'auto' : 40,
                            display: canOpenDeveloperSettings ? 'flex' : 'none',
                            justifyContent: 'center',
                            alignSelf: isNavExpanded ? 'stretch' : 'center',
                            lineHeight: 1,
                            transition: 'width 180ms ease, height 180ms ease, padding 180ms ease, border-radius 180ms ease, background-color 150ms ease, border-color 150ms ease',
                            '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0 }
                        }}
                    >
                        <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    opacity: isNavExpanded ? 0 : 1,
                                    transition: 'opacity 150ms ease',
                                    pointerEvents: 'none'
                                }}
                            >
                                <AdminPanelSettingsIcon fontSize="small" />
                            </Box>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{
                                    opacity: isNavExpanded ? 1 : 0,
                                    transition: 'opacity 150ms ease',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <AdminPanelSettingsIcon fontSize="small" />
                                <Box component="span">Developer Settings</Box>
                            </Stack>
                        </Box>
                    </Button>
                    <Button
                        fullWidth={isNavExpanded}
                        variant="outlined"
                        onClick={handleReportIssue}
                        sx={{
                            mt: 1,
                            fontWeight: 800,
                            color: '#fff',
                            borderColor: alpha('#ffffff', 0.4),
                            '&:hover': { borderColor: alpha('#ffffff', 0.7), bgcolor: alpha('#ffffff', 0.08) },
                            minWidth: 0,
                            px: isNavExpanded ? 2 : 0,
                            py: isNavExpanded ? 1 : 0,
                            borderRadius: isNavExpanded ? 999 : 2,
                            width: isNavExpanded ? '100%' : 40,
                            height: isNavExpanded ? 'auto' : 40,
                            display: 'flex',
                            justifyContent: 'center',
                            alignSelf: isNavExpanded ? 'stretch' : 'center',
                            lineHeight: 1,
                            transition: 'width 180ms ease, height 180ms ease, padding 180ms ease, border-radius 180ms ease, background-color 150ms ease, border-color 150ms ease',
                            '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0 }
                        }}
                    >
                        <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    opacity: isNavExpanded ? 0 : 1,
                                    transition: 'opacity 150ms ease',
                                    pointerEvents: 'none'
                                }}
                            >
                                <ConstructionIcon fontSize="small" />
                            </Box>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{
                                    opacity: isNavExpanded ? 1 : 0,
                                    transition: 'opacity 150ms ease',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <ConstructionIcon fontSize="small" />
                                <Box component="span">Report An Issue</Box>
                            </Stack>
                        </Box>
                    </Button>
                </Paper>
            </Box>
        </Box>
    );

    const drawerMobile = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 1.25, pt: 1.25, pb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Avatar
                        src={authPhotoSrc || undefined}
                        alt=""
                        sx={{
                            width: 34,
                            height: 34,
                            bgcolor: alpha(theme.palette.primary.main, 0.75),
                            fontWeight: 900,
                            // If no profile photo (e.g. shows initials like "AD"), shrink text for comfortable fit.
                            fontSize: authPhotoSrc ? undefined : '0.72rem',
                        }}
                    >
                        {authInitials}
                    </Avatar>
                    <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ fontWeight: 900, minWidth: 0, color: '#ffffff' }}
                        title={authDisplayName || 'User'}
                    >
                        {authDisplayName || 'User'}
                    </Typography>
                </Stack>
            </Box>

            <Divider />

            <Box
                sx={{
                    px: 0.75,
                    py: 0.75,
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarGutter: 'stable',
                    '&::-webkit-scrollbar': { width: 0, height: 0 }
                }}
            >
                <List disablePadding sx={{ display: 'grid', gap: 0.35 }}>
                    {navItems.map((item) => (
                        <ListItemButton
                            key={item.key}
                            selected={view === item.key}
                            onClick={() => handleNavClick(item)}
                            sx={{
                                px: 1,
                                py: 0.55,
                                minHeight: 36,
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                borderRadius: 2,
                                '&.Mui-selected': {
                                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.22),
                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.20 : 0.26) },
                                },
                            }}
                            aria-label={item.label}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 30,
                                    mr: 0.75,
                                    color: view === item.key ? theme.palette.primary.main : 'inherit',
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    transform: 'scale(0.85)',
                                    transformOrigin: 'left center'
                                }}
                            >
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    fontWeight: view === item.key ? 800 : 650,
                                    fontSize: '0.84rem',
                                    noWrap: true,
                                    color: view === item.key ? 'inherit' : alpha('#e6eef8', theme.palette.mode === 'dark' ? 0.85 : 0.82)
                                }}
                                sx={{ m: 0 }}
                            />
                        </ListItemButton>
                    ))}
                </List>
            </Box>

            {['admin', 'surveyor'].includes(String(authUser?.role || '').toLowerCase()) && <Box sx={{ px: 0.75, py: 1 }}>
                <VoiceNavLauncher active={voiceActive} open={voiceOpen} onClick={() => { setVoiceOpen(true); setMobileNavOpen(false); }} />
            </Box>}
            <Divider />

            <Box sx={{ px: 0.75, py: 0.75, display: 'grid', gap: 0.35 }}>
                <ListItemButton
                    onClick={(e) => {
                        setChatAnchorEl(e.currentTarget);
                        setChatOpen(true);
                    }}
                    sx={{
                        px: 1,
                        py: 0.55,
                        minHeight: 36,
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        borderRadius: 2,
                        bgcolor: alpha('#ffffff', theme.palette.mode === 'dark' ? 0.06 : 0.08)
                    }}
                    aria-label="Ask MonsurvAI"
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 30,
                            mr: 0.75,
                            display: 'flex',
                            justifyContent: 'flex-start',
                            transform: 'scale(0.85)',
                            transformOrigin: 'left center'
                        }}
                    >
                        <SmartToyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="MonsurvAI"
                        primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 500, noWrap: true }}
                        sx={{ m: 0 }}
                    />
                </ListItemButton>

                <ListItemButton
                    onClick={handleOpenDeveloperSettings}
                    sx={{
                        px: 1,
                        py: 0.55,
                        minHeight: 36,
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        borderRadius: 2,
                        border: `1px solid ${alpha('#ffffff', 0.28)}`,
                        display: canOpenDeveloperSettings ? 'flex' : 'none'
                    }}
                    aria-label="Developer Settings"
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 30,
                            mr: 0.75,
                            display: 'flex',
                            justifyContent: 'flex-start',
                            transform: 'scale(0.85)',
                            transformOrigin: 'left center'
                        }}
                    >
                        <AdminPanelSettingsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Developer Settings"
                        primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 500, noWrap: true }}
                        sx={{ m: 0 }}
                    />
                </ListItemButton>

                <ListItemButton
                    onClick={handleReportIssue}
                    sx={{
                        px: 1,
                        py: 0.55,
                        minHeight: 36,
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        borderRadius: 2,
                        border: `1px solid ${alpha('#ffffff', 0.28)}`
                    }}
                    aria-label="Report Issue"
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 30,
                            mr: 0.75,
                            display: 'flex',
                            justifyContent: 'flex-start',
                            transform: 'scale(0.85)',
                            transformOrigin: 'left center'
                        }}
                    >
                        <ConstructionIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Report Issue"
                        primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 500, noWrap: true }}
                        sx={{ m: 0 }}
                    />
                </ListItemButton>
            </Box>
        </Box>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                minHeight: 'calc(var(--app-vh, 1vh) * 100)',
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden'
            }}
        >
            <AppBar
                position="fixed"
                color="transparent"
                elevation={0}
                sx={{
                    borderBottom: `1px solid ${alpha('#ffffff', 0.54)}`,
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(11, 18, 32, 0.8)'
                        : 'rgba(247, 250, 253, 0.76)',
                    backgroundImage: theme.palette.mode === 'dark'
                        ? `linear-gradient(135deg, ${alpha('#0f172a', 0.9)} 0%, ${alpha('#172554', 0.56)} 100%)`
                        : `linear-gradient(135deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#edf4fb', 0.76)} 58%, ${alpha('#dbeafe', 0.46)} 100%)`,
                    backdropFilter: 'blur(18px)',
                    WebkitBackdropFilter: 'blur(18px)',
                    boxShadow: '0 14px 38px rgba(15, 23, 42, 0.08)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        background: `linear-gradient(90deg, ${alpha('#ffffff', 0.46)} 0%, ${alpha('#ffffff', 0)} 48%)`
                    }
                }}
            >
                <Toolbar
                    sx={{
                        minHeight: { xs: 56, sm: 78 },
                        px: { xs: 1, sm: 2.25 },
                        py: { xs: 0.45, sm: 0.8 },
                        gap: { xs: 0.75, sm: 1.75 },
                        pl: authUser
                            ? { md: `${navLayoutWidth + 20}px` }
                            : 0,
                        justifyContent: authUser ? 'flex-start' : 'center',
                        transition: 'padding-left 180ms ease',
                        alignItems: 'center'
                    }}
                >
                    {authUser ? (
                        <IconButton
                            color="inherit"
                            onClick={() => setMobileNavOpen(true)}
                            size="small"
                            sx={{
                                display: { md: 'none' },
                                width: { xs: 28, sm: 36 },
                                height: { xs: 28, sm: 36 }
                            }}
                            aria-label="Open navigation"
                        >
                            <MenuIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
                        </IconButton>
                    ) : null}
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: authUser ? 'flex-start' : 'center'
                        }}
                    >
                        <Box
                            sx={{
                                minWidth: 0,
                                display: 'grid',
                                gap: { xs: 0.05, sm: 0.28 },
                                justifyItems: authUser ? 'start' : 'center'
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 900,
                                    lineHeight: 1.02,
                                    letterSpacing: '-0.03em',
                                    color: 'text.primary',
                                    fontSize: { xs: '1rem', sm: '1.38rem' }
                                }}
                                noWrap
                            >
                                {viewTitle}
                            </Typography>
                        </Box>
                    </Box>

                    {authUser ? (
                        <Stack direction="row" alignItems="center" sx={{ ml: { xs: 0.25, sm: 1.1 }, gap: { xs: 0.5, sm: 1 } }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 0.45, sm: 0.9 },
                                    pl: { xs: 0.45, sm: 0.75 },
                                    pr: { xs: 0.45, sm: 0.55 },
                                    py: { xs: 0.35, sm: 0.45 },
                                    borderRadius: 999,
                                    border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#e2e8f0', 0.12) : alpha('#ffffff', 0.62)}`,
                                    background: theme.palette.mode === 'dark'
                                        ? `linear-gradient(180deg, ${alpha('#0f172a', 0.84)} 0%, ${alpha('#162033', 0.78)} 100%)`
                                        : `linear-gradient(180deg, ${alpha('#ffffff', 0.82)} 0%, ${alpha('#eff5fb', 0.68)} 100%)`,
                                    boxShadow: theme.palette.mode === 'dark'
                                        ? '0 18px 36px rgba(2, 6, 23, 0.28)'
                                        : '0 16px 34px rgba(15, 23, 42, 0.1)',
                                    backdropFilter: 'blur(16px)',
                                    WebkitBackdropFilter: 'blur(16px)'
                                }}
                            >
                                <NotificationBell compact={isPhoneHeader} variant="header" />
                                <Box
                                    sx={{
                                        display: { xs: 'none', sm: 'block' },
                                        width: 1,
                                        alignSelf: 'stretch',
                                        backgroundColor: theme.palette.mode === 'dark' ? alpha('#e2e8f0', 0.12) : alpha('#0f172a', 0.1)
                                    }}
                                />
                                <AccountMenu
                                    displayName={authDisplayName}
                                    position={authPosition}
                                    role={authRole}
                                    profilePhotoUrl={authUser?.profile_photo_url || null}
                                    compact={isPhoneHeader}
                                    chromeVariant="header"
                                    onOpenSettingsSection={openSettingsSection}
                                    onChangePassword={() => setShowChangePassword(true)}
                                    onSignOut={signOut}
                                />
                            </Box>
                        </Stack>
                    ) : null}
                </Toolbar>
            </AppBar>

            {authUser ? (
                <Box
                    component="nav"
                    sx={{
                        width: { md: navLayoutWidth },
                        flexShrink: { md: 0 },
                        transition: 'width 180ms ease'
                    }}
                    aria-label="Navigation"
                >
                    <Drawer
                        variant="temporary"
                        open={mobileNavOpen}
                        onClose={() => setMobileNavOpen(false)}
                        ModalProps={{ keepMounted: true }}
                        sx={{
                            display: { xs: 'block', md: 'none' },
                            '& .MuiDrawer-paper': {
                                width: drawerMobileWidth,
                                overflowX: 'hidden',
                                overflowY: 'auto'
                            },
                        }}
                    >
                        {drawerMobile}
                    </Drawer>
                    <Drawer
                        variant="permanent"
                        open
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            '& .MuiDrawer-paper': {
                                width: navLayoutWidth,
                                boxSizing: 'border-box',
                                transition: 'width 180ms ease',
                                overflowX: 'hidden',
                                overflowY: 'auto'
                            },
                        }}
                    >
                        {drawer}
                    </Drawer>
                </Box>
            ) : null}

            <ChatAssistantDrawer
                open={chatOpen}
                onClose={() => setChatOpen(false)}
                anchorEl={chatAnchorEl}
                displayName={authDisplayName}
                role={String(authUser?.role || '').trim()}
                projectReference={''}
                reportInstance={'initial'}
                onNavigate={(nav) => {
                    setChatOpen(false);
                    const view = typeof nav === 'string' ? nav : String(nav?.view || '').trim();
                    const section = typeof nav === 'object' && nav ? String(nav?.section || '').trim() : '';
                    const tab = typeof nav === 'object' && nav ? String(nav?.tab || '').trim().toLowerCase() : '';
                    if (!view) return;
                    if (view === 'settings' && section) {
                        openSettingsSection(section);
                        return;
                    }
                    if (view === 'workload') {
                        setAnalyticsTab('workload');
                        handleNavClick('analytics');
                        return;
                    }
                    if (view === 'analytics' && tab) {
                        setAnalyticsTab(tab);
                    }
                    handleNavClick(view);
                }}
            />
            {['admin', 'surveyor'].includes(String(authUser?.role || '').toLowerCase()) && (
                <MonsurvVoicePanel key={String(authUser?.id || authUser?.username || '')} open={voiceOpen}
                    onClose={() => setVoiceOpen(false)} onReopen={() => setVoiceOpen(true)} onActiveChange={setVoiceActive} />
            )}

            <Dialog open={idleWarningOpen} onClose={keepSessionAlive} maxWidth="xs" fullWidth>
                <DialogTitle>Session expiring soon</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={1.25}>
                        <Typography variant="body2">
                            Your dashboard session is about to expire due to inactivity.
                        </Typography>
                        <Alert severity="warning">
                            You will be signed out in {idleWarningSeconds} second{idleWarningSeconds === 1 ? '' : 's'} unless you continue working.
                        </Alert>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => signOut()}>Sign out now</Button>
                    <Button variant="contained" onClick={keepSessionAlive}>Stay signed in</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={issueOpen} onClose={() => setIssueOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Report an Issue</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <TextField
                            fullWidth
                            value={issueTitle}
                            onChange={(e) => setIssueTitle(e.target.value)}
                            placeholder="Title"
                            inputProps={{ maxLength: 200 }}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '6px !important' },
                                '& .MuiOutlinedInput-notchedOutline': { borderRadius: '6px !important' },
                                '& .MuiOutlinedInput-root fieldset': { borderRadius: '6px !important' }
                            }}
                        />
                        <TextField
                            select
                            fullWidth
                            value={issuePriority}
                            onChange={(e) => setIssuePriority(e.target.value)}
                            SelectProps={{ native: true }}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '6px !important' },
                                '& .MuiOutlinedInput-notchedOutline': { borderRadius: '6px !important' },
                                '& .MuiOutlinedInput-root fieldset': { borderRadius: '6px !important' }
                            }}
                        >
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                        </TextField>
                        <TextField
                            fullWidth
                            multiline
                            minRows={5}
                            value={issueDescription}
                            onChange={(e) => setIssueDescription(e.target.value)}
                            placeholder="Describe the Issue"
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '6px !important' },
                                '& .MuiOutlinedInput-notchedOutline': { borderRadius: '6px !important' },
                                '& .MuiOutlinedInput-root fieldset': { borderRadius: '6px !important' }
                            }}
                        />
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                                Upload an Image of the error (optional)
                            </Typography>
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(e) => {
                                    const f = e.target.files && e.target.files[0];
                                    setIssueImage(f || null);
                                }}
                            />
                        </Box>
                        {issueError ? (
                            <Typography variant="body2" sx={{ color: '#b91c1c', fontWeight: 700 }}>
                                {issueError}
                            </Typography>
                        ) : null}
                        {issueSuccess ? (
                            <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 700 }}>
                                {issueSuccess}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIssueOpen(false)} disabled={issueSubmitting}>
                        Close
                    </Button>
                    <Button variant="contained" onClick={submitIssueReport} disabled={issueSubmitting}>
                        {issueSubmitting ? 'Submitting…' : 'Submit Issue Report'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '100%',
                    overflowX: 'hidden',
                    p: { xs: 1.5, sm: 2, md: 3 }
                }}
            >
                <Toolbar />

                {!authUser ? (
                    (() => {
                        const hasToken = (() => {
                            try {
                                return Boolean(String(localStorage.getItem('monsurv_auth_token') || '').trim());
                            } catch {
                                return false;
                            }
                        })();

                        if (authLoading && hasToken) {
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 240, gap: 1.5 }}>
                                    <CircularProgress size={26} />
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 650 }}>
                                        Loading dashboard…
                                    </Typography>
                                </Box>
                            );
                        }

                        return (
                            <LoginPage
                                onLogin={handleLogin}
                                onMfaComplete={handleMfaComplete}
                                onMfaCancel={handleMfaCancel}
                                mfaRequired={mfaRequired}
                                mfaUser={mfaLoginUser}
                                loading={authLoading}
                                error={authError}
                            />
                        );
                    })()
                ) : (
                    <>
                        {view === 'overview' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gap: { xs: 1.25, sm: 2 },
                                        width: '100%',
                                        maxWidth: '100%',
                                        gridTemplateColumns: {
                                            xs: 'repeat(2, minmax(0, 1fr))',
                                            sm: 'repeat(2, minmax(0, 1fr))',
                                            md: `repeat(${overviewMetricDesktopColumns}, minmax(0, 1fr))`
                                        },
                                        alignItems: 'stretch'
                                    }}
                                >
                                    {overviewDisplay.activeProjects ? (
                                        <Paper sx={buildPremiumMetricCardSx(metricAccents.activeProjects)}>
                                            <Typography
                                                variant="caption"
                                                noWrap
                                                sx={premiumMetricLabelSx}
                                            >
                                                Active Projects
                                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                                    {' '}(Existing List)
                                                </Box>
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                noWrap
                                                sx={premiumMetricValueSx}
                                            >
                                                {projectsCount}
                                            </Typography>
                                        </Paper>
                                    ) : null}
                                    {overviewDisplay.monthlyFees ? (
                                        <Paper sx={buildPremiumMetricCardSx(metricAccents.monthlyFees)}>
                                            <Typography
                                                variant="caption"
                                                noWrap
                                                sx={premiumMetricLabelSx}
                                            >
                                                <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                                                    {new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(new Date())} Fees
                                                </Box>
                                                <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                                                    {new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(new Date())} Fees
                                                </Box>
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                noWrap
                                                sx={premiumMetricValueSx}
                                            >
                                                {currentMonthFees === null ? '—' : formatCurrency(currentMonthFees)}
                                            </Typography>
                                        </Paper>
                                    ) : null}
                                    {overviewDisplay.feeForecast ? (
                                        <Paper sx={buildPremiumMetricCardSx(metricAccents.feeForecast)}>
                                            <Typography
                                                variant="caption"
                                                noWrap
                                                sx={premiumMetricLabelSx}
                                            >
                                                Fee Forecast
                                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                                    {' '}({new Date().getFullYear()})
                                                </Box>
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                noWrap
                                                sx={premiumMetricValueSx}
                                            >
                                                {feeForecastYear === null ? '—' : formatCurrency(feeForecastYear)}
                                            </Typography>
                                        </Paper>
                                    ) : null}
                                    {overviewDisplay.reportsDueWeek ? (
                                        <Tooltip
                                            title={reportsDueLoading
                                                ? 'Updating…'
                                                : `Initial ${reportsDueWeek.initial} • Interim ${reportsDueWeek.interim}`}
                                            arrow
                                        >
                                            <Paper sx={buildPremiumMetricCardSx(metricAccents.reportsDueWeek)} aria-label="Report's Due This Week">
                                                <Typography
                                                    variant="caption"
                                                    noWrap
                                                    sx={premiumMetricLabelSx}
                                                >
                                                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                                        Reports Due
                                                    </Box>
                                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                                        Report's Due This Week
                                                    </Box>
                                                </Typography>
                                                <Typography
                                                    variant="h4"
                                                    noWrap
                                                    sx={premiumMetricValueSx}
                                                >
                                                    {reportsDueWeek.total === null ? '—' : reportsDueWeek.total}
                                                </Typography>
                                            </Paper>
                                        </Tooltip>
                                    ) : null}
                                    {overviewDisplay.avgCostPerReport ? (
                                        <Paper sx={buildPremiumMetricCardSx(metricAccents.avgCostPerReport)}>
                                            <Typography
                                                variant="caption"
                                                noWrap
                                                sx={premiumMetricLabelSx}
                                            >
                                                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                                    Avg Cost / Report
                                                </Box>
                                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                                    Average Cost Per Report Generation
                                                </Box>
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                noWrap
                                                sx={premiumMetricValueSx}
                                            >
                                                {avgCostLoading ? 'Updating…' : (avgCostPerReport === null ? '—' : formatCurrency(avgCostPerReport))}
                                            </Typography>
                                        </Paper>
                                    ) : null}
                                    {overviewDisplay.awsForecastCost ? (
                                        <Paper sx={buildPremiumMetricCardSx(metricAccents.awsForecastCost)}>
                                            <Typography
                                                variant="caption"
                                                noWrap
                                                sx={premiumMetricLabelSx}
                                            >
                                                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                                    AWS Forecast
                                                </Box>
                                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                                    AWS Forecast Cost ({awsForecastLabel || new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(new Date())})
                                                </Box>
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                noWrap
                                                sx={premiumMetricValueSx}
                                            >
                                                {awsForecastLoading ? 'Updating…' : (awsForecastCost === null ? '—' : formatCurrency(awsForecastCost))}
                                            </Typography>
                                        </Paper>
                                    ) : null}
                                    {overviewDisplay.priorityTask ? (
                                        <Paper sx={buildPremiumMetricCardSx(metricAccents.priorityTask)}>
                                            <Typography
                                                variant="caption"
                                                noWrap
                                                sx={premiumMetricLabelSx}
                                            >
                                                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                                    Task Priority
                                                </Box>
                                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                                    Report Task Priority
                                                </Box>
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                sx={{
                                                    ...premiumMetricValueSx,
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                {priorityTaskLoading ? 'Updating…' : (priorityTask || '—')}
                                            </Typography>
                                        </Paper>
                                    ) : null}
                                    {overviewDisplay.reportsIssuedThisMonth ? (
                                        <Tooltip
                                            title={reportsIssuedLoading
                                                ? 'Updating…'
                                                : `Initial ${reportsIssuedMonth.initial} • Interim ${reportsIssuedMonth.interim}`}
                                            arrow
                                        >
                                            <Paper sx={buildPremiumMetricCardSx(metricAccents.reportsIssuedThisMonth)} aria-label="Reports Issued This Month">
                                                <Typography
                                                    variant="caption"
                                                    noWrap
                                                    sx={premiumMetricLabelSx}
                                                >
                                                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                                        Reports Issued
                                                    </Box>
                                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                                        Reports Issued This Month
                                                    </Box>
                                                </Typography>
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        ...premiumMetricValueSx,
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    {reportsIssuedMonth.total === null ? '—' : reportsIssuedMonth.total}
                                                </Typography>
                                            </Paper>
                                        </Tooltip>
                                    ) : null}

                                    {overviewDisplay.openIssueReports && isAdmin ? (
                                        <Paper sx={buildPremiumMetricCardSx(metricAccents.openIssueReports)}>
                                            <Typography
                                                variant="caption"
                                                noWrap
                                                sx={premiumMetricLabelSx}
                                            >
                                                Open Issues
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                noWrap
                                                sx={premiumMetricValueSx}
                                            >
                                                {openIssueReportsLoading ? 'Updating…' : (openIssueReportsCount === null ? '—' : openIssueReportsCount)}
                                            </Typography>
                                        </Paper>
                                    ) : null}
                                </Box>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gap: 2,
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            lg: 'minmax(0, 1.15fr) minmax(0, 1fr) minmax(280px, 0.9fr)'
                                        }
                                    }}
                                >
                                    <Paper sx={premiumPanelSx}>
                                        <Box sx={premiumPanelHeaderSx}>
                                            <Typography variant="subtitle1" sx={premiumPanelTitleSx}>
                                                Upcoming Deadlines
                                            </Typography>
                                        </Box>
                                        <Box sx={{ ...premiumPanelBodySx, pt: { xs: 0.9, sm: 1.1 } }}>
                                            <Box sx={{ borderRadius: 4, overflow: 'hidden', border: `1px solid ${alpha('#ffffff', 0.62)}`, background: `linear-gradient(180deg, ${alpha('#ffffff', 0.55)} 0%, ${alpha('#f4f8fc', 0.72)} 100%)`, p: { xs: 1, sm: 1.25 }, display: 'grid', gap: 1 }}>
                                                {overviewInsightsLoading && !upcomingDeadlines.length ? (
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading deadlines…</Typography>
                                                ) : upcomingDeadlines.length ? upcomingDeadlines.map((item) => {
                                                    const status = String(item?.status || '').toLowerCase();
                                                    const tone = status === 'overdue'
                                                        ? { bg: alpha('#ef4444', 0.12), fg: '#b91c1c' }
                                                        : (status === 'due_soon'
                                                            ? { bg: alpha('#f59e0b', 0.14), fg: '#b45309' }
                                                            : { bg: alpha('#2563eb', 0.10), fg: '#1d4ed8' });
                                                    return (
                                                        <Button
                                                            component="a"
                                                            href={buildOverviewPortalUrl(item)}
                                                            key={`${item.reference}-${item.report_instance}`}
                                                            sx={{
                                                                display: 'flex',
                                                                width: '100%',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                gap: 1.25,
                                                                p: 1.1,
                                                                borderRadius: 3,
                                                                background: alpha('#ffffff', 0.74),
                                                                border: `1px solid ${alpha('#0f172a', 0.06)}`,
                                                                color: 'inherit',
                                                                textAlign: 'left',
                                                                textTransform: 'none',
                                                                '&:hover': { background: alpha('#ffffff', 0.98), borderColor: alpha('#2563eb', 0.28) }
                                                            }}
                                                        >
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', lineHeight: 1.15 }}>
                                                                    {String(item.reference || '').trim()} · {formatOverviewReportType(item)}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {item.project_name || item.lead_surveyor || 'Unassigned'}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                                <Typography sx={{ fontWeight: 800, fontSize: '0.88rem' }}>
                                                                    {formatOverviewDueDate(item.due_at)}
                                                                </Typography>
                                                                <Box sx={{ mt: 0.4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 74, px: 1, py: 0.35, borderRadius: 999, background: tone.bg, color: tone.fg, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                    {status.replace('_', ' ')}
                                                                </Box>
                                                            </Box>
                                                        </Button>
                                                    );
                                                }) : (
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No upcoming deadlines in the current window.</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>

                                    <Paper sx={premiumPanelSx}>
                                        <Box sx={{ ...premiumPanelHeaderSx, justifyContent: 'space-between' }}>
                                            <Typography variant="subtitle1" sx={premiumPanelTitleSx}>
                                                Actions for the Week
                                            </Typography>
                                            <Button size="small" variant="outlined" onClick={() => handleNavClick('actions_hub')}>
                                                Action Hub
                                            </Button>
                                        </Box>
                                        <Box sx={{ ...premiumPanelBodySx, pt: { xs: 0.9, sm: 1.1 } }}>
                                            {priorityActionWarning ? (
                                                <Alert severity="warning" onClose={() => setPriorityActionWarning('')} sx={{ mb: 1 }}>
                                                    {priorityActionWarning}
                                                </Alert>
                                            ) : null}
                                            <Box sx={{ borderRadius: 4, overflow: 'hidden', border: `1px solid ${alpha('#ffffff', 0.62)}`, background: `linear-gradient(180deg, ${alpha('#ffffff', 0.55)} 0%, ${alpha('#f4f8fc', 0.72)} 100%)`, p: { xs: 1, sm: 1.25 }, display: 'grid', gap: 1 }}>
                                                {overviewInsightsLoading && !priorityActions.length ? (
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading priorities…</Typography>
                                                ) : priorityActions.length ? priorityActions.map((action) => {
                                                    const status = String(action?.status || '').toLowerCase();
                                                    const tone = status === 'overdue' ? '#dc2626' : (status === 'due_soon' ? '#d97706' : '#2563eb');
                                                    const isConfirmLiveAction = action.action_type === 'confirm_live_projects';
                                                    const actionContext = isConfirmLiveAction
                                                        ? 'System · Project Status'
                                                        : (action.source === 'manual' ? 'Personal Action' : `${String(action.reference || '').trim()} · ${formatOverviewReportType(action)}`);
                                                    const statusLabel = status === 'overdue'
                                                        ? 'Overdue'
                                                        : (status === 'due_soon' ? 'Due soon' : (status === 'due_today' ? 'Due today' : (action.priority_flag ? 'Priority' : 'Open')));
                                                    return (
                                                        <Button
                                                            component={isConfirmLiveAction ? 'button' : 'a'}
                                                            href={isConfirmLiveAction ? undefined : buildOverviewPortalUrl(action)}
                                                            key={action.key || `${action.reference}-${action.report_instance}`}
                                                            onClick={isConfirmLiveAction ? () => setConfirmLiveAction(action) : undefined}
                                                            onContextMenu={isConfirmLiveAction ? undefined : (event) => openPriorityActionMenu(event, action)}
                                                            sx={{ display: 'flex', width: '100%', gap: 1.1, alignItems: 'flex-start', justifyContent: 'flex-start', p: 1.05, borderRadius: 3, background: alpha('#ffffff', 0.74), border: `1px solid ${alpha('#0f172a', 0.06)}`, color: 'inherit', textAlign: 'left', textTransform: 'none', '&:hover': { background: alpha('#ffffff', 0.98), borderColor: alpha(tone, 0.3) } }}
                                                        >
                                                            <Box sx={{ mt: 0.35, width: 10, height: 10, borderRadius: '50%', background: tone, boxShadow: `0 0 0 4px ${alpha(tone, 0.14)}`, flexShrink: 0 }} />
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', lineHeight: 1.2, textAlign: 'left' }}>
                                                                    {action.priority_flag ? <FlagIcon sx={{ fontSize: '0.95rem', verticalAlign: '-0.12em', color: '#dc2626', mr: 0.35 }} /> : null}
                                                                    {String(action.title || action.project_name || action.reference || '').trim() || 'Unnamed action'}{' '}
                                                                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                                        ({actionContext} · {statusLabel}{action.delegated_by ? ` · Delegated by ${action.delegated_by}` : ''})
                                                                    </Box>
                                                                </Typography>
                                                            </Box>
                                                        </Button>
                                                    );
                                                }) : (
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No outstanding actions are due within the next seven days.</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>

                                    <Paper sx={premiumPanelSx}>
                                        <Box sx={premiumPanelHeaderSx}>
                                            <Typography variant="subtitle1" sx={premiumPanelTitleSx}>
                                                Workload Summary
                                            </Typography>
                                        </Box>
                                        <Box sx={{ ...premiumPanelBodySx, pt: { xs: 0.9, sm: 1.1 } }}>
                                            <Box sx={{ borderRadius: 4, overflow: 'hidden', border: `1px solid ${alpha('#ffffff', 0.62)}`, background: `linear-gradient(180deg, ${alpha('#ffffff', 0.55)} 0%, ${alpha('#f4f8fc', 0.72)} 100%)`, p: { xs: 1.1, sm: 1.25 }, display: 'grid', gap: 1.2 }}>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                                                    {[
                                                        { key: 'overdue', label: 'Overdue', value: workloadSummary.overdue, tone: '#dc2626' },
                                                        { key: 'dueSoon', label: 'Due Soon', value: workloadSummary.dueSoon, tone: '#d97706' },
                                                        { key: 'onTrack', label: 'On Track', value: workloadSummary.onTrack, tone: '#2563eb' },
                                                        { key: 'late', label: 'Late', value: workloadSummary.late, tone: '#7c3aed' }
                                                    ].map((stat) => (
                                                        <Button
                                                            key={stat.key}
                                                            onClick={() => setOverviewDetail({
                                                                open: true,
                                                                title: `${stat.label} reports`,
                                                                items: workloadItems.filter((item) => String(item?.status || '').toLowerCase() === stat.key.replace('dueSoon', 'due_soon').replace('onTrack', 'on_track'))
                                                            })}
                                                            sx={{ p: 1.1, borderRadius: 3, background: alpha('#ffffff', 0.74), border: `1px solid ${alpha('#0f172a', 0.06)}`, textAlign: 'center', color: 'inherit', textTransform: 'none', '&:hover': { background: alpha('#ffffff', 0.98), borderColor: alpha(stat.tone, 0.3) } }}
                                                        >
                                                            <Typography sx={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 800, color: 'text.secondary' }}>
                                                                {stat.label}
                                                            </Typography>
                                                            <Typography sx={{ mt: 0.35, fontSize: '1.45rem', lineHeight: 1, fontWeight: 900, color: stat.tone }}>
                                                                {stat.value}
                                                            </Typography>
                                                        </Button>
                                                    ))}
                                                </Box>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                                    Current focus set contains {workloadSummary.focusCount} active report{workloadSummary.focusCount === 1 ? '' : 's'}.
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Box>

                                <Paper sx={premiumPanelSx} id="invoice-revenue">
                                    <Box sx={premiumPanelHeaderSx}>
                                        <Typography variant="subtitle1" sx={premiumPanelTitleSx}>
                                            Invoice Revenue
                                        </Typography>
                                    </Box>
                                    <Box sx={{ ...premiumPanelBodySx, pt: { xs: 0.9, sm: 1.1 } }}>
                                        <Box
                                            sx={{
                                                borderRadius: 4,
                                                overflow: 'hidden',
                                                border: `1px solid ${alpha('#ffffff', 0.62)}`,
                                                background: `linear-gradient(180deg, ${alpha('#ffffff', 0.58)} 0%, ${alpha('#f4f8fc', 0.74)} 100%)`,
                                                p: { xs: 1, sm: 1.25 }
                                            }}
                                        >
                                            <InvoiceLineChart />
                                        </Box>
                                    </Box>
                                </Paper>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'stretch',
                                        flexDirection: { xs: 'column', md: 'row' }
                                    }}
                                >
                                    <Paper
                                        sx={{
                                            ...premiumPanelSx,
                                            flex: { xs: '0 0 auto', md: 1 },
                                            width: { xs: '100%', md: 'auto' },
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}
                                    >
                                        <Box sx={premiumPanelHeaderSx}>
                                            <Typography variant="subtitle1" sx={premiumPanelTitleSx}>
                                                Project Locations
                                            </Typography>
                                        </Box>
                                        <Box sx={{ ...premiumPanelBodySx, pt: { xs: 0.9, sm: 1.1 } }}>
                                            <Box
                                                sx={{
                                                    borderRadius: 4,
                                                    overflow: 'hidden',
                                                    border: `1px solid ${alpha('#ffffff', 0.62)}`,
                                                    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.55)} 0%, ${alpha('#f4f8fc', 0.72)} 100%)`,
                                                    p: { xs: 0.75, sm: 1 }
                                                }}
                                            >
                                                <UKProjectMap projects={projectLocations} />
                                            </Box>
                                        </Box>
                                    </Paper>

                                    <Box sx={{ flex: { xs: '0 0 auto', md: 1 }, width: { xs: '100%', md: 'auto' }, minWidth: 0, display: 'flex' }}>
                                        <KpiMetricsCard authUser={authUser} style={{ marginBottom: 0, flex: 1 }} />
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {view === 'reports' && <Reports key={`reports-${reportsNavNonce}`} />}
                        {view === 'clientdeal' && (
                            <FeasibilityHub
                                authUser={authUser}
                                organisationName={authUser?.organisation_name || (authUser?.tenant_slug === 'montech' ? 'MonTech' : 'Organisation')}
                                onGoToReports={() => {
                                    clearReportsDeepLinkParamsFromUrl();
                                    setReportsNavNonce((n) => n + 1);
                                    setView('reports');
                                }}
                            />
                        )}
                        {view === 'activity' && <ActivityFeed mode="dashboard" siteWideOnly />}
                        {view === 'request_valuation' && <RequestValuation authUser={authUser} />}
                        {view === 'calendar' && <Calendar isAdmin={isAdmin} authUser={authUser} />}
                        {view === 'projects' && isAdmin && <ProjectSetup />}
                        {view === 'issue_reports' && <IssueReports isAdmin={isAdmin} />}

                        {view === 'financials' && (isAdmin || isFinance) && (
                            <Financials
                                onGoToInvoiceRevenue={() => {
                                    setView('overview');
                                    // Wait for the Overview DOM to render, then scroll to the invoice section.
                                    setTimeout(() => {
                                        const el = document.getElementById('invoice-revenue');
                                        if (el && typeof el.scrollIntoView === 'function') {
                                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }
                                    }, 50);
                                }}
                            />
                        )}

                        {view === 'actions_hub' && (isAdmin || isSurveyor) && <ActionsHub authUser={authUser} />}

                        {view === 'analytics' && (isAdmin || isSurveyor || isDeveloper || isFunder) && (
                            <AnalyticsHub
                                tab={analyticsTab}
                                onTabChange={setAnalyticsTab}
                                authUser={authUser}
                                portalMode={portalMode}
                                onGoToReports={() => setView('reports')}
                            />
                        )}

                        {view === 'team' && (isAdmin || isSurveyor) && <Team authUser={authUser} />}
                        {view === 'clients' && (isAdmin || isSurveyor) && <Clients authUser={authUser} />}
                        {view === 'business_development' && (isAdmin || isSurveyor) && <BusinessDevelopment />}
                        {view === 'fee_proposals' && (isAdmin || isSurveyor) && <FeeProposals authUser={authUser} />}

                        {view === 'settings' && (
                            <Settings
                                isAdmin={isAdmin}
                                isDeveloper={isDeveloper}
                                authUser={authUser}
                                onRefreshAuth={bootstrapAuth}
                                overviewDisplay={overviewDisplay}
                                onOverviewDisplayChange={setOverviewDisplay}
                                sessionPolicy={sessionPolicy}
                                sessionPolicyLoading={sessionPolicyLoading}
                                onRefreshSessionPolicy={loadCurrentSessionPolicy}
                                onOpenSection={openSettingsSection}
                            />
                        )}
                    </>
                )}
            </Box>

            <Menu
                open={priorityActionMenu.open}
                onClose={closePriorityActionMenu}
                anchorReference="anchorPosition"
                anchorPosition={priorityActionMenu.open ? { top: priorityActionMenu.y, left: priorityActionMenu.x } : undefined}
            >
                <MenuItem
                    onClick={() => {
                        const item = priorityActionMenu.item;
                        const action = item?.priority_flag ? 'remove_priority' : 'priority';
                        closePriorityActionMenu();
                        submitPriorityAction(action, item);
                    }}
                >
                    <FlagIcon fontSize="small" sx={{ mr: 1 }} />
                    {priorityActionMenu.item?.priority_flag ? 'Remove Priority' : 'Set Priority'}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const item = priorityActionMenu.item;
                        if (item?.direct_completion || item?.source === 'manual') {
                            closePriorityActionMenu();
                            submitPriorityAction('complete', item);
                        } else {
                            openPriorityActionDialog('complete');
                        }
                    }}
                >
                    <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                    Complete Task
                </MenuItem>
                <MenuItem onClick={() => openPriorityActionDialog('delegate')}>
                    <PersonAddAlt1Icon fontSize="small" sx={{ mr: 1 }} />
                    Delegate Task
                </MenuItem>
                <MenuItem onClick={() => openPriorityActionDialog('dismiss')}>
                    <RemoveCircleOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                    Dismiss Task
                </MenuItem>
            </Menu>

            <ConfirmLiveProjectsDialog action={confirmLiveAction} onClose={() => setConfirmLiveAction(null)} />

            <Dialog open={priorityActionDialog.open} onClose={closePriorityActionDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {priorityActionDialog.type === 'complete'
                        ? 'Report upload confirmation'
                        : (priorityActionDialog.type === 'priority' || priorityActionDialog.type === 'remove_priority'
                            ? (priorityActionDialog.type === 'remove_priority' ? 'Remove Task Priority' : 'Set Task Priority')
                            : (priorityActionDialog.type === 'delegate' ? 'Delegate Task' : 'Dismiss Task'))}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {String(priorityActionDialog.item?.project_name || priorityActionDialog.item?.reference || '').trim() || 'Selected report'}
                            {' · '}
                            {formatOverviewReportType(priorityActionDialog.item || {})}
                        </Typography>
                        {priorityActionDialog.type === 'priority' ? (
                            <Typography variant="body2">
                                Mark this task as highest priority in your My Priority Actions list?
                            </Typography>
                        ) : null}
                        {priorityActionDialog.type === 'remove_priority' ? (
                            <Typography variant="body2">
                                Remove this task from your highest-priority actions?
                            </Typography>
                        ) : null}
                        {priorityActionDialog.type === 'complete' ? (
                            <>
                                <Typography>Has the completed report been uploaded to the Information Portal?</Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Selecting No will complete this action and create a new System action to upload the PDF. That action closes automatically after a successful Information Portal upload, or it can be completed manually.
                                </Typography>
                            </>
                        ) : null}
                        {priorityActionDialog.type === 'delegate' ? (
                            <Stack spacing={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Delegate to"
                                    value={priorityActionTarget}
                                    onChange={(event) => setPriorityActionTarget(event.target.value)}
                                >
                                    {priorityActionUsers.map((person) => (
                                        <MenuItem key={person.key} value={person.key}>
                                            {person.name}{person.email ? ` (${person.email})` : ''}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <FormControlLabel
                                    control={<Checkbox checked={priorityActionSendEmail} onChange={(event) => setPriorityActionSendEmail(event.target.checked)} />}
                                    label="Send Email to User"
                                />
                                {priorityActionSendEmail ? (
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        label="Reason for delegation"
                                        value={priorityActionDelegationReason}
                                        onChange={(event) => setPriorityActionDelegationReason(event.target.value)}
                                        inputProps={{ maxLength: 2000 }}
                                        required
                                    />
                                ) : null}
                            </Stack>
                        ) : null}
                        {priorityActionDialog.type === 'dismiss' ? (
                            <TextField
                                fullWidth
                                multiline
                                minRows={4}
                                label="Reason for dismissal"
                                value={priorityActionDescription}
                                onChange={(event) => setPriorityActionDescription(event.target.value)}
                                inputProps={{ maxLength: 2000 }}
                            />
                        ) : null}
                        {priorityActionError ? (
                            <Alert severity="error">{priorityActionError}</Alert>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePriorityActionDialog} disabled={priorityActionSubmitting}>Cancel</Button>
                    {priorityActionDialog.type === 'complete' ? (
                        <>
                            <Button variant="outlined" onClick={() => submitPriorityAction('complete', null, { report_uploaded: false })} disabled={priorityActionSubmitting}>No</Button>
                            <Button variant="contained" onClick={() => submitPriorityAction('complete', null, { report_uploaded: true })} disabled={priorityActionSubmitting}>Yes</Button>
                        </>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={() => submitPriorityAction()}
                            disabled={priorityActionSubmitting || (priorityActionDialog.type === 'delegate' && (!priorityActionTarget || (priorityActionSendEmail && !priorityActionDelegationReason.trim())))}
                        >
                            {priorityActionSubmitting ? 'Saving…' : (priorityActionDialog.type === 'priority' ? 'Set Priority' : (priorityActionDialog.type === 'remove_priority' ? 'Remove Priority' : (priorityActionDialog.type === 'delegate' ? 'Delegate Task' : 'Dismiss Task')))}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={overviewDetail.open}
                onClose={() => setOverviewDetail((current) => ({ ...current, open: false }))}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{overviewDetail.title}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={1}>
                        {overviewDetail.items.length ? overviewDetail.items.map((item) => (
                            <Button
                                component="a"
                                href={buildOverviewPortalUrl(item)}
                                key={`${item.reference}-${item.report_instance}`}
                                sx={{ justifyContent: 'space-between', alignItems: 'flex-start', textAlign: 'left', textTransform: 'none', color: 'inherit', border: `1px solid ${alpha('#0f172a', 0.08)}`, borderRadius: 2, px: 1.5, py: 1.1 }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 800 }}>
                                        {String(item.project_name || item.reference || '').trim() || 'Unnamed project'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {String(item.reference || '').trim()} · {formatOverviewReportType(item)}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary', flexShrink: 0, ml: 1 }}>
                                    {formatOverviewDueDate(item.due_at)}
                                </Typography>
                            </Button>
                        )) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                No reports currently match this category.
                            </Typography>
                        )}
                    </Stack>
                </DialogContent>
            </Dialog>

            {authUser && (mustChangePassword || showChangePassword) ? (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.25)',
                        zIndex: 5000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 16,
                    }}
                >
                    <div style={{ background: '#fff', borderRadius: 6, padding: 24, width: 'min(520px, 100%)', boxShadow: '0 10px 30px rgba(0,0,0,0.18)' }}>
                        <h2 style={{ marginTop: 0, marginBottom: 8 }}>Change your password</h2>
                        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
                            You must set a new password before continuing.
                        </div>

                        {pwError ? (
                            <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b' }}>
                                {pwError}
                            </div>
                        ) : null}

                        <form onSubmit={submitPasswordChange}>
                            <div style={{ display: 'grid', gap: 10 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Temporary Password</label>
                                    <input
                                        type="password"
                                        value={pwForm.temporaryPassword}
                                        onChange={(e) => setPwForm((f) => ({ ...f, temporaryPassword: e.target.value }))}
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>New Password</label>
                                    <input
                                        type="password"
                                        value={pwForm.newPassword}
                                        onChange={(e) => setPwForm((f) => ({ ...f, newPassword: e.target.value }))}
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Confirm New Password</label>
                                    <input
                                        type="password"
                                        value={pwForm.confirmNewPassword}
                                        onChange={(e) => setPwForm((f) => ({ ...f, confirmNewPassword: e.target.value }))}
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
                                    />
                                </div>
                            </div>
                            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button type="submit" disabled={pwSaving}>
                                    {pwSaving ? 'Saving…' : 'Save Password'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </Box>
    );
}

function RequestValuation({ authUser }) {
    const baseUrl = String(process.env.REACT_APP_INTQ_BASE_URL || '').trim();
    const email = String(authUser?.email || authUser?.username || '').trim();

    const questionnaireUrl = useMemo(() => {
        if (!baseUrl) return '';
        try {
            const url = new URL(baseUrl);
            if (email) url.searchParams.set('email', email);
            return url.toString();
        } catch {
            return baseUrl;
        }
    }, [baseUrl, email]);

    return (
        <Box sx={{ display: 'grid', gap: 2, maxWidth: 760 }}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                    Request a valuation
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    Use the Interim Questionnaire to request a new valuation for your project.
                </Typography>
                {questionnaireUrl ? (
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => window.open(questionnaireUrl, '_blank', 'noopener,noreferrer')}
                    >
                        Open Interim Questionnaire
                    </Button>
                ) : (
                    <Box sx={{ display: 'grid', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'error.main' }}>
                            Interim Questionnaire URL is not configured. Set REACT_APP_INTQ_BASE_URL.
                        </Typography>
                        <Button variant="contained" disabled>
                            Open Interim Questionnaire
                        </Button>
                    </Box>
                )}
            </Paper>
            <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    What happens next?
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    After submitting the questionnaire, the surveyor team is notified and the valuation request will appear against your project.
                </Typography>
            </Paper>
        </Box>
    );
}

function CollapsibleSection({ id, title, description, defaultOpen, children, statusItems = [] }) {
    const normalizedId = String(id || '').trim() || 'section';
    const storageKey = `monsurv_settings_section_${normalizedId}_open`;
    const sectionAnchorId = `settings-section-${normalizedId}`;
    const [open, setOpen] = useState(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw === null) return Boolean(defaultOpen);
            return raw === '1' || raw === 'true';
        } catch {
            return Boolean(defaultOpen);
        }
    });

    useEffect(() => {
        function handleOpenEvent(e) {
            const target = String(e?.detail?.id || '').trim();
            if (target && target === normalizedId) {
                setOpen(true);
            }
        }

        window.addEventListener('monsurv_open_settings_section', handleOpenEvent);
        return () => {
            window.removeEventListener('monsurv_open_settings_section', handleOpenEvent);
        };
    }, [normalizedId]);

    useEffect(() => {
        try {
            localStorage.setItem(storageKey, open ? '1' : '0');
        } catch {
            // ignore
        }
    }, [open, storageKey]);

    return (
        <Accordion
            id={sectionAnchorId}
            expanded={open}
            onChange={(_, next) => setOpen(Boolean(next))}
            disableGutters
            elevation={0}
            sx={{
                mt: 1.75,
                border: `1px solid ${alpha('#ffffff', 0.72)}`,
                borderRadius: 4,
                overflow: 'hidden',
                background: `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#f7fbff', 0.78)} 100%)`,
                boxShadow: '0 18px 42px rgba(15, 23, 42, 0.08)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
                '&:before': { display: 'none' }
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.1, sm: 1.25 },
                    minHeight: 0,
                    '& .MuiAccordionSummary-content': { my: 0.5 },
                    '& .MuiAccordionSummary-expandIconWrapper': { color: 'text.secondary' },
                    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.54)} 0%, ${alpha('#f4f8fc', 0.24)} 100%)`
                }}
            >
                <Box sx={{ minWidth: 0, width: '100%', display: 'flex', gap: 1.25, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                            {title}
                        </Typography>
                        {description ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 900 }}>
                                {description}
                            </Typography>
                        ) : null}
                    </Box>
                    {Array.isArray(statusItems) && statusItems.length ? (
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ pr: { sm: 1 } }}>
                            {statusItems.filter(Boolean).map((item) => (
                                <Chip
                                    key={`${item.label}-${item.color || 'default'}-${item.variant || 'filled'}`}
                                    size="small"
                                    label={item.label}
                                    color={item.color || 'default'}
                                    variant={item.variant || 'filled'}
                                    sx={{ fontWeight: 800 }}
                                />
                            ))}
                        </Stack>
                    ) : null}
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2, sm: 2.5 }, pt: 0, pb: { xs: 2, sm: 2.4 } }}>
                {children}
            </AccordionDetails>
        </Accordion>
    );
}

function createDefaultInvoicingSettingsState() {
    return {
        vat_reg_no: '',
        invoicing_company: '',
        address_lines: ['', '', '', ''],
        pay_terms: '',
        bank_acc_name: '',
        bank_acc_nr: '',
        bank_sort: '',
        invoice_sequence_next: 1000,
        template: {
            enabled: false,
            original_file_name: '',
            content_type: '',
            storage_bucket: '',
            storage_key: '',
            storage_region: '',
            size_bytes: 0,
            uploaded_at: '',
            uploaded_by: ''
        }
    };
}

function Settings({ isAdmin, isDeveloper, authUser, onRefreshAuth, overviewDisplay, onOverviewDisplayChange, sessionPolicy, sessionPolicyLoading, onRefreshSessionPolicy, onOpenSection }) {
    const AI_CONSISTENCY_KEY = 'monsurv_ai_report_consistency';
    const PHI_KEY = 'monsurv_phi_config';
    const DEV_SETTINGS_KEY = 'monsurv_developer_settings_v1';
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
    const authRole = String(authUser?.role || '').trim().toLowerCase();
    const tenantStorageScope = useMemo(() => {
        const fromUser = String(authUser?.tenant_slug || '').trim().toLowerCase();
        if (fromUser) return fromUser;
        try {
            const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
            return tryGetDashboardTenantSlugFromToken(token) || 'iesis';
        } catch {
            return 'iesis';
        }
    }, [authUser?.tenant_slug]);
    const officeMapCacheStorageKey = useMemo(
        () => `${OFFICE_MAP_LOCAL_CACHE_KEY_BASE}:${tenantStorageScope}`,
        [tenantStorageScope]
    );
    const isSurveyorRole = authRole === 'surveyor' || authRole === 'standard';
    const canConfigureFeasibilityReport = Boolean(
        authUser?.feature_visibility?.clientdeal_portal
        ?? (Array.isArray(authUser?.tenant_capabilities) && authUser.tenant_capabilities.includes('feasibility_reports'))
    );
    const canEditAccountEmail = !isSurveyorRole;
    const [dark, setDark] = useState(() => getDarkMode());
    const [reportVersionNaming, setReportVersionNaming] = useState(() => getReportVersionNaming());
    const [appendixDocsText, setAppendixDocsText] = useState(() => loadAppendixDocsFromStorage().join('\n'));

    const [devSettings, setDevSettings] = useState(() => {
        try {
            const raw = localStorage.getItem(DEV_SETTINGS_KEY);
            const parsed = raw ? JSON.parse(raw) : null;
            if (!parsed || typeof parsed !== 'object') {
                return {
                    defaultStart: 'initial',
                    autoOpenLatestDrawdown: true,
                    notifyDrawdown: true,
                    notifyReportReady: true,
                    emailSummary: 'weekly'
                };
            }
            return {
                defaultStart: String(parsed.defaultStart || 'initial'),
                autoOpenLatestDrawdown: Boolean(parsed.autoOpenLatestDrawdown),
                notifyDrawdown: Boolean(parsed.notifyDrawdown),
                notifyReportReady: Boolean(parsed.notifyReportReady),
                emailSummary: String(parsed.emailSummary || 'weekly')
            };
        } catch {
            return {
                defaultStart: 'initial',
                autoOpenLatestDrawdown: true,
                notifyDrawdown: true,
                notifyReportReady: true,
                emailSummary: 'weekly'
            };
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(DEV_SETTINGS_KEY, JSON.stringify(devSettings));
        } catch {
            // ignore
        }
    }, [devSettings]);

    const defaultAiConsistency = useMemo(() => ({
        enabled: false,
        auto_fix: true,
        apply_to_non_ai: false,
        strictness: 70,
        rewrite_strength: 55,
        weights: {
            voice: 90,
            tone: 75,
            structure: 70,
            clarity: 60,
            repetition: 40
        }
    }), []);

    const [aiConsistency, setAiConsistency] = useState(() => {
        try {
            const raw = localStorage.getItem(AI_CONSISTENCY_KEY);
            const parsed = raw ? JSON.parse(raw) : null;
            if (!parsed || typeof parsed !== 'object') return defaultAiConsistency;
            return {
                ...defaultAiConsistency,
                ...parsed,
                weights: { ...defaultAiConsistency.weights, ...(parsed.weights || {}) }
            };
        } catch {
            return defaultAiConsistency;
        }
    });

    const defaultPhiConfig = useMemo(() => ({
        enabled: true,
        use_ai: true,
        ttl_minutes: 30,
        report_instances: {
            initial: {
                weights: {
                    risk_matrix: 100,
                    report_recency: 100
                }
            },
            interim: {
                weights: {
                    risk_matrix: 100,
                    report_recency: 100
                }
            }
        },
        factors: {
            risk_matrix: true,
            report_recency: true
        }
    }), []);

    const [phiConfig, setPhiConfig] = useState(() => {
        try {
            const raw = localStorage.getItem(PHI_KEY);
            const parsed = raw ? JSON.parse(raw) : null;
            if (!parsed || typeof parsed !== 'object') return defaultPhiConfig;
            const factors = parsed.factors && typeof parsed.factors === 'object' ? parsed.factors : {};

            const parsedInstances = parsed.report_instances && typeof parsed.report_instances === 'object' ? parsed.report_instances : {};
            const parsedInitial = parsedInstances.initial && typeof parsedInstances.initial === 'object' ? parsedInstances.initial : {};
            const parsedInterim = parsedInstances.interim && typeof parsedInstances.interim === 'object' ? parsedInstances.interim : {};

            // Back-compat: old schema stored a single `weighting` per report instance.
            const legacyInitialWeighting = parsedInitial.weighting;
            const legacyInterimWeighting = parsedInterim.weighting;

            const mergeWeights = (defaults, rawNode, legacyWeighting) => {
                const rawWeights = rawNode && typeof rawNode.weights === 'object' ? rawNode.weights : {};
                const legacy = clamp0to200(legacyWeighting, null);
                const legacyFill = legacy == null ? {} : {
                    risk_matrix: legacy,
                    report_recency: legacy
                };
                return {
                    ...defaults,
                    ...legacyFill,
                    ...rawWeights
                };
            };

            return {
                ...defaultPhiConfig,
                ...parsed,
                report_instances: {
                    ...defaultPhiConfig.report_instances,
                    ...parsedInstances,
                    initial: {
                        ...defaultPhiConfig.report_instances.initial,
                        ...parsedInitial,
                        weights: mergeWeights(defaultPhiConfig.report_instances.initial.weights, parsedInitial, legacyInitialWeighting)
                    },
                    interim: {
                        ...defaultPhiConfig.report_instances.interim,
                        ...parsedInterim,
                        weights: mergeWeights(defaultPhiConfig.report_instances.interim.weights, parsedInterim, legacyInterimWeighting)
                    }
                },
                factors: { ...defaultPhiConfig.factors, ...factors }
            };
        } catch {
            return defaultPhiConfig;
        }
    });

    const [smtpLoading, setSmtpLoading] = useState(false);
    const [smtpSaving, setSmtpSaving] = useState(false);
    const [smtpError, setSmtpError] = useState('');
    const [smtpMessage, setSmtpMessage] = useState('');
    const [smtpSource, setSmtpSource] = useState('');

    const [smtp, setSmtp] = useState({
        enabled: false,
        host: '',
        port: '',
        secure: false,
        user: '',
        from: '',
        reply_to: '',
        has_password: false
    });
    const [smtpPassword, setSmtpPassword] = useState('');
    const [smtpClearPassword, setSmtpClearPassword] = useState(false);
    const [smtpTestTo, setSmtpTestTo] = useState('');
    const [smtpTestResult, setSmtpTestResult] = useState('');
    const [smtpBaseline, setSmtpBaseline] = useState({
        enabled: false,
        host: '',
        port: '',
        secure: false,
        user: '',
        from: '',
        reply_to: ''
    });

    const [issueReportEmail, setIssueReportEmail] = useState('');
    const [issueReportResolvedRetentionDays, setIssueReportResolvedRetentionDays] = useState('3');
    const [issueReportLoading, setIssueReportLoading] = useState(false);
    const [issueReportSaving, setIssueReportSaving] = useState(false);
    const [issueReportError, setIssueReportError] = useState('');
    const [issueReportMessage, setIssueReportMessage] = useState('');
    const [issueReportBaseline, setIssueReportBaseline] = useState({ admin_email: '', resolved_retention_days: '3' });

    const [slaLoading, setSlaLoading] = useState(false);
    const [slaSaving, setSlaSaving] = useState(false);
    const [slaError, setSlaError] = useState('');
    const [slaMessage, setSlaMessage] = useState('');
    const [sla, setSla] = useState({ initial_report_turnaround_days: '10', interim_report_turnaround_days: '10' });
    const [slaBaseline, setSlaBaseline] = useState({ initial_report_turnaround_days: '10', interim_report_turnaround_days: '10' });

    const [financeLoading, setFinanceLoading] = useState(false);
    const [financeSaving, setFinanceSaving] = useState(false);
    const [financeError, setFinanceError] = useState('');
    const [financeMessage, setFinanceMessage] = useState('');
    const [invoiceRevenueTarget, setInvoiceRevenueTarget] = useState('40000');
    const [financeBaseline, setFinanceBaseline] = useState('40000');

    const [invoicingLoading, setInvoicingLoading] = useState(false);
    const [invoicingSaving, setInvoicingSaving] = useState(false);
    const [invoicingUploading, setInvoicingUploading] = useState(false);
    const [invoicingError, setInvoicingError] = useState('');
    const [invoicingMessage, setInvoicingMessage] = useState('');
    const [invoicingSettings, setInvoicingSettings] = useState(() => createDefaultInvoicingSettingsState());
    const [invoicingBaseline, setInvoicingBaseline] = useState(() => createDefaultInvoicingSettingsState());

    const [mfaLoading, setMfaLoading] = useState(false);
    const [mfaError, setMfaError] = useState('');
    const [mfaStatus, setMfaStatus] = useState({ enabled: false, verified_at: null, rollout_enabled: false, db_enabled: false });
    const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
    const [mfaSetupLoading, setMfaSetupLoading] = useState(false);
    const [mfaSetup, setMfaSetup] = useState(null);
    const [mfaSetupCode, setMfaSetupCode] = useState('');

    const [calendarLoading, setCalendarLoading] = useState(false);
    const [calendarError, setCalendarError] = useState('');
    const [calendarMessage, setCalendarMessage] = useState('');
    const [calendarStatus, setCalendarStatus] = useState({ connected: false, provider: null, email: null, share_enabled: false });

    const USER_PREFS_CACHE_KEY = 'monsurv_user_preferences_v1';

    function getDefaultUserPreferences() {
        return {
            notifications: {
                enabled: true,
                poll_interval_seconds: 15,
                toast_enabled: true,
                quiet_hours_start: '',
                quiet_hours_end: '',
                scope: 'all',
                digest: {
                    frequency: 'off',
                    time_local: '08:00',
                    weekday: 'mon',
                    types: {
                        drawdown_requested: true,
                        report_ready: true,
                        questionnaire_submitted: true,
                        issue_reported: true,
                        client_approval_requested: true
                    }
                },
                types: {
                    drawdown_requested: true,
                    report_ready: true,
                    questionnaire_submitted: true,
                    issue_reported: true,
                    client_approval_requested: true
                }
            },
            ui: {
                default_landing: 'overview',
                table_density: 'comfortable',
                sidebar_pinned: true,
                remember_last_project: true
            },
            reports: {
                default_format: 'docx',
                auto_download: false,
                max_photos: 4,
                fee_proposal_reference_seed: '1000'
            },
            portal: {
                show_ai_alerts: true
            },
            ai: {
                enabled: true,
                show_metadata: false
            },
            security: {
                inactivity_logout_minutes: 30
            },
            actions: {
                live_project_chaser: { ...LIVE_PROJECT_CHASER_DEFAULTS }
            }
        };
    }

    function normalizeUserPreferencesState(source) {
        const defaults = getDefaultUserPreferences();
        const raw = source && typeof source === 'object' ? source : {};

        const notifications = raw.notifications && typeof raw.notifications === 'object' ? raw.notifications : {};
        const notificationTypes = notifications.types && typeof notifications.types === 'object' ? notifications.types : {};
        const notificationDigest = notifications.digest && typeof notifications.digest === 'object' ? notifications.digest : {};
        const notificationDigestTypes = notificationDigest.types && typeof notificationDigest.types === 'object' ? notificationDigest.types : {};

        const ui = raw.ui && typeof raw.ui === 'object' ? raw.ui : {};
        const reports = raw.reports && typeof raw.reports === 'object' ? raw.reports : {};
        const portal = raw.portal && typeof raw.portal === 'object' ? raw.portal : {};
        const ai = raw.ai && typeof raw.ai === 'object' ? raw.ai : {};
        const security = raw.security && typeof raw.security === 'object' ? raw.security : {};
        const actions = raw.actions && typeof raw.actions === 'object' ? raw.actions : {};
        const liveProjectChaser = normalizeLiveProjectChaserThresholds(actions.live_project_chaser);

        const rawInactivity = Number(security.inactivity_logout_minutes);
        const inactivityLogoutMinutes = [0, 15, 30, 60, 120].includes(rawInactivity)
            ? rawInactivity
            : defaults.security.inactivity_logout_minutes;

        return {
            notifications: {
                ...defaults.notifications,
                ...notifications,
                digest: {
                    ...defaults.notifications.digest,
                    ...notificationDigest,
                    types: {
                        ...defaults.notifications.digest.types,
                        ...notificationDigestTypes
                    }
                },
                types: {
                    ...defaults.notifications.types,
                    ...notificationTypes
                }
            },
            ui: {
                ...defaults.ui,
                ...ui,
                default_landing: String(ui.default_landing ?? ui.default_landing_view ?? defaults.ui.default_landing)
            },
            reports: {
                ...defaults.reports,
                ...reports,
                default_format: String(reports.default_format ?? reports.default_output ?? defaults.reports.default_format),
                fee_proposal_reference_seed: String(reports.fee_proposal_reference_seed ?? reports.feeProposalReferenceSeed ?? defaults.reports.fee_proposal_reference_seed).replace(/\D/g, '') || defaults.reports.fee_proposal_reference_seed
            },
            portal: {
                ...defaults.portal,
                ...portal,
                show_ai_alerts: portal.show_ai_alerts ?? portal.show_ai_alerts_by_default ?? defaults.portal.show_ai_alerts
            },
            ai: {
                ...defaults.ai,
                ...ai
            },
            security: {
                ...defaults.security,
                ...security,
                inactivity_logout_minutes: inactivityLogoutMinutes
            },
            actions: {
                ...defaults.actions,
                ...actions,
                live_project_chaser: liveProjectChaser
            }
        };
    }

    function loadUserPreferencesFromStorage() {
        try {
            const raw = localStorage.getItem(USER_PREFS_CACHE_KEY);
            const parsed = raw ? JSON.parse(raw) : null;
            if (!parsed || typeof parsed !== 'object') return getDefaultUserPreferences();
            return normalizeUserPreferencesState(parsed);
        } catch {
            return getDefaultUserPreferences();
        }
    }

    function persistUserPreferences(nextPrefs) {
        try {
            localStorage.setItem(USER_PREFS_CACHE_KEY, JSON.stringify(nextPrefs || {}));
        } catch {
            // ignore
        }
        try {
            window.dispatchEvent(new CustomEvent('monsurv:user-preferences-updated', { detail: nextPrefs || {} }));
        } catch {
            // ignore
        }
    }

    const [userPrefsLoading, setUserPrefsLoading] = useState(false);
    const [userPrefsSaving, setUserPrefsSaving] = useState(false);
    const [userPrefsError, setUserPrefsError] = useState('');
    const [userPrefsMessage, setUserPrefsMessage] = useState('');
    const [userPrefs, setUserPrefs] = useState(() => loadUserPreferencesFromStorage());

    const [notifSubsLoading, setNotifSubsLoading] = useState(false);
    const [notifSubsError, setNotifSubsError] = useState('');
    const [notifSubsMessage, setNotifSubsMessage] = useState('');
    const [notifSubscribedProjects, setNotifSubscribedProjects] = useState([]);
    const [notifProjectOptions, setNotifProjectOptions] = useState([]);
    const [notifProjectQuery, setNotifProjectQuery] = useState('');
    const [notifSubsLoaded, setNotifSubsLoaded] = useState(false);
    const [notifProjectsLoaded, setNotifProjectsLoaded] = useState(false);

    const [orgProfileLoading, setOrgProfileLoading] = useState(false);
    const [orgProfileSaving, setOrgProfileSaving] = useState(false);
    const [orgProfileError, setOrgProfileError] = useState('');
    const [orgProfileMessage, setOrgProfileMessage] = useState('');
    const [orgProfile, setOrgProfile] = useState({ organisation_name: '', report_company_shorthand: '', report_header_text: '', report_footer_text: '', portal_brand_color: '', portal_logo_url: '' });
    const [orgProfileBaseline, setOrgProfileBaseline] = useState({ organisation_name: '', report_company_shorthand: '', report_header_text: '', report_footer_text: '', portal_brand_color: '', portal_logo_url: '' });

    const [reportVersionControlLoading, setReportVersionControlLoading] = useState(false);
    const [reportVersionControlSaving, setReportVersionControlSaving] = useState(false);
    const [reportVersionControlError, setReportVersionControlError] = useState('');
    const [reportVersionControlMessage, setReportVersionControlMessage] = useState('');
    const [reportVersionControlRetained, setReportVersionControlRetained] = useState(5);
    const [reportVersionControlBaseline, setReportVersionControlBaseline] = useState(5);

    const [reportStyleTemplateLoading, setReportStyleTemplateLoading] = useState(false);
    const [reportStyleTemplateSaving, setReportStyleTemplateSaving] = useState(false);
    const [reportStyleTemplateUploading, setReportStyleTemplateUploading] = useState(false);
    const [reportStyleTemplateError, setReportStyleTemplateError] = useState('');
    const [reportStyleTemplateMessage, setReportStyleTemplateMessage] = useState('');
    const [reportStyleTemplate, setReportStyleTemplate] = useState({
        enabled: false,
        original_file_name: '',
        content_type: '',
        storage_bucket: '',
        storage_key: '',
        storage_region: '',
        size_bytes: 0,
        uploaded_at: '',
        uploaded_by: ''
    });
    const [reportStyleTemplateBaseline, setReportStyleTemplateBaseline] = useState({
        enabled: false,
        original_file_name: '',
        content_type: '',
        storage_bucket: '',
        storage_key: '',
        storage_region: '',
        size_bytes: 0,
        uploaded_at: '',
        uploaded_by: ''
    });

    const [interimReportTemplateLoading, setInterimReportTemplateLoading] = useState(false);
    const [interimReportTemplateSaving, setInterimReportTemplateSaving] = useState(false);
    const [interimReportTemplateUploading, setInterimReportTemplateUploading] = useState(false);
    const [interimReportTemplateError, setInterimReportTemplateError] = useState('');
    const [interimReportTemplateMessage, setInterimReportTemplateMessage] = useState('');
    const [interimReportTemplate, setInterimReportTemplate] = useState({
        enabled: false,
        original_file_name: '',
        content_type: '',
        storage_bucket: '',
        storage_key: '',
        storage_region: '',
        size_bytes: 0,
        uploaded_at: '',
        uploaded_by: '',
        placeholders: {
            title_page: [],
            header_footer: []
        }
    });
    const [interimReportTemplateBaseline, setInterimReportTemplateBaseline] = useState({
        enabled: false,
        original_file_name: '',
        content_type: '',
        storage_bucket: '',
        storage_key: '',
        storage_region: '',
        size_bytes: 0,
        uploaded_at: '',
        uploaded_by: '',
        placeholders: {
            title_page: [],
            header_footer: []
        }
    });

    const emptyFeasibilityReportSettings = {
        enabled: false,
        template_uploaded: false,
        template_version: 'feasibility-v0.1',
        model_name: '',
        prompt_version: 'feasibility-v0.1',
        original_file_name: '',
        content_type: '',
        size_bytes: 0,
        uploaded_at: '',
        uploaded_by: '',
        placeholders: []
    };
    const [feasibilityReportLoading, setFeasibilityReportLoading] = useState(false);
    const [feasibilityReportSaving, setFeasibilityReportSaving] = useState(false);
    const [feasibilityReportUploading, setFeasibilityReportUploading] = useState(false);
    const [feasibilityReportError, setFeasibilityReportError] = useState('');
    const [feasibilityReportMessage, setFeasibilityReportMessage] = useState('');
    const [feasibilityReport, setFeasibilityReport] = useState(emptyFeasibilityReportSettings);
    const [feasibilityReportBaseline, setFeasibilityReportBaseline] = useState(emptyFeasibilityReportSettings);

    const [feeProposalTemplateLoading, setFeeProposalTemplateLoading] = useState(false);
    const [feeProposalTemplateSaving, setFeeProposalTemplateSaving] = useState(false);
    const [feeProposalTemplateUploading, setFeeProposalTemplateUploading] = useState(false);
    const [feeProposalTemplateError, setFeeProposalTemplateError] = useState('');
    const [feeProposalTemplateMessage, setFeeProposalTemplateMessage] = useState('');
    const FEE_PROPOSAL_TEMPLATE_SERVICE_LINES = useMemo(() => ([
        { key: 'monitoring_surveyor_full_report', label: 'Monitoring Surveyor - Full Report' },
        { key: 'monitoring_surveyor_dashboard', label: 'Monitoring Surveyor - Dashboard' }
    ]), []);
    const [feeProposalTemplate, setFeeProposalTemplate] = useState({
        service_lines: {
            monitoring_surveyor_full_report: {
                enabled: false,
                original_file_name: '',
                content_type: '',
                storage_bucket: '',
                storage_key: '',
                storage_region: '',
                size_bytes: 0,
                uploaded_at: '',
                uploaded_by: ''
            },
            monitoring_surveyor_dashboard: {
                enabled: false,
                original_file_name: '',
                content_type: '',
                storage_bucket: '',
                storage_key: '',
                storage_region: '',
                size_bytes: 0,
                uploaded_at: '',
                uploaded_by: ''
            }
        }
    });
    const [feeProposalTemplateBaseline, setFeeProposalTemplateBaseline] = useState({
        service_lines: {
            monitoring_surveyor_full_report: {
                enabled: false,
                original_file_name: '',
                content_type: '',
                storage_bucket: '',
                storage_key: '',
                storage_region: '',
                size_bytes: 0,
                uploaded_at: '',
                uploaded_by: ''
            },
            monitoring_surveyor_dashboard: {
                enabled: false,
                original_file_name: '',
                content_type: '',
                storage_bucket: '',
                storage_key: '',
                storage_region: '',
                size_bytes: 0,
                uploaded_at: '',
                uploaded_by: ''
            }
        }
    });

    const [officeMapLoading, setOfficeMapLoading] = useState(false);
    const [officeMapSaving, setOfficeMapSaving] = useState(false);
    const [officeMapUploading, setOfficeMapUploading] = useState(false);
    const [officeMapError, setOfficeMapError] = useState('');
    const [officeMapMessage, setOfficeMapMessage] = useState('');
    const [officeMap, setOfficeMap] = useState(() => normalizeOfficeMapFormSettings(null));
    const [officeMapBaseline, setOfficeMapBaseline] = useState(() => normalizeOfficeMapFormSettings(null));

    const [retentionLoading, setRetentionLoading] = useState(false);
    const [retentionSaving, setRetentionSaving] = useState(false);
    const [retentionError, setRetentionError] = useState('');
    const [retentionMessage, setRetentionMessage] = useState('');
    const [retention, setRetention] = useState({ uploads_retention_days: '365', notifications_retention_days: '365', activity_retention_days: '365', max_upload_mb: '20', allowed_upload_types_text: 'application/pdf\nimage/jpeg\nimage/png\nimage/webp' });
    const [retentionBaseline, setRetentionBaseline] = useState({ uploads_retention_days: '365', notifications_retention_days: '365', activity_retention_days: '365', max_upload_mb: '20', allowed_upload_types_text: 'application/pdf\nimage/jpeg\nimage/png\nimage/webp' });

    const [integrationsLoading, setIntegrationsLoading] = useState(false);
    const [integrationsSaving, setIntegrationsSaving] = useState(false);
    const [integrationsError, setIntegrationsError] = useState('');
    const [integrationsMessage, setIntegrationsMessage] = useState('');
    const [integrations, setIntegrations] = useState({ slack_webhook_url: '', teams_webhook_url: '', powerbi_enabled: false, powerbi_workspace_id: '', powerbi_dataset_id: '' });
    const [integrationsBaseline, setIntegrationsBaseline] = useState({ slack_webhook_url: '', teams_webhook_url: '', powerbi_enabled: false, powerbi_workspace_id: '', powerbi_dataset_id: '' });

    const [securityPolicyLoading, setSecurityPolicyLoading] = useState(false);
    const [securityPolicySaving, setSecurityPolicySaving] = useState(false);
    const [securityPolicyError, setSecurityPolicyError] = useState('');
    const [securityPolicyMessage, setSecurityPolicyMessage] = useState('');
    const [securityPolicy, setSecurityPolicy] = useState({ require_mfa_for_admins: false, session_timeout_minutes: '720', idle_timeout_minutes: '240', password_min_length: '12' });
    const [securityPolicyBaseline, setSecurityPolicyBaseline] = useState({ require_mfa_for_admins: false, session_timeout_minutes: '720', idle_timeout_minutes: '240', password_min_length: '12' });

    const [deletionGuardLoading, setDeletionGuardLoading] = useState(false);
    const [deletionGuardSaving, setDeletionGuardSaving] = useState(false);
    const [deletionGuardError, setDeletionGuardError] = useState('');
    const [deletionGuardMessage, setDeletionGuardMessage] = useState('');
    const [deletionGuard, setDeletionGuard] = useState({ enabled: false, configured: false });
    const [deletionGuardPassword, setDeletionGuardPassword] = useState('');
    const [deletionGuardPasswordConfirm, setDeletionGuardPasswordConfirm] = useState('');

    const [aiPolicyLoading, setAiPolicyLoading] = useState(false);
    const [aiPolicySaving, setAiPolicySaving] = useState(false);
    const [aiPolicyError, setAiPolicyError] = useState('');
    const [aiPolicyMessage, setAiPolicyMessage] = useState('');
    const [aiPolicy, setAiPolicy] = useState({ ai_enabled: true, allow_ai_photo_analysis: true, allow_online_lookup: true, default_model: '', temperature_cap: '0.6' });
    const [aiPolicyBaseline, setAiPolicyBaseline] = useState({ ai_enabled: true, allow_ai_photo_analysis: true, allow_online_lookup: true, default_model: '', temperature_cap: '0.6' });

    const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);
    const [emailTemplatesSaving, setEmailTemplatesSaving] = useState(false);
    const [emailTemplatesError, setEmailTemplatesError] = useState('');
    const [emailTemplatesMessage, setEmailTemplatesMessage] = useState('');
    const [emailTemplateHelpOpen, setEmailTemplateHelpOpen] = useState('');
    const [emailTemplateFocusedField, setEmailTemplateFocusedField] = useState('');
    const emailTemplateFieldRefs = useRef({});
    const [emailTemplates, setEmailTemplates] = useState({
        invite_subject: 'You have been invited to MonSurv',
        invite_body: '',
        new_client_access_subject: 'Welcome to Monsurv Broker Access - <broker_company_name>',
        new_client_access_body:
            'Hello <first_name>,\n\n'
            + 'Welcome to Monsurv Broker Access. An account has been created for you on behalf of <broker_company_name>.\n\n'
            + 'Your login details are:\n\n'
            + 'Email: <contact_email>\n'
            + 'Portal: <login_link>\n'
            + 'Temporary password: <temporary_password>\n\n'
            + 'When you sign in for the first time, you will be asked to create your own password. For security, this temporary password can only be used to complete that step.\n\n'
            + 'Many thanks,\n\n'
            + 'Monsurv',
        developer_questionnaire_subject: 'Information Portal Access - <project_reference>',
        developer_questionnaire_body:
            'Hello <developer_first_name>,\n\n'
            + 'We have been appointed by <funder_name> to provide Independent Monitoring for your project at <site_address>.\n\n'
            + 'Company: <developer_company>\n\n'
            + 'Your unique project reference is <project_reference> and your questionnaire/Information Dashboard password is <temporary_password>.\n\n'
            + 'Please use this link to access the questionnaire:\n\n'
            + '<portal_link>\n\n'
            + 'Many thanks,\n\n'
            + '<lead_surveyor_name>\n',
        information_outstanding_subject: 'Information outstanding',
        information_outstanding_body: '',
        insurance_expiry_subject: 'Insurance expiry reminder',
        insurance_expiry_body: '',
        valuation_reminder_subject: 'Valuation reminder',
        valuation_reminder_body: '',
        report_ready_subject: 'Your report is ready',
        report_ready_body: '',
        feasibility_report_issued_subject: 'Your Feasibility Analysis Report has been issued - <deal_reference>',
        feasibility_report_issued_body: 'Hello <client_first_name>,\n\nYour Feasibility Analysis Report for <deal_name> (<deal_reference>) has been issued and is ready to download.\n\nReport: <report_name>\nIssued: <issued_date>\n\nSign in to Monsurv Broker Access to download the report:\n<broker_portal_link>\n\nMany thanks,\n\nMonsurv',
        action_report_subject: 'Your Monsurv Action Report - <report_date>',
        action_report_body: 'Hello <user_first_name>,\n\nHere is your current Monsurv action list for <report_date>.\n\n<action_list>\n\nOpen actions: <open_action_count>\nPriority actions: <priority_action_count>\n\nOpen the Actions Hub: <action_hub_link>\n\nMany thanks,\n\nMonsurv',
        action_delegated_subject: 'A Monsurv task has been delegated to you - <task_title>',
        action_delegated_body: 'Hello <recipient_first_name>,\n\n<delegated_by_name> has delegated the following task to you:\n\n<task_title>\n<task_description>\n\nReason for delegation:\n<delegation_reason>\n\nOpen the Actions Hub: <action_hub_link>\n\nMany thanks,\n\nMonsurv',
        password_reset_subject: 'Password reset',
        password_reset_body: ''
    });
    const [emailTemplatesBaseline, setEmailTemplatesBaseline] = useState({
        invite_subject: 'You have been invited to MonSurv',
        invite_body: '',
        new_client_access_subject: 'Welcome to Monsurv Broker Access - <broker_company_name>',
        new_client_access_body:
            'Hello <first_name>,\n\n'
            + 'Welcome to Monsurv Broker Access. An account has been created for you on behalf of <broker_company_name>.\n\n'
            + 'Your login details are:\n\n'
            + 'Email: <contact_email>\n'
            + 'Portal: <login_link>\n'
            + 'Temporary password: <temporary_password>\n\n'
            + 'When you sign in for the first time, you will be asked to create your own password. For security, this temporary password can only be used to complete that step.\n\n'
            + 'Many thanks,\n\n'
            + 'Monsurv',
        developer_questionnaire_subject: 'Information Portal Access - <project_reference>',
        developer_questionnaire_body:
            'Hello <developer_first_name>,\n\n'
            + 'We have been appointed by <funder_name> to provide Independent Monitoring for your project at <site_address>.\n\n'
            + 'Company: <developer_company>\n\n'
            + 'Your unique project reference is <project_reference> and your questionnaire/Information Dashboard password is <temporary_password>.\n\n'
            + 'Please use this link to access the questionnaire:\n\n'
            + '<portal_link>\n\n'
            + 'Many thanks,\n\n'
            + '<lead_surveyor_name>\n',
        information_outstanding_subject: 'Information outstanding',
        information_outstanding_body: '',
        insurance_expiry_subject: 'Insurance expiry reminder',
        insurance_expiry_body: '',
        valuation_reminder_subject: 'Valuation reminder',
        valuation_reminder_body: '',
        report_ready_subject: 'Your report is ready',
        report_ready_body: '',
        feasibility_report_issued_subject: 'Your Feasibility Analysis Report has been issued - <deal_reference>',
        feasibility_report_issued_body: 'Hello <client_first_name>,\n\nYour Feasibility Analysis Report for <deal_name> (<deal_reference>) has been issued and is ready to download.\n\nReport: <report_name>\nIssued: <issued_date>\n\nSign in to Monsurv Broker Access to download the report:\n<broker_portal_link>\n\nMany thanks,\n\nMonsurv',
        action_report_subject: 'Your Monsurv Action Report - <report_date>',
        action_report_body: 'Hello <user_first_name>,\n\nHere is your current Monsurv action list for <report_date>.\n\n<action_list>\n\nOpen actions: <open_action_count>\nPriority actions: <priority_action_count>\n\nOpen the Actions Hub: <action_hub_link>\n\nMany thanks,\n\nMonsurv',
        action_delegated_subject: 'A Monsurv task has been delegated to you - <task_title>',
        action_delegated_body: 'Hello <recipient_first_name>,\n\n<delegated_by_name> has delegated the following task to you:\n\n<task_title>\n<task_description>\n\nReason for delegation:\n<delegation_reason>\n\nOpen the Actions Hub: <action_hub_link>\n\nMany thanks,\n\nMonsurv',
        password_reset_subject: 'Password reset',
        password_reset_body: ''
    });

    const emailTemplateTagsByGroup = useMemo(() => {
        const baseProject = [
            { tag: '<project_reference>', label: 'Project reference' },
            { tag: '<funder_name>', label: 'Funder name' },
            { tag: '<site_address>', label: 'Site address' },
            { tag: '<lead_surveyor_name>', label: 'Lead surveyor name' },
        ];

        return {
            developer_questionnaire: [
                { tag: '<developer_first_name>', label: 'Developer first name' },
                { tag: '<developer_full_name>', label: 'Developer full name' },
                { tag: '<developer_company>', label: 'Developer company' },
                { tag: '<temporary_password>', label: 'Temporary password' },
                { tag: '<portal_link>', label: 'Portal link' },
                ...baseProject
            ],
            new_team_member: [
                { tag: '<new_user_first_name>', label: 'New user first name' },
                { tag: '<new_user_full_name>', label: 'New user full name' },
                { tag: '<new_user_email>', label: 'New user email' },
                { tag: '<temporary_password>', label: 'Temporary password' },
                { tag: '<login_link>', label: 'Login link' },
                { tag: '<dashboard_url>', label: 'Dashboard URL' },
            ],
            new_client_access: [
                { tag: '<broker_or_funder>', label: 'Broker or Funder label' },
                { tag: '<client_type_label>', label: 'Broker or Funder label' },
                { tag: '<broker_company_name>', label: 'Broker company name' },
                { tag: '<contact_email>', label: 'Contact login email' },
                { tag: '<temporary_password>', label: 'Temporary password' },
                { tag: '<first_name>', label: 'Contact first name' },
                { tag: '<login_link>', label: 'Login link' },
                { tag: '<dashboard_url>', label: 'Dashboard URL' },
            ],
            password_reset: [
                { tag: '<user_first_name>', label: 'User first name' },
                { tag: '<user_full_name>', label: 'User full name' },
                { tag: '<username>', label: 'Username / email' },
                { tag: '<reset_link>', label: 'Password reset link' },
                { tag: '<expires_minutes>', label: 'Expiry (minutes)' },
                { tag: '<dashboard_url>', label: 'Dashboard URL' },
            ],
            report_ready: [
                ...baseProject,
            ],
            feasibility_report_issued: [
                { tag: '<client_first_name>', label: 'Client first name' },
                { tag: '<client_full_name>', label: 'Client full name' },
                { tag: '<client_email>', label: 'Client email address' },
                { tag: '<deal_name>', label: 'Deal name' },
                { tag: '<deal_reference>', label: 'Deal reference' },
                { tag: '<report_name>', label: 'Issued PDF name' },
                { tag: '<issued_date>', label: 'Issue date' },
                { tag: '<broker_portal_link>', label: 'Broker portal link' }
            ],
            action_report: [
                { tag: '<user_first_name>', label: 'User first name' },
                { tag: '<user_full_name>', label: 'User full name' },
                { tag: '<report_date>', label: 'Report date' },
                { tag: '<action_list>', label: 'Formatted action list' },
                { tag: '<open_action_count>', label: 'Open action count' },
                { tag: '<priority_action_count>', label: 'Priority action count' },
                { tag: '<action_hub_link>', label: 'Actions Hub link' }
            ],
            action_delegated: [
                { tag: '<recipient_first_name>', label: 'Recipient first name' },
                { tag: '<recipient_full_name>', label: 'Recipient full name' },
                { tag: '<delegated_by_name>', label: 'Delegating user name' },
                { tag: '<task_title>', label: 'Task title' },
                { tag: '<task_description>', label: 'Task description' },
                { tag: '<delegation_reason>', label: 'Delegation reason' },
                { tag: '<project_reference>', label: 'Project reference' },
                { tag: '<report_instance>', label: 'Report instance' },
                { tag: '<action_hub_link>', label: 'Actions Hub link' }
            ],
            information_outstanding: [
                ...baseProject
            ],
            insurance_expiry: [
                ...baseProject
            ],
            valuation_reminder: [
                ...baseProject
            ],
            other: [
                ...baseProject
            ]
        };
    }, []);

    function setEmailTemplateFieldRef(key, el) {
        if (!key) return;
        if (el) emailTemplateFieldRefs.current[key] = el;
    }

    function insertEmailTemplateTag(tag, fieldKeyOverride) {
        const key = String(fieldKeyOverride || emailTemplateFocusedField || '').trim();
        if (!key) return;
        const text = String(tag || '');
        if (!text) return;

        if (key && key !== String(emailTemplateFocusedField || '').trim()) setEmailTemplateFocusedField(key);

        const input = emailTemplateFieldRefs.current[key];
        const current = String(emailTemplates?.[key] || '');
        const start = input && typeof input.selectionStart === 'number' ? input.selectionStart : current.length;
        const end = input && typeof input.selectionEnd === 'number' ? input.selectionEnd : current.length;

        const next = `${current.slice(0, start)}${text}${current.slice(end)}`;
        setEmailTemplates((s) => ({ ...s, [key]: next }));

        if (input && typeof input.focus === 'function') {
            setTimeout(() => {
                try {
                    input.focus();
                    const cursor = start + text.length;
                    if (typeof input.setSelectionRange === 'function') input.setSelectionRange(cursor, cursor);
                } catch {
                    // ignore
                }
            }, 0);
        }
    }

    const [analyticsExportLoading, setAnalyticsExportLoading] = useState(false);
    const [analyticsExportSaving, setAnalyticsExportSaving] = useState(false);
    const [analyticsExportError, setAnalyticsExportError] = useState('');
    const [analyticsExportMessage, setAnalyticsExportMessage] = useState('');
    const [analyticsExport, setAnalyticsExport] = useState({ exports_enabled: false, exports_frequency: 'weekly', exports_email_to: '', include_project_financials: true, include_notifications: false });
    const [analyticsExportBaseline, setAnalyticsExportBaseline] = useState({ exports_enabled: false, exports_frequency: 'weekly', exports_email_to: '', include_project_financials: true, include_notifications: false });

    const [notificationDigestAdminLoading, setNotificationDigestAdminLoading] = useState(false);
    const [notificationDigestAdminSaving, setNotificationDigestAdminSaving] = useState(false);
    const [notificationDigestRunLoading, setNotificationDigestRunLoading] = useState(false);
    const [notificationDigestAdminError, setNotificationDigestAdminError] = useState('');
    const [notificationDigestAdminMessage, setNotificationDigestAdminMessage] = useState('');
    const [notificationDigestAdmin, setNotificationDigestAdmin] = useState({
        shared_mailbox_enabled: false,
        shared_mailbox_to: '',
        shared_mailbox_only: false,
        max_items_per_digest: '50',
        lookback_hours: String(24 * 7)
    });
    const [notificationDigestAdminBaseline, setNotificationDigestAdminBaseline] = useState({
        shared_mailbox_enabled: false,
        shared_mailbox_to: '',
        shared_mailbox_only: false,
        max_items_per_digest: '50',
        lookback_hours: String(24 * 7)
    });

    const [actionNotificationsLoading, setActionNotificationsLoading] = useState(false);
    const [actionNotificationsSaving, setActionNotificationsSaving] = useState(false);
    const [actionNotificationsPreviewing, setActionNotificationsPreviewing] = useState(false);
    const [actionNotificationsError, setActionNotificationsError] = useState('');
    const [actionNotificationsMessage, setActionNotificationsMessage] = useState('');
    const [actionNotifications, setActionNotifications] = useState({ enabled: false, weekday: 'mon', time_local: '08:00', timezone: 'Europe/London' });
    const [actionNotificationsBaseline, setActionNotificationsBaseline] = useState({ enabled: false, weekday: 'mon', time_local: '08:00', timezone: 'Europe/London' });

    const [accountEmail, setAccountEmail] = useState('');
    const [accountEmailSaving, setAccountEmailSaving] = useState(false);
    const [accountEmailError, setAccountEmailError] = useState('');
    const [accountEmailMessage, setAccountEmailMessage] = useState('');

    const [photoUploading, setPhotoUploading] = useState(false);
    const [photoError, setPhotoError] = useState('');
    const [photoMessage, setPhotoMessage] = useState('');

    useEffect(() => {
        const email = String(authUser?.email || '').trim();
        setAccountEmail(email);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser?.email]);

    function authHeadersJson() {
        const token = localStorage.getItem('monsurv_auth_token') || '';
        return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }

    function authHeaderOnly() {
        const token = localStorage.getItem('monsurv_auth_token') || '';
        return { Authorization: `Bearer ${token}` };
    }

    async function saveAccountEmail() {
        if (!canEditAccountEmail) {
            setAccountEmailError('Email changes are disabled for surveyor accounts. Please contact an admin.');
            setAccountEmailMessage('');
            return;
        }
        setAccountEmailError('');
        setAccountEmailMessage('');
        try {
            const email = String(accountEmail || '').trim();
            const body = { email: email || null };
            await fetchJson(`${API_BASE_URL}/auth/account`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });
            setAccountEmailMessage('Account updated.');
            await onRefreshAuth?.();
        } catch (e) {
            setAccountEmailError(e?.message || 'Failed to update email');
        } finally {
            setAccountEmailSaving(false);
        }
    }

    async function uploadProfilePhoto(file) {
        setPhotoError('');
        setPhotoMessage('');
        setPhotoUploading(true);
        try {
            if (!file) throw new Error('Choose an image first');
            const form = new FormData();
            form.append('photo', file);
            const resp = await fetch(`${API_BASE_URL}/auth/profile/photo`, {
                method: 'POST',
                headers: authHeaderOnly(),
                body: form
            });
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(json?.error || 'Upload failed');
            setPhotoMessage('Profile photo updated.');
            await onRefreshAuth?.();
        } catch (e) {
            setPhotoError(e?.message || 'Failed to upload photo');
        } finally {
            setPhotoUploading(false);
        }
    }

    async function deleteProfilePhoto() {
        setPhotoError('');
        setPhotoMessage('');
        setPhotoUploading(true);
        try {
            const resp = await fetch(`${API_BASE_URL}/auth/profile/photo`, {
                method: 'DELETE',
                headers: authHeaderOnly()
            });
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(json?.error || 'Delete failed');
            setPhotoMessage('Profile photo removed.');
            await onRefreshAuth?.();
        } catch (e) {
            setPhotoError(e?.message || 'Failed to remove photo');
        } finally {
            setPhotoUploading(false);
        }
    }

    async function loadCalendarStatus() {
        setCalendarError('');
        setCalendarLoading(true);
        try {
            const token = authHeadersJson().Authorization;
            const json = await fetchJson(`${API_BASE_URL}/auth/calendar/status`, {
                method: 'GET',
                headers: { Authorization: token }
            });
            setCalendarStatus({
                connected: Boolean(json?.status?.connected),
                provider: json?.status?.provider || null,
                email: json?.status?.email || null,
                share_enabled: Boolean(json?.status?.share_enabled)
            });
        } catch (e) {
            setCalendarStatus({ connected: false, provider: null, email: null, share_enabled: false });
            setCalendarError(e?.message || 'Failed to load calendar status');
        } finally {
            setCalendarLoading(false);
        }
    }

    async function connectCalendar() {
        setCalendarError('');
        setCalendarMessage('');
        setCalendarLoading(true);
        try {
            const token = authHeadersJson().Authorization;
            const json = await fetchJson(`${API_BASE_URL}/auth/calendar/connect`, {
                method: 'POST',
                headers: { Authorization: token }
            });
            const url = String(json?.url || '').trim();
            if (!url) throw new Error('No connection URL returned');
            window.location.href = url;
        } catch (e) {
            setCalendarError(e?.message || 'Failed to start calendar connection');
        } finally {
            setCalendarLoading(false);
        }
    }

    async function connectGoogleCalendar() {
        setCalendarError('');
        setCalendarMessage('');
        setCalendarLoading(true);
        try {
            const token = authHeadersJson().Authorization;
            const json = await fetchJson(`${API_BASE_URL}/auth/calendar/connect/google`, {
                method: 'POST',
                headers: { Authorization: token }
            });
            const url = String(json?.url || '').trim();
            if (!url) throw new Error('No connection URL returned');
            window.location.href = url;
        } catch (e) {
            setCalendarError(e?.message || 'Failed to start Google calendar connection');
        } finally {
            setCalendarLoading(false);
        }
    }

    async function toggleCalendarSharing(next) {
        setCalendarError('');
        setCalendarMessage('');
        setCalendarLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/auth/calendar/share`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify({ share_enabled: Boolean(next) })
            });
            setCalendarStatus((s) => ({ ...s, share_enabled: Boolean(json?.status?.share_enabled) }));
            setCalendarMessage(Boolean(next)
                ? 'Busy-time sharing enabled for the Team Calendar.'
                : 'Busy-time sharing disabled. Your own calendar remains connected.');
        } catch (e) {
            setCalendarError(e?.message || 'Failed to update calendar sharing');
        } finally {
            setCalendarLoading(false);
        }
    }

    async function disconnectCalendar() {
        setCalendarError('');
        setCalendarMessage('');
        setCalendarLoading(true);
        try {
            await fetchJson(`${API_BASE_URL}/auth/calendar/disconnect`, {
                method: 'POST',
                headers: authHeadersJson()
            });
            await loadCalendarStatus();
            setCalendarMessage('Calendar connection removed.');
        } catch (e) {
            setCalendarError(e?.message || 'Failed to disconnect calendar');
        } finally {
            setCalendarLoading(false);
        }
    }

    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search || '');
            const calendarResult = String(params.get('calendar') || '').trim().toLowerCase();
            if (!calendarResult) return;

            params.set('view', 'settings');
            params.set('section', 'calendar');

            if (calendarResult === 'connected') {
                setCalendarMessage('Calendar connected successfully. Monsurv can now read your availability and sync configured reminders.');
                setTimeout(() => {
                    try {
                        window.dispatchEvent(new CustomEvent('monsurv_open_settings_section', { detail: { id: 'calendar' } }));
                    } catch {
                        // ignore
                    }
                }, 0);
            } else if (calendarResult === 'error') {
                setCalendarError('Calendar connection could not be completed. Please try again or check the provider configuration.');
            }

            params.delete('calendar');
            const nextSearch = params.toString();
            const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
            window.history.replaceState({}, document.title, nextUrl);
        } catch {
            // ignore
        }
    }, []);

    async function loadMfaStatus() {
        setMfaError('');
        setMfaLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/auth/mfa/status`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            setMfaStatus({
                enabled: Boolean(json?.enabled),
                verified_at: json?.verified_at ?? null,
                rollout_enabled: Boolean(json?.rollout_enabled),
                db_enabled: Boolean(json?.db_enabled)
            });
        } catch (e) {
            setMfaError(e?.message || 'Failed to load MFA status');
        } finally {
            setMfaLoading(false);
        }
    }

    async function startMfaSetup() {
        setMfaError('');
        setMfaSetupCode('');
        setMfaSetupLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/auth/mfa/setup/start`, {
                method: 'POST',
                headers: authHeadersJson()
            });
            setMfaSetup({
                qr_data_url: String(json?.qr_data_url || ''),
                manual_entry_key: String(json?.manual_entry_key || ''),
                otpauth_url: String(json?.otpauth_url || '')
            });
            setMfaSetupOpen(true);
        } catch (e) {
            setMfaError(e?.message || 'Failed to start MFA setup');
        } finally {
            setMfaSetupLoading(false);
        }
    }

    async function verifyMfaSetup() {
        setMfaError('');
        setMfaSetupLoading(true);
        try {
            const code = String(mfaSetupCode || '').trim();
            if (!code) throw new Error('Enter the 6-digit code from your authenticator app');

            await fetchJson(`${API_BASE_URL}/auth/mfa/setup/verify`, {
                method: 'POST',
                headers: authHeadersJson(),
                body: JSON.stringify({ code })
            });

            setMfaSetupOpen(false);
            setMfaSetup(null);
            setMfaSetupCode('');
            await loadMfaStatus();
        } catch (e) {
            setMfaError(e?.message || 'Failed to verify MFA setup');
        } finally {
            setMfaSetupLoading(false);
        }
    }

    async function loadSmtpSettings() {
        setSmtpError('');
        setSmtpMessage('');
        setSmtpTestResult('');
        setSmtpLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/smtp`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            setSmtpSource(String(json?.source || ''));
            const s = json?.smtp || {};
            const loaded = {
                enabled: Boolean(s.enabled),
                host: String(s.host || ''),
                port: s.port == null ? '' : String(s.port),
                secure: Boolean(s.secure),
                user: String(s.user || ''),
                from: String(s.from || ''),
                reply_to: String(s.reply_to || ''),
                has_password: Boolean(s.has_password)
            };
            setSmtp(loaded);
            setSmtpBaseline({
                enabled: Boolean(loaded.enabled),
                host: String(loaded.host || ''),
                port: String(loaded.port || ''),
                secure: Boolean(loaded.secure),
                user: String(loaded.user || ''),
                from: String(loaded.from || ''),
                reply_to: String(loaded.reply_to || '')
            });
        } catch (e) {
            setSmtpError(e?.message || 'Failed to load SMTP settings');
            setSmtpSource('');
        } finally {
            setSmtpLoading(false);
        }
    }

    async function loadIssueReportSettings() {
        setIssueReportError('');
        setIssueReportMessage('');
        setIssueReportLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/issue-report`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const loadedEmail = String(json?.settings?.admin_email || '');
            setIssueReportEmail(loadedEmail);
            const days = Number(json?.settings?.resolved_retention_days);
            const loadedDays = Number.isFinite(days) ? String(days) : '3';
            setIssueReportResolvedRetentionDays(loadedDays);
            setIssueReportBaseline({ admin_email: loadedEmail, resolved_retention_days: loadedDays });
        } catch (e) {
            setIssueReportEmail('');
            setIssueReportResolvedRetentionDays('3');
            setIssueReportBaseline({ admin_email: '', resolved_retention_days: '3' });
            setIssueReportError(e?.message || 'Failed to load issue report settings');
        } finally {
            setIssueReportLoading(false);
        }
    }

    async function loadSlaSettings() {
        setSlaError('');
        setSlaMessage('');
        setSlaLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/sla`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const s = json?.sla || {};
            const loaded = {
                initial_report_turnaround_days: s.initial_report_turnaround_days == null ? '10' : String(s.initial_report_turnaround_days),
                interim_report_turnaround_days: s.interim_report_turnaround_days == null ? '10' : String(s.interim_report_turnaround_days)
            };
            setSla(loaded);
            setSlaBaseline(loaded);
        } catch (e) {
            setSlaError(e?.message || 'Failed to load SLA settings');
        } finally {
            setSlaLoading(false);
        }
    }

    async function loadFinanceSettings() {
        setFinanceError('');
        setFinanceMessage('');
        setFinanceLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/finance`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const f = json?.finance || {};
            const v = f.invoice_revenue_target_gbp;
            const loadedTarget = v == null ? '40000' : String(v);
            setInvoiceRevenueTarget(loadedTarget);
            setFinanceBaseline(loadedTarget);
        } catch (e) {
            setInvoiceRevenueTarget('40000');
            setFinanceBaseline('40000');
            setFinanceError(e?.message || 'Failed to load finance settings');
        } finally {
            setFinanceLoading(false);
        }
    }

    function normalizeInvoicingSettingsState(value) {
        const defaults = createDefaultInvoicingSettingsState();
        const src = value && typeof value === 'object' ? value : {};
        const addressLines = Array.isArray(src.address_lines)
            ? src.address_lines.map((line) => String(line || '')).slice(0, 10)
            : [];
        while (addressLines.length < 4) addressLines.push('');

        return {
            ...defaults,
            ...src,
            vat_reg_no: String(src.vat_reg_no || ''),
            invoicing_company: String(src.invoicing_company || ''),
            address_lines: addressLines,
            pay_terms: String(src.pay_terms || ''),
            bank_acc_name: String(src.bank_acc_name || ''),
            bank_acc_nr: String(src.bank_acc_nr || ''),
            bank_sort: String(src.bank_sort || ''),
            invoice_sequence_next: Math.max(1, Number(src.invoice_sequence_next) || 1000),
            template: {
                ...defaults.template,
                ...(src.template && typeof src.template === 'object' ? src.template : {}),
                original_file_name: String(src?.template?.original_file_name || ''),
                content_type: String(src?.template?.content_type || ''),
                storage_bucket: String(src?.template?.storage_bucket || ''),
                storage_key: String(src?.template?.storage_key || ''),
                storage_region: String(src?.template?.storage_region || ''),
                size_bytes: Number(src?.template?.size_bytes) || 0,
                uploaded_at: String(src?.template?.uploaded_at || ''),
                uploaded_by: String(src?.template?.uploaded_by || '')
            }
        };
    }

    async function loadInvoicingSettings() {
        setInvoicingError('');
        setInvoicingMessage('');
        setInvoicingLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/invoicing`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const loaded = normalizeInvoicingSettingsState(json?.settings || {});
            setInvoicingSettings(loaded);
            setInvoicingBaseline(loaded);
        } catch (e) {
            setInvoicingError(e?.message || 'Failed to load invoicing settings');
        } finally {
            setInvoicingLoading(false);
        }
    }

    async function saveInvoicingSettings() {
        setInvoicingError('');
        setInvoicingMessage('');
        setInvoicingSaving(true);
        try {
            const body = {
                vat_reg_no: String(invoicingSettings.vat_reg_no || '').trim(),
                invoicing_company: String(invoicingSettings.invoicing_company || '').trim(),
                address_lines: Array.isArray(invoicingSettings.address_lines)
                    ? invoicingSettings.address_lines.map((line) => String(line || '').trim()).slice(0, 10)
                    : [],
                pay_terms: String(invoicingSettings.pay_terms || '').trim(),
                bank_acc_name: String(invoicingSettings.bank_acc_name || '').trim(),
                bank_acc_nr: String(invoicingSettings.bank_acc_nr || '').trim(),
                bank_sort: String(invoicingSettings.bank_sort || '').trim(),
                invoice_sequence_next: Math.max(1, Number(invoicingSettings.invoice_sequence_next) || 1000)
            };

            const json = await fetchJson(`${API_BASE_URL}/admin/settings/invoicing`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });
            const saved = normalizeInvoicingSettingsState(json?.settings || body);
            setInvoicingSettings(saved);
            setInvoicingBaseline(saved);
            setInvoicingMessage('Invoicing settings saved.');
        } catch (e) {
            setInvoicingError(e?.message || 'Failed to save invoicing settings');
        } finally {
            setInvoicingSaving(false);
        }
    }

    async function uploadInvoicingTemplate(file) {
        setInvoicingError('');
        setInvoicingMessage('');
        setInvoicingUploading(true);
        try {
            if (!file) throw new Error('Choose a DOCX template first');
            const form = new FormData();
            form.append('file', file);

            const resp = await fetch(`${API_BASE_URL}/admin/settings/invoicing/template/upload`, {
                method: 'POST',
                headers: authHeaderOnly(),
                body: form
            });
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(json?.error || 'Upload failed');

            const uploaded = normalizeInvoicingSettingsState(json?.settings || {});
            setInvoicingSettings(uploaded);
            setInvoicingBaseline(uploaded);
            setInvoicingMessage('Invoice template uploaded.');
        } catch (e) {
            setInvoicingError(e?.message || 'Failed to upload invoice template');
        } finally {
            setInvoicingUploading(false);
        }
    }

    function updateInvoicingSetting(field, value) {
        setInvoicingSettings((prev) => ({ ...prev, [field]: value }));
    }

    function updateInvoicingAddressLine(index, value) {
        setInvoicingSettings((prev) => {
            const lines = Array.isArray(prev.address_lines) ? prev.address_lines.slice() : [];
            while (lines.length <= index) lines.push('');
            lines[index] = value;
            return { ...prev, address_lines: lines };
        });
    }

    async function loadUserPreferences() {
        setUserPrefsError('');
        setUserPrefsMessage('');
        setUserPrefsLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/auth/preferences`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const prefs = json?.preferences || json?.settings || json || {};
            const next = normalizeUserPreferencesState(prefs);
            setUserPrefs(next);
            persistUserPreferences(next);

            if (String(next?.notifications?.scope || 'all') === 'subscribed') {
                // Lazy-load subscriptions when the user opts into subscription mode.
                loadNotificationSubscriptionProjects();
                loadNotificationSubscriptions();
            }
        } catch (e) {
            setUserPrefsError(e?.message || 'Failed to load user preferences');
        } finally {
            setUserPrefsLoading(false);
        }
    }

    async function loadNotificationSubscriptionProjects() {
        if (notifProjectsLoaded || notifSubsLoading) return;
        try {
            const data = await fetchJson(`${API_BASE_URL}/key_project_data`, { method: 'GET' });
            const rows = Array.isArray(data) ? data : [];
            const active = rows
                .map((p) => ({
                    reference: String(p?.reference || '').trim(),
                    project_name: String(p?.project_name || '').trim()
                }))
                .filter((p) => p.reference);

            active.sort((a, b) => String(a.reference).localeCompare(String(b.reference)));
            setNotifProjectOptions(active);
            setNotifProjectsLoaded(true);
        } catch {
            setNotifProjectOptions([]);
            setNotifProjectsLoaded(false);
        }
    }

    async function loadNotificationSubscriptions() {
        if (notifSubsLoaded || notifSubsLoading) return;
        setNotifSubsError('');
        setNotifSubsMessage('');
        setNotifSubsLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/api/notifications/subscriptions`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });

            const items = Array.isArray(json?.items) ? json.items : [];
            const refs = items
                .map((it) => String(it?.project_reference || '').trim())
                .filter(Boolean);

            setNotifSubscribedProjects(Array.from(new Set(refs)).sort());
            setNotifSubsLoaded(true);
        } catch (e) {
            setNotifSubsLoaded(false);
            setNotifSubsError(e?.message || 'Failed to load notification subscriptions');
        } finally {
            setNotifSubsLoading(false);
        }
    }

    async function saveNotificationSubscriptions() {
        setNotifSubsError('');
        setNotifSubsMessage('');

        const refs = Array.from(new Set((Array.isArray(notifSubscribedProjects) ? notifSubscribedProjects : [])
            .map((r) => String(r || '').trim())
            .filter(Boolean)));

        setNotifSubsLoading(true);
        try {
            const payload = {
                items: refs.map((r) => ({ project_reference: r, channel: 'in_app', enabled: true }))
            };

            await fetchJson(`${API_BASE_URL}/api/notifications/subscriptions`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(payload)
            });

            setNotifSubsMessage('Subscriptions saved.');
            setNotifSubsLoaded(true);
        } catch (e) {
            setNotifSubsError(e?.message || 'Failed to save notification subscriptions');
        } finally {
            setNotifSubsLoading(false);
        }
    }

    async function saveUserPreferences() {
        setUserPrefsError('');
        setUserPrefsMessage('');
        setUserPrefsSaving(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/auth/preferences`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(userPrefs || {})
            });
            const prefs = json?.preferences || json?.settings || json || {};
            const next = normalizeUserPreferencesState({
                ...(userPrefs && typeof userPrefs === 'object' ? userPrefs : {}),
                ...(prefs && typeof prefs === 'object' ? prefs : {})
            });
            setUserPrefs(next);
            persistUserPreferences(next);

            if (String(next?.notifications?.scope || 'all') === 'subscribed') {
                await saveNotificationSubscriptions();
                setUserPrefsMessage('Preferences saved (subscription mode).');
            } else {
                setUserPrefsMessage('Preferences saved.');
            }
        } catch (e) {
            setUserPrefsError(e?.message || 'Failed to save user preferences');
        } finally {
            setUserPrefsSaving(false);
        }
    }

    useEffect(() => {
        if (!authUser) return;
        if (String(userPrefs?.notifications?.scope || 'all') !== 'subscribed') return;

        loadNotificationSubscriptionProjects();
        loadNotificationSubscriptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser, userPrefs?.notifications?.scope]);

    async function loadOrgProfileSettings() {
        setOrgProfileError('');
        setOrgProfileMessage('');
        setOrgProfileLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/org-profile`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const s = json?.settings || {};
            const loaded = {
                organisation_name: String(s.organisation_name || ''),
                report_company_shorthand: String(s.report_company_shorthand || ''),
                report_header_text: String(s.report_header_text || ''),
                report_footer_text: String(s.report_footer_text || ''),
                portal_brand_color: String(s.portal_brand_color || ''),
                portal_logo_url: String(s.portal_logo_url || '')
            };
            setOrgProfile(loaded);
            setOrgProfileBaseline(loaded);
        } catch (e) {
            setOrgProfileError(e?.message || 'Failed to load organisation profile settings');
        } finally {
            setOrgProfileLoading(false);
        }
    }

    async function saveOrgProfileSettings() {
        setOrgProfileError('');
        setOrgProfileMessage('');
        setOrgProfileSaving(true);
        try {
            const body = {
                organisation_name: String(orgProfile.organisation_name || '').trim(),
                report_company_shorthand: String(orgProfile.report_company_shorthand || '').trim(),
                report_header_text: String(orgProfile.report_header_text || ''),
                report_footer_text: String(orgProfile.report_footer_text || ''),
                portal_brand_color: String(orgProfile.portal_brand_color || '').trim(),
                portal_logo_url: String(orgProfile.portal_logo_url || '').trim()
            };
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/org-profile`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });
            const s = json?.settings || {};
            const saved = {
                organisation_name: String(s.organisation_name || ''),
                report_company_shorthand: String(s.report_company_shorthand || ''),
                report_header_text: String(s.report_header_text || ''),
                report_footer_text: String(s.report_footer_text || ''),
                portal_brand_color: String(s.portal_brand_color || ''),
                portal_logo_url: String(s.portal_logo_url || '')
            };
            setOrgProfile(saved);
            setOrgProfileBaseline(saved);
            setOrgProfileMessage('Organisation profile saved.');
        } catch (e) {
            setOrgProfileError(e?.message || 'Failed to save organisation profile settings');
        } finally {
            setOrgProfileSaving(false);
        }
    }

    function normalizeReportStyleTemplateState(value) {
        const s = value && typeof value === 'object' ? value : {};
        return {
            enabled: Boolean(s.enabled),
            original_file_name: String(s.original_file_name || ''),
            content_type: String(s.content_type || ''),
            storage_bucket: String(s.storage_bucket || ''),
            storage_key: String(s.storage_key || ''),
            storage_region: String(s.storage_region || ''),
            size_bytes: Number(s.size_bytes) || 0,
            uploaded_at: String(s.uploaded_at || ''),
            uploaded_by: String(s.uploaded_by || '')
        };
    }

    function normalizeInterimReportTemplateState(value) {
        const s = value && typeof value === 'object' ? value : {};
        const titlePage = Array.isArray(s?.placeholders?.title_page)
            ? s.placeholders.title_page.map((x) => String(x || '').trim()).filter(Boolean)
            : [];
        const headerFooter = Array.isArray(s?.placeholders?.header_footer)
            ? s.placeholders.header_footer.map((x) => String(x || '').trim()).filter(Boolean)
            : [];

        return {
            enabled: Boolean(s.enabled),
            original_file_name: String(s.original_file_name || ''),
            content_type: String(s.content_type || ''),
            storage_bucket: String(s.storage_bucket || ''),
            storage_key: String(s.storage_key || ''),
            storage_region: String(s.storage_region || ''),
            size_bytes: Number(s.size_bytes) || 0,
            uploaded_at: String(s.uploaded_at || ''),
            uploaded_by: String(s.uploaded_by || ''),
            placeholders: {
                title_page: titlePage,
                header_footer: headerFooter
            }
        };
    }

    function normalizeFeasibilityReportState(value) {
        const settings = value && typeof value === 'object' ? value : {};
        return {
            enabled: Boolean(settings.enabled),
            template_uploaded: Boolean(settings.template_uploaded),
            template_version: String(settings.template_version || 'feasibility-v0.1'),
            model_name: String(settings.model_name || ''),
            prompt_version: String(settings.prompt_version || 'feasibility-v0.1'),
            original_file_name: String(settings.original_file_name || ''),
            content_type: String(settings.content_type || ''),
            size_bytes: Number(settings.size_bytes) || 0,
            uploaded_at: String(settings.uploaded_at || ''),
            uploaded_by: String(settings.uploaded_by || ''),
            placeholders: Array.isArray(settings.placeholders)
                ? settings.placeholders.map((item) => String(item || '').trim()).filter(Boolean)
                : []
        };
    }

    function parsePlaceholderTextList(value) {
        return String(value || '')
            .split(/[,\n]/g)
            .map((x) => String(x || '').trim())
            .filter(Boolean);
    }

    const normalizeFeeProposalTemplateState = useCallback((value) => {
        const s = value && typeof value === 'object' ? value : {};
        const normalizeEntry = (entry) => {
            const item = entry && typeof entry === 'object' ? entry : {};
            return {
                enabled: Boolean(item.enabled),
                original_file_name: String(item.original_file_name || ''),
                content_type: String(item.content_type || ''),
                storage_bucket: String(item.storage_bucket || ''),
                storage_key: String(item.storage_key || ''),
                storage_region: String(item.storage_region || ''),
                size_bytes: Number(item.size_bytes) || 0,
                uploaded_at: String(item.uploaded_at || ''),
                uploaded_by: String(item.uploaded_by || '')
            };
        };

        const rawServiceLines = s.service_lines && typeof s.service_lines === 'object' ? s.service_lines : {};
        return {
            service_lines: Object.fromEntries(
                FEE_PROPOSAL_TEMPLATE_SERVICE_LINES.map(({ key }) => [key, normalizeEntry(rawServiceLines[key])])
            )
        };
    }, [FEE_PROPOSAL_TEMPLATE_SERVICE_LINES]);

    async function loadReportStyleTemplateSettings() {
        setReportStyleTemplateError('');
        setReportStyleTemplateMessage('');
        setReportStyleTemplateLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/report-style-template`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const loaded = normalizeReportStyleTemplateState(json?.settings || {});
            setReportStyleTemplate(loaded);
            setReportStyleTemplateBaseline(loaded);
        } catch (e) {
            setReportStyleTemplateError(e?.message || 'Failed to load report style template settings');
        } finally {
            setReportStyleTemplateLoading(false);
        }
    }

    async function saveReportStyleTemplateSettings() {
        setReportStyleTemplateError('');
        setReportStyleTemplateMessage('');
        setReportStyleTemplateSaving(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/report-style-template`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify({ enabled: Boolean(reportStyleTemplate.enabled) })
            });
            const saved = normalizeReportStyleTemplateState(json?.settings || {});
            setReportStyleTemplate(saved);
            setReportStyleTemplateBaseline(saved);
            setReportStyleTemplateMessage('Report style template settings saved.');
        } catch (e) {
            setReportStyleTemplateError(e?.message || 'Failed to save report style template settings');
        } finally {
            setReportStyleTemplateSaving(false);
        }
    }

    function normalizeReportVersionControlCount(value) {
        const n = Number(value);
        if (!Number.isFinite(n)) return 5;
        return Math.max(1, Math.min(10, Math.trunc(n)));
    }

    async function loadReportVersionControlSettings() {
        setReportVersionControlError('');
        setReportVersionControlMessage('');
        setReportVersionControlLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/report-version-control`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const retained = normalizeReportVersionControlCount(json?.settings?.retained_versions);
            setReportVersionControlRetained(retained);
            setReportVersionControlBaseline(retained);
        } catch (e) {
            setReportVersionControlError(e?.message || 'Failed to load report version control settings');
        } finally {
            setReportVersionControlLoading(false);
        }
    }

    async function saveReportVersionControlSettings() {
        setReportVersionControlError('');
        setReportVersionControlMessage('');
        setReportVersionControlSaving(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/report-version-control`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify({ retained_versions: normalizeReportVersionControlCount(reportVersionControlRetained) })
            });
            const retained = normalizeReportVersionControlCount(json?.settings?.retained_versions);
            setReportVersionControlRetained(retained);
            setReportVersionControlBaseline(retained);
            setReportVersionControlMessage('Report version control settings saved.');
        } catch (e) {
            setReportVersionControlError(e?.message || 'Failed to save report version control settings');
        } finally {
            setReportVersionControlSaving(false);
        }
    }

    async function uploadReportStyleTemplate(file) {
        setReportStyleTemplateError('');
        setReportStyleTemplateMessage('');
        setReportStyleTemplateUploading(true);
        try {
            if (!file) throw new Error('Choose a DOCX template first');
            const form = new FormData();
            form.append('file', file);
            const resp = await fetch(`${API_BASE_URL}/admin/settings/report-style-template/upload`, {
                method: 'POST',
                headers: authHeaderOnly(),
                body: form
            });
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(json?.error || 'Upload failed');
            const uploaded = normalizeReportStyleTemplateState(json?.settings || {});
            setReportStyleTemplate(uploaded);
            setReportStyleTemplateBaseline(uploaded);
            setReportStyleTemplateMessage('Report style template uploaded.');
        } catch (e) {
            setReportStyleTemplateError(e?.message || 'Failed to upload report style template');
        } finally {
            setReportStyleTemplateUploading(false);
        }
    }

    async function loadInterimReportTemplateSettings() {
        setInterimReportTemplateError('');
        setInterimReportTemplateMessage('');
        setInterimReportTemplateLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/interim-report-template`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const loaded = normalizeInterimReportTemplateState(json?.settings || {});
            setInterimReportTemplate(loaded);
            setInterimReportTemplateBaseline(loaded);
        } catch (e) {
            setInterimReportTemplateError(e?.message || 'Failed to load interim report template settings');
        } finally {
            setInterimReportTemplateLoading(false);
        }
    }

    async function saveInterimReportTemplateSettings() {
        setInterimReportTemplateError('');
        setInterimReportTemplateMessage('');
        setInterimReportTemplateSaving(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/interim-report-template`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify({
                    enabled: Boolean(interimReportTemplate.enabled),
                    placeholders: {
                        title_page: parsePlaceholderTextList(interimReportTemplate.placeholders?.title_page?.join('\n')),
                        header_footer: parsePlaceholderTextList(interimReportTemplate.placeholders?.header_footer?.join('\n'))
                    }
                })
            });
            const saved = normalizeInterimReportTemplateState(json?.settings || {});
            setInterimReportTemplate(saved);
            setInterimReportTemplateBaseline(saved);
            setInterimReportTemplateMessage('Interim report template settings saved.');
        } catch (e) {
            setInterimReportTemplateError(e?.message || 'Failed to save interim report template settings');
        } finally {
            setInterimReportTemplateSaving(false);
        }
    }

    async function uploadInterimReportTemplate(file) {
        setInterimReportTemplateError('');
        setInterimReportTemplateMessage('');
        setInterimReportTemplateUploading(true);
        try {
            if (!file) throw new Error('Choose a DOCX template first');
            const form = new FormData();
            form.append('file', file);
            const resp = await fetch(`${API_BASE_URL}/admin/settings/interim-report-template/upload`, {
                method: 'POST',
                headers: authHeaderOnly(),
                body: form
            });
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(json?.error || 'Upload failed');
            const uploaded = normalizeInterimReportTemplateState(json?.settings || {});
            setInterimReportTemplate(uploaded);
            setInterimReportTemplateBaseline(uploaded);
            setInterimReportTemplateMessage('Interim report template uploaded.');
        } catch (e) {
            setInterimReportTemplateError(e?.message || 'Failed to upload interim report template');
        } finally {
            setInterimReportTemplateUploading(false);
        }
    }

    async function loadFeasibilityReportSettings() {
        if (!canConfigureFeasibilityReport) return;
        setFeasibilityReportError('');
        setFeasibilityReportMessage('');
        setFeasibilityReportLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/feasibility-report`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const loaded = normalizeFeasibilityReportState(json?.settings || {});
            setFeasibilityReport(loaded);
            setFeasibilityReportBaseline(loaded);
        } catch (e) {
            setFeasibilityReportError(e?.message || 'Failed to load Feasibility report settings');
        } finally {
            setFeasibilityReportLoading(false);
        }
    }

    async function saveFeasibilityReportSettings() {
        setFeasibilityReportError('');
        setFeasibilityReportMessage('');
        setFeasibilityReportSaving(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/feasibility-report`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify({
                    enabled: Boolean(feasibilityReport.enabled),
                    template_version: feasibilityReport.template_version,
                    model_name: feasibilityReport.model_name,
                    prompt_version: feasibilityReport.prompt_version
                })
            });
            const saved = normalizeFeasibilityReportState(json?.settings || {});
            setFeasibilityReport(saved);
            setFeasibilityReportBaseline(saved);
            setFeasibilityReportMessage('Feasibility report settings saved.');
        } catch (e) {
            setFeasibilityReportError(e?.message || 'Failed to save Feasibility report settings');
        } finally {
            setFeasibilityReportSaving(false);
        }
    }

    async function uploadFeasibilityReportTemplate(file) {
        setFeasibilityReportError('');
        setFeasibilityReportMessage('');
        setFeasibilityReportUploading(true);
        try {
            if (!file) throw new Error('Choose a DOCX template first');
            const form = new FormData();
            form.append('file', file);
            const response = await fetch(`${API_BASE_URL}/admin/settings/feasibility-report/upload`, {
                method: 'POST',
                headers: authHeaderOnly(),
                body: form
            });
            const json = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(json?.error || 'Upload failed');
            const uploaded = normalizeFeasibilityReportState(json?.settings || {});
            setFeasibilityReport(uploaded);
            setFeasibilityReportBaseline(uploaded);
            setFeasibilityReportMessage('Feasibility report template uploaded.');
        } catch (e) {
            setFeasibilityReportError(e?.message || 'Failed to upload Feasibility report template');
        } finally {
            setFeasibilityReportUploading(false);
        }
    }

    async function loadFeeProposalTemplateSettings() {
        setFeeProposalTemplateError('');
        setFeeProposalTemplateMessage('');
        setFeeProposalTemplateLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/fee-proposal-template`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const loaded = normalizeFeeProposalTemplateState(json?.settings || {});
            setFeeProposalTemplate(loaded);
            setFeeProposalTemplateBaseline(loaded);
        } catch (e) {
            setFeeProposalTemplateError(e?.message || 'Failed to load fee proposal template settings');
        } finally {
            setFeeProposalTemplateLoading(false);
        }
    }

    async function saveFeeProposalTemplateSettings() {
        setFeeProposalTemplateError('');
        setFeeProposalTemplateMessage('');
        setFeeProposalTemplateSaving(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/fee-proposal-template`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify({ service_lines: feeProposalTemplate.service_lines || {} })
            });
            const saved = normalizeFeeProposalTemplateState(json?.settings || {});
            setFeeProposalTemplate(saved);
            setFeeProposalTemplateBaseline(saved);
            setFeeProposalTemplateMessage('Fee proposal template settings saved.');
        } catch (e) {
            setFeeProposalTemplateError(e?.message || 'Failed to save fee proposal template settings');
        } finally {
            setFeeProposalTemplateSaving(false);
        }
    }

    async function uploadFeeProposalTemplate(serviceLine, file) {
        setFeeProposalTemplateError('');
        setFeeProposalTemplateMessage('');
        setFeeProposalTemplateUploading(true);
        try {
            if (!file) throw new Error('Choose a DOCX template first');
            const form = new FormData();
            form.append('service_line', String(serviceLine || ''));
            form.append('file', file);
            const resp = await fetch(`${API_BASE_URL}/admin/settings/fee-proposal-template/upload`, {
                method: 'POST',
                headers: authHeaderOnly(),
                body: form
            });
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(json?.error || 'Upload failed');
            const uploaded = normalizeFeeProposalTemplateState(json?.settings || {});
            setFeeProposalTemplate(uploaded);
            setFeeProposalTemplateBaseline(uploaded);
            const selectedLine = FEE_PROPOSAL_TEMPLATE_SERVICE_LINES.find((item) => item.key === serviceLine);
            setFeeProposalTemplateMessage(`${selectedLine?.label || 'Fee proposal'} template uploaded.`);
        } catch (e) {
            setFeeProposalTemplateError(e?.message || 'Failed to upload fee proposal template');
        } finally {
            setFeeProposalTemplateUploading(false);
        }
    }

    async function loadOfficeMapSettings() {
        setOfficeMapError('');
        setOfficeMapMessage('');
        setOfficeMapLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/office-map`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const merged = choosePreferredOfficeMapSettings(json?.settings || null, readOfficeMapSettingsCache(officeMapCacheStorageKey));
            setOfficeMap(merged);
            setOfficeMapBaseline(merged);
        } catch (e) {
            const cached = readOfficeMapSettingsCache(officeMapCacheStorageKey);
            if (cached) {
                const normalized = normalizeOfficeMapFormSettings(cached);
                setOfficeMap(normalized);
                setOfficeMapBaseline(normalized);
            } else {
                setOfficeMapError(e?.message || 'Failed to load office map settings');
            }
        } finally {
            setOfficeMapLoading(false);
        }
    }

    async function saveOfficeMapSettings() {
        setOfficeMapError('');
        setOfficeMapMessage('');
        setOfficeMapSaving(true);
        try {
            const draftOffices = (Array.isArray(officeMap.offices) ? officeMap.offices : [])
                .map((office, index) => ({
                    ...createOfficeMapDraftOffice(index, office),
                    office_location: String(office?.office_location || ''),
                    address: String(office?.address || ''),
                    pin_color: String(office?.pin_color || '#0f3b68').trim() || '#0f3b68'
                }));
            const incompleteOffice = draftOffices.find((office) => {
                const hasAnyContent = String(office.office_location || '').trim() || String(office.address || '').trim();
                if (!hasAnyContent) return false;
                return !String(office.address || '').trim();
            });
            if (incompleteOffice) {
                throw new Error('Each office needs an address before it can be saved.');
            }

            const cleanedOffices = draftOffices
                .map((office) => ({
                    ...office,
                    office_location: String(office.office_location || '').trim(),
                    address: String(office.address || '').trim()
                }))
                .filter((office) => office.office_location || office.address);

            if (!cleanedOffices.length) {
                throw new Error('Add at least one office address before saving.');
            }

            const primaryOfficeId = cleanedOffices.some((office) => office.id === officeMap.primary_office_id)
                ? String(officeMap.primary_office_id || '')
                : (cleanedOffices[0]?.id || '');
            const body = {
                offices: cleanedOffices,
                primary_office_id: primaryOfficeId,
                brand_upload_url: String(officeMap.brand_upload_url || '').trim()
            };
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/office-map`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });
            const normalized = choosePreferredOfficeMapSettings(json?.settings || null, {
                ...body,
                brand_upload_url: String(officeMap.brand_upload_url || '').trim()
            });
            setOfficeMap(normalized);
            setOfficeMapBaseline(normalized);
            writeOfficeMapSettingsCache(normalized, officeMapCacheStorageKey);
            try {
                window.dispatchEvent(new CustomEvent(OFFICE_MAP_SETTINGS_UPDATED_EVENT, { detail: { settings: normalized } }));
            } catch {
                // ignore
            }
            setOfficeMapMessage('Office map settings saved.');
        } catch (e) {
            setOfficeMapError(e?.message || 'Failed to save office map settings');
        } finally {
            setOfficeMapSaving(false);
        }
    }

    async function uploadOfficeMapBrand(file) {
        setOfficeMapError('');
        setOfficeMapMessage('');
        setOfficeMapUploading(true);
        try {
            if (!file) throw new Error('Choose an image first');
            const form = new FormData();
            form.append('brand', file);
            const resp = await fetch(`${API_BASE_URL}/admin/settings/office-map/brand-upload`, {
                method: 'POST',
                headers: authHeaderOnly(),
                body: form
            });
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(json?.error || 'Upload failed');
            const previousOfficeMap = officeMap;
            const normalized = choosePreferredOfficeMapSettings({
                ...(previousOfficeMap || {}),
                ...(json?.settings || {}),
                brand_upload_url: String(json?.settings?.brand_upload_url || json?.brand_upload_url || previousOfficeMap?.brand_upload_url || '')
            }, readOfficeMapSettingsCache(officeMapCacheStorageKey));
            setOfficeMap(normalized);
            setOfficeMapBaseline(normalized);
            writeOfficeMapSettingsCache(normalized, officeMapCacheStorageKey);
            try {
                window.dispatchEvent(new CustomEvent(OFFICE_MAP_SETTINGS_UPDATED_EVENT, { detail: { settings: normalized } }));
            } catch {
                // ignore
            }
            setOfficeMapMessage('Office pin brand uploaded.');
        } catch (e) {
            setOfficeMapError(e?.message || 'Failed to upload office pin brand');
        } finally {
            setOfficeMapUploading(false);
        }
    }

    function updateOfficeMapOffice(officeId, patch) {
        setOfficeMap((prev) => {
            const nextOffices = (Array.isArray(prev?.offices) ? prev.offices : []).map((office, index) => {
                if (office.id !== officeId) return office;
                const nextPatch = typeof patch === 'function' ? patch(office) : patch;
                return createOfficeMapDraftOffice(index, { ...office, ...(nextPatch || {}) });
            });
            return normalizeOfficeMapFormSettings({ ...(prev || {}), offices: nextOffices });
        });
    }

    function addOfficeMapOffice() {
        setOfficeMap((prev) => {
            const existing = Array.isArray(prev?.offices) ? prev.offices : [];
            const nextOffices = [...existing, createOfficeMapDraftOffice(existing.length)];
            return normalizeOfficeMapFormSettings({ ...(prev || {}), offices: nextOffices });
        });
    }

    function removeOfficeMapOffice(officeId) {
        setOfficeMap((prev) => {
            const existing = Array.isArray(prev?.offices) ? prev.offices : [];
            const filtered = existing.filter((office) => office.id !== officeId);
            return normalizeOfficeMapFormSettings({ ...(prev || {}), offices: filtered });
        });
    }

    function setPrimaryOfficeMapOffice(officeId) {
        setOfficeMap((prev) => normalizeOfficeMapFormSettings({ ...(prev || {}), primary_office_id: officeId }));
    }

    async function loadRetentionSettings() {
        setRetentionError('');
        setRetentionMessage('');
        setRetentionLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/retention`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const s = json?.settings || {};
            const types = Array.isArray(s.allowed_upload_types) ? s.allowed_upload_types : [];
            const loaded = {
                uploads_retention_days: s.uploads_retention_days == null ? '365' : String(s.uploads_retention_days),
                notifications_retention_days: s.notifications_retention_days == null ? '365' : String(s.notifications_retention_days),
                activity_retention_days: s.activity_retention_days == null ? '365' : String(s.activity_retention_days),
                max_upload_mb: s.max_upload_mb == null ? '20' : String(s.max_upload_mb),
                allowed_upload_types_text: (types.length ? types : ['application/pdf', 'image/jpeg', 'image/png', 'image/webp']).join('\n')
            };
            setRetention(loaded);
            setRetentionBaseline(loaded);
        } catch (e) {
            setRetentionError(e?.message || 'Failed to load retention settings');
        } finally {
            setRetentionLoading(false);
        }
    }

    async function saveRetentionSettings() {
        setRetentionError('');
        setRetentionMessage('');
        setRetentionSaving(true);
        try {
            const allowed = String(retention.allowed_upload_types_text || '')
                .split(/\r?\n/)
                .map((s) => String(s || '').trim())
                .filter(Boolean);

            const body = {
                uploads_retention_days: Number(retention.uploads_retention_days),
                notifications_retention_days: Number(retention.notifications_retention_days),
                activity_retention_days: Number(retention.activity_retention_days),
                max_upload_mb: Number(retention.max_upload_mb),
                allowed_upload_types: allowed
            };
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/retention`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });
            const s = json?.settings || {};
            const types = Array.isArray(s.allowed_upload_types) ? s.allowed_upload_types : allowed;
            const saved = {
                uploads_retention_days: s.uploads_retention_days == null ? String(body.uploads_retention_days || 365) : String(s.uploads_retention_days),
                notifications_retention_days: s.notifications_retention_days == null ? String(body.notifications_retention_days || 365) : String(s.notifications_retention_days),
                activity_retention_days: s.activity_retention_days == null ? String(body.activity_retention_days || 365) : String(s.activity_retention_days),
                max_upload_mb: s.max_upload_mb == null ? String(body.max_upload_mb || 20) : String(s.max_upload_mb),
                allowed_upload_types_text: (types || []).join('\n')
            };
            setRetention(saved);
            setRetentionBaseline(saved);
            setRetentionMessage('Retention settings saved.');
        } catch (e) {
            setRetentionError(e?.message || 'Failed to save retention settings');
        } finally {
            setRetentionSaving(false);
        }
    }

    async function loadIntegrationsSettings() {
        setIntegrationsError('');
        setIntegrationsMessage('');
        setIntegrationsLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/integrations`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const s = json?.settings || {};
            const loaded = {
                slack_webhook_url: String(s.slack_webhook_url || ''),
                teams_webhook_url: String(s.teams_webhook_url || ''),
                powerbi_enabled: Boolean(s.powerbi_enabled),
                powerbi_workspace_id: String(s.powerbi_workspace_id || ''),
                powerbi_dataset_id: String(s.powerbi_dataset_id || '')
            };
            setIntegrations(loaded);
            setIntegrationsBaseline(loaded);
        } catch (e) {
            setIntegrationsError(e?.message || 'Failed to load integrations settings');
        } finally {
            setIntegrationsLoading(false);
        }
    }

    async function saveIntegrationsSettings() {
        setIntegrationsError('');
        setIntegrationsMessage('');
        setIntegrationsSaving(true);
        try {
            const body = {
                slack_webhook_url: String(integrations.slack_webhook_url || '').trim(),
                teams_webhook_url: String(integrations.teams_webhook_url || '').trim(),
                powerbi_enabled: Boolean(integrations.powerbi_enabled),
                powerbi_workspace_id: String(integrations.powerbi_workspace_id || '').trim(),
                powerbi_dataset_id: String(integrations.powerbi_dataset_id || '').trim()
            };
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/integrations`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });
            const s = json?.settings || {};
            const saved = {
                slack_webhook_url: String(s.slack_webhook_url || ''),
                teams_webhook_url: String(s.teams_webhook_url || ''),
                powerbi_enabled: Boolean(s.powerbi_enabled),
                powerbi_workspace_id: String(s.powerbi_workspace_id || ''),
                powerbi_dataset_id: String(s.powerbi_dataset_id || '')
            };
            setIntegrations(saved);
            setIntegrationsBaseline(saved);
            setIntegrationsMessage('Integrations settings saved.');
        } catch (e) {
            setIntegrationsError(e?.message || 'Failed to save integrations settings');
        } finally {
            setIntegrationsSaving(false);
        }
    }

    async function loadSecurityPolicySettings() {
        setSecurityPolicyError('');
        setSecurityPolicyMessage('');
        setSecurityPolicyLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/security`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const s = json?.settings || {};
            const loaded = {
                require_mfa_for_admins: Boolean(s.require_mfa_for_admins),
                session_timeout_minutes: s.session_timeout_minutes == null ? '720' : String(s.session_timeout_minutes),
                idle_timeout_minutes: s.idle_timeout_minutes == null ? '240' : String(s.idle_timeout_minutes),
                password_min_length: s.password_min_length == null ? '12' : String(s.password_min_length)
            };
            setSecurityPolicy(loaded);
            setSecurityPolicyBaseline(loaded);
        } catch (e) {
            setSecurityPolicyError(e?.message || 'Failed to load security policy settings');
        } finally {
            setSecurityPolicyLoading(false);
        }
    }

    async function saveSecurityPolicySettings() {
        setSecurityPolicyError('');
        setSecurityPolicyMessage('');
        setSecurityPolicySaving(true);
        try {
            const body = {
                require_mfa_for_admins: Boolean(securityPolicy.require_mfa_for_admins),
                session_timeout_minutes: Number(securityPolicy.session_timeout_minutes),
                idle_timeout_minutes: Number(securityPolicy.idle_timeout_minutes),
                password_min_length: Number(securityPolicy.password_min_length)
            };
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/security`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });
            const s = json?.settings || {};
            const saved = {
                require_mfa_for_admins: Boolean(s.require_mfa_for_admins),
                session_timeout_minutes: s.session_timeout_minutes == null ? String(body.session_timeout_minutes || 720) : String(s.session_timeout_minutes),
                idle_timeout_minutes: s.idle_timeout_minutes == null ? String(body.idle_timeout_minutes || 240) : String(s.idle_timeout_minutes),
                password_min_length: s.password_min_length == null ? String(body.password_min_length || 12) : String(s.password_min_length)
            };
            setSecurityPolicy(saved);
            setSecurityPolicyBaseline(saved);
            setSecurityPolicyMessage('Security policy saved.');
        } catch (e) {
            setSecurityPolicyError(e?.message || 'Failed to save security policy settings');
        } finally {
            setSecurityPolicySaving(false);
        }
    }

    async function loadDeletionGuardSettings() {
        setDeletionGuardError('');
        setDeletionGuardMessage('');
        setDeletionGuardLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/deletion-guard`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const s = json?.settings || {};
            setDeletionGuard({ enabled: Boolean(s.enabled), configured: Boolean(s.configured) });
        } catch (e) {
            setDeletionGuardError(e?.message || 'Failed to load deletion guard settings');
        } finally {
            setDeletionGuardLoading(false);
        }
    }

    async function saveDeletionGuardSettings() {
        setDeletionGuardError('');
        setDeletionGuardMessage('');
        setDeletionGuardSaving(true);
        try {
            const pw = String(deletionGuardPassword || '');
            const pw2 = String(deletionGuardPasswordConfirm || '');
            if (!pw || !pw2) throw new Error('Enter the deletion password twice');
            if (pw !== pw2) throw new Error('Deletion passwords do not match');
            if (pw.length < 8) throw new Error('Deletion password must be at least 8 characters');

            const json = await fetchJson(`${API_BASE_URL}/admin/settings/deletion-guard`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify({ deletion_password: pw })
            });
            const s = json?.settings || {};
            setDeletionGuard({ enabled: Boolean(s.enabled), configured: Boolean(s.configured) });
            setDeletionGuardPassword('');
            setDeletionGuardPasswordConfirm('');
            setDeletionGuardMessage('Deletion password saved. This password is now required for admin deletions.');
        } catch (e) {
            setDeletionGuardError(e?.message || 'Failed to save deletion guard settings');
        } finally {
            setDeletionGuardSaving(false);
        }
    }

    async function loadAiPolicySettings() {
        setAiPolicyError('');
        setAiPolicyMessage('');
        setAiPolicyLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/ai-policy`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const s = json?.settings || {};
            const loaded = {
                ai_enabled: Boolean(s.ai_enabled),
                allow_ai_photo_analysis: Boolean(s.allow_ai_photo_analysis),
                allow_online_lookup: Boolean(s.allow_online_lookup),
                default_model: String(s.default_model || ''),
                temperature_cap: s.temperature_cap == null ? '0.6' : String(s.temperature_cap)
            };
            setAiPolicy(loaded);
            setAiPolicyBaseline(loaded);
        } catch (e) {
            setAiPolicyError(e?.message || 'Failed to load AI policy settings');
        } finally {
            setAiPolicyLoading(false);
        }
    }

    async function saveAiPolicySettings() {
        setAiPolicyError('');
        setAiPolicyMessage('');
        setAiPolicySaving(true);
        try {
            const body = {
                ai_enabled: Boolean(aiPolicy.ai_enabled),
                allow_ai_photo_analysis: Boolean(aiPolicy.allow_ai_photo_analysis),
                allow_online_lookup: Boolean(aiPolicy.allow_online_lookup),
                default_model: String(aiPolicy.default_model || '').trim(),
                temperature_cap: Number(aiPolicy.temperature_cap)
            };
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/ai-policy`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });
            const s = json?.settings || {};
            const saved = {
                ai_enabled: Boolean(s.ai_enabled),
                allow_ai_photo_analysis: Boolean(s.allow_ai_photo_analysis),
                allow_online_lookup: Boolean(s.allow_online_lookup),
                default_model: String(s.default_model || ''),
                temperature_cap: s.temperature_cap == null ? String(body.temperature_cap ?? 0.6) : String(s.temperature_cap)
            };
            setAiPolicy(saved);
            setAiPolicyBaseline(saved);
            setAiPolicyMessage('AI policy saved.');
        } catch (e) {
            setAiPolicyError(e?.message || 'Failed to save AI policy settings');
        } finally {
            setAiPolicySaving(false);
        }
    }

    async function loadEmailTemplatesSettings() {
        setEmailTemplatesError('');
        setEmailTemplatesMessage('');
        setEmailTemplatesLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/email-templates`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const s = json?.settings || {};
            const loaded = {
                invite_subject: String(s.invite_subject || 'You have been invited to MonSurv'),
                invite_body: String(s.invite_body || ''),
                new_client_access_subject: String(s.new_client_access_subject || 'Welcome to Monsurv Broker Access - <broker_company_name>'),
                new_client_access_body: String(s.new_client_access_body || (
                    'Hello <first_name>,\n\n'
                    + 'Welcome to Monsurv Broker Access. An account has been created for you on behalf of <broker_company_name>.\n\n'
                    + 'Your login details are:\n\n'
                    + 'Email: <contact_email>\n'
                    + 'Portal: <login_link>\n'
                    + 'Temporary password: <temporary_password>\n\n'
                    + 'When you sign in for the first time, you will be asked to create your own password. For security, this temporary password can only be used to complete that step.\n\n'
                    + 'Many thanks,\n\n'
                    + 'Monsurv'
                )),
                developer_questionnaire_subject: String(s.developer_questionnaire_subject || 'Information Portal Access - <project_reference>'),
                developer_questionnaire_body: String(s.developer_questionnaire_body || (
                    'Hello <developer_first_name>,\n\n'
                    + 'We have been appointed by <funder_name> to provide Independent Monitoring for your project at <site_address>.\n\n'
                    + 'Company: <developer_company>\n\n'
                    + 'Your unique project reference is <project_reference> and your questionnaire/Information Dashboard password is <temporary_password>.\n\n'
                    + 'Please use this link to access the questionnaire:\n\n'
                    + '<portal_link>\n\n'
                    + 'Many thanks,\n\n'
                    + '<lead_surveyor_name>\n'
                )),
                information_outstanding_subject: String(s.information_outstanding_subject || 'Information outstanding'),
                information_outstanding_body: String(s.information_outstanding_body || ''),
                insurance_expiry_subject: String(s.insurance_expiry_subject || 'Insurance expiry reminder'),
                insurance_expiry_body: String(s.insurance_expiry_body || ''),
                valuation_reminder_subject: String(s.valuation_reminder_subject || 'Valuation reminder'),
                valuation_reminder_body: String(s.valuation_reminder_body || ''),
                report_ready_subject: String(s.report_ready_subject || 'Your report is ready'),
                report_ready_body: String(s.report_ready_body || ''),
                feasibility_report_issued_subject: String(s.feasibility_report_issued_subject || 'Your Feasibility Analysis Report has been issued - <deal_reference>'),
                feasibility_report_issued_body: String(s.feasibility_report_issued_body || ''),
                action_report_subject: String(s.action_report_subject || 'Your Monsurv Action Report - <report_date>'),
                action_report_body: String(s.action_report_body || ''),
                action_delegated_subject: String(s.action_delegated_subject || 'A Monsurv task has been delegated to you - <task_title>'),
                action_delegated_body: String(s.action_delegated_body || ''),
                password_reset_subject: String(s.password_reset_subject || 'Password reset'),
                password_reset_body: String(s.password_reset_body || '')
            };
            setEmailTemplates(loaded);
            setEmailTemplatesBaseline(loaded);
        } catch (e) {
            setEmailTemplatesError(e?.message || 'Failed to load email templates');
        } finally {
            setEmailTemplatesLoading(false);
        }
    }

    async function saveEmailTemplatesSettings() {
        setEmailTemplatesError('');
        setEmailTemplatesMessage('');
        setEmailTemplatesSaving(true);
        try {
            const body = {
                invite_subject: String(emailTemplates.invite_subject || '').trim(),
                invite_body: String(emailTemplates.invite_body || ''),
                new_client_access_subject: String(emailTemplates.new_client_access_subject || '').trim(),
                new_client_access_body: String(emailTemplates.new_client_access_body || ''),
                developer_questionnaire_subject: String(emailTemplates.developer_questionnaire_subject || '').trim(),
                developer_questionnaire_body: String(emailTemplates.developer_questionnaire_body || ''),
                information_outstanding_subject: String(emailTemplates.information_outstanding_subject || '').trim(),
                information_outstanding_body: String(emailTemplates.information_outstanding_body || ''),
                insurance_expiry_subject: String(emailTemplates.insurance_expiry_subject || '').trim(),
                insurance_expiry_body: String(emailTemplates.insurance_expiry_body || ''),
                valuation_reminder_subject: String(emailTemplates.valuation_reminder_subject || '').trim(),
                valuation_reminder_body: String(emailTemplates.valuation_reminder_body || ''),
                report_ready_subject: String(emailTemplates.report_ready_subject || '').trim(),
                report_ready_body: String(emailTemplates.report_ready_body || ''),
                feasibility_report_issued_subject: String(emailTemplates.feasibility_report_issued_subject || '').trim(),
                feasibility_report_issued_body: String(emailTemplates.feasibility_report_issued_body || ''),
                action_report_subject: String(emailTemplates.action_report_subject || '').trim(),
                action_report_body: String(emailTemplates.action_report_body || ''),
                action_delegated_subject: String(emailTemplates.action_delegated_subject || '').trim(),
                action_delegated_body: String(emailTemplates.action_delegated_body || ''),
                password_reset_subject: String(emailTemplates.password_reset_subject || '').trim(),
                password_reset_body: String(emailTemplates.password_reset_body || '')
            };
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/email-templates`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });
            const s = json?.settings || {};
            const saved = {
                invite_subject: String(s.invite_subject || ''),
                invite_body: String(s.invite_body || ''),
                new_client_access_subject: String(s.new_client_access_subject || ''),
                new_client_access_body: String(s.new_client_access_body || ''),
                developer_questionnaire_subject: String(s.developer_questionnaire_subject || ''),
                developer_questionnaire_body: String(s.developer_questionnaire_body || ''),
                information_outstanding_subject: String(s.information_outstanding_subject || ''),
                information_outstanding_body: String(s.information_outstanding_body || ''),
                insurance_expiry_subject: String(s.insurance_expiry_subject || ''),
                insurance_expiry_body: String(s.insurance_expiry_body || ''),
                valuation_reminder_subject: String(s.valuation_reminder_subject || ''),
                valuation_reminder_body: String(s.valuation_reminder_body || ''),
                report_ready_subject: String(s.report_ready_subject || ''),
                report_ready_body: String(s.report_ready_body || ''),
                feasibility_report_issued_subject: String(s.feasibility_report_issued_subject || ''),
                feasibility_report_issued_body: String(s.feasibility_report_issued_body || ''),
                action_report_subject: String(s.action_report_subject || ''),
                action_report_body: String(s.action_report_body || ''),
                action_delegated_subject: String(s.action_delegated_subject || ''),
                action_delegated_body: String(s.action_delegated_body || ''),
                password_reset_subject: String(s.password_reset_subject || ''),
                password_reset_body: String(s.password_reset_body || '')
            };
            setEmailTemplates(saved);
            setEmailTemplatesBaseline(saved);
            setEmailTemplatesMessage('Email templates saved.');
        } catch (e) {
            setEmailTemplatesError(e?.message || 'Failed to save email templates');
        } finally {
            setEmailTemplatesSaving(false);
        }
    }

    async function loadAnalyticsExportSettings() {
        setAnalyticsExportError('');
        setAnalyticsExportMessage('');
        setAnalyticsExportLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/analytics-export`, {
                method: 'GET',
                headers: { Authorization: authHeadersJson().Authorization }
            });
            const s = json?.settings || {};
            const loaded = {
                exports_enabled: Boolean(s.exports_enabled),
                exports_frequency: String(s.exports_frequency || 'weekly'),
                exports_email_to: String(s.exports_email_to || ''),
                include_project_financials: Boolean(s.include_project_financials),
                include_notifications: Boolean(s.include_notifications)
            };
            setAnalyticsExport(loaded);
            setAnalyticsExportBaseline(loaded);
        } catch (e) {
            setAnalyticsExportError(e?.message || 'Failed to load analytics export settings');
        } finally {
            setAnalyticsExportLoading(false);
        }
    }

    async function saveAnalyticsExportSettings() {
        setAnalyticsExportError('');
        setAnalyticsExportMessage('');
        setAnalyticsExportSaving(true);
        try {
            const body = {
                exports_enabled: Boolean(analyticsExport.exports_enabled),
                exports_frequency: String(analyticsExport.exports_frequency || 'weekly').trim(),
                exports_email_to: String(analyticsExport.exports_email_to || '').trim(),
                include_project_financials: Boolean(analyticsExport.include_project_financials),
                include_notifications: Boolean(analyticsExport.include_notifications)
            };
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/analytics-export`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });
            const s = json?.settings || {};
            const saved = {
                exports_enabled: Boolean(s.exports_enabled),
                exports_frequency: String(s.exports_frequency || 'weekly'),
                exports_email_to: String(s.exports_email_to || ''),
                include_project_financials: Boolean(s.include_project_financials),
                include_notifications: Boolean(s.include_notifications)
            };
            setAnalyticsExport(saved);
            setAnalyticsExportBaseline(saved);
            setAnalyticsExportMessage('Analytics export settings saved.');
        } catch (e) {
            setAnalyticsExportError(e?.message || 'Failed to save analytics export settings');
        } finally {
            setAnalyticsExportSaving(false);
        }
    }

    async function loadNotificationDigestAdminSettings() {
        setNotificationDigestAdminError('');
        setNotificationDigestAdminMessage('');
        setNotificationDigestAdminLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/notification-digest`, {
                method: 'GET',
                headers: authHeadersJson()
            });
            const s = json?.settings || {};
            const loaded = {
                shared_mailbox_enabled: Boolean(s.shared_mailbox_enabled),
                shared_mailbox_to: String(s.shared_mailbox_to || ''),
                shared_mailbox_only: Boolean(s.shared_mailbox_only),
                max_items_per_digest: String(s.max_items_per_digest ?? 50),
                lookback_hours: String(s.lookback_hours ?? (24 * 7))
            };
            setNotificationDigestAdmin(loaded);
            setNotificationDigestAdminBaseline(loaded);
        } catch (e) {
            setNotificationDigestAdminError(e?.message || 'Failed to load notification digest settings');
        } finally {
            setNotificationDigestAdminLoading(false);
        }
    }

    async function saveNotificationDigestAdminSettings() {
        setNotificationDigestAdminError('');
        setNotificationDigestAdminMessage('');
        setNotificationDigestAdminSaving(true);
        try {
            const body = {
                shared_mailbox_enabled: Boolean(notificationDigestAdmin.shared_mailbox_enabled),
                shared_mailbox_to: String(notificationDigestAdmin.shared_mailbox_to || '').trim(),
                shared_mailbox_only: Boolean(notificationDigestAdmin.shared_mailbox_only),
                max_items_per_digest: Number(notificationDigestAdmin.max_items_per_digest || 50),
                lookback_hours: Number(notificationDigestAdmin.lookback_hours || (24 * 7))
            };
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/notification-digest`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });
            const s = json?.settings || {};
            const saved = {
                shared_mailbox_enabled: Boolean(s.shared_mailbox_enabled),
                shared_mailbox_to: String(s.shared_mailbox_to || ''),
                shared_mailbox_only: Boolean(s.shared_mailbox_only),
                max_items_per_digest: String(s.max_items_per_digest ?? 50),
                lookback_hours: String(s.lookback_hours ?? (24 * 7))
            };
            setNotificationDigestAdmin(saved);
            setNotificationDigestAdminBaseline(saved);
            setNotificationDigestAdminMessage('Notification digest settings saved.');
        } catch (e) {
            setNotificationDigestAdminError(e?.message || 'Failed to save notification digest settings');
        } finally {
            setNotificationDigestAdminSaving(false);
        }
    }

    async function runNotificationDigestNow({ dryRun } = {}) {
        setNotificationDigestAdminError('');
        setNotificationDigestAdminMessage('');
        setNotificationDigestRunLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/notifications/digests/run`, {
                method: 'POST',
                headers: authHeadersJson(),
                body: JSON.stringify({ dry_run: Boolean(dryRun) })
            });
            setNotificationDigestAdminMessage(`Digest run complete: attempted=${json?.attempted ?? 0} sent=${json?.sent ?? 0} skipped=${json?.skipped ?? 0} errors=${json?.errors ?? 0}`);
        } catch (e) {
            setNotificationDigestAdminError(e?.message || 'Failed to run digests');
        } finally {
            setNotificationDigestRunLoading(false);
        }
    }

    function normalizeActionNotificationForm(settings) {
        const source = settings && typeof settings === 'object' ? settings : {};
        return {
            enabled: Boolean(source.enabled),
            weekday: String(source.weekday || 'mon'),
            time_local: String(source.time_local || '08:00'),
            timezone: String(source.timezone || 'Europe/London')
        };
    }

    async function loadActionNotificationSettings() {
        setActionNotificationsError('');
        setActionNotificationsMessage('');
        setActionNotificationsLoading(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/action-notifications`, {
                method: 'GET',
                headers: authHeadersJson()
            });
            const loaded = normalizeActionNotificationForm(json?.settings);
            setActionNotifications(loaded);
            setActionNotificationsBaseline(loaded);
        } catch (e) {
            setActionNotificationsError(e?.message || 'Failed to load action notification settings');
        } finally {
            setActionNotificationsLoading(false);
        }
    }

    async function saveActionNotificationSettings() {
        setActionNotificationsError('');
        setActionNotificationsMessage('');
        setActionNotificationsSaving(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/action-notifications`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(actionNotifications)
            });
            const saved = normalizeActionNotificationForm(json?.settings);
            setActionNotifications(saved);
            setActionNotificationsBaseline(saved);
            setActionNotificationsMessage('Action Notification System settings saved.');
        } catch (e) {
            setActionNotificationsError(e?.message || 'Failed to save action notification settings');
        } finally {
            setActionNotificationsSaving(false);
        }
    }

    async function previewActionNotifications() {
        setActionNotificationsError('');
        setActionNotificationsMessage('');
        setActionNotificationsPreviewing(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/action-notifications/run`, {
                method: 'POST',
                headers: authHeadersJson(),
                body: JSON.stringify({ dry_run: true })
            });
            const previews = Array.isArray(json?.previews) ? json.previews : [];
            const actionCount = previews.reduce((total, item) => total + Number(item?.action_count || 0), 0);
            setActionNotificationsMessage(`Preview complete: ${previews.length} user${previews.length === 1 ? '' : 's'}, ${actionCount} action${actionCount === 1 ? '' : 's'}. No emails were sent.`);
        } catch (e) {
            setActionNotificationsError(e?.message || 'Failed to preview action notifications');
        } finally {
            setActionNotificationsPreviewing(false);
        }
    }

    const settingsIdentityKey = useMemo(
        () => [
            String(authUser?.user_uuid || '').trim().toLowerCase(),
            String(authUser?.username || '').trim().toLowerCase(),
            String(authUser?.email || '').trim().toLowerCase(),
            String(authUser?.tenant_slug || '').trim().toLowerCase(),
            String(authUser?.organisation_id || authUser?.organisationId || '').trim().toLowerCase(),
            String(authUser?.role || '').trim().toLowerCase()
        ].join('|'),
        [
            authUser?.user_uuid,
            authUser?.username,
            authUser?.email,
            authUser?.tenant_slug,
            authUser?.organisation_id,
            authUser?.organisationId,
            authUser?.role
        ]
    );

    useEffect(() => {
        if (!authUser) return;
        if (isAdmin) {
            loadSmtpSettings();
            loadSlaSettings();
            loadIssueReportSettings();
            loadFinanceSettings();
            loadInvoicingSettings();

            loadOrgProfileSettings();
            loadReportVersionControlSettings();
            loadReportStyleTemplateSettings();
            loadInterimReportTemplateSettings();
            if (canConfigureFeasibilityReport) loadFeasibilityReportSettings();
            loadFeeProposalTemplateSettings();
            loadOfficeMapSettings();
            loadRetentionSettings();
            loadIntegrationsSettings();
            loadSecurityPolicySettings();
            loadDeletionGuardSettings();
            loadAiPolicySettings();
            loadEmailTemplatesSettings();
            loadAnalyticsExportSettings();
            loadNotificationDigestAdminSettings();
            loadActionNotificationSettings();
        }
        loadUserPreferences();
        loadMfaStatus();
        loadCalendarStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser, isAdmin, settingsIdentityKey, canConfigureFeasibilityReport]);

    useEffect(() => {
        setDarkModePreference(Boolean(dark));
    }, [dark]);

    useEffect(() => {
        try {
            localStorage.setItem(AI_CONSISTENCY_KEY, JSON.stringify(aiConsistency));
        } catch {
            // ignore
        }
    }, [aiConsistency]);

    useEffect(() => {
        try {
            localStorage.setItem(REPORT_VERSION_NAMING_KEY, normalizeReportVersionNaming(reportVersionNaming));
        } catch {
            // ignore
        }
    }, [reportVersionNaming]);

    useEffect(() => {
        try {
            localStorage.setItem(PHI_KEY, JSON.stringify(phiConfig));
            try {
                window.dispatchEvent(new CustomEvent('monsurv_phi_config_changed'));
            } catch {
                // ignore
            }
        } catch {
            // ignore
        }
    }, [phiConfig]);

    function clamp0to100(v, fallback) {
        const n = Number(v);
        if (!Number.isFinite(n)) return fallback;
        return Math.max(0, Math.min(100, Math.round(n)));
    }

    function clamp0to200(v, fallback) {
        const n = Number(v);
        if (!Number.isFinite(n)) return fallback;
        return Math.max(0, Math.min(200, Math.round(n)));
    }

    function Slider({ label, value, onChange, hint }) {
        return (
            <Box sx={{ py: 1 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
                    <Typography sx={{ width: 44, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                        {value}
                    </Typography>
                </Box>
                {hint ? (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        {hint}
                    </Typography>
                ) : null}
                <MuiSlider
                    value={value}
                    min={0}
                    max={100}
                    step={1}
                    onChange={(_, v) => onChange(clamp0to100(v, value))}
                    sx={{ mt: 1 }}
                />
            </Box>
        );
    }

    function InlineWeightControl({ value, onCommit, disabled, labelForA11y }) {
        const [draft, setDraft] = useState(() => clamp0to200(value, 100));

        useEffect(() => {
            setDraft(clamp0to200(value, 100));
        }, [value]);

        if (isPhone) {
            return (
                <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 200, step: 1 }}
                    value={String(draft)}
                    disabled={disabled}
                    aria-label={labelForA11y}
                    onChange={(e) => {
                        const next = clamp0to200(e.target.value, draft);
                        setDraft(next);
                    }}
                    onBlur={() => onCommit(draft)}
                    sx={{ width: 76 }}
                />
            );
        }

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 240 }}>
                <MuiSlider
                    value={draft}
                    min={0}
                    max={200}
                    step={1}
                    disabled={disabled}
                    onChange={(_, v) => setDraft((prev) => clamp0to200(v, prev))}
                    onChangeCommitted={(_, v) => onCommit(clamp0to200(v, draft))}
                    aria-label={labelForA11y}
                    sx={{ flex: 1 }}
                />
                <Typography sx={{ width: 54, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                    {draft}%
                </Typography>
            </Box>
        );
    }

    const phiFactorRows = useMemo(() => ([
        // PHI is intentionally independent of report readiness.
        { key: 'risk_matrix', label: 'Risk Matrix (summary RAG)' },
        { key: 'report_recency', label: 'Latest report recency' }
    ]), []);

    function saveAppendixDocs() {
        const list = String(appendixDocsText || '')
            .split(/\r?\n/)
            .map((s) => String(s || '').trim())
            .filter(Boolean);
        try {
            localStorage.setItem(APPENDIX_DOCS_KEY, JSON.stringify(list));
        } catch {
            // ignore
        }
    }

    async function saveSmtpSettings() {
        setSmtpError('');
        setSmtpMessage('');
        setSmtpTestResult('');
        setSmtpSaving(true);
        try {
            const body = {
                enabled: Boolean(smtp.enabled),
                host: String(smtp.host || ''),
                port: String(smtp.port || ''),
                secure: Boolean(smtp.secure),
                user: String(smtp.user || ''),
                from: String(smtp.from || ''),
                reply_to: String(smtp.reply_to || '')
            };

            if (smtpClearPassword) body.clear_password = true;
            if (!smtpClearPassword && String(smtpPassword || '').trim() !== '') body.pass = String(smtpPassword);

            const json = await fetchJson(`${API_BASE_URL}/admin/settings/smtp`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });

            const s = json?.smtp || {};
            setSmtp((prev) => ({
                ...prev,
                enabled: Boolean(s.enabled),
                host: String(s.host || ''),
                port: s.port == null ? '' : String(s.port),
                secure: Boolean(s.secure),
                user: String(s.user || ''),
                from: String(s.from || ''),
                reply_to: String(s.reply_to || ''),
                has_password: Boolean(s.has_password)
            }));
            setSmtpBaseline({
                enabled: Boolean(s.enabled),
                host: String(s.host || ''),
                port: s.port == null ? '' : String(s.port),
                secure: Boolean(s.secure),
                user: String(s.user || ''),
                from: String(s.from || ''),
                reply_to: String(s.reply_to || '')
            });

            setSmtpPassword('');
            setSmtpClearPassword(false);
            setSmtpMessage('SMTP settings saved.');
        } catch (e) {
            setSmtpError(e?.message || 'Failed to save SMTP settings');
        } finally {
            setSmtpSaving(false);
        }
    }

    async function sendSmtpTestEmail() {
        setSmtpError('');
        setSmtpMessage('');
        setSmtpTestResult('');
        try {
            const to = String(smtpTestTo || '').trim();
            if (!to) throw new Error('Enter a test email address');
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/smtp/test`, {
                method: 'POST',
                headers: authHeadersJson(),
                body: JSON.stringify({ to })
            });
            const sent = Boolean(json?.result?.sent);
            setSmtpTestResult(sent ? 'Test email sent.' : `Email not sent (${json?.result?.reason || 'unknown reason'})`);
        } catch (e) {
            setSmtpError(e?.message || 'Failed to send test email');
        }
    }

    async function saveIssueReportSettings() {
        setIssueReportError('');
        setIssueReportMessage('');
        setIssueReportSaving(true);
        try {
            const rawDays = String(issueReportResolvedRetentionDays || '').trim();
            const daysNum = rawDays === '' ? null : Number(rawDays);
            if (daysNum != null && (!Number.isFinite(daysNum) || daysNum < 1 || daysNum > 3650)) {
                throw new Error('Resolved retention days must be a number between 1 and 3650');
            }

            const body = {
                admin_email: String(issueReportEmail || '').trim() || null,
                resolved_retention_days: daysNum == null ? null : Math.trunc(daysNum)
            };
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/issue-report`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });
            setIssueReportEmail(String(json?.settings?.admin_email || ''));
            const savedDays = Number(json?.settings?.resolved_retention_days);
            const savedEmail = String(json?.settings?.admin_email || '');
            const savedDaysText = Number.isFinite(savedDays) ? String(savedDays) : (rawDays || '3');
            setIssueReportEmail(savedEmail);
            setIssueReportResolvedRetentionDays(savedDaysText);
            setIssueReportBaseline({ admin_email: savedEmail, resolved_retention_days: savedDaysText });
            setIssueReportMessage('Issue reporting settings saved.');
        } catch (e) {
            setIssueReportError(e?.message || 'Failed to save issue report settings');
        } finally {
            setIssueReportSaving(false);
        }
    }

    async function saveSlaSettings() {
        setSlaError('');
        setSlaMessage('');
        setSlaSaving(true);
        try {
            const body = {
                initial_report_turnaround_days: Number(sla.initial_report_turnaround_days),
                interim_report_turnaround_days: Number(sla.interim_report_turnaround_days)
            };

            const json = await fetchJson(`${API_BASE_URL}/admin/settings/sla`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });

            const s = json?.sla || {};
            const saved = {
                initial_report_turnaround_days: s.initial_report_turnaround_days == null ? String(body.initial_report_turnaround_days || 10) : String(s.initial_report_turnaround_days),
                interim_report_turnaround_days: s.interim_report_turnaround_days == null ? String(body.interim_report_turnaround_days || 10) : String(s.interim_report_turnaround_days)
            };
            setSla(saved);
            setSlaBaseline(saved);
            setSlaMessage('SLA settings saved.');
        } catch (e) {
            setSlaError(e?.message || 'Failed to save SLA settings');
        } finally {
            setSlaSaving(false);
        }
    }

    async function saveFinanceSettings() {
        setFinanceError('');
        setFinanceMessage('');
        setFinanceSaving(true);
        try {
            const n = Number(String(invoiceRevenueTarget || '').trim());
            if (!Number.isFinite(n) || n < 0) throw new Error('Invoice revenue target must be a number ≥ 0');

            const body = { invoice_revenue_target_gbp: Math.round(n) };

            const json = await fetchJson(`${API_BASE_URL}/admin/settings/finance`, {
                method: 'PUT',
                headers: authHeadersJson(),
                body: JSON.stringify(body)
            });

            const f = json?.finance || {};
            const savedTarget = f.invoice_revenue_target_gbp == null ? String(body.invoice_revenue_target_gbp) : String(f.invoice_revenue_target_gbp);
            setInvoiceRevenueTarget(savedTarget);
            setFinanceBaseline(savedTarget);
            setFinanceMessage('Finance settings saved.');
        } catch (e) {
            setFinanceError(e?.message || 'Failed to save finance settings');
        } finally {
            setFinanceSaving(false);
        }
    }

    const pillTextFieldSx = useMemo(() => ({
        '& .MuiOutlinedInput-root': {
            borderRadius: 3
        }
    }), []);

    const emailTemplateTextFieldSx = useMemo(() => ({
        ...pillTextFieldSx,
        '& .MuiInputBase-inputMultiline': {
            resize: 'horizontal',
            overflow: 'auto',
            maxWidth: '100%',
            boxSizing: 'border-box'
        }
    }), [pillTextFieldSx]);

    const issueReportDirty = useMemo(() => {
        return String(issueReportEmail || '').trim() !== String(issueReportBaseline?.admin_email || '').trim()
            || String(issueReportResolvedRetentionDays || '').trim() !== String(issueReportBaseline?.resolved_retention_days || '').trim();
    }, [issueReportEmail, issueReportResolvedRetentionDays, issueReportBaseline]);

    const slaDirty = useMemo(() => {
        return String(sla?.initial_report_turnaround_days || '').trim() !== String(slaBaseline?.initial_report_turnaround_days || '').trim()
            || String(sla?.interim_report_turnaround_days || '').trim() !== String(slaBaseline?.interim_report_turnaround_days || '').trim();
    }, [sla, slaBaseline]);

    const financeDirty = useMemo(() => {
        return String(invoiceRevenueTarget || '').trim() !== String(financeBaseline || '').trim();
    }, [invoiceRevenueTarget, financeBaseline]);

    const invoicingDirty = useMemo(() => {
        return JSON.stringify(normalizeInvoicingSettingsState(invoicingSettings))
            !== JSON.stringify(normalizeInvoicingSettingsState(invoicingBaseline));
    }, [invoicingSettings, invoicingBaseline]);

    const issueReportingStatusItems = useMemo(() => {
        if (issueReportSaving || issueReportLoading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (issueReportError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (issueReportDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (issueReportMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [issueReportSaving, issueReportLoading, issueReportError, issueReportDirty, issueReportMessage]);

    const slaStatusItems = useMemo(() => {
        if (slaSaving || slaLoading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (slaError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (slaDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (slaMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [slaSaving, slaLoading, slaError, slaDirty, slaMessage]);

    const financeStatusItems = useMemo(() => {
        if (financeSaving || financeLoading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (financeError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (financeDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (financeMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [financeSaving, financeLoading, financeError, financeDirty, financeMessage]);

    const invoicingStatusItems = useMemo(() => {
        if (invoicingSaving || invoicingLoading || invoicingUploading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (invoicingError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (invoicingDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (invoicingMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [invoicingSaving, invoicingLoading, invoicingUploading, invoicingError, invoicingDirty, invoicingMessage]);

    const integrationsDirty = useMemo(() => {
        return String(integrations?.slack_webhook_url || '').trim() !== String(integrationsBaseline?.slack_webhook_url || '').trim()
            || String(integrations?.teams_webhook_url || '').trim() !== String(integrationsBaseline?.teams_webhook_url || '').trim()
            || Boolean(integrations?.powerbi_enabled) !== Boolean(integrationsBaseline?.powerbi_enabled)
            || String(integrations?.powerbi_workspace_id || '').trim() !== String(integrationsBaseline?.powerbi_workspace_id || '').trim()
            || String(integrations?.powerbi_dataset_id || '').trim() !== String(integrationsBaseline?.powerbi_dataset_id || '').trim();
    }, [integrations, integrationsBaseline]);

    const securityPolicyDirty = useMemo(() => {
        return Boolean(securityPolicy?.require_mfa_for_admins) !== Boolean(securityPolicyBaseline?.require_mfa_for_admins)
            || String(securityPolicy?.session_timeout_minutes || '').trim() !== String(securityPolicyBaseline?.session_timeout_minutes || '').trim()
            || String(securityPolicy?.idle_timeout_minutes || '').trim() !== String(securityPolicyBaseline?.idle_timeout_minutes || '').trim()
            || String(securityPolicy?.password_min_length || '').trim() !== String(securityPolicyBaseline?.password_min_length || '').trim();
    }, [securityPolicy, securityPolicyBaseline]);

    const integrationsStatusItems = useMemo(() => {
        if (integrationsSaving || integrationsLoading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (integrationsError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (integrationsDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (integrationsMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [integrationsSaving, integrationsLoading, integrationsError, integrationsDirty, integrationsMessage]);

    const securityPolicyStatusItems = useMemo(() => {
        if (securityPolicySaving || securityPolicyLoading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (securityPolicyError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (securityPolicyDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (securityPolicyMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [securityPolicySaving, securityPolicyLoading, securityPolicyError, securityPolicyDirty, securityPolicyMessage]);

    const retentionDirty = useMemo(() => {
        return String(retention?.uploads_retention_days || '').trim() !== String(retentionBaseline?.uploads_retention_days || '').trim()
            || String(retention?.notifications_retention_days || '').trim() !== String(retentionBaseline?.notifications_retention_days || '').trim()
            || String(retention?.activity_retention_days || '').trim() !== String(retentionBaseline?.activity_retention_days || '').trim()
            || String(retention?.max_upload_mb || '').trim() !== String(retentionBaseline?.max_upload_mb || '').trim()
            || String(retention?.allowed_upload_types_text || '').trim() !== String(retentionBaseline?.allowed_upload_types_text || '').trim();
    }, [retention, retentionBaseline]);

    const aiPolicyDirty = useMemo(() => {
        return Boolean(aiPolicy?.ai_enabled) !== Boolean(aiPolicyBaseline?.ai_enabled)
            || Boolean(aiPolicy?.allow_ai_photo_analysis) !== Boolean(aiPolicyBaseline?.allow_ai_photo_analysis)
            || Boolean(aiPolicy?.allow_online_lookup) !== Boolean(aiPolicyBaseline?.allow_online_lookup)
            || String(aiPolicy?.default_model || '').trim() !== String(aiPolicyBaseline?.default_model || '').trim()
            || String(aiPolicy?.temperature_cap || '').trim() !== String(aiPolicyBaseline?.temperature_cap || '').trim();
    }, [aiPolicy, aiPolicyBaseline]);

    const retentionStatusItems = useMemo(() => {
        if (retentionSaving || retentionLoading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (retentionError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (retentionDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (retentionMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [retentionSaving, retentionLoading, retentionError, retentionDirty, retentionMessage]);

    const aiPolicyStatusItems = useMemo(() => {
        if (aiPolicySaving || aiPolicyLoading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (aiPolicyError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (aiPolicyDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (aiPolicyMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [aiPolicySaving, aiPolicyLoading, aiPolicyError, aiPolicyDirty, aiPolicyMessage]);

    const analyticsExportDirty = useMemo(() => {
        return Boolean(analyticsExport?.exports_enabled) !== Boolean(analyticsExportBaseline?.exports_enabled)
            || String(analyticsExport?.exports_frequency || '').trim() !== String(analyticsExportBaseline?.exports_frequency || '').trim()
            || String(analyticsExport?.exports_email_to || '').trim() !== String(analyticsExportBaseline?.exports_email_to || '').trim()
            || Boolean(analyticsExport?.include_project_financials) !== Boolean(analyticsExportBaseline?.include_project_financials)
            || Boolean(analyticsExport?.include_notifications) !== Boolean(analyticsExportBaseline?.include_notifications);
    }, [analyticsExport, analyticsExportBaseline]);

    const analyticsExportStatusItems = useMemo(() => {
        if (analyticsExportSaving || analyticsExportLoading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (analyticsExportError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (analyticsExportDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (analyticsExportMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [analyticsExportSaving, analyticsExportLoading, analyticsExportError, analyticsExportDirty, analyticsExportMessage]);

    const notificationDigestAdminDirty = useMemo(() => {
        return Boolean(notificationDigestAdmin?.shared_mailbox_enabled) !== Boolean(notificationDigestAdminBaseline?.shared_mailbox_enabled)
            || String(notificationDigestAdmin?.shared_mailbox_to || '').trim() !== String(notificationDigestAdminBaseline?.shared_mailbox_to || '').trim()
            || Boolean(notificationDigestAdmin?.shared_mailbox_only) !== Boolean(notificationDigestAdminBaseline?.shared_mailbox_only)
            || String(notificationDigestAdmin?.max_items_per_digest || '').trim() !== String(notificationDigestAdminBaseline?.max_items_per_digest || '').trim()
            || String(notificationDigestAdmin?.lookback_hours || '').trim() !== String(notificationDigestAdminBaseline?.lookback_hours || '').trim();
    }, [notificationDigestAdmin, notificationDigestAdminBaseline]);

    const notificationDigestAdminStatusItems = useMemo(() => {
        if (!isAdmin) return [];
        if (notificationDigestAdminSaving || notificationDigestAdminLoading || notificationDigestRunLoading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (notificationDigestAdminError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (notificationDigestAdminDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (notificationDigestAdminMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [
        isAdmin,
        notificationDigestAdminSaving,
        notificationDigestAdminLoading,
        notificationDigestRunLoading,
        notificationDigestAdminError,
        notificationDigestAdminDirty,
        notificationDigestAdminMessage
    ]);

    const actionNotificationsDirty = useMemo(() => (
        Boolean(actionNotifications.enabled) !== Boolean(actionNotificationsBaseline.enabled)
        || String(actionNotifications.weekday) !== String(actionNotificationsBaseline.weekday)
        || String(actionNotifications.time_local) !== String(actionNotificationsBaseline.time_local)
        || String(actionNotifications.timezone) !== String(actionNotificationsBaseline.timezone)
    ), [actionNotifications, actionNotificationsBaseline]);

    const actionNotificationsStatusItems = useMemo(() => {
        if (actionNotificationsLoading || actionNotificationsSaving || actionNotificationsPreviewing) return [{ label: 'Working', color: 'info', variant: 'filled' }];
        if (actionNotificationsError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (actionNotificationsDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (actionNotificationsMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: actionNotifications.enabled ? 'Active' : 'Off', color: actionNotifications.enabled ? 'success' : 'default', variant: 'outlined' }];
    }, [actionNotificationsLoading, actionNotificationsSaving, actionNotificationsPreviewing, actionNotificationsError, actionNotificationsDirty, actionNotificationsMessage, actionNotifications.enabled]);

    const smtpDirty = useMemo(() => {
        return Boolean(smtp?.enabled) !== Boolean(smtpBaseline?.enabled)
            || String(smtp?.host || '').trim() !== String(smtpBaseline?.host || '').trim()
            || String(smtp?.port || '').trim() !== String(smtpBaseline?.port || '').trim()
            || Boolean(smtp?.secure) !== Boolean(smtpBaseline?.secure)
            || String(smtp?.user || '').trim() !== String(smtpBaseline?.user || '').trim()
            || String(smtp?.from || '').trim() !== String(smtpBaseline?.from || '').trim()
            || String(smtp?.reply_to || '').trim() !== String(smtpBaseline?.reply_to || '').trim();
    }, [smtp, smtpBaseline]);

    const orgProfileDirty = useMemo(() => {
        return String(orgProfile?.organisation_name || '').trim() !== String(orgProfileBaseline?.organisation_name || '').trim()
            || String(orgProfile?.report_company_shorthand || '').trim() !== String(orgProfileBaseline?.report_company_shorthand || '').trim()
            || String(orgProfile?.report_header_text || '') !== String(orgProfileBaseline?.report_header_text || '')
            || String(orgProfile?.report_footer_text || '') !== String(orgProfileBaseline?.report_footer_text || '')
            || String(orgProfile?.portal_brand_color || '').trim() !== String(orgProfileBaseline?.portal_brand_color || '').trim()
            || String(orgProfile?.portal_logo_url || '').trim() !== String(orgProfileBaseline?.portal_logo_url || '').trim();
    }, [orgProfile, orgProfileBaseline]);

    const smtpStatusItems = useMemo(() => {
        if (smtpSaving || smtpLoading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (smtpError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (smtpDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (smtpMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [smtpSaving, smtpLoading, smtpError, smtpDirty, smtpMessage]);

    const orgProfileStatusItems = useMemo(() => {
        if (orgProfileSaving || orgProfileLoading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (orgProfileError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (orgProfileDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (orgProfileMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [orgProfileSaving, orgProfileLoading, orgProfileError, orgProfileDirty, orgProfileMessage]);

    function serializeOfficeMapComparable(state) {
        const source = state && typeof state === 'object' ? state : {};
        const offices = (Array.isArray(source.offices) ? source.offices : [])
            .map((office) => ({
                office_location: String(office?.office_location || '').trim(),
                address: String(office?.address || '').trim(),
                pin_color: String(office?.pin_color || '#0f3b68').trim() || '#0f3b68'
            }))
            .filter((office) => office.office_location || office.address);

        return JSON.stringify({
            offices,
            primary_office_id: String(source.primary_office_id || '').trim(),
            brand_upload_url: String(source.brand_upload_url || '').trim()
        });
    }

    function serializeEmailTemplatesComparable(state) {
        const source = state && typeof state === 'object' ? state : {};
        return JSON.stringify({
            invite_subject: String(source.invite_subject || '').trim(),
            invite_body: String(source.invite_body || ''),
            new_client_access_subject: String(source.new_client_access_subject || '').trim(),
            new_client_access_body: String(source.new_client_access_body || ''),
            developer_questionnaire_subject: String(source.developer_questionnaire_subject || '').trim(),
            developer_questionnaire_body: String(source.developer_questionnaire_body || ''),
            information_outstanding_subject: String(source.information_outstanding_subject || '').trim(),
            information_outstanding_body: String(source.information_outstanding_body || ''),
            insurance_expiry_subject: String(source.insurance_expiry_subject || '').trim(),
            insurance_expiry_body: String(source.insurance_expiry_body || ''),
            valuation_reminder_subject: String(source.valuation_reminder_subject || '').trim(),
            valuation_reminder_body: String(source.valuation_reminder_body || ''),
            report_ready_subject: String(source.report_ready_subject || '').trim(),
            report_ready_body: String(source.report_ready_body || ''),
            feasibility_report_issued_subject: String(source.feasibility_report_issued_subject || '').trim(),
            feasibility_report_issued_body: String(source.feasibility_report_issued_body || ''),
            action_report_subject: String(source.action_report_subject || '').trim(),
            action_report_body: String(source.action_report_body || ''),
            password_reset_subject: String(source.password_reset_subject || '').trim(),
            password_reset_body: String(source.password_reset_body || '')
        });
    }

    const officeMapDirty = useMemo(() => {
        return serializeOfficeMapComparable(officeMap) !== serializeOfficeMapComparable(officeMapBaseline);
    }, [officeMap, officeMapBaseline]);

    const emailTemplatesDirty = useMemo(() => {
        return serializeEmailTemplatesComparable(emailTemplates) !== serializeEmailTemplatesComparable(emailTemplatesBaseline);
    }, [emailTemplates, emailTemplatesBaseline]);

    const officeMapStatusItems = useMemo(() => {
        if (officeMapSaving || officeMapLoading || officeMapUploading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (officeMapError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (officeMapDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (officeMapMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [officeMapSaving, officeMapLoading, officeMapUploading, officeMapError, officeMapDirty, officeMapMessage]);

    const emailTemplatesStatusItems = useMemo(() => {
        if (emailTemplatesSaving || emailTemplatesLoading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (emailTemplatesError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (emailTemplatesDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (emailTemplatesMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [emailTemplatesSaving, emailTemplatesLoading, emailTemplatesError, emailTemplatesDirty, emailTemplatesMessage]);

    const reportStyleTemplateDirty = useMemo(() => {
        return JSON.stringify(normalizeReportStyleTemplateState(reportStyleTemplate))
            !== JSON.stringify(normalizeReportStyleTemplateState(reportStyleTemplateBaseline));
    }, [reportStyleTemplate, reportStyleTemplateBaseline]);

    const interimReportTemplateDirty = useMemo(() => {
        return JSON.stringify(normalizeInterimReportTemplateState(interimReportTemplate))
            !== JSON.stringify(normalizeInterimReportTemplateState(interimReportTemplateBaseline));
    }, [interimReportTemplate, interimReportTemplateBaseline]);

    const feasibilityReportDirty = useMemo(() => (
        JSON.stringify(normalizeFeasibilityReportState(feasibilityReport))
        !== JSON.stringify(normalizeFeasibilityReportState(feasibilityReportBaseline))
    ), [feasibilityReport, feasibilityReportBaseline]);

    const feeProposalTemplateDirty = useMemo(() => {
        return JSON.stringify(normalizeFeeProposalTemplateState(feeProposalTemplate))
            !== JSON.stringify(normalizeFeeProposalTemplateState(feeProposalTemplateBaseline));
    }, [feeProposalTemplate, feeProposalTemplateBaseline, normalizeFeeProposalTemplateState]);

    const reportStyleTemplateStatusItems = useMemo(() => {
        if (reportStyleTemplateSaving || reportStyleTemplateLoading || reportStyleTemplateUploading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (reportStyleTemplateError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (reportStyleTemplateDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (reportStyleTemplateMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [reportStyleTemplateSaving, reportStyleTemplateLoading, reportStyleTemplateUploading, reportStyleTemplateError, reportStyleTemplateDirty, reportStyleTemplateMessage]);

    const interimReportTemplateStatusItems = useMemo(() => {
        if (interimReportTemplateSaving || interimReportTemplateLoading || interimReportTemplateUploading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (interimReportTemplateError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (interimReportTemplateDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (interimReportTemplateMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [interimReportTemplateSaving, interimReportTemplateLoading, interimReportTemplateUploading, interimReportTemplateError, interimReportTemplateDirty, interimReportTemplateMessage]);

    const feasibilityReportStatusItems = useMemo(() => {
        if (feasibilityReportSaving || feasibilityReportLoading || feasibilityReportUploading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (feasibilityReportError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (feasibilityReportDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (feasibilityReportMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [feasibilityReportSaving, feasibilityReportLoading, feasibilityReportUploading, feasibilityReportError, feasibilityReportDirty, feasibilityReportMessage]);

    const feeProposalTemplateStatusItems = useMemo(() => {
        if (feeProposalTemplateSaving || feeProposalTemplateLoading || feeProposalTemplateUploading) return [{ label: 'Saving', color: 'info', variant: 'filled' }];
        if (feeProposalTemplateError) return [{ label: 'Error', color: 'error', variant: 'filled' }];
        if (feeProposalTemplateDirty) return [{ label: 'Unsaved changes', color: 'warning', variant: 'outlined' }];
        if (feeProposalTemplateMessage) return [{ label: 'Saved', color: 'success', variant: 'filled' }];
        return [{ label: 'Up to date', color: 'default', variant: 'outlined' }];
    }, [feeProposalTemplateSaving, feeProposalTemplateLoading, feeProposalTemplateUploading, feeProposalTemplateError, feeProposalTemplateDirty, feeProposalTemplateMessage]);

    const settingsCategories = useMemo(() => {
        const items = [
            { id: 'personal', label: 'Personal', description: 'Account identity, display, and everyday dashboard preferences.' },
            { id: 'notifications', label: 'Notifications', description: 'Alert channels, project scope, digests, and delivery routing.' },
            { id: 'reports', label: 'Reports', description: 'Default report output, version naming, and appendix configuration.' },
            { id: 'ai-health', label: 'AI & Health', description: 'AI feature preferences, report consistency, and PHI weighting.' },
            { id: 'security', label: 'Security', description: 'MFA, policy controls, and deletion safeguards.' },
            { id: 'integrations', label: 'Integrations', description: 'Calendar sharing and external platform connections.' },
            { id: 'branding', label: 'Branding', description: 'Organisation profile and office-map presentation.', adminOnly: true },
            { id: 'communications', label: 'Communications', description: 'Voice preferences and authorised outbound communication controls.' },
            { id: 'operations', label: 'Operations', description: 'SLA, finance, exports, and issue-routing operations.', adminOnly: true },
            { id: 'system-admin', label: 'System Admin', description: 'Retention, UUID audits, and platform-level administration.', adminOnly: true },
        ];
        return items.filter((item) => !item.adminOnly || isAdmin);
    }, [isAdmin]);

    const settingsSections = useMemo(() => {
        const items = [
            { id: 'account', label: 'Account', category: 'personal' },
            { id: 'overview_display', label: 'Overview Display', category: 'personal' },
            { id: 'live_project_chaser', label: 'Live Project Chaser', category: 'personal' },
            { id: 'appearance', label: 'Appearance', category: 'personal' },
            { id: 'ui_preferences', label: 'Dashboard UI', category: 'personal' },
            { id: 'notifications', label: 'Notifications', category: 'notifications' },
            { id: 'report_defaults', label: 'Report Defaults', category: 'reports' },
            { id: 'report_identity', label: 'Report Identity', category: 'reports', adminOnly: true },
            { id: 'report_version_naming', label: 'Report Versions', category: 'reports' },
            { id: 'report_version_control', label: 'Report Version Control', category: 'reports', adminOnly: true },
            ...(canConfigureFeasibilityReport ? [{ id: 'feasibility_report', label: 'Feasibility Report', category: 'reports', adminOnly: true }] : []),
            { id: 'report_appendices', label: 'Appendices', category: 'reports' },
            { id: 'ai_preferences', label: 'AI Preferences', category: 'ai-health' },
            { id: 'ai_report_consistency', label: 'AI Consistency', category: 'ai-health' },
            { id: 'phi', label: 'PHI', category: 'ai-health' },
            { id: 'ai_policy', label: 'AI Policy', category: 'ai-health', adminOnly: true },
            { id: 'mfa', label: 'MFA', category: 'security' },
            { id: 'security_policy', label: 'Security Policy', category: 'security', adminOnly: true },
            { id: 'deletion_guard', label: 'Deletion Guard', category: 'security', adminOnly: true },
            { id: 'calendar', label: 'Calendar', category: 'integrations' },
            { id: 'gmail', label: 'Gmail inbox', category: 'integrations' },
            { id: 'integrations_admin', label: 'Admin Integrations', category: 'integrations', adminOnly: true },
            { id: 'org_profile', label: 'Organisation Profile', category: 'branding', adminOnly: true },
            { id: 'office_map', label: 'Office Map', category: 'branding', adminOnly: true },
            { id: 'smtp', label: 'SMTP', category: 'communications', adminOnly: true },
            { id: 'monsurv_voice', label: 'Monsurv Voice', category: 'communications' },
            { id: 'action_notifications', label: 'Action Notification System', category: 'communications', adminOnly: true },
            { id: 'email_templates', label: 'Email Templates', category: 'communications', adminOnly: true },
            { id: 'analytics_export', label: 'Analytics Export', category: 'operations', adminOnly: true },
            { id: 'issue_reporting', label: 'Issue Reporting', category: 'operations', adminOnly: true },
            { id: 'sla', label: 'SLA', category: 'operations', adminOnly: true },
            { id: 'finance', label: 'Finance', category: 'operations', adminOnly: true },
            { id: 'invoicing', label: 'Invoicing', category: 'operations', adminOnly: true },
            { id: 'retention', label: 'Retention', category: 'system-admin', adminOnly: true },
            { id: 'uuid_info', label: 'UUID Info', category: 'system-admin', adminOnly: true },
        ];
        return items.filter((item) => !item.adminOnly || isAdmin);
    }, [isAdmin, canConfigureFeasibilityReport]);

    const [activeSettingsCategory, setActiveSettingsCategory] = useState(() => {
        const query = new URLSearchParams(window.location.search);
        return query.has('gmail') || query.get('settings_category') === 'integrations' ? 'integrations' : 'personal';
    });

    useEffect(() => {
        const hasActive = settingsCategories.some((item) => item.id === activeSettingsCategory);
        if (!hasActive) setActiveSettingsCategory(settingsCategories[0]?.id || 'personal');
    }, [activeSettingsCategory, settingsCategories]);

    const sectionCategoryMap = useMemo(() => {
        const map = {};
        settingsSections.forEach((item) => {
            map[item.id] = item.category;
        });
        return map;
    }, [settingsSections]);

    const visibleJumpItems = useMemo(
        () => settingsSections.filter((item) => item.category === activeSettingsCategory),
        [activeSettingsCategory, settingsSections]
    );

    const activeCategoryMeta = useMemo(
        () => settingsCategories.find((item) => item.id === activeSettingsCategory) || settingsCategories[0] || null,
        [activeSettingsCategory, settingsCategories]
    );

    const settingsMetrics = useMemo(() => ({
        categoryCount: settingsCategories.length,
        visibleCount: visibleJumpItems.length,
        adminCount: settingsSections.filter((item) => item.adminOnly).length,
        personalCount: settingsSections.filter((item) => !item.adminOnly).length,
    }), [settingsCategories, settingsSections, visibleJumpItems.length]);

    const settingsRecommendations = useMemo(() => ([
        'Timezone and localisation defaults',
        'Accessibility preferences beyond dark mode',
        'Integration health diagnostics',
        'Settings audit history for admins'
    ]), []);

    const [uuidInfoLoading, setUuidInfoLoading] = useState(false);
    const [uuidInfoError, setUuidInfoError] = useState('');
    const [uuidInfo, setUuidInfo] = useState(null);
    const [uuidDeleteBusy, setUuidDeleteBusy] = useState('');

    const [uuidDeleteDialogOpen, setUuidDeleteDialogOpen] = useState(false);
    const [uuidDeleteTarget, setUuidDeleteTarget] = useState(null);
    const [uuidDeletePassword, setUuidDeletePassword] = useState('');
    const [uuidDeleteError, setUuidDeleteError] = useState('');

    async function loadUuidInformation() {
        setUuidInfoLoading(true);
        setUuidInfoError('');
        try {
            const json = await fetchJson(`${API_BASE_URL}/admin/uuid-information`, {
                method: 'GET',
                headers: authHeadersJson()
            });
            setUuidInfo(json);
        } catch (e) {
            setUuidInfoError(e?.message || 'Failed to load UUID information');
            setUuidInfo(null);
        } finally {
            setUuidInfoLoading(false);
        }
    }

    async function deleteDashboardUserByUuid(userUuid, deletionPassword) {
        const uu = String(userUuid || '').trim();
        if (!uu) throw new Error('Missing user UUID');

        return fetchJson(`${API_BASE_URL}/admin/dashboard-users/${encodeURIComponent(uu)}`, {
            method: 'DELETE',
            headers: {
                ...authHeadersJson(),
                ...(deletionPassword ? { 'X-Deletion-Password': String(deletionPassword) } : {})
            }
        });
    }

    async function onDeleteDashboardUser(u) {
        const userUuid = String(u?.user_uuid || '').trim();
        const username = String(u?.username || '').trim();
        const displayName = String(u?.display_name || u?.displayName || '').trim();

        if (!userUuid || userUuid === '—') {
            window.alert('This user has no UUID value; deletion from this screen requires a UUID.');
            return;
        }

        const label = displayName || username || userUuid;
        setUuidDeleteTarget({ userUuid, label });
        setUuidDeletePassword('');
        setUuidDeleteError('');
        setUuidDeleteDialogOpen(true);
    }

    function closeUuidDeleteDialog() {
        const busy = uuidDeleteTarget?.userUuid && uuidDeleteBusy === uuidDeleteTarget.userUuid;
        if (busy) return;
        setUuidDeleteDialogOpen(false);
        setUuidDeleteTarget(null);
        setUuidDeletePassword('');
        setUuidDeleteError('');
    }

    async function confirmUuidDeleteDialog() {
        const userUuid = String(uuidDeleteTarget?.userUuid || '').trim();
        const label = String(uuidDeleteTarget?.label || '').trim() || userUuid;
        const pw = String(uuidDeletePassword || '').trim();
        if (!userUuid) return;
        if (!pw) {
            setUuidDeleteError('Deletion password is required.');
            return;
        }

        setUuidDeleteBusy(userUuid);
        setUuidDeleteError('');
        try {
            await deleteDashboardUserByUuid(userUuid, pw);
            await loadUuidInformation();
            setUuidDeleteDialogOpen(false);
            setUuidDeleteTarget(null);
            setUuidDeletePassword('');
            window.alert(`User "${label}" deleted.`);
        } catch (e) {
            setUuidDeleteError(String(e?.message || 'Failed to delete user'));
        } finally {
            setUuidDeleteBusy('');
        }
    }

    function jumpToSection(sectionId) {
        const id = String(sectionId || '').trim();
        if (!id) return;

        const nextCategory = sectionCategoryMap[id];
        if (nextCategory) setActiveSettingsCategory(nextCategory);

        if (typeof onOpenSection === 'function') {
            onOpenSection(id);
            return;
        }

        try {
            localStorage.setItem(`monsurv_settings_section_${id}_open`, '1');
        } catch {
            // ignore
        }

        try {
            window.dispatchEvent(new CustomEvent('monsurv_open_settings_section', { detail: { id } }));
        } catch {
            // ignore
        }

        setTimeout(() => {
            try {
                const el = document.getElementById(`settings-section-${id}`);
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            } catch {
                // ignore
            }
        }, 60);
    }

    function SettingsGroupHeader({ title, description }) {
        return (
            <Box sx={{ mt: 3.5, mb: 1.25 }}>
                <Typography variant="overline" sx={{ fontWeight: 900, letterSpacing: 0.8, color: 'text.secondary' }}>
                    {title}
                </Typography>
                {description ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, maxWidth: 900 }}>
                        {description}
                    </Typography>
                ) : null}
                <Divider sx={{ mt: 1.1, borderColor: alpha('#0f172a', 0.08) }} />
            </Box>
        );
    }

    if (isDeveloper && !isAdmin) {
        return (
            <Box sx={{ width: '100%', maxWidth: 'none', display: 'grid', gap: 2.25 }}>
                <AnalyticsPageHero
                    eyebrow="Developer Settings"
                    title="A cleaner control surface for the developer portal"
                    chips={[
                        `Landing: ${String(devSettings.defaultStart || 'initial')}`,
                        `Summaries: ${String(devSettings.emailSummary || 'weekly')}`,
                        devSettings.notifyDrawdown ? 'Drawdown alerts on' : 'Drawdown alerts off'
                    ]}
                />

                <AnalyticsPanelSection eyebrow="Portal Controls" title="Developer preferences">
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <Paper sx={{ ...glassSubCardSx, p: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Notifications</Typography>
                            <Stack spacing={1}>
                                <FormControlLabel
                                    control={<Checkbox checked={devSettings.notifyDrawdown} onChange={(e) => setDevSettings((prev) => ({ ...prev, notifyDrawdown: e.target.checked }))} />}
                                    label="Notify me when a drawdown is requested"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={devSettings.notifyReportReady} onChange={(e) => setDevSettings((prev) => ({ ...prev, notifyReportReady: e.target.checked }))} />}
                                    label="Notify me when interim information is ready"
                                />
                            </Stack>
                        </Paper>

                        <Paper sx={{ ...glassSubCardSx, p: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Start Page</Typography>
                            <Stack spacing={1.25} sx={{ maxWidth: 480 }}>
                                <TextField
                                    select
                                    size="small"
                                    label="Default landing view"
                                    value={devSettings.defaultStart}
                                    onChange={(e) => setDevSettings((prev) => ({ ...prev, defaultStart: e.target.value }))}
                                    sx={pillTextFieldSx}
                                >
                                    <MenuItem value="initial">Initial report overview</MenuItem>
                                    <MenuItem value="interim">Latest interim information</MenuItem>
                                    <MenuItem value="drawdown">Drawdown tracker</MenuItem>
                                </TextField>
                                <FormControlLabel
                                    control={<Checkbox checked={devSettings.autoOpenLatestDrawdown} onChange={(e) => setDevSettings((prev) => ({ ...prev, autoOpenLatestDrawdown: e.target.checked }))} />}
                                    label="Auto-open the latest requested drawdown"
                                />
                            </Stack>
                        </Paper>

                        <Paper sx={{ ...glassSubCardSx, p: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Email Summaries</Typography>
                            <Stack spacing={1.25} sx={{ maxWidth: 480 }}>
                                <TextField
                                    select
                                    size="small"
                                    label="Project summary frequency"
                                    value={devSettings.emailSummary}
                                    onChange={(e) => setDevSettings((prev) => ({ ...prev, emailSummary: e.target.value }))}
                                    sx={pillTextFieldSx}
                                >
                                    <MenuItem value="off">Off</MenuItem>
                                    <MenuItem value="weekly">Weekly</MenuItem>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                </TextField>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Summaries include drawdown activity, outstanding questionnaires, and key report milestones.
                                </Typography>
                            </Stack>
                        </Paper>
                    </Box>
                </AnalyticsPanelSection>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', maxWidth: 'none', display: 'grid', gap: 2.25 }}>
            <AnalyticsPageHero
                eyebrow={isAdmin ? 'Control Centre' : 'Personal Settings'}
                title="Settings"
                chips={[
                    `${settingsMetrics.categoryCount} categories`,
                    `${settingsMetrics.visibleCount} sections in view`,
                    isAdmin ? `${settingsMetrics.adminCount} admin controls` : `${settingsMetrics.personalCount} personal controls`
                ]}
            />

            <AnalyticsPanelSection
                eyebrow="Settings Navigation"
                title={activeCategoryMeta?.label || 'Settings'}
                subtitle={activeCategoryMeta?.description || 'Choose a category, then jump directly to a section within it.'}
                right={
                    <Paper sx={{ ...glassSubCardSx, px: 1.35, py: 1 }}>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            In this category
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 800 }}>
                            {settingsMetrics.visibleCount} sections
                        </Typography>
                    </Paper>
                }
            >
                <Tabs
                    value={activeSettingsCategory}
                    onChange={(_, next) => setActiveSettingsCategory(next)}
                    variant="scrollable"
                    allowScrollButtonsMobile
                    scrollButtons="auto"
                    sx={{
                        minHeight: 46,
                        '& .MuiTabs-indicator': {
                            height: 3,
                            borderRadius: 999,
                            background: 'linear-gradient(90deg, #2563eb, #0f766e)'
                        },
                        '& .MuiTab-root': {
                            minHeight: 46,
                            textTransform: 'none',
                            fontWeight: 800,
                            alignItems: 'flex-start'
                        }
                    }}
                >
                    {settingsCategories.map((category) => (
                        <Tab key={category.id} value={category.id} label={category.label} />
                    ))}
                </Tabs>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
                    {visibleJumpItems.map((item) => (
                        <Button key={item.id} size="small" variant="outlined" onClick={() => jumpToSection(item.id)}>
                            {item.label}
                        </Button>
                    ))}
                </Stack>
            </AnalyticsPanelSection>

            {activeSettingsCategory === 'personal' ? (
                <SettingsGroupHeader
                    title="Personal"
                    description="Your account identity, dashboard display, and day-to-day workspace defaults."
                />
            ) : null}

            {activeSettingsCategory === 'personal' ? (
                <CollapsibleSection
                    id="account"
                    title="Account"
                    description="Your signed-in account details and shortcuts to configurable features."
                    defaultOpen
                >
                    <Box sx={{ display: 'grid', gap: 2, maxWidth: 900 }}>
                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Display name</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 8 }}>
                                <Typography sx={{ fontWeight: 700 }}>
                                    {authUser?.display_name || authUser?.displayName || authUser?.username || '—'}
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Role</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 8 }}>
                                <Typography sx={{ fontWeight: 700 }}>
                                    {String(authUser?.role || '—').toUpperCase()}
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>Email</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 8 }}>
                                <Stack spacing={1} sx={{ maxWidth: 520 }}>
                                    <TextField
                                        size="small"
                                        type="email"
                                        value={accountEmail}
                                        label="Email"
                                        placeholder="name@company.com"
                                        onChange={(e) => setAccountEmail(e.target.value)}
                                        disabled={!canEditAccountEmail}
                                        fullWidth
                                        sx={pillTextFieldSx}
                                    />
                                    {canEditAccountEmail ? (
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                            <Button variant="contained" onClick={saveAccountEmail} disabled={accountEmailSaving}>
                                                {accountEmailSaving ? 'Saving…' : 'Save'}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    setAccountEmail('');
                                                    setAccountEmailError('');
                                                    setAccountEmailMessage('');
                                                }}
                                                disabled={accountEmailSaving}
                                            >
                                                Clear
                                            </Button>
                                        </Stack>
                                    ) : null}

                                    {accountEmailError ? <Alert severity="error">{accountEmailError}</Alert> : null}
                                    {accountEmailMessage ? <Alert severity="success">{accountEmailMessage}</Alert> : null}

                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {canEditAccountEmail
                                            ? 'Used for notifications and calendar features (where supported).'
                                            : 'Email is managed by admins to avoid login issues.'}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>

                        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, alignItems: 'start' }}>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 10 }}>Profile photo</div>
                            <div style={{ display: 'grid', gap: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                    <div style={{ width: 54, height: 54, borderRadius: '50%', overflow: 'hidden', border: '1px solid rgba(15, 23, 42, 0.10)', background: '#f3f4f6' }}>
                                        {authUser?.profile_photo_url ? (
                                            <img
                                                src={`${API_BASE_URL}${authUser.profile_photo_url}`}
                                                alt=""
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                            />
                                        ) : null}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp"
                                            disabled={photoUploading}
                                            onChange={(e) => {
                                                const f = e.target.files && e.target.files[0];
                                                if (f) uploadProfilePhoto(f);
                                                e.target.value = '';
                                            }}
                                        />
                                        <button type="button" onClick={deleteProfilePhoto} disabled={photoUploading || !authUser?.profile_photo_url}>
                                            Remove
                                        </button>
                                        {photoError ? <span style={{ color: '#b91c1c', fontSize: 12 }}>{photoError}</span> : null}
                                        {photoMessage ? <span style={{ color: '#065f46', fontSize: 12 }}>{photoMessage}</span> : null}
                                    </div>
                                </div>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>JPG/PNG/WebP up to 5MB.</div>
                            </div>
                        </div>

                        <Box sx={{ mt: 1, color: 'text.secondary' }}>
                            <Typography variant="body2">Configurable features:</Typography>
                            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                                <li>Appearance (dark mode)</li>
                                <li>Calendar Sharing (connect + consent)</li>
                                <li>Multi-Factor Authentication (planned)</li>
                                {isAdmin ? <li>Service Level Agreement (Admin)</li> : null}
                            </Box>
                        </Box>
                    </Box>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'personal' ? (
                <CollapsibleSection
                    id="overview_display"
                    title="Overview Display"
                    description="Choose which additional metrics appear on the Overview dashboard."
                    defaultOpen={false}
                >
                    <FormGroup sx={{ maxWidth: 820 }}>
                        <FormControlLabel
                            control={<Checkbox checked={Boolean(overviewDisplay?.activeProjects)} onChange={(e) => onOverviewDisplayChange?.((prev) => ({ ...(prev || {}), activeProjects: e.target.checked }))} />}
                            label="Active Projects"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={Boolean(overviewDisplay?.monthlyFees)} onChange={(e) => onOverviewDisplayChange?.((prev) => ({ ...(prev || {}), monthlyFees: e.target.checked }))} />}
                            label="Monthly Fees"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={Boolean(overviewDisplay?.feeForecast)} onChange={(e) => onOverviewDisplayChange?.((prev) => ({ ...(prev || {}), feeForecast: e.target.checked }))} />}
                            label="Fee Forecast (year)"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={Boolean(overviewDisplay?.reportsDueWeek)} onChange={(e) => onOverviewDisplayChange?.((prev) => ({ ...(prev || {}), reportsDueWeek: e.target.checked }))} />}
                            label="Report's Due This Week"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={Boolean(overviewDisplay?.avgCostPerReport)} onChange={(e) => onOverviewDisplayChange?.((prev) => ({ ...(prev || {}), avgCostPerReport: e.target.checked }))} />}
                            label="Average Cost Per Report Generation"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={Boolean(overviewDisplay?.awsForecastCost)} onChange={(e) => onOverviewDisplayChange?.((prev) => ({ ...(prev || {}), awsForecastCost: e.target.checked }))} />}
                            label="AWS Forecast Cost (monthly)"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={Boolean(overviewDisplay?.priorityTask)} onChange={(e) => onOverviewDisplayChange?.((prev) => ({ ...(prev || {}), priorityTask: e.target.checked }))} />}
                            label="Report Task Priority"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={Boolean(overviewDisplay?.reportsIssuedThisMonth)} onChange={(e) => onOverviewDisplayChange?.((prev) => ({ ...(prev || {}), reportsIssuedThisMonth: e.target.checked }))} />}
                            label="Reports Issued This Month"
                        />
                        {isAdmin ? (
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(overviewDisplay?.openIssueReports)} onChange={(e) => onOverviewDisplayChange?.((prev) => ({ ...(prev || {}), openIssueReports: e.target.checked }))} />}
                                label="Open Issue Reports"
                            />
                        ) : null}
                    </FormGroup>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'personal' ? (
                <CollapsibleSection
                    id="live_project_chaser"
                    title="Live Project Chaser"
                    description="Set the elapsed-time thresholds used to prioritise Initial Report follow-up."
                    defaultOpen={false}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 720 }}>
                        {userPrefsError ? <Alert severity="error">{userPrefsError}</Alert> : null}
                        {userPrefsMessage ? <Alert severity="success">{userPrefsMessage}</Alert> : null}
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    type="number"
                                    size="small"
                                    label="Amber after (days)"
                                    value={userPrefs?.actions?.live_project_chaser?.amber_after_days ?? LIVE_PROJECT_CHASER_DEFAULTS.amber_after_days}
                                    onChange={(event) => setUserPrefs((current) => ({
                                        ...(current || getDefaultUserPreferences()),
                                        actions: {
                                            ...(current?.actions || getDefaultUserPreferences().actions),
                                            live_project_chaser: {
                                                ...(current?.actions?.live_project_chaser || LIVE_PROJECT_CHASER_DEFAULTS),
                                                amber_after_days: Number(event.target.value)
                                            }
                                        }
                                    }))}
                                    inputProps={{ min: 0, max: 3649, step: 1 }}
                                    helperText="Green applies up to and including this day."
                                    fullWidth
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    type="number"
                                    size="small"
                                    label="Red after (days)"
                                    value={userPrefs?.actions?.live_project_chaser?.red_after_days ?? LIVE_PROJECT_CHASER_DEFAULTS.red_after_days}
                                    onChange={(event) => setUserPrefs((current) => ({
                                        ...(current || getDefaultUserPreferences()),
                                        actions: {
                                            ...(current?.actions || getDefaultUserPreferences().actions),
                                            live_project_chaser: {
                                                ...(current?.actions?.live_project_chaser || LIVE_PROJECT_CHASER_DEFAULTS),
                                                red_after_days: Number(event.target.value)
                                            }
                                        }
                                    }))}
                                    inputProps={{ min: 1, max: 3650, step: 1 }}
                                    helperText="Amber applies after the green threshold through this day."
                                    error={Number(userPrefs?.actions?.live_project_chaser?.red_after_days) <= Number(userPrefs?.actions?.live_project_chaser?.amber_after_days)}
                                    fullWidth
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                        </Grid>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {[
                                { label: `Green: 0–${Number(userPrefs?.actions?.live_project_chaser?.amber_after_days ?? 40)} days`, bg: '#dcfce7', fg: '#166534' },
                                { label: `Amber: ${Number(userPrefs?.actions?.live_project_chaser?.amber_after_days ?? 40) + 1}–${Number(userPrefs?.actions?.live_project_chaser?.red_after_days ?? 60)} days`, bg: '#fef3c7', fg: '#92400e' },
                                { label: `Red: ${Number(userPrefs?.actions?.live_project_chaser?.red_after_days ?? 60) + 1}+ days`, bg: '#fee2e2', fg: '#b91c1c' }
                            ].map((range) => (
                                <Box key={range.label} sx={{ px: 1.25, py: 0.65, borderRadius: 1, backgroundColor: range.bg, color: range.fg, fontWeight: 800, fontSize: '0.8rem' }}>
                                    {range.label}
                                </Box>
                            ))}
                        </Stack>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button
                                variant="contained"
                                onClick={saveUserPreferences}
                                disabled={userPrefsSaving || userPrefsLoading || Number(userPrefs?.actions?.live_project_chaser?.red_after_days) <= Number(userPrefs?.actions?.live_project_chaser?.amber_after_days)}
                            >
                                {userPrefsSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadUserPreferences} disabled={userPrefsLoading}>
                                {userPrefsLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'personal' ? (
                <CollapsibleSection
                    id="appearance"
                    title="Appearance"
                    description="Personal display preferences for the dashboard."
                    defaultOpen
                >
                    <FormControlLabel
                        control={<Switch checked={dark} onChange={(e) => setDark(e.target.checked)} />}
                        label="Enable Dark Mode"
                    />
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'notifications' ? (
                <CollapsibleSection
                    id="notifications"
                    title="Notifications"
                    description="How you receive alerts and reminders."
                    defaultOpen={false}
                    statusItems={notificationDigestAdminStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {userPrefsError ? <Alert severity="error">{userPrefsError}</Alert> : null}
                        {userPrefsMessage ? <Alert severity="success">{userPrefsMessage}</Alert> : null}

                        <FormControlLabel
                            control={<Switch checked={Boolean(userPrefs?.notifications?.enabled)} onChange={(e) => setUserPrefs((p) => ({
                                ...(p || getDefaultUserPreferences()),
                                notifications: { ...(p?.notifications || getDefaultUserPreferences().notifications), enabled: e.target.checked }
                            }))} />}
                            label="Enable notifications"
                        />

                        <FormControlLabel
                            control={<Switch checked={Boolean(userPrefs?.notifications?.toast_enabled)} onChange={(e) => setUserPrefs((p) => ({
                                ...(p || getDefaultUserPreferences()),
                                notifications: { ...(p?.notifications || getDefaultUserPreferences().notifications), toast_enabled: e.target.checked }
                            }))} />}
                            label="Show toast popups"
                        />

                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    select
                                    size="small"
                                    label="Polling interval"
                                    value={String(userPrefs?.notifications?.poll_interval_seconds ?? 60)}
                                    onChange={(e) => setUserPrefs((p) => ({
                                        ...(p || getDefaultUserPreferences()),
                                        notifications: {
                                            ...(p?.notifications || getDefaultUserPreferences().notifications),
                                            poll_interval_seconds: Number(e.target.value)
                                        }
                                    }))}
                                    sx={pillTextFieldSx}
                                    fullWidth
                                >
                                    <MenuItem value="15">Every 15s</MenuItem>
                                    <MenuItem value="30">Every 30s</MenuItem>
                                    <MenuItem value="60">Every 60s</MenuItem>
                                    <MenuItem value="120">Every 2m</MenuItem>
                                    <MenuItem value="300">Every 5m</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                                    Quiet hours (optional)
                                </Typography>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                                    <TextField
                                        size="small"
                                        label="Start (HH:MM)"
                                        value={String(userPrefs?.notifications?.quiet_hours_start || '')}
                                        onChange={(e) => setUserPrefs((p) => ({
                                            ...(p || getDefaultUserPreferences()),
                                            notifications: { ...(p?.notifications || getDefaultUserPreferences().notifications), quiet_hours_start: e.target.value }
                                        }))}
                                        sx={pillTextFieldSx}
                                    />
                                    <TextField
                                        size="small"
                                        label="End (HH:MM)"
                                        value={String(userPrefs?.notifications?.quiet_hours_end || '')}
                                        onChange={(e) => setUserPrefs((p) => ({
                                            ...(p || getDefaultUserPreferences()),
                                            notifications: { ...(p?.notifications || getDefaultUserPreferences().notifications), quiet_hours_end: e.target.value }
                                        }))}
                                        sx={pillTextFieldSx}
                                    />
                                </Stack>
                            </Grid>
                        </Grid>

                        <Divider />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            Notify me about
                        </Typography>
                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(userPrefs?.notifications?.types?.drawdown_requested)} onChange={(e) => setUserPrefs((p) => ({
                                    ...(p || getDefaultUserPreferences()),
                                    notifications: {
                                        ...(p?.notifications || getDefaultUserPreferences().notifications),
                                        types: { ...(p?.notifications?.types || getDefaultUserPreferences().notifications.types), drawdown_requested: e.target.checked }
                                    }
                                }))} />}
                                label="Drawdown requested"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(userPrefs?.notifications?.types?.report_ready)} onChange={(e) => setUserPrefs((p) => ({
                                    ...(p || getDefaultUserPreferences()),
                                    notifications: {
                                        ...(p?.notifications || getDefaultUserPreferences().notifications),
                                        types: { ...(p?.notifications?.types || getDefaultUserPreferences().notifications.types), report_ready: e.target.checked }
                                    }
                                }))} />}
                                label="Report ready"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(userPrefs?.notifications?.types?.questionnaire_submitted)} onChange={(e) => setUserPrefs((p) => ({
                                    ...(p || getDefaultUserPreferences()),
                                    notifications: {
                                        ...(p?.notifications || getDefaultUserPreferences().notifications),
                                        types: { ...(p?.notifications?.types || getDefaultUserPreferences().notifications.types), questionnaire_submitted: e.target.checked }
                                    }
                                }))} />}
                                label="Questionnaire submitted"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(userPrefs?.notifications?.types?.issue_reported)} onChange={(e) => setUserPrefs((p) => ({
                                    ...(p || getDefaultUserPreferences()),
                                    notifications: {
                                        ...(p?.notifications || getDefaultUserPreferences().notifications),
                                        types: { ...(p?.notifications?.types || getDefaultUserPreferences().notifications.types), issue_reported: e.target.checked }
                                    }
                                }))} />}
                                label="Issues and system alerts"
                            />
                            {isAdmin ? (
                                <FormControlLabel
                                    control={<Checkbox checked={Boolean(userPrefs?.notifications?.types?.client_approval_requested)} onChange={(e) => setUserPrefs((p) => ({
                                        ...(p || getDefaultUserPreferences()),
                                        notifications: {
                                            ...(p?.notifications || getDefaultUserPreferences().notifications),
                                            types: { ...(p?.notifications?.types || getDefaultUserPreferences().notifications.types), client_approval_requested: e.target.checked }
                                        }
                                    }))} />}
                                    label={(
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <span>Client approvals (new funders to review)</span>
                                            <Chip size="small" label="Admin only" variant="outlined" />
                                        </Stack>
                                    )}
                                />
                            ) : null}
                        </FormGroup>

                        <Divider />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            Delivery
                        </Typography>

                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            Scope controls which projects are included in your in-app notifications feed.
                        </Typography>

                        <RadioGroup
                            value={String(userPrefs?.notifications?.scope || 'all')}
                            onChange={(e) => {
                                const nextScope = String(e?.target?.value || 'all');
                                setUserPrefs((p) => ({
                                    ...(p || getDefaultUserPreferences()),
                                    notifications: { ...(p?.notifications || getDefaultUserPreferences().notifications), scope: nextScope }
                                }));
                                if (nextScope === 'subscribed') {
                                    loadNotificationSubscriptionProjects();
                                    loadNotificationSubscriptions();
                                }
                            }}
                        >
                            <FormControlLabel value="all" control={<Radio />} label="All projects (default)" />
                            <FormControlLabel value="subscribed" control={<Radio />} label="Only projects I subscribe to" />
                        </RadioGroup>

                        {String(userPrefs?.notifications?.scope || 'all') === 'subscribed' ? (
                            <Box sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                {notifSubsError ? <Alert severity="error">{notifSubsError}</Alert> : null}
                                {notifSubsMessage ? <Alert severity="success">{notifSubsMessage}</Alert> : null}

                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            const all = (Array.isArray(notifProjectOptions) ? notifProjectOptions : [])
                                                .map((p) => String(p.reference || '').trim())
                                                .filter(Boolean);
                                            setNotifSubscribedProjects(Array.from(new Set(all)).sort());
                                        }}
                                        disabled={notifSubsLoading}
                                    >
                                        Subscribe to all active
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setNotifSubscribedProjects([])}
                                        disabled={notifSubsLoading}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            setNotifSubsLoaded(false);
                                            setNotifProjectsLoaded(false);
                                            loadNotificationSubscriptionProjects();
                                            loadNotificationSubscriptions();
                                        }}
                                        disabled={notifSubsLoading}
                                    >
                                        Reload
                                    </Button>
                                </Stack>

                                <TextField
                                    size="small"
                                    label="Filter projects"
                                    value={notifProjectQuery}
                                    onChange={(e) => setNotifProjectQuery(e.target.value)}
                                    sx={pillTextFieldSx}
                                    fullWidth
                                />

                                <Box sx={{ mt: 1, maxHeight: 260, overflow: 'auto', borderTop: '1px solid', borderColor: 'divider' }}>
                                    {(Array.isArray(notifProjectOptions) ? notifProjectOptions : [])
                                        .filter((p) => {
                                            const q = String(notifProjectQuery || '').trim().toLowerCase();
                                            if (!q) return true;
                                            const hay = `${String(p.reference || '')} ${String(p.project_name || '')}`.toLowerCase();
                                            return hay.includes(q);
                                        })
                                        .slice(0, 250)
                                        .map((p) => {
                                            const ref = String(p.reference || '').trim();
                                            const label = p.project_name ? `${ref} — ${p.project_name}` : ref;
                                            const checked = (Array.isArray(notifSubscribedProjects) ? notifSubscribedProjects : []).includes(ref);
                                            return (
                                                <FormControlLabel
                                                    key={ref}
                                                    control={
                                                        <Checkbox
                                                            checked={checked}
                                                            onChange={(e) => {
                                                                const on = Boolean(e.target.checked);
                                                                setNotifSubscribedProjects((prev) => {
                                                                    const list = Array.isArray(prev) ? prev : [];
                                                                    const set = new Set(list);
                                                                    if (on) set.add(ref);
                                                                    else set.delete(ref);
                                                                    return Array.from(set).sort();
                                                                });
                                                            }}
                                                        />
                                                    }
                                                    label={label}
                                                />
                                            );
                                        })}
                                </Box>

                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                                    You will still receive global notifications (no project reference).
                                </Typography>
                            </Box>
                        ) : null}

                        <Divider />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            Digest (email)
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            Choose how often you want email digests and which items they include.
                        </Typography>
                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    select
                                    size="small"
                                    label="Email digest frequency"
                                    value={String(userPrefs?.notifications?.digest?.frequency || 'off')}
                                    onChange={(e) => setUserPrefs((p) => ({
                                        ...(p || getDefaultUserPreferences()),
                                        notifications: {
                                            ...(p?.notifications || getDefaultUserPreferences().notifications),
                                            digest: { ...(p?.notifications?.digest || getDefaultUserPreferences().notifications.digest), frequency: e.target.value }
                                        }
                                    }))}
                                    sx={pillTextFieldSx}
                                    fullWidth
                                >
                                    <MenuItem value="off">Off</MenuItem>
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="weekly">Weekly</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    size="small"
                                    label="Digest time (HH:MM)"
                                    value={String(userPrefs?.notifications?.digest?.time_local || '08:00')}
                                    onChange={(e) => setUserPrefs((p) => ({
                                        ...(p || getDefaultUserPreferences()),
                                        notifications: {
                                            ...(p?.notifications || getDefaultUserPreferences().notifications),
                                            digest: { ...(p?.notifications?.digest || getDefaultUserPreferences().notifications.digest), time_local: e.target.value }
                                        }
                                    }))}
                                    sx={pillTextFieldSx}
                                    fullWidth
                                />
                            </Grid>

                            {String(userPrefs?.notifications?.digest?.frequency || 'off') === 'weekly' ? (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        select
                                        size="small"
                                        label="Weekly digest day"
                                        value={String(userPrefs?.notifications?.digest?.weekday || 'mon')}
                                        onChange={(e) => setUserPrefs((p) => ({
                                            ...(p || getDefaultUserPreferences()),
                                            notifications: {
                                                ...(p?.notifications || getDefaultUserPreferences().notifications),
                                                digest: { ...(p?.notifications?.digest || getDefaultUserPreferences().notifications.digest), weekday: e.target.value }
                                            }
                                        }))}
                                        sx={pillTextFieldSx}
                                        fullWidth
                                    >
                                        <MenuItem value="mon">Monday</MenuItem>
                                        <MenuItem value="tue">Tuesday</MenuItem>
                                        <MenuItem value="wed">Wednesday</MenuItem>
                                        <MenuItem value="thu">Thursday</MenuItem>
                                        <MenuItem value="fri">Friday</MenuItem>
                                        <MenuItem value="sat">Saturday</MenuItem>
                                        <MenuItem value="sun">Sunday</MenuItem>
                                    </TextField>
                                </Grid>
                            ) : null}
                        </Grid>

                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 1 }}>
                            Digest includes
                        </Typography>
                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(userPrefs?.notifications?.digest?.types?.drawdown_requested)} onChange={(e) => setUserPrefs((p) => ({
                                    ...(p || getDefaultUserPreferences()),
                                    notifications: {
                                        ...(p?.notifications || getDefaultUserPreferences().notifications),
                                        digest: {
                                            ...(p?.notifications?.digest || getDefaultUserPreferences().notifications.digest),
                                            types: { ...(p?.notifications?.digest?.types || getDefaultUserPreferences().notifications.digest.types), drawdown_requested: e.target.checked }
                                        }
                                    }
                                }))} />}
                                label="Drawdown requested"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(userPrefs?.notifications?.digest?.types?.report_ready)} onChange={(e) => setUserPrefs((p) => ({
                                    ...(p || getDefaultUserPreferences()),
                                    notifications: {
                                        ...(p?.notifications || getDefaultUserPreferences().notifications),
                                        digest: {
                                            ...(p?.notifications?.digest || getDefaultUserPreferences().notifications.digest),
                                            types: { ...(p?.notifications?.digest?.types || getDefaultUserPreferences().notifications.digest.types), report_ready: e.target.checked }
                                        }
                                    }
                                }))} />}
                                label="Report ready"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(userPrefs?.notifications?.digest?.types?.questionnaire_submitted)} onChange={(e) => setUserPrefs((p) => ({
                                    ...(p || getDefaultUserPreferences()),
                                    notifications: {
                                        ...(p?.notifications || getDefaultUserPreferences().notifications),
                                        digest: {
                                            ...(p?.notifications?.digest || getDefaultUserPreferences().notifications.digest),
                                            types: { ...(p?.notifications?.digest?.types || getDefaultUserPreferences().notifications.digest.types), questionnaire_submitted: e.target.checked }
                                        }
                                    }
                                }))} />}
                                label="Questionnaire submitted"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(userPrefs?.notifications?.digest?.types?.issue_reported)} onChange={(e) => setUserPrefs((p) => ({
                                    ...(p || getDefaultUserPreferences()),
                                    notifications: {
                                        ...(p?.notifications || getDefaultUserPreferences().notifications),
                                        digest: {
                                            ...(p?.notifications?.digest || getDefaultUserPreferences().notifications.digest),
                                            types: { ...(p?.notifications?.digest?.types || getDefaultUserPreferences().notifications.digest.types), issue_reported: e.target.checked }
                                        }
                                    }
                                }))} />}
                                label="Issues and system alerts"
                            />
                        </FormGroup>

                        {isAdmin ? (
                            <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                    Digest delivery (admin)
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                    Optional override to copy or route digests to a shared mailbox.
                                </Typography>

                                {notificationDigestAdminError ? (
                                    <Typography variant="body2" sx={{ color: 'error.main' }}>{notificationDigestAdminError}</Typography>
                                ) : null}
                                {notificationDigestAdminMessage ? (
                                    <Typography variant="body2" sx={{ color: 'success.main' }}>{notificationDigestAdminMessage}</Typography>
                                ) : null}

                                <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FormControlLabel
                                            control={<Checkbox checked={Boolean(notificationDigestAdmin.shared_mailbox_enabled)} onChange={(e) => setNotificationDigestAdmin((p) => ({ ...(p || {}), shared_mailbox_enabled: Boolean(e.target.checked) }))} />}
                                            label="Enable shared mailbox override"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FormControlLabel
                                            control={<Checkbox checked={Boolean(notificationDigestAdmin.shared_mailbox_only)} onChange={(e) => setNotificationDigestAdmin((p) => ({ ...(p || {}), shared_mailbox_only: Boolean(e.target.checked) }))} />}
                                            label="Send only to shared mailbox"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 8 }}>
                                        <TextField
                                            size="small"
                                            label="Shared mailbox email"
                                            value={String(notificationDigestAdmin.shared_mailbox_to || '')}
                                            onChange={(e) => setNotificationDigestAdmin((p) => ({ ...(p || {}), shared_mailbox_to: e.target.value }))}
                                            sx={pillTextFieldSx}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <TextField
                                            size="small"
                                            label="Max items per digest"
                                            value={String(notificationDigestAdmin.max_items_per_digest || '')}
                                            onChange={(e) => setNotificationDigestAdmin((p) => ({ ...(p || {}), max_items_per_digest: e.target.value }))}
                                            sx={pillTextFieldSx}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <TextField
                                            size="small"
                                            label="Lookback hours"
                                            value={String(notificationDigestAdmin.lookback_hours || '')}
                                            onChange={(e) => setNotificationDigestAdmin((p) => ({ ...(p || {}), lookback_hours: e.target.value }))}
                                            sx={pillTextFieldSx}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>

                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    <Button
                                        variant="contained"
                                        onClick={saveNotificationDigestAdminSettings}
                                        disabled={notificationDigestAdminSaving || notificationDigestAdminLoading}
                                    >
                                        {notificationDigestAdminSaving ? 'Saving…' : 'Save digest settings'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={loadNotificationDigestAdminSettings}
                                        disabled={notificationDigestAdminLoading}
                                    >
                                        {notificationDigestAdminLoading ? 'Refreshing…' : 'Refresh'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => runNotificationDigestNow({ dryRun: true })}
                                        disabled={notificationDigestRunLoading}
                                    >
                                        {notificationDigestRunLoading ? 'Running…' : 'Run digest (dry-run)'}
                                    </Button>
                                </Stack>
                            </>
                        ) : null}

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveUserPreferences} disabled={userPrefsSaving || userPrefsLoading}>
                                {userPrefsSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadUserPreferences} disabled={userPrefsLoading}>
                                {userPrefsLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'personal' ? (
                <CollapsibleSection
                    id="ui_preferences"
                    title="Dashboard UI"
                    description="Productivity preferences for navigation and tables."
                    defaultOpen={false}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    select
                                    size="small"
                                    label="Default landing view"
                                    value={String(userPrefs?.ui?.default_landing || 'overview')}
                                    onChange={(e) => setUserPrefs((p) => ({
                                        ...(p || getDefaultUserPreferences()),
                                        ui: { ...(p?.ui || getDefaultUserPreferences().ui), default_landing: e.target.value }
                                    }))}
                                    sx={pillTextFieldSx}
                                    fullWidth
                                >
                                    <MenuItem value="overview">Overview</MenuItem>
                                    <MenuItem value="projects">Projects</MenuItem>
                                    <MenuItem value="reports">Reports</MenuItem>
                                    <MenuItem value="team">Team</MenuItem>
                                    <MenuItem value="clients">Clients</MenuItem>
                                    <MenuItem value="financials">Financials</MenuItem>
                                    <MenuItem value="analytics">Analytics</MenuItem>
                                    <MenuItem value="calendar">Calendar</MenuItem>
                                </TextField>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    If you don’t have access to the selected view, Overview is used.
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    select
                                    size="small"
                                    label="Table density"
                                    value={String(userPrefs?.ui?.table_density || 'comfortable')}
                                    onChange={(e) => setUserPrefs((p) => ({
                                        ...(p || getDefaultUserPreferences()),
                                        ui: { ...(p?.ui || getDefaultUserPreferences().ui), table_density: e.target.value }
                                    }))}
                                    sx={pillTextFieldSx}
                                    fullWidth
                                >
                                    <MenuItem value="comfortable">Comfortable</MenuItem>
                                    <MenuItem value="compact">Compact</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>

                        <FormGroup>
                            <FormControlLabel
                                control={<Switch checked={Boolean(userPrefs?.ui?.sidebar_pinned)} onChange={(e) => setUserPrefs((p) => ({
                                    ...(p || getDefaultUserPreferences()),
                                    ui: { ...(p?.ui || getDefaultUserPreferences().ui), sidebar_pinned: e.target.checked }
                                }))} />}
                                label="Keep sidebar pinned by default"
                            />
                            <FormControlLabel
                                control={<Switch checked={Boolean(userPrefs?.ui?.remember_last_project)} onChange={(e) => setUserPrefs((p) => ({
                                    ...(p || getDefaultUserPreferences()),
                                    ui: { ...(p?.ui || getDefaultUserPreferences().ui), remember_last_project: e.target.checked }
                                }))} />}
                                label="Remember my last opened project"
                            />
                        </FormGroup>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveUserPreferences} disabled={userPrefsSaving || userPrefsLoading}>
                                {userPrefsSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadUserPreferences} disabled={userPrefsLoading}>
                                {userPrefsLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'reports' ? (
                <SettingsGroupHeader
                    title="Reports"
                    description="Output defaults, naming rules, and appendix configuration for report workflows."
                />
            ) : null}

            {isAdmin && activeSettingsCategory === 'reports' ? (
                <CollapsibleSection id="report_identity" title="Report Identity" defaultOpen={false} statusItems={orgProfileStatusItems}>
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 600 }}>
                        {orgProfileError ? <Alert severity="error">{orgProfileError}</Alert> : null}
                        {orgProfileMessage ? <Alert severity="success">{orgProfileMessage}</Alert> : null}
                        <TextField
                            label="Shorthand Company Name"
                            value={orgProfile.report_company_shorthand || ''}
                            onChange={(event) => setOrgProfile((current) => ({ ...current, report_company_shorthand: event.target.value }))}
                            disabled={orgProfileLoading || orgProfileSaving}
                            inputProps={{ maxLength: 40 }}
                            fullWidth
                            size="small"
                            sx={pillTextFieldSx}
                        />
                        {orgProfile.report_company_shorthand ? <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {orgProfile.report_company_shorthand} Assessment
                        </Typography> : null}
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={saveOrgProfileSettings} disabled={orgProfileSaving || orgProfileLoading}>
                                {orgProfileSaving ? 'Saving...' : 'Save'}
                            </Button>
                            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadOrgProfileSettings} disabled={orgProfileSaving || orgProfileLoading}>
                                Refresh
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'reports' ? (
                <CollapsibleSection
                    id="report_style_template"
                    title="Report Style Template"
                    description="Upload a DOCX shell that defines the visual direction and placeholder entry points for report generation."
                    defaultOpen={false}
                    statusItems={reportStyleTemplateStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {reportStyleTemplateError ? <Alert severity="error">{reportStyleTemplateError}</Alert> : null}
                        {reportStyleTemplateMessage ? <Alert severity="success">{reportStyleTemplateMessage}</Alert> : null}

                        <FormControlLabel
                            control={(
                                <Switch
                                    checked={Boolean(reportStyleTemplate.enabled)}
                                    onChange={(e) => setReportStyleTemplate((s) => ({ ...s, enabled: e.target.checked }))}
                                    disabled={reportStyleTemplateLoading || reportStyleTemplateUploading || !reportStyleTemplate.storage_key}
                                />
                            )}
                            label="Use uploaded report style template for Initial Report generation"
                        />

                        <Stack spacing={1}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Upload template
                            </Typography>
                            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
                                <input
                                    type="file"
                                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    disabled={reportStyleTemplateUploading}
                                    onChange={(e) => {
                                        const file = e.target.files && e.target.files[0];
                                        if (file) uploadReportStyleTemplate(file);
                                        e.target.value = '';
                                    }}
                                />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Stored to S3 under the User Template folder.
                                </Typography>
                            </Stack>
                            {reportStyleTemplate.original_file_name ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Current template: {reportStyleTemplate.original_file_name}
                                    {reportStyleTemplate.uploaded_at ? `, uploaded ${new Date(reportStyleTemplate.uploaded_at).toLocaleString()}` : ''}
                                    {reportStyleTemplate.uploaded_by ? ` by ${reportStyleTemplate.uploaded_by}` : ''}
                                </Typography>
                            ) : (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    No report style template uploaded yet.
                                </Typography>
                            )}
                        </Stack>

                        <Box sx={{ p: 1.5, borderRadius: 3, border: `1px solid ${alpha('#0f172a', 0.08)}`, background: `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#f8fbff', 0.82)} 100%)` }}>
                            <Stack spacing={0.75}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                    Word placeholder entry points
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Use double braces in the DOCX template, for example: {'{{report_type}}'}, {'{{report_version_label}}'}, {'{{report_date_long}}'}, {'{{borrower_company}}'}, {'{{funder_company}}'}, {'{{report_header_text}}'}, {'{{report_footer_text}}'}.
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Existing project placeholders also remain available, including {'{{project_reference}}'}, {'{{site_address}}'}, {'{{lead_surveyor_name}}'}, and {'{{report_title}}'}.
                                </Typography>
                            </Stack>
                        </Box>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveReportStyleTemplateSettings} disabled={reportStyleTemplateSaving || reportStyleTemplateLoading || reportStyleTemplateUploading || !reportStyleTemplate.storage_key}>
                                {reportStyleTemplateSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadReportStyleTemplateSettings} disabled={reportStyleTemplateLoading || reportStyleTemplateUploading}>
                                {reportStyleTemplateLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'reports' ? (
                <CollapsibleSection
                    id="interim_report_template"
                    title="Interim Report Template"
                    description="Upload and configure the dedicated Interim DOCX template shell used for Interim report generation."
                    defaultOpen={false}
                    statusItems={interimReportTemplateStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {interimReportTemplateError ? <Alert severity="error">{interimReportTemplateError}</Alert> : null}
                        {interimReportTemplateMessage ? <Alert severity="success">{interimReportTemplateMessage}</Alert> : null}

                        <FormControlLabel
                            control={(
                                <Switch
                                    checked={Boolean(interimReportTemplate.enabled)}
                                    onChange={(e) => setInterimReportTemplate((s) => ({ ...s, enabled: e.target.checked }))}
                                    disabled={interimReportTemplateLoading || interimReportTemplateUploading || !interimReportTemplate.storage_key}
                                />
                            )}
                            label="Use uploaded template for Interim Report generation"
                        />

                        <Stack spacing={1}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Upload template
                            </Typography>
                            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
                                <input
                                    type="file"
                                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    disabled={interimReportTemplateUploading}
                                    onChange={(e) => {
                                        const file = e.target.files && e.target.files[0];
                                        if (file) uploadInterimReportTemplate(file);
                                        e.target.value = '';
                                    }}
                                />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Stored to S3 under projects/INT-Templates.
                                </Typography>
                            </Stack>
                            {interimReportTemplate.original_file_name ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Current template: {interimReportTemplate.original_file_name}
                                    {interimReportTemplate.uploaded_at ? `, uploaded ${new Date(interimReportTemplate.uploaded_at).toLocaleString()}` : ''}
                                    {interimReportTemplate.uploaded_by ? ` by ${interimReportTemplate.uploaded_by}` : ''}
                                </Typography>
                            ) : (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    No interim report template uploaded yet.
                                </Typography>
                            )}
                        </Stack>

                        <Box sx={{ p: 1.5, borderRadius: 3, border: `1px solid ${alpha('#0f172a', 0.08)}`, background: `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#f8fbff', 0.82)} 100%)` }}>
                            <Stack spacing={1}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                    Configurable placeholders
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Enter one placeholder per line. These are the allowed entry points for the Interim template structure (Title Page and Header/Footer), aligned to Initial Report placeholder style.
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            label="Title Page placeholders"
                                            multiline
                                            minRows={6}
                                            fullWidth
                                            value={(interimReportTemplate.placeholders?.title_page || []).join('\n')}
                                            onChange={(e) => {
                                                const next = parsePlaceholderTextList(e.target.value);
                                                setInterimReportTemplate((s) => ({
                                                    ...s,
                                                    placeholders: {
                                                        ...(s.placeholders || {}),
                                                        title_page: next
                                                    }
                                                }));
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            label="Header/Footer placeholders"
                                            multiline
                                            minRows={6}
                                            fullWidth
                                            value={(interimReportTemplate.placeholders?.header_footer || []).join('\n')}
                                            onChange={(e) => {
                                                const next = parsePlaceholderTextList(e.target.value);
                                                setInterimReportTemplate((s) => ({
                                                    ...s,
                                                    placeholders: {
                                                        ...(s.placeholders || {}),
                                                        header_footer: next
                                                    }
                                                }));
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Stack>
                        </Box>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button
                                variant="contained"
                                onClick={saveInterimReportTemplateSettings}
                                disabled={interimReportTemplateSaving || interimReportTemplateLoading || interimReportTemplateUploading || !interimReportTemplate.storage_key}
                            >
                                {interimReportTemplateSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={loadInterimReportTemplateSettings}
                                disabled={interimReportTemplateLoading || interimReportTemplateUploading}
                            >
                                {interimReportTemplateLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && canConfigureFeasibilityReport && activeSettingsCategory === 'reports' ? (
                <CollapsibleSection
                    id="feasibility_report"
                    title="Feasibility Report"
                    description="Organisation-specific template and generation configuration for Feasibility Analysis Reports."
                    defaultOpen={false}
                    statusItems={feasibilityReportStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {feasibilityReportError ? <Alert severity="error">{feasibilityReportError}</Alert> : null}
                        {feasibilityReportMessage ? <Alert severity="success">{feasibilityReportMessage}</Alert> : null}

                        <FormControlLabel
                            control={(
                                <Switch
                                    checked={Boolean(feasibilityReport.enabled)}
                                    onChange={(event) => setFeasibilityReport((current) => ({ ...current, enabled: event.target.checked }))}
                                    disabled={feasibilityReportLoading || feasibilityReportUploading || !feasibilityReport.template_uploaded}
                                />
                            )}
                            label="Use uploaded template for Feasibility Report generation"
                        />

                        <Stack spacing={1}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Upload DOCX template</Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                <input
                                    type="file"
                                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    disabled={feasibilityReportUploading}
                                    onChange={(event) => {
                                        const file = event.target.files && event.target.files[0];
                                        if (file) uploadFeasibilityReportTemplate(file);
                                        event.target.value = '';
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {feasibilityReport.original_file_name
                                        ? `Current template: ${feasibilityReport.original_file_name}`
                                        : 'No Feasibility report template uploaded yet.'}
                                </Typography>
                            </Stack>
                            {feasibilityReport.uploaded_at ? (
                                <Typography variant="caption" color="text.secondary">
                                    Uploaded {new Date(feasibilityReport.uploaded_at).toLocaleString()}
                                    {feasibilityReport.uploaded_by ? ` by ${feasibilityReport.uploaded_by}` : ''}
                                </Typography>
                            ) : null}
                        </Stack>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField fullWidth size="small" label="Template version" value={feasibilityReport.template_version} onChange={(event) => setFeasibilityReport((current) => ({ ...current, template_version: event.target.value }))} />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField fullWidth size="small" label="AI model override" placeholder="Organisation default" value={feasibilityReport.model_name} onChange={(event) => setFeasibilityReport((current) => ({ ...current, model_name: event.target.value }))} />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField fullWidth size="small" label="Prompt version" value={feasibilityReport.prompt_version} onChange={(event) => setFeasibilityReport((current) => ({ ...current, prompt_version: event.target.value }))} />
                            </Grid>
                        </Grid>

                        <TextField
                            fullWidth
                            multiline
                            minRows={7}
                            label="DOCX placeholder registry v0.1"
                            value={(feasibilityReport.placeholders || []).join('\n')}
                            slotProps={{ input: { readOnly: true } }}
                            helperText={`${(feasibilityReport.placeholders || []).length} controlled placeholders`}
                        />

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveFeasibilityReportSettings} disabled={feasibilityReportSaving || feasibilityReportLoading || feasibilityReportUploading}>
                                {feasibilityReportSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadFeasibilityReportSettings} disabled={feasibilityReportLoading || feasibilityReportUploading}>
                                {feasibilityReportLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'reports' ? (
                <CollapsibleSection
                    id="fee_proposal_template"
                    title="Fee Proposal Template"
                    description="Upload a DOCX template used as the admin-managed shell for fee proposal generation."
                    defaultOpen={false}
                    statusItems={feeProposalTemplateStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {feeProposalTemplateError ? <Alert severity="error">{feeProposalTemplateError}</Alert> : null}
                        {feeProposalTemplateMessage ? <Alert severity="success">{feeProposalTemplateMessage}</Alert> : null}

                        {FEE_PROPOSAL_TEMPLATE_SERVICE_LINES.map(({ key, label }) => {
                            const template = feeProposalTemplate.service_lines?.[key] || {};
                            return (
                                <Box key={key} sx={{ p: 1.5, borderRadius: 3, border: `1px solid ${alpha('#0f172a', 0.08)}`, background: `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#f8fbff', 0.82)} 100%)` }}>
                                    <Stack spacing={1.25}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                            {label}
                                        </Typography>

                                        <FormControlLabel
                                            control={(
                                                <Switch
                                                    checked={Boolean(template.enabled)}
                                                    onChange={(e) => setFeeProposalTemplate((s) => ({
                                                        ...s,
                                                        service_lines: {
                                                            ...(s.service_lines || {}),
                                                            [key]: {
                                                                ...(s.service_lines?.[key] || {}),
                                                                enabled: e.target.checked
                                                            }
                                                        }
                                                    }))}
                                                    disabled={feeProposalTemplateLoading || feeProposalTemplateUploading || !template.storage_key}
                                                />
                                            )}
                                            label={`Use uploaded ${label} template for fee proposal generation`}
                                        />

                                        <Stack spacing={1}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                Upload template
                                            </Typography>
                                            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
                                                <input
                                                    type="file"
                                                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                    disabled={feeProposalTemplateUploading}
                                                    onChange={(e) => {
                                                        const file = e.target.files && e.target.files[0];
                                                        if (file) uploadFeeProposalTemplate(key, file);
                                                        e.target.value = '';
                                                    }}
                                                />
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    Stored to S3 under system/FeeProposals/Templates.
                                                </Typography>
                                            </Stack>
                                            {template.original_file_name ? (
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    Current template: {template.original_file_name}
                                                    {template.uploaded_at ? `, uploaded ${new Date(template.uploaded_at).toLocaleString()}` : ''}
                                                    {template.uploaded_by ? ` by ${template.uploaded_by}` : ''}
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    No {label.toLowerCase()} template uploaded yet.
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Stack>
                                </Box>
                            );
                        })}

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveFeeProposalTemplateSettings} disabled={feeProposalTemplateSaving || feeProposalTemplateLoading || feeProposalTemplateUploading}>
                                {feeProposalTemplateSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadFeeProposalTemplateSettings} disabled={feeProposalTemplateLoading || feeProposalTemplateUploading}>
                                {feeProposalTemplateLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'reports' ? (
                <CollapsibleSection
                    id="report_defaults"
                    title="Report Defaults"
                    description="Default output format and generation options."
                    defaultOpen={false}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    select
                                    size="small"
                                    label="Default format"
                                    value={String(userPrefs?.reports?.default_format || 'docx')}
                                    onChange={(e) => setUserPrefs((p) => ({
                                        ...(p || getDefaultUserPreferences()),
                                        reports: { ...(p?.reports || getDefaultUserPreferences().reports), default_format: e.target.value }
                                    }))}
                                    sx={pillTextFieldSx}
                                    fullWidth
                                >
                                    <MenuItem value="docx">DOCX</MenuItem>
                                    <MenuItem value="pdf">PDF</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    size="small"
                                    type="number"
                                    inputProps={{ min: 0, step: 1 }}
                                    label="Fee proposal reference number"
                                    value={String(userPrefs?.reports?.fee_proposal_reference_seed ?? '1000')}
                                    onChange={(e) => setUserPrefs((p) => ({
                                        ...(p || getDefaultUserPreferences()),
                                        reports: {
                                            ...(p?.reports || getDefaultUserPreferences().reports),
                                            fee_proposal_reference_seed: String(e.target.value || '').replace(/\D/g, '')
                                        }
                                    }))}
                                    sx={pillTextFieldSx}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    size="small"
                                    type="number"
                                    inputProps={{ min: 0, max: 50 }}
                                    label="Max photos (per report)"
                                    value={String(userPrefs?.reports?.max_photos ?? 4)}
                                    onChange={(e) => setUserPrefs((p) => ({
                                        ...(p || getDefaultUserPreferences()),
                                        reports: { ...(p?.reports || getDefaultUserPreferences().reports), max_photos: Number(e.target.value) }
                                    }))}
                                    sx={pillTextFieldSx}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>

                        <FormControlLabel
                            control={<Switch checked={Boolean(userPrefs?.reports?.auto_download)} onChange={(e) => setUserPrefs((p) => ({
                                ...(p || getDefaultUserPreferences()),
                                reports: { ...(p?.reports || getDefaultUserPreferences().reports), auto_download: e.target.checked }
                            }))} />}
                            label="Auto-download after generation (where supported)"
                        />

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveUserPreferences} disabled={userPrefsSaving || userPrefsLoading}>
                                {userPrefsSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadUserPreferences} disabled={userPrefsLoading}>
                                {userPrefsLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'reports' ? (
                <CollapsibleSection
                    id="report_version_naming"
                    title="Report Version Naming"
                    description='Controls whether report revisions are displayed as "Version 1/2/3" or "Revision A/B/C".'
                    defaultOpen={false}
                >
                    <Stack spacing={1.25} sx={{ maxWidth: 560 }}>
                        <TextField
                            select
                            size="small"
                            label="Scheme"
                            value={reportVersionNaming}
                            onChange={(e) => setReportVersionNaming(normalizeReportVersionNaming(e.target.value))}
                            sx={pillTextFieldSx}
                            fullWidth
                        >
                            <MenuItem value="numbered">Numbered (Initial Report “Version 1”)</MenuItem>
                            <MenuItem value="lettered">Lettered (Initial Report “Revision A”)</MenuItem>
                        </TextField>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Applies to report log tables, Information Portal “latest report” labels, and generation notifications.
                        </Typography>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'reports' && isAdmin ? (
                <CollapsibleSection
                    id="report_version_control"
                    title="Report Version Control"
                    description="Controls how many Collabora working report versions are retained in S3. Older working versions are pruned after each new save."
                    defaultOpen={false}
                >
                    <Stack spacing={1.25} sx={{ maxWidth: 560 }}>
                        <TextField
                            select
                            size="small"
                            label="Versions retained per report"
                            value={String(reportVersionControlRetained)}
                            onChange={(e) => setReportVersionControlRetained(normalizeReportVersionControlCount(e.target.value))}
                            sx={pillTextFieldSx}
                            fullWidth
                        >
                            {Array.from({ length: 10 }, (_, idx) => idx + 1).map((n) => (
                                <MenuItem key={n} value={String(n)}>{n}</MenuItem>
                            ))}
                        </TextField>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Default is 5. For example, if a report reaches Version 25 and this is set to 5, Versions 21-25 are retained and Version 20 is deleted from S3.
                        </Typography>
                        {reportVersionControlError ? <Alert severity="error">{reportVersionControlError}</Alert> : null}
                        {reportVersionControlMessage ? <Alert severity="success">{reportVersionControlMessage}</Alert> : null}
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button
                                variant="contained"
                                onClick={saveReportVersionControlSettings}
                                disabled={reportVersionControlSaving || reportVersionControlLoading || reportVersionControlRetained === reportVersionControlBaseline}
                            >
                                {reportVersionControlSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadReportVersionControlSettings} disabled={reportVersionControlLoading || reportVersionControlSaving}>
                                {reportVersionControlLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'reports' ? (
                <CollapsibleSection
                    id="report_appendices"
                    title="Report Appendices"
                    description="Define appendix document options shown during report generation."
                    defaultOpen={false}
                >
                    <div style={{ display: 'grid', gap: 10, maxWidth: 820 }}>
                        <TextField
                            label="Appendix document options (one per line)"
                            multiline
                            minRows={4}
                            value={appendixDocsText}
                            onChange={(e) => setAppendixDocsText(e.target.value)}
                            sx={pillTextFieldSx}
                        />
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <Button variant="contained" onClick={saveAppendixDocs}>
                                Save Appendix Options
                            </Button>
                        </div>
                    </div>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'ai-health' ? (
                <SettingsGroupHeader
                    title="AI & Health"
                    description="AI assist controls, report consistency tuning, and PHI weighting settings."
                />
            ) : null}

            {activeSettingsCategory === 'ai-health' ? (
                <CollapsibleSection
                    id="ai_preferences"
                    title="AI Preferences"
                    description="Personal controls for AI assist features."
                    defaultOpen={false}
                >
                    <Stack spacing={1.25} sx={{ mt: 1.5, maxWidth: 900 }}>
                        <FormControlLabel
                            control={<Switch checked={Boolean(userPrefs?.ai?.enabled)} onChange={(e) => setUserPrefs((p) => ({
                                ...(p || getDefaultUserPreferences()),
                                ai: { ...(p?.ai || getDefaultUserPreferences().ai), enabled: e.target.checked }
                            }))} />}
                            label="Enable AI features"
                        />
                        <FormControlLabel
                            control={<Switch checked={Boolean(userPrefs?.ai?.show_metadata)} onChange={(e) => setUserPrefs((p) => ({
                                ...(p || getDefaultUserPreferences()),
                                ai: { ...(p?.ai || getDefaultUserPreferences().ai), show_metadata: e.target.checked }
                            }))} />}
                            label="Show AI metadata (debug info)"
                        />

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveUserPreferences} disabled={userPrefsSaving || userPrefsLoading}>
                                {userPrefsSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadUserPreferences} disabled={userPrefsLoading}>
                                {userPrefsLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'ai-health' ? (
                <CollapsibleSection
                    id="ai_report_consistency"
                    title="AI Report Consistency Checker"
                    description="Adds a second AI pass after report generation to enforce MonSurv report style (UK English, first-person plural surveyor voice: “We note…”, “We observed…”, “We were advised by the Borrower…”), and to reduce tone/style drift."
                    defaultOpen={false}
                >
                    <div style={{ display: 'grid', gap: 10, maxWidth: 820 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                                type="checkbox"
                                checked={Boolean(aiConsistency.enabled)}
                                onChange={(e) => setAiConsistency((s) => ({ ...s, enabled: e.target.checked }))}
                            />
                            <span>Enable consistency checker</span>
                        </label>

                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                                type="checkbox"
                                checked={Boolean(aiConsistency.auto_fix)}
                                onChange={(e) => setAiConsistency((s) => ({ ...s, auto_fix: e.target.checked }))}
                                disabled={!aiConsistency.enabled}
                            />
                            <span>Auto-fix (rewrite to match MonSurv style)</span>
                        </label>

                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                                type="checkbox"
                                checked={Boolean(aiConsistency.apply_to_non_ai)}
                                onChange={(e) => setAiConsistency((s) => ({ ...s, apply_to_non_ai: e.target.checked }))}
                                disabled={!aiConsistency.enabled}
                            />
                            <span>Apply to non-AI interim fallback text</span>
                        </label>

                        <div style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 5, background: '#fff' }}>
                            <Slider
                                label="Strictness"
                                value={clamp0to100(aiConsistency.strictness, 70)}
                                onChange={(v) => setAiConsistency((s) => ({ ...s, strictness: v }))}
                                hint="Higher = flags and corrects more aggressively."
                            />
                            <Slider
                                label="Rewrite Strength"
                                value={clamp0to100(aiConsistency.rewrite_strength, 55)}
                                onChange={(v) => setAiConsistency((s) => ({ ...s, rewrite_strength: v }))}
                                hint="Higher = heavier rewrites; lower = minimal touch edits."
                            />

                            <div style={{ marginTop: 10, fontSize: 13, fontWeight: 650 }}>Weighting</div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                                Controls which types of issues matter most when scoring and rewriting.
                            </div>
                            <Slider
                                label={'Voice ("We…" surveyor style)'}
                                value={clamp0to100(aiConsistency.weights?.voice, 90)}
                                onChange={(v) => setAiConsistency((s) => ({ ...s, weights: { ...(s.weights || {}), voice: v } }))}
                                hint="Enforces first-person plural and avoids borrower/author confusion."
                            />
                            <Slider
                                label="Tone (formal, neutral, lender-facing)"
                                value={clamp0to100(aiConsistency.weights?.tone, 75)}
                                onChange={(v) => setAiConsistency((s) => ({ ...s, weights: { ...(s.weights || {}), tone: v } }))}
                            />
                            <Slider
                                label="Structure (numbered paragraphs/format)"
                                value={clamp0to100(aiConsistency.weights?.structure, 70)}
                                onChange={(v) => setAiConsistency((s) => ({ ...s, weights: { ...(s.weights || {}), structure: v } }))}
                                hint="Keeps 1.0/1.1 style numbering consistent across sections."
                            />
                            <Slider
                                label="Clarity (tight, technical prose)"
                                value={clamp0to100(aiConsistency.weights?.clarity, 60)}
                                onChange={(v) => setAiConsistency((s) => ({ ...s, weights: { ...(s.weights || {}), clarity: v } }))}
                            />
                            <Slider
                                label="Repetition (remove duplicated phrasing)"
                                value={clamp0to100(aiConsistency.weights?.repetition, 40)}
                                onChange={(v) => setAiConsistency((s) => ({ ...s, weights: { ...(s.weights || {}), repetition: v } }))}
                            />
                        </div>
                    </div>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'ai-health' ? (
                <CollapsibleSection
                    id="phi"
                    title="Project Health Index (PHI)"
                    description="Controls which signals are used to calculate the PHI score shown next to each project on the Projects list."
                    defaultOpen={false}
                >
                    <div style={{ display: 'grid', gap: 10, maxWidth: 820 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                                type="checkbox"
                                checked={Boolean(phiConfig.enabled)}
                                onChange={(e) => setPhiConfig((s) => ({ ...s, enabled: e.target.checked }))}
                            />
                            <span>Enable PHI score on the Projects list</span>
                        </label>

                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                                type="checkbox"
                                checked={Boolean(phiConfig.use_ai)}
                                onChange={(e) => setPhiConfig((s) => ({ ...s, use_ai: e.target.checked }))}
                                disabled={!phiConfig.enabled}
                            />
                            <span>Use AI to refine the PHI score (falls back to heuristic if unavailable)</span>
                        </label>

                        <div style={{ padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 5, background: '#fff' }}>
                            <div style={{ fontSize: 13, fontWeight: 650, marginBottom: 6 }}>Include and weight signals</div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                                Toggle each signal and set weighting per report type. 100% = default behavior.
                            </div>

                            <Box
                                sx={{
                                    mt: 1.25,
                                    display: 'grid',
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: isPhone ? '1fr 76px 76px' : 'minmax(260px, 1fr) minmax(240px, 280px) minmax(240px, 280px)',
                                        gap: 1,
                                        alignItems: 'center',
                                        pb: 0.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider'
                                    }}
                                >
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
                                        Signal
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textAlign: 'right' }}>
                                        Initial
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textAlign: 'right' }}>
                                        Interim
                                    </Typography>
                                </Box>

                                {phiFactorRows.map((row) => {
                                    const enabled = Boolean(phiConfig?.factors?.[row.key]);
                                    const disabled = !phiConfig.enabled;
                                    const initialVal = clamp0to200(phiConfig?.report_instances?.initial?.weights?.[row.key], 100);
                                    const interimVal = clamp0to200(phiConfig?.report_instances?.interim?.weights?.[row.key], 100);

                                    return (
                                        <Box
                                            key={row.key}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: isPhone ? '1fr 76px 76px' : 'minmax(260px, 1fr) minmax(240px, 280px) minmax(240px, 280px)',
                                                gap: 1,
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={enabled}
                                                    onChange={(e) => setPhiConfig((s) => ({
                                                        ...s,
                                                        factors: { ...(s.factors || {}), [row.key]: e.target.checked }
                                                    }))}
                                                    disabled={disabled}
                                                />
                                                <Typography
                                                    sx={{ fontSize: 13, minWidth: 0 }}
                                                    noWrap
                                                    title={row.label}
                                                >
                                                    {row.label}
                                                </Typography>
                                            </Box>

                                            <InlineWeightControl
                                                value={initialVal}
                                                disabled={disabled || !enabled}
                                                labelForA11y={`${row.label} initial weighting`}
                                                onCommit={(v) => setPhiConfig((s) => ({
                                                    ...s,
                                                    report_instances: {
                                                        ...(s.report_instances || {}),
                                                        initial: {
                                                            ...((s.report_instances || {}).initial || {}),
                                                            weights: { ...(((s.report_instances || {}).initial || {}).weights || {}), [row.key]: v }
                                                        }
                                                    }
                                                }))}
                                            />

                                            <InlineWeightControl
                                                value={interimVal}
                                                disabled={disabled || !enabled}
                                                labelForA11y={`${row.label} interim weighting`}
                                                onCommit={(v) => setPhiConfig((s) => ({
                                                    ...s,
                                                    report_instances: {
                                                        ...(s.report_instances || {}),
                                                        interim: {
                                                            ...((s.report_instances || {}).interim || {}),
                                                            weights: { ...(((s.report_instances || {}).interim || {}).weights || {}), [row.key]: v }
                                                        }
                                                    }
                                                }))}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        </div>

                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                            Changes apply immediately to new PHI requests. Existing cached values refresh on expiry.
                        </div>
                    </div>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'communications' ? (
                <SettingsGroupHeader
                    title="Communications"
                    description="Voice preferences and authorised communication settings."
                />
            ) : null}

            {['admin', 'surveyor'].includes(authRole) && activeSettingsCategory === 'communications' ? (
                <CollapsibleSection id="monsurv_voice" title="Monsurv Voice" defaultOpen={false}>
                    <MonsurvVoiceSettings />
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'communications' ? (
                <CollapsibleSection
                    id="smtp"
                    title="SMTP Email"
                    description="Configure outbound email for invites and portal initiation."
                    defaultOpen={false}
                    statusItems={smtpStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {smtpError ? <Alert severity="error">{smtpError}</Alert> : null}
                        {smtpMessage ? <Alert severity="success">{smtpMessage}</Alert> : null}
                        {smtpTestResult ? <Alert severity="info">{smtpTestResult}</Alert> : null}

                        {smtpSource ? (
                            <Alert severity={smtpSource === 'db' ? 'success' : 'warning'}>
                                SMTP source: {smtpSource === 'db' ? 'Database (configured in-app)' : 'Environment fallback'}
                            </Alert>
                        ) : null}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(smtp.enabled)}
                                    onChange={(e) => setSmtp((s) => ({ ...s, enabled: e.target.checked }))}
                                    disabled={smtpLoading}
                                />
                            }
                            label="Enable SMTP sending"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(smtp.secure)}
                                    onChange={(e) => setSmtp((s) => ({ ...s, secure: e.target.checked }))}
                                    disabled={smtpLoading}
                                />
                            }
                            label="Secure (TLS/SSL, usually port 465)"
                        />

                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Host"
                                    value={smtp.host}
                                    onChange={(e) => setSmtp((s) => ({ ...s, host: e.target.value }))}
                                    disabled={smtpLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Port"
                                    value={smtp.port}
                                    onChange={(e) => setSmtp((s) => ({ ...s, port: e.target.value }))}
                                    disabled={smtpLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="User"
                                    value={smtp.user}
                                    onChange={(e) => setSmtp((s) => ({ ...s, user: e.target.value }))}
                                    disabled={smtpLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="From"
                                    value={smtp.from}
                                    onChange={(e) => setSmtp((s) => ({ ...s, from: e.target.value }))}
                                    disabled={smtpLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Reply-To (optional)"
                                    value={smtp.reply_to}
                                    onChange={(e) => setSmtp((s) => ({ ...s, reply_to: e.target.value }))}
                                    disabled={smtpLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Password"
                                    type="password"
                                    value={smtpPassword}
                                    onChange={(e) => setSmtpPassword(e.target.value)}
                                    placeholder={smtp.has_password ? '(stored — leave blank to keep unchanged)' : '(required when enabled)'}
                                    disabled={smtpLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={smtpClearPassword}
                                            onChange={(e) => setSmtpClearPassword(e.target.checked)}
                                            disabled={smtpLoading}
                                        />
                                    }
                                    label="Clear stored password"
                                    sx={{ mt: 0.5 }}
                                />
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                            <Button variant="contained" onClick={saveSmtpSettings} disabled={smtpSaving || smtpLoading}>
                                {smtpSaving ? 'Saving…' : 'Save SMTP Settings'}
                            </Button>
                            <Button variant="outlined" onClick={loadSmtpSettings} disabled={smtpLoading}>
                                {smtpLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap" alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <TextField
                                label="Test email"
                                value={smtpTestTo}
                                onChange={(e) => setSmtpTestTo(e.target.value)}
                                placeholder="test@domain.com"
                                sx={pillTextFieldSx}
                                size="small"
                                fullWidth
                            />
                            <Button variant="outlined" onClick={sendSmtpTestEmail} disabled={smtpLoading}>
                                Send Test Email
                            </Button>
                        </Stack>

                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Note: password is stored encrypted in the database. Set a stable `EMAIL_SETTINGS_SECRET` on the backend (preferred) so SMTP stays configurable in-app across deployments.
                        </Typography>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'communications' ? (
                <CollapsibleSection
                    id="action_notifications"
                    title="Action Notification System"
                    description="Email each active user their overdue and next-seven-day actions once a week."
                    defaultOpen={false}
                    statusItems={actionNotificationsStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {actionNotificationsError ? <Alert severity="error">{actionNotificationsError}</Alert> : null}
                        {actionNotificationsMessage ? <Alert severity="success">{actionNotificationsMessage}</Alert> : null}

                        <FormControlLabel
                            control={(
                                <Switch
                                    checked={Boolean(actionNotifications.enabled)}
                                    onChange={(event) => setActionNotifications((state) => ({ ...state, enabled: event.target.checked }))}
                                    disabled={actionNotificationsLoading}
                                />
                            )}
                            label="Enable weekly action emails"
                        />

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    select
                                    size="small"
                                    label="Send day"
                                    value={actionNotifications.weekday}
                                    onChange={(event) => setActionNotifications((state) => ({ ...state, weekday: event.target.value }))}
                                    disabled={actionNotificationsLoading}
                                    fullWidth
                                    sx={pillTextFieldSx}
                                >
                                    <MenuItem value="mon">Monday</MenuItem>
                                    <MenuItem value="tue">Tuesday</MenuItem>
                                    <MenuItem value="wed">Wednesday</MenuItem>
                                    <MenuItem value="thu">Thursday</MenuItem>
                                    <MenuItem value="fri">Friday</MenuItem>
                                    <MenuItem value="sat">Saturday</MenuItem>
                                    <MenuItem value="sun">Sunday</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    size="small"
                                    label="Send time"
                                    type="time"
                                    value={actionNotifications.time_local}
                                    onChange={(event) => setActionNotifications((state) => ({ ...state, time_local: event.target.value }))}
                                    disabled={actionNotificationsLoading}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    fullWidth
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    select
                                    size="small"
                                    label="Timezone"
                                    value={actionNotifications.timezone}
                                    onChange={(event) => setActionNotifications((state) => ({ ...state, timezone: event.target.value }))}
                                    disabled={actionNotificationsLoading}
                                    fullWidth
                                    sx={pillTextFieldSx}
                                >
                                    <MenuItem value="Europe/London">Europe/London</MenuItem>
                                    <MenuItem value="Europe/Dublin">Europe/Dublin</MenuItem>
                                    <MenuItem value="UTC">UTC</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>

                        <Alert severity="info">
                            Each email includes outstanding actions that are overdue or due within seven days. Completed, dismissed, and undated actions are excluded. Recipients come from active organisation accounts.
                        </Alert>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveActionNotificationSettings} disabled={actionNotificationsSaving || actionNotificationsLoading}>
                                {actionNotificationsSaving ? 'Saving…' : 'Save settings'}
                            </Button>
                            <Button variant="outlined" onClick={loadActionNotificationSettings} disabled={actionNotificationsLoading}>
                                {actionNotificationsLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                            <Button variant="outlined" onClick={previewActionNotifications} disabled={actionNotificationsPreviewing || actionNotificationsLoading}>
                                {actionNotificationsPreviewing ? 'Previewing…' : 'Preview weekly run'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'branding' ? (
                <SettingsGroupHeader
                    title="Branding"
                    description="Organisation identity, colour, and office-map presentation."
                />
            ) : null}

            {isAdmin && activeSettingsCategory === 'branding' ? (
                <CollapsibleSection
                    id="org_profile"
                    title="Organisation Profile"
                    description="Branding and default report header/footer text."
                    defaultOpen={false}
                    statusItems={orgProfileStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {orgProfileError ? <Alert severity="error">{orgProfileError}</Alert> : null}
                        {orgProfileMessage ? <Alert severity="success">{orgProfileMessage}</Alert> : null}

                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Organisation name"
                                    value={orgProfile.organisation_name}
                                    onChange={(e) => setOrgProfile((s) => ({ ...s, organisation_name: e.target.value }))}
                                    disabled={orgProfileLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <ColorSettingField
                                    label="Portal brand colour (CSS)"
                                    value={orgProfile.portal_brand_color}
                                    onChange={(nextValue) => setOrgProfile((s) => ({ ...s, portal_brand_color: nextValue }))}
                                    disabled={orgProfileLoading}
                                    sx={pillTextFieldSx}
                                    fallbackColor="#1d4ed8"
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    label="Portal logo URL"
                                    value={orgProfile.portal_logo_url}
                                    onChange={(e) => setOrgProfile((s) => ({ ...s, portal_logo_url: e.target.value }))}
                                    disabled={orgProfileLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    label="Report header text"
                                    value={orgProfile.report_header_text}
                                    onChange={(e) => setOrgProfile((s) => ({ ...s, report_header_text: e.target.value }))}
                                    disabled={orgProfileLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                    multiline
                                    minRows={2}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    label="Report footer text"
                                    value={orgProfile.report_footer_text}
                                    onChange={(e) => setOrgProfile((s) => ({ ...s, report_footer_text: e.target.value }))}
                                    disabled={orgProfileLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                    multiline
                                    minRows={2}
                                />
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveOrgProfileSettings} disabled={orgProfileSaving || orgProfileLoading}>
                                {orgProfileSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadOrgProfileSettings} disabled={orgProfileLoading}>
                                {orgProfileLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'branding' ? (
                <CollapsibleSection
                    id="office_map"
                    title="Office Map Marker"
                    description="Configure one or more offices for map pins and route directions."
                    defaultOpen={false}
                    statusItems={officeMapStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {officeMapError ? <Alert severity="error">{officeMapError}</Alert> : null}
                        {officeMapMessage ? <Alert severity="success">{officeMapMessage}</Alert> : null}

                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 7 }}>
                                <TextField
                                    select
                                    label="Default route office"
                                    value={officeMap.primary_office_id || ''}
                                    onChange={(e) => setPrimaryOfficeMapOffice(e.target.value)}
                                    disabled={officeMapLoading || !officeMap.offices?.length}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                    helperText="This office is used by default in the project dashboard map until another office is selected there."
                                >
                                    {(Array.isArray(officeMap.offices) ? officeMap.offices : []).map((office, index) => (
                                        <MenuItem key={office.id} value={office.id}>
                                            {office.office_location || `Office ${index + 1}`}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 5 }}>
                                <Stack direction="row" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }} alignItems="center" sx={{ height: '100%' }}>
                                    <Button variant="outlined" onClick={addOfficeMapOffice} disabled={officeMapLoading}>
                                        Add Office
                                    </Button>
                                </Stack>
                            </Grid>
                            <Grid size={12}>
                                <Stack spacing={1.25}>
                                    {(Array.isArray(officeMap.offices) ? officeMap.offices : []).map((office, index) => {
                                        const isPrimary = office.id === officeMap.primary_office_id;
                                        return (
                                            <Box
                                                key={office.id}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 3,
                                                    border: `1px solid ${alpha('#0f172a', 0.08)}`,
                                                    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#f8fbff', 0.82)} 100%)`
                                                }}
                                            >
                                                <Stack spacing={1.25}>
                                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
                                                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                                                {office.office_location || `Office ${index + 1}`}
                                                            </Typography>
                                                            {isPrimary ? <Chip size="small" label="Default" color="primary" /> : null}
                                                        </Stack>
                                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                            {!isPrimary ? (
                                                                <Button size="small" variant="outlined" onClick={() => setPrimaryOfficeMapOffice(office.id)} disabled={officeMapLoading}>
                                                                    Set Default
                                                                </Button>
                                                            ) : null}
                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                variant="outlined"
                                                                onClick={() => removeOfficeMapOffice(office.id)}
                                                                disabled={officeMapLoading || (Array.isArray(officeMap.offices) ? officeMap.offices.length : 0) <= 1}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </Stack>
                                                    </Stack>

                                                    <Grid container spacing={2}>
                                                        <Grid size={{ xs: 12, sm: 6 }}>
                                                            <TextField
                                                                label="Office Location"
                                                                value={office.office_location}
                                                                onChange={(e) => updateOfficeMapOffice(office.id, { office_location: e.target.value })}
                                                                disabled={officeMapLoading}
                                                                fullWidth
                                                                size="small"
                                                                sx={pillTextFieldSx}
                                                                helperText="Used for the marker label if no upload is provided, e.g. Bristol = B"
                                                            />
                                                        </Grid>
                                                        <Grid size={{ xs: 12, sm: 6 }}>
                                                            <ColorSettingField
                                                                label="Pin Colour"
                                                                value={office.pin_color}
                                                                onChange={(nextValue) => updateOfficeMapOffice(office.id, { pin_color: nextValue })}
                                                                disabled={officeMapLoading}
                                                                sx={pillTextFieldSx}
                                                                fallbackColor="#0f3b68"
                                                            />
                                                        </Grid>
                                                        <Grid size={12}>
                                                            <TextField
                                                                label="Address"
                                                                value={office.address}
                                                                onChange={(e) => updateOfficeMapOffice(office.id, { address: e.target.value })}
                                                                disabled={officeMapLoading}
                                                                fullWidth
                                                                size="small"
                                                                sx={pillTextFieldSx}
                                                                helperText="Used to place the pin and as a route origin option on the project dashboard"
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Stack>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Grid>
                            <Grid size={12}>
                                <Stack spacing={1}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Brand upload
                                    </Typography>
                                    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/svg+xml"
                                            disabled={officeMapUploading}
                                            onChange={(e) => {
                                                const file = e.target.files && e.target.files[0];
                                                if (file) uploadOfficeMapBrand(file);
                                                e.target.value = '';
                                            }}
                                        />
                                        {officeMap.brand_upload_url ? (
                                            <Box sx={{ width: 48, height: 48, borderRadius: 1.5, overflow: 'hidden', border: '1px solid rgba(15, 23, 42, 0.12)', background: '#fff' }}>
                                                <img src={officeMap.brand_upload_url} alt="Office pin brand" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                                            </Box>
                                        ) : null}
                                    </Stack>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Upload a branded pin image used across the configured offices. If no upload is provided, the first letter of each office location is used.
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveOfficeMapSettings} disabled={officeMapSaving || officeMapLoading || officeMapUploading}>
                                {officeMapSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadOfficeMapSettings} disabled={officeMapLoading || officeMapUploading}>
                                {officeMapLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'system-admin' ? (
                <SettingsGroupHeader
                    title="System Admin"
                    description="Advanced platform administration, audits, and retention controls."
                />
            ) : null}

            {isAdmin && activeSettingsCategory === 'system-admin' ? (
                <CollapsibleSection
                    id="uuid_info"
                    title="UUID Information"
                    description="Audit stable UUID identities for accounts and developer portal links."
                    defaultOpen={false}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 980 }}>
                        <Alert severity="info">
                            UUIDs are the stable identifiers used to link accounts to data safely. Email changes should not break project access.
                        </Alert>

                        {uuidInfoError ? <Alert severity="error">{uuidInfoError}</Alert> : null}

                        <Dialog
                            open={uuidDeleteDialogOpen}
                            onClose={closeUuidDeleteDialog}
                            maxWidth="xs"
                            fullWidth
                        >
                            <DialogTitle>
                                Delete dashboard user {uuidDeleteTarget?.label ? `"${uuidDeleteTarget.label}"` : ''}
                            </DialogTitle>
                            <DialogContent>
                                {uuidDeleteError ? <Alert severity="error" sx={{ mb: 2 }}>{uuidDeleteError}</Alert> : null}
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    This will permanently remove the user’s account data from the SQL database. This cannot be undone.
                                </Alert>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    label="Deletion password"
                                    type="password"
                                    value={uuidDeletePassword}
                                    onChange={(e) => setUuidDeletePassword(e.target.value)}
                                    disabled={Boolean(uuidDeleteTarget?.userUuid && uuidDeleteBusy === uuidDeleteTarget.userUuid)}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={closeUuidDeleteDialog}
                                    disabled={Boolean(uuidDeleteTarget?.userUuid && uuidDeleteBusy === uuidDeleteTarget.userUuid)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    color="error"
                                    variant="contained"
                                    onClick={confirmUuidDeleteDialog}
                                    disabled={Boolean(uuidDeleteTarget?.userUuid && uuidDeleteBusy === uuidDeleteTarget.userUuid)}
                                >
                                    {uuidDeleteTarget?.userUuid && uuidDeleteBusy === uuidDeleteTarget.userUuid ? 'Deleting…' : 'Delete'}
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                            <Button variant="contained" onClick={loadUuidInformation} disabled={uuidInfoLoading}>
                                {uuidInfoLoading ? 'Loading…' : 'Refresh UUID List'}
                            </Button>
                        </Stack>

                        {uuidInfo ? (
                            <Stack spacing={2}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                                        Team members (dashboard users) ({Array.isArray(uuidInfo?.dashboard_users) ? uuidInfo.dashboard_users.length : 0})
                                    </Typography>
                                    <TableContainer sx={{ maxHeight: 360, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                        <Table size="small" stickyHeader aria-label="Dashboard user UUIDs">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 900 }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 900 }}>Username</TableCell>
                                                    <TableCell sx={{ fontWeight: 900 }}>Role</TableCell>
                                                    <TableCell sx={{ fontWeight: 900 }}>Email</TableCell>
                                                    <TableCell sx={{ fontWeight: 900 }}>UUID</TableCell>
                                                    <TableCell sx={{ fontWeight: 900, width: 120 }}>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(Array.isArray(uuidInfo?.dashboard_users) ? uuidInfo.dashboard_users : []).map((u) => {
                                                    const key = String(u?.user_uuid || u?.id || u?.username || Math.random());
                                                    const userUuid = String(u?.user_uuid || '').trim();
                                                    const deleteDisabled = uuidInfoLoading || uuidDeleteBusy === userUuid;
                                                    return (
                                                        <TableRow key={key} hover>
                                                            <TableCell>{String(u?.display_name || u?.displayName || '—')}</TableCell>
                                                            <TableCell>{String(u?.username || '—')}</TableCell>
                                                            <TableCell>
                                                                <Chip size="small" label={String(u?.role || '—')} />
                                                            </TableCell>
                                                            <TableCell>{String(u?.email || '—')}</TableCell>
                                                            <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontSize: 12 }}>
                                                                {String(u?.user_uuid || '—')}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="error"
                                                                    disabled={deleteDisabled}
                                                                    onClick={() => onDeleteDashboardUser(u)}
                                                                >
                                                                    {uuidDeleteBusy === userUuid ? 'Deleting…' : 'Delete'}
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>

                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                                        Portal developer accounts ({Array.isArray(uuidInfo?.portal_developers) ? uuidInfo.portal_developers.length : 0})
                                    </Typography>
                                    <TableContainer sx={{ maxHeight: 360, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                        <Table size="small" stickyHeader aria-label="Portal developer UUIDs">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 900 }}>Email</TableCell>
                                                    <TableCell sx={{ fontWeight: 900 }}>Developer UUID</TableCell>
                                                    <TableCell sx={{ fontWeight: 900 }}>Projects</TableCell>
                                                    <TableCell sx={{ fontWeight: 900 }}>Links</TableCell>
                                                    <TableCell sx={{ fontWeight: 900 }}>Updated</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(Array.isArray(uuidInfo?.portal_developers) ? uuidInfo.portal_developers : []).map((d) => {
                                                    const key = String(d?.developer_uuid || d?.email || Math.random());
                                                    const projects = Array.isArray(d?.projects) ? d.projects : [];
                                                    return (
                                                        <TableRow key={key} hover>
                                                            <TableCell>{String(d?.email || '—')}</TableCell>
                                                            <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontSize: 12 }}>
                                                                {String(d?.developer_uuid || '—')}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                                                    {projects.length ? projects.slice(0, 12).map((p) => (
                                                                        <Chip key={String(p)} size="small" label={String(p)} />
                                                                    )) : <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>}
                                                                    {projects.length > 12 ? (
                                                                        <Chip size="small" variant="outlined" label={`+${projects.length - 12}`} />
                                                                    ) : null}
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell>{Number(d?.link_rows || 0) || 0}</TableCell>
                                                            <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                                                                {String(d?.updated_at || '—')}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Stack>
                        ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Click “Refresh UUID List” to load current mappings.
                            </Typography>
                        )}
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'system-admin' ? (
                <CollapsibleSection
                    id="retention"
                    title="Data Retention"
                    description="Upload limits and retention periods."
                    defaultOpen={false}
                    statusItems={retentionStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {retentionError ? <Alert severity="error">{retentionError}</Alert> : null}
                        {retentionMessage ? <Alert severity="success">{retentionMessage}</Alert> : null}

                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Uploads retention (days)"
                                    type="number"
                                    inputProps={{ min: 1, max: 3650 }}
                                    value={retention.uploads_retention_days}
                                    onChange={(e) => setRetention((s) => ({ ...s, uploads_retention_days: e.target.value }))}
                                    disabled={retentionLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Notifications retention (days)"
                                    type="number"
                                    inputProps={{ min: 1, max: 3650 }}
                                    value={retention.notifications_retention_days}
                                    onChange={(e) => setRetention((s) => ({ ...s, notifications_retention_days: e.target.value }))}
                                    disabled={retentionLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Activity retention (days)"
                                    helperText="Site-wide Activity page only (project activity is retained until project end)."
                                    type="number"
                                    inputProps={{ min: 1, max: 3650 }}
                                    value={retention.activity_retention_days}
                                    onChange={(e) => setRetention((s) => ({ ...s, activity_retention_days: e.target.value }))}
                                    disabled={retentionLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Max upload (MB)"
                                    type="number"
                                    inputProps={{ min: 1, max: 200 }}
                                    value={retention.max_upload_mb}
                                    onChange={(e) => setRetention((s) => ({ ...s, max_upload_mb: e.target.value }))}
                                    disabled={retentionLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    label="Allowed upload MIME types (one per line)"
                                    value={retention.allowed_upload_types_text}
                                    onChange={(e) => setRetention((s) => ({ ...s, allowed_upload_types_text: e.target.value }))}
                                    disabled={retentionLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                    multiline
                                    minRows={4}
                                />
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveRetentionSettings} disabled={retentionSaving || retentionLoading}>
                                {retentionSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadRetentionSettings} disabled={retentionLoading}>
                                {retentionLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'integrations' ? (
                <SettingsGroupHeader
                    title="Integrations"
                    description="Calendar sharing and external integration points."
                />
            ) : null}

            {activeSettingsCategory === 'integrations' ? (
                <CollapsibleSection id="gmail" title="Gmail inbox" description="Your personal read-only inbox connection." defaultOpen>
                    <GmailIntegrationSettings />
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'integrations' ? (
                <CollapsibleSection
                    id="integrations_admin"
                    title="Integrations (Admin)"
                    description="Organisation-wide webhooks and BI configuration."
                    defaultOpen={false}
                    statusItems={integrationsStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {integrationsError ? <Alert severity="error">{integrationsError}</Alert> : null}
                        {integrationsMessage ? <Alert severity="success">{integrationsMessage}</Alert> : null}

                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Slack webhook URL"
                                    value={integrations.slack_webhook_url}
                                    onChange={(e) => setIntegrations((s) => ({ ...s, slack_webhook_url: e.target.value }))}
                                    disabled={integrationsLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Microsoft Teams webhook URL"
                                    value={integrations.teams_webhook_url}
                                    onChange={(e) => setIntegrations((s) => ({ ...s, teams_webhook_url: e.target.value }))}
                                    disabled={integrationsLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                        </Grid>

                        <FormControlLabel
                            control={<Checkbox checked={Boolean(integrations.powerbi_enabled)} onChange={(e) => setIntegrations((s) => ({ ...s, powerbi_enabled: e.target.checked }))} disabled={integrationsLoading} />}
                            label="Enable Power BI integration"
                        />
                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Power BI Workspace ID"
                                    value={integrations.powerbi_workspace_id}
                                    onChange={(e) => setIntegrations((s) => ({ ...s, powerbi_workspace_id: e.target.value }))}
                                    disabled={integrationsLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Power BI Dataset ID"
                                    value={integrations.powerbi_dataset_id}
                                    onChange={(e) => setIntegrations((s) => ({ ...s, powerbi_dataset_id: e.target.value }))}
                                    disabled={integrationsLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveIntegrationsSettings} disabled={integrationsSaving || integrationsLoading}>
                                {integrationsSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadIntegrationsSettings} disabled={integrationsLoading}>
                                {integrationsLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'security' ? (
                <SettingsGroupHeader
                    title="Security"
                    description="Login protection, policy controls, and safeguards around sensitive actions."
                />
            ) : null}

            {isAdmin && activeSettingsCategory === 'security' ? (
                <CollapsibleSection
                    id="security_policy"
                    title="Security Policy"
                    description="Session timeouts and password policy defaults."
                    defaultOpen={false}
                    statusItems={securityPolicyStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {securityPolicyError ? <Alert severity="error">{securityPolicyError}</Alert> : null}
                        {securityPolicyMessage ? <Alert severity="success">{securityPolicyMessage}</Alert> : null}

                        <FormControlLabel
                            control={<Checkbox checked={Boolean(securityPolicy.require_mfa_for_admins)} onChange={(e) => setSecurityPolicy((s) => ({ ...s, require_mfa_for_admins: e.target.checked }))} disabled={securityPolicyLoading} />}
                            label="Require MFA for admin users (planned enforcement)"
                        />

                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Session timeout (minutes)"
                                    type="number"
                                    inputProps={{ min: 5, max: 1440 }}
                                    value={securityPolicy.session_timeout_minutes}
                                    onChange={(e) => setSecurityPolicy((s) => ({ ...s, session_timeout_minutes: e.target.value }))}
                                    disabled={securityPolicyLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Idle timeout (minutes)"
                                    type="number"
                                    inputProps={{ min: 1, max: 1440 }}
                                    value={securityPolicy.idle_timeout_minutes}
                                    onChange={(e) => setSecurityPolicy((s) => ({ ...s, idle_timeout_minutes: e.target.value }))}
                                    disabled={securityPolicyLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Password min length"
                                    type="number"
                                    inputProps={{ min: 8, max: 64 }}
                                    value={securityPolicy.password_min_length}
                                    onChange={(e) => setSecurityPolicy((s) => ({ ...s, password_min_length: e.target.value }))}
                                    disabled={securityPolicyLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveSecurityPolicySettings} disabled={securityPolicySaving || securityPolicyLoading}>
                                {securityPolicySaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadSecurityPolicySettings} disabled={securityPolicyLoading}>
                                {securityPolicyLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'security' ? (
                <CollapsibleSection
                    id="deletion_guard"
                    title="Deletion Guard"
                    description="Require a separate password for deleting Team Members, Clients, and Projects (GDPR-safe purge)."
                    defaultOpen={false}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {deletionGuardError ? <Alert severity="error">{deletionGuardError}</Alert> : null}
                        {deletionGuardMessage ? <Alert severity="success">{deletionGuardMessage}</Alert> : null}

                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Status: {deletionGuard.configured ? (deletionGuard.enabled ? 'Enabled' : 'Configured (disabled)') : 'Not configured'}
                        </Typography>

                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="New deletion password"
                                    type="password"
                                    value={deletionGuardPassword}
                                    onChange={(e) => setDeletionGuardPassword(e.target.value)}
                                    disabled={deletionGuardLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Confirm deletion password"
                                    type="password"
                                    value={deletionGuardPasswordConfirm}
                                    onChange={(e) => setDeletionGuardPasswordConfirm(e.target.value)}
                                    disabled={deletionGuardLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveDeletionGuardSettings} disabled={deletionGuardSaving || deletionGuardLoading}>
                                {deletionGuardSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadDeletionGuardSettings} disabled={deletionGuardLoading}>
                                {deletionGuardLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'ai-health' ? (
                <CollapsibleSection
                    id="ai_policy"
                    title="AI Policy (Admin)"
                    description="Organisation-wide AI feature controls and limits."
                    defaultOpen={false}
                    statusItems={aiPolicyStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {aiPolicyError ? <Alert severity="error">{aiPolicyError}</Alert> : null}
                        {aiPolicyMessage ? <Alert severity="success">{aiPolicyMessage}</Alert> : null}

                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(aiPolicy.ai_enabled)} onChange={(e) => setAiPolicy((s) => ({ ...s, ai_enabled: e.target.checked }))} disabled={aiPolicyLoading} />}
                                label="Enable AI features"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(aiPolicy.allow_ai_photo_analysis)} onChange={(e) => setAiPolicy((s) => ({ ...s, allow_ai_photo_analysis: e.target.checked }))} disabled={aiPolicyLoading} />}
                                label="Allow AI photo analysis"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(aiPolicy.allow_online_lookup)} onChange={(e) => setAiPolicy((s) => ({ ...s, allow_online_lookup: e.target.checked }))} disabled={aiPolicyLoading} />}
                                label="Allow online lookup"
                            />
                        </FormGroup>

                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Default model (optional)"
                                    value={aiPolicy.default_model}
                                    onChange={(e) => setAiPolicy((s) => ({ ...s, default_model: e.target.value }))}
                                    disabled={aiPolicyLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Temperature cap"
                                    type="number"
                                    inputProps={{ min: 0, max: 2, step: 0.1 }}
                                    value={aiPolicy.temperature_cap}
                                    onChange={(e) => setAiPolicy((s) => ({ ...s, temperature_cap: e.target.value }))}
                                    disabled={aiPolicyLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveAiPolicySettings} disabled={aiPolicySaving || aiPolicyLoading}>
                                {aiPolicySaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadAiPolicySettings} disabled={aiPolicyLoading}>
                                {aiPolicyLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'communications' ? (
                <CollapsibleSection
                    id="email_templates"
                    title="Email Templates"
                    description="Customise outbound email subject/body defaults."
                    defaultOpen={false}
                    statusItems={emailTemplatesStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, width: '100%' }}>
                        {emailTemplatesError ? <Alert severity="error">{emailTemplatesError}</Alert> : null}
                        {emailTemplatesMessage ? <Alert severity="success">{emailTemplatesMessage}</Alert> : null}

                        <Dialog open={Boolean(emailTemplateHelpOpen)} onClose={() => setEmailTemplateHelpOpen('')} maxWidth="sm" fullWidth>
                            <DialogTitle sx={{ pr: 5 }}>
                                Template tags
                            </DialogTitle>
                            <DialogContent dividers>
                                <Stack spacing={1.25}>
                                    <Typography variant="body2" color="text.secondary">
                                        Click a tag to insert it into the last-focused subject/body field.
                                    </Typography>
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {(emailTemplateTagsByGroup?.[emailTemplateHelpOpen] || []).map((t) => (
                                            <Chip
                                                key={t.tag}
                                                label={t.tag}
                                                onClick={() => insertEmailTemplateTag(t.tag)}
                                                size="small"
                                                variant="outlined"
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        ))}
                                    </Stack>

                                    <Divider />

                                    <Stack spacing={0.75}>
                                        {(emailTemplateTagsByGroup?.[emailTemplateHelpOpen] || []).map((t) => (
                                            <Box key={`${t.tag}::desc`}>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {t.tag}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t.label}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Stack>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setEmailTemplateHelpOpen('')} variant="contained">Close</Button>
                            </DialogActions>
                        </Dialog>

                        <Stack spacing={1.75} sx={{ width: '100%' }}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Developer Questionnaire Email
                                    </Typography>
                                    <Tooltip title="Template tags">
                                        <IconButton size="small" onClick={() => setEmailTemplateHelpOpen('developer_questionnaire')}>
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                                    <Box sx={{ width: '100%', flex: 1, minWidth: 0 }}>
                                        <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%' }}>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <TextField
                                                    label="Email Subject"
                                                    value={emailTemplates.developer_questionnaire_subject}
                                                    onChange={(e) => setEmailTemplates((s) => ({ ...s, developer_questionnaire_subject: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    size="medium"
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={2}
                                                    inputRef={(el) => setEmailTemplateFieldRef('developer_questionnaire_subject', el)}
                                                    onFocus={() => setEmailTemplateFocusedField('developer_questionnaire_subject')}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 8 }}>
                                                <TextField
                                                    label="Email Body"
                                                    value={emailTemplates.developer_questionnaire_body}
                                                    onChange={(e) => setEmailTemplates((s) => ({ ...s, developer_questionnaire_body: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    size="medium"
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={12}
                                                    inputRef={(el) => setEmailTemplateFieldRef('developer_questionnaire_body', el)}
                                                    onFocus={() => setEmailTemplateFocusedField('developer_questionnaire_body')}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box
                                        sx={{
                                            width: { xs: '100%', md: 240 },
                                            flexShrink: 0,
                                            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                            borderRadius: 2,
                                            p: 1.25
                                        }}
                                    >
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Insert tag
                                            </Typography>
                                            <Stack spacing={1}>
                                                {(emailTemplateTagsByGroup?.developer_questionnaire || []).slice(0, 5).map((t) => {
                                                    const subjectKey = 'developer_questionnaire_subject';
                                                    const bodyKey = 'developer_questionnaire_body';
                                                    const focused = String(emailTemplateFocusedField || '').trim();
                                                    const targetKey = focused === subjectKey || focused === bodyKey ? focused : bodyKey;
                                                    return (
                                                        <Button
                                                            key={`dq::${t.tag}`}
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => insertEmailTemplateTag(t.tag, targetKey)}
                                                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                                            fullWidth
                                                        >
                                                            {t.tag}
                                                        </Button>
                                                    );
                                                })}
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Delegated Action Email
                                    </Typography>
                                    <Tooltip title="Template tags">
                                        <IconButton size="small" onClick={() => setEmailTemplateHelpOpen('action_delegated')}>
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                                    <Box sx={{ width: '100%', flex: 1, minWidth: 0 }}>
                                        <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%' }}>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <TextField
                                                    label="Email Subject"
                                                    value={emailTemplates.action_delegated_subject}
                                                    onChange={(event) => setEmailTemplates((state) => ({ ...state, action_delegated_subject: event.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={2}
                                                    inputRef={(element) => setEmailTemplateFieldRef('action_delegated_subject', element)}
                                                    onFocus={() => setEmailTemplateFocusedField('action_delegated_subject')}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 8 }}>
                                                <TextField
                                                    label="Email Body"
                                                    value={emailTemplates.action_delegated_body}
                                                    onChange={(event) => setEmailTemplates((state) => ({ ...state, action_delegated_body: event.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={12}
                                                    inputRef={(element) => setEmailTemplateFieldRef('action_delegated_body', element)}
                                                    onFocus={() => setEmailTemplateFocusedField('action_delegated_body')}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Box sx={{ width: { xs: '100%', md: 240 }, flexShrink: 0, border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`, borderRadius: 2, p: 1.25 }}>
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Insert tag</Typography>
                                            {(emailTemplateTagsByGroup?.action_delegated || []).map((templateTag) => {
                                                const subjectKey = 'action_delegated_subject';
                                                const bodyKey = 'action_delegated_body';
                                                const targetKey = [subjectKey, bodyKey].includes(emailTemplateFocusedField) ? emailTemplateFocusedField : bodyKey;
                                                return (
                                                    <Button key={`ad::${templateTag.tag}`} variant="outlined" size="small" onClick={() => insertEmailTemplateTag(templateTag.tag, targetKey)} sx={{ justifyContent: 'flex-start', textTransform: 'none' }} fullWidth>
                                                        {templateTag.tag}
                                                    </Button>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Feasibility Report Issued Email
                                    </Typography>
                                    <Tooltip title="Template tags">
                                        <IconButton size="small" onClick={() => setEmailTemplateHelpOpen('feasibility_report_issued')}>
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                                    <Box sx={{ width: '100%', flex: 1, minWidth: 0 }}>
                                        <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%' }}>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <TextField
                                                    label="Email Subject"
                                                    value={emailTemplates.feasibility_report_issued_subject}
                                                    onChange={(e) => setEmailTemplates((state) => ({ ...state, feasibility_report_issued_subject: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={2}
                                                    inputRef={(element) => setEmailTemplateFieldRef('feasibility_report_issued_subject', element)}
                                                    onFocus={() => setEmailTemplateFocusedField('feasibility_report_issued_subject')}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 8 }}>
                                                <TextField
                                                    label="Email Body"
                                                    value={emailTemplates.feasibility_report_issued_body}
                                                    onChange={(e) => setEmailTemplates((state) => ({ ...state, feasibility_report_issued_body: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={12}
                                                    inputRef={(element) => setEmailTemplateFieldRef('feasibility_report_issued_body', element)}
                                                    onFocus={() => setEmailTemplateFocusedField('feasibility_report_issued_body')}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Box sx={{ width: { xs: '100%', md: 240 }, flexShrink: 0, border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`, borderRadius: 2, p: 1.25 }}>
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Insert tag</Typography>
                                            {(emailTemplateTagsByGroup?.feasibility_report_issued || []).map((templateTag) => {
                                                const subjectKey = 'feasibility_report_issued_subject';
                                                const bodyKey = 'feasibility_report_issued_body';
                                                const targetKey = [subjectKey, bodyKey].includes(emailTemplateFocusedField) ? emailTemplateFocusedField : bodyKey;
                                                return (
                                                    <Button key={`fri::${templateTag.tag}`} variant="outlined" size="small" onClick={() => insertEmailTemplateTag(templateTag.tag, targetKey)} sx={{ justifyContent: 'flex-start', textTransform: 'none' }} fullWidth>
                                                        {templateTag.tag}
                                                    </Button>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Action Report Email
                                    </Typography>
                                    <Tooltip title="Template tags">
                                        <IconButton size="small" onClick={() => setEmailTemplateHelpOpen('action_report')}>
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                                    <Box sx={{ width: '100%', flex: 1, minWidth: 0 }}>
                                        <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%' }}>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <TextField
                                                    label="Email Subject"
                                                    value={emailTemplates.action_report_subject}
                                                    onChange={(event) => setEmailTemplates((state) => ({ ...state, action_report_subject: event.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={2}
                                                    inputRef={(element) => setEmailTemplateFieldRef('action_report_subject', element)}
                                                    onFocus={() => setEmailTemplateFocusedField('action_report_subject')}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 8 }}>
                                                <TextField
                                                    label="Email Body"
                                                    value={emailTemplates.action_report_body}
                                                    onChange={(event) => setEmailTemplates((state) => ({ ...state, action_report_body: event.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={12}
                                                    inputRef={(element) => setEmailTemplateFieldRef('action_report_body', element)}
                                                    onFocus={() => setEmailTemplateFocusedField('action_report_body')}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Box sx={{ width: { xs: '100%', md: 240 }, flexShrink: 0, border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`, borderRadius: 2, p: 1.25 }}>
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Insert tag</Typography>
                                            {(emailTemplateTagsByGroup?.action_report || []).map((templateTag) => {
                                                const subjectKey = 'action_report_subject';
                                                const bodyKey = 'action_report_body';
                                                const targetKey = [subjectKey, bodyKey].includes(emailTemplateFocusedField) ? emailTemplateFocusedField : bodyKey;
                                                return (
                                                    <Button key={`ar::${templateTag.tag}`} variant="outlined" size="small" onClick={() => insertEmailTemplateTag(templateTag.tag, targetKey)} sx={{ justifyContent: 'flex-start', textTransform: 'none' }} fullWidth>
                                                        {templateTag.tag}
                                                    </Button>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        New Team Member Email
                                    </Typography>
                                    <Tooltip title="Template tags">
                                        <IconButton size="small" onClick={() => setEmailTemplateHelpOpen('new_team_member')}>
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                                    <Box sx={{ width: '100%', flex: 1, minWidth: 0 }}>
                                        <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%' }}>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <TextField
                                                    label="Email Subject"
                                                    value={emailTemplates.invite_subject}
                                                    onChange={(e) => setEmailTemplates((s) => ({ ...s, invite_subject: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    size="medium"
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={2}
                                                    inputRef={(el) => setEmailTemplateFieldRef('invite_subject', el)}
                                                    onFocus={() => setEmailTemplateFocusedField('invite_subject')}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 8 }}>
                                                <TextField
                                                    label="Email Body"
                                                    value={emailTemplates.invite_body}
                                                    onChange={(e) => setEmailTemplates((s) => ({ ...s, invite_body: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    size="medium"
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={12}
                                                    inputRef={(el) => setEmailTemplateFieldRef('invite_body', el)}
                                                    onFocus={() => setEmailTemplateFocusedField('invite_body')}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box
                                        sx={{
                                            width: { xs: '100%', md: 240 },
                                            flexShrink: 0,
                                            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                            borderRadius: 2,
                                            p: 1.25
                                        }}
                                    >
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Insert tag
                                            </Typography>
                                            <Stack spacing={1}>
                                                {(emailTemplateTagsByGroup?.new_team_member || []).slice(0, 5).map((t) => {
                                                    const subjectKey = 'invite_subject';
                                                    const bodyKey = 'invite_body';
                                                    const focused = String(emailTemplateFocusedField || '').trim();
                                                    const targetKey = focused === subjectKey || focused === bodyKey ? focused : bodyKey;
                                                    return (
                                                        <Button
                                                            key={`invite::${t.tag}`}
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => insertEmailTemplateTag(t.tag, targetKey)}
                                                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                                            fullWidth
                                                        >
                                                            {t.tag}
                                                        </Button>
                                                    );
                                                })}
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Broker Welcome Email
                                    </Typography>
                                    <Tooltip title="Template tags">
                                        <IconButton size="small" onClick={() => setEmailTemplateHelpOpen('new_client_access')}>
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                                    <Box sx={{ width: '100%', flex: 1, minWidth: 0 }}>
                                        <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%' }}>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <TextField
                                                    label="Email Subject"
                                                    value={emailTemplates.new_client_access_subject}
                                                    onChange={(e) => setEmailTemplates((s) => ({ ...s, new_client_access_subject: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    size="medium"
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={2}
                                                    inputRef={(el) => setEmailTemplateFieldRef('new_client_access_subject', el)}
                                                    onFocus={() => setEmailTemplateFocusedField('new_client_access_subject')}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 8 }}>
                                                <TextField
                                                    label="Email Body"
                                                    value={emailTemplates.new_client_access_body}
                                                    onChange={(e) => setEmailTemplates((s) => ({ ...s, new_client_access_body: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    size="medium"
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={12}
                                                    inputRef={(el) => setEmailTemplateFieldRef('new_client_access_body', el)}
                                                    onFocus={() => setEmailTemplateFocusedField('new_client_access_body')}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box
                                        sx={{
                                            width: { xs: '100%', md: 240 },
                                            flexShrink: 0,
                                            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                            borderRadius: 2,
                                            p: 1.25
                                        }}
                                    >
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Insert tag
                                            </Typography>
                                            <Stack spacing={1}>
                                                {(emailTemplateTagsByGroup?.new_client_access || []).slice(0, 6).map((t) => {
                                                    const subjectKey = 'new_client_access_subject';
                                                    const bodyKey = 'new_client_access_body';
                                                    const focused = String(emailTemplateFocusedField || '').trim();
                                                    const targetKey = focused === subjectKey || focused === bodyKey ? focused : bodyKey;
                                                    return (
                                                        <Button
                                                            key={`nca::${t.tag}`}
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => insertEmailTemplateTag(t.tag, targetKey)}
                                                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                                            fullWidth
                                                        >
                                                            {t.tag}
                                                        </Button>
                                                    );
                                                })}
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Information Outstanding Email
                                    </Typography>
                                    <Tooltip title="Template tags">
                                        <IconButton size="small" onClick={() => setEmailTemplateHelpOpen('information_outstanding')}>
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                                    <Box sx={{ width: '100%', flex: 1, minWidth: 0 }}>
                                        <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%' }}>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <TextField
                                                    label="Email Subject"
                                                    value={emailTemplates.information_outstanding_subject}
                                                    onChange={(e) => setEmailTemplates((s) => ({ ...s, information_outstanding_subject: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    size="medium"
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={2}
                                                    inputRef={(el) => setEmailTemplateFieldRef('information_outstanding_subject', el)}
                                                    onFocus={() => setEmailTemplateFocusedField('information_outstanding_subject')}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 8 }}>
                                                <TextField
                                                    label="Email Body"
                                                    value={emailTemplates.information_outstanding_body}
                                                    onChange={(e) => setEmailTemplates((s) => ({ ...s, information_outstanding_body: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    size="medium"
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={12}
                                                    inputRef={(el) => setEmailTemplateFieldRef('information_outstanding_body', el)}
                                                    onFocus={() => setEmailTemplateFocusedField('information_outstanding_body')}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box
                                        sx={{
                                            width: { xs: '100%', md: 240 },
                                            flexShrink: 0,
                                            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                            borderRadius: 2,
                                            p: 1.25
                                        }}
                                    >
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Insert tag
                                            </Typography>
                                            <Stack spacing={1}>
                                                {(emailTemplateTagsByGroup?.information_outstanding || []).slice(0, 5).map((t) => {
                                                    const subjectKey = 'information_outstanding_subject';
                                                    const bodyKey = 'information_outstanding_body';
                                                    const focused = String(emailTemplateFocusedField || '').trim();
                                                    const targetKey = focused === subjectKey || focused === bodyKey ? focused : bodyKey;
                                                    return (
                                                        <Button
                                                            key={`io::${t.tag}`}
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => insertEmailTemplateTag(t.tag, targetKey)}
                                                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                                            fullWidth
                                                        >
                                                            {t.tag}
                                                        </Button>
                                                    );
                                                })}
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Insurance Expiry Email
                                    </Typography>
                                    <Tooltip title="Template tags">
                                        <IconButton size="small" onClick={() => setEmailTemplateHelpOpen('insurance_expiry')}>
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                                    <Box sx={{ width: '100%', flex: 1, minWidth: 0 }}>
                                        <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%' }}>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <TextField
                                                    label="Email Subject"
                                                    value={emailTemplates.insurance_expiry_subject}
                                                    onChange={(e) => setEmailTemplates((s) => ({ ...s, insurance_expiry_subject: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    size="medium"
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={2}
                                                    inputRef={(el) => setEmailTemplateFieldRef('insurance_expiry_subject', el)}
                                                    onFocus={() => setEmailTemplateFocusedField('insurance_expiry_subject')}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 8 }}>
                                                <TextField
                                                    label="Email Body"
                                                    value={emailTemplates.insurance_expiry_body}
                                                    onChange={(e) => setEmailTemplates((s) => ({ ...s, insurance_expiry_body: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    size="medium"
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={12}
                                                    inputRef={(el) => setEmailTemplateFieldRef('insurance_expiry_body', el)}
                                                    onFocus={() => setEmailTemplateFocusedField('insurance_expiry_body')}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box
                                        sx={{
                                            width: { xs: '100%', md: 240 },
                                            flexShrink: 0,
                                            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                            borderRadius: 2,
                                            p: 1.25
                                        }}
                                    >
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Insert tag
                                            </Typography>
                                            <Stack spacing={1}>
                                                {(emailTemplateTagsByGroup?.insurance_expiry || []).slice(0, 5).map((t) => {
                                                    const subjectKey = 'insurance_expiry_subject';
                                                    const bodyKey = 'insurance_expiry_body';
                                                    const focused = String(emailTemplateFocusedField || '').trim();
                                                    const targetKey = focused === subjectKey || focused === bodyKey ? focused : bodyKey;
                                                    return (
                                                        <Button
                                                            key={`ins::${t.tag}`}
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => insertEmailTemplateTag(t.tag, targetKey)}
                                                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                                            fullWidth
                                                        >
                                                            {t.tag}
                                                        </Button>
                                                    );
                                                })}
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Valuation Reminder Email
                                    </Typography>
                                    <Tooltip title="Template tags">
                                        <IconButton size="small" onClick={() => setEmailTemplateHelpOpen('valuation_reminder')}>
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                                    <Box sx={{ width: '100%', flex: 1, minWidth: 0 }}>
                                        <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%' }}>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <TextField
                                                    label="Email Subject"
                                                    value={emailTemplates.valuation_reminder_subject}
                                                    onChange={(e) => setEmailTemplates((s) => ({ ...s, valuation_reminder_subject: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    size="medium"
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={2}
                                                    inputRef={(el) => setEmailTemplateFieldRef('valuation_reminder_subject', el)}
                                                    onFocus={() => setEmailTemplateFocusedField('valuation_reminder_subject')}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 8 }}>
                                                <TextField
                                                    label="Email Body"
                                                    value={emailTemplates.valuation_reminder_body}
                                                    onChange={(e) => setEmailTemplates((s) => ({ ...s, valuation_reminder_body: e.target.value }))}
                                                    disabled={emailTemplatesLoading}
                                                    fullWidth
                                                    size="medium"
                                                    sx={emailTemplateTextFieldSx}
                                                    multiline
                                                    rows={12}
                                                    inputRef={(el) => setEmailTemplateFieldRef('valuation_reminder_body', el)}
                                                    onFocus={() => setEmailTemplateFocusedField('valuation_reminder_body')}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box
                                        sx={{
                                            width: { xs: '100%', md: 240 },
                                            flexShrink: 0,
                                            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                            borderRadius: 2,
                                            p: 1.25
                                        }}
                                    >
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Insert tag
                                            </Typography>
                                            <Stack spacing={1}>
                                                {(emailTemplateTagsByGroup?.valuation_reminder || []).slice(0, 5).map((t) => {
                                                    const subjectKey = 'valuation_reminder_subject';
                                                    const bodyKey = 'valuation_reminder_body';
                                                    const focused = String(emailTemplateFocusedField || '').trim();
                                                    const targetKey = focused === subjectKey || focused === bodyKey ? focused : bodyKey;
                                                    return (
                                                        <Button
                                                            key={`val::${t.tag}`}
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => insertEmailTemplateTag(t.tag, targetKey)}
                                                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                                            fullWidth
                                                        >
                                                            {t.tag}
                                                        </Button>
                                                    );
                                                })}
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>

                            <Accordion
                                disableGutters
                                elevation={0}
                                sx={{
                                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                    borderRadius: 2,
                                    '&:before': { display: 'none' },
                                    overflow: 'hidden'
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        minHeight: 44,
                                        '& .MuiAccordionSummary-content': { my: 0.5 },
                                        '& .MuiAccordionSummary-expandIconWrapper': { color: 'text.secondary' }
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Other Templates
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                                    <Stack spacing={1.5}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                                                Report Ready Email
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                                                <Box sx={{ width: '100%', flex: 1, minWidth: 0 }}>
                                                    <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%' }}>
                                                        <Grid size={{ xs: 12, md: 4 }}>
                                                            <TextField
                                                                label="Email Subject"
                                                                value={emailTemplates.report_ready_subject}
                                                                onChange={(e) => setEmailTemplates((s) => ({ ...s, report_ready_subject: e.target.value }))}
                                                                disabled={emailTemplatesLoading}
                                                                fullWidth
                                                                size="medium"
                                                                sx={emailTemplateTextFieldSx}
                                                                multiline
                                                                rows={2}
                                                                inputRef={(el) => setEmailTemplateFieldRef('report_ready_subject', el)}
                                                                onFocus={() => setEmailTemplateFocusedField('report_ready_subject')}
                                                            />
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 8 }}>
                                                            <TextField
                                                                label="Email Body"
                                                                value={emailTemplates.report_ready_body}
                                                                onChange={(e) => setEmailTemplates((s) => ({ ...s, report_ready_body: e.target.value }))}
                                                                disabled={emailTemplatesLoading}
                                                                fullWidth
                                                                size="medium"
                                                                sx={emailTemplateTextFieldSx}
                                                                multiline
                                                                rows={12}
                                                                inputRef={(el) => setEmailTemplateFieldRef('report_ready_body', el)}
                                                                onFocus={() => setEmailTemplateFocusedField('report_ready_body')}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        width: { xs: '100%', md: 240 },
                                                        flexShrink: 0,
                                                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                                        borderRadius: 2,
                                                        p: 1.25
                                                    }}
                                                >
                                                    <Stack spacing={1}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                            Insert tag
                                                        </Typography>
                                                        <Stack spacing={1}>
                                                            {(emailTemplateTagsByGroup?.report_ready || []).slice(0, 5).map((t) => {
                                                                const subjectKey = 'report_ready_subject';
                                                                const bodyKey = 'report_ready_body';
                                                                const focused = String(emailTemplateFocusedField || '').trim();
                                                                const targetKey = focused === subjectKey || focused === bodyKey ? focused : bodyKey;
                                                                return (
                                                                    <Button
                                                                        key={`rr::${t.tag}`}
                                                                        variant="outlined"
                                                                        size="small"
                                                                        onClick={() => insertEmailTemplateTag(t.tag, targetKey)}
                                                                        sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                                                        fullWidth
                                                                    >
                                                                        {t.tag}
                                                                    </Button>
                                                                );
                                                            })}
                                                        </Stack>
                                                    </Stack>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Divider />

                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                                                Password Reset Email
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                                                <Box sx={{ width: '100%', flex: 1, minWidth: 0 }}>
                                                    <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%' }}>
                                                        <Grid size={{ xs: 12, md: 4 }}>
                                                            <TextField
                                                                label="Email Subject"
                                                                value={emailTemplates.password_reset_subject}
                                                                onChange={(e) => setEmailTemplates((s) => ({ ...s, password_reset_subject: e.target.value }))}
                                                                disabled={emailTemplatesLoading}
                                                                fullWidth
                                                                size="medium"
                                                                sx={emailTemplateTextFieldSx}
                                                                multiline
                                                                rows={2}
                                                                inputRef={(el) => setEmailTemplateFieldRef('password_reset_subject', el)}
                                                                onFocus={() => setEmailTemplateFocusedField('password_reset_subject')}
                                                            />
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 8 }}>
                                                            <TextField
                                                                label="Email Body"
                                                                value={emailTemplates.password_reset_body}
                                                                onChange={(e) => setEmailTemplates((s) => ({ ...s, password_reset_body: e.target.value }))}
                                                                disabled={emailTemplatesLoading}
                                                                fullWidth
                                                                size="medium"
                                                                sx={emailTemplateTextFieldSx}
                                                                multiline
                                                                rows={12}
                                                                inputRef={(el) => setEmailTemplateFieldRef('password_reset_body', el)}
                                                                onFocus={() => setEmailTemplateFocusedField('password_reset_body')}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        width: { xs: '100%', md: 240 },
                                                        flexShrink: 0,
                                                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                                        borderRadius: 2,
                                                        p: 1.25
                                                    }}
                                                >
                                                    <Stack spacing={1}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                            Insert tag
                                                        </Typography>
                                                        <Stack spacing={1}>
                                                            {(emailTemplateTagsByGroup?.password_reset || []).slice(0, 5).map((t) => {
                                                                const subjectKey = 'password_reset_subject';
                                                                const bodyKey = 'password_reset_body';
                                                                const focused = String(emailTemplateFocusedField || '').trim();
                                                                const targetKey = focused === subjectKey || focused === bodyKey ? focused : bodyKey;
                                                                return (
                                                                    <Button
                                                                        key={`pr::${t.tag}`}
                                                                        variant="outlined"
                                                                        size="small"
                                                                        onClick={() => insertEmailTemplateTag(t.tag, targetKey)}
                                                                        sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                                                        fullWidth
                                                                    >
                                                                        {t.tag}
                                                                    </Button>
                                                                );
                                                            })}
                                                        </Stack>
                                                    </Stack>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        </Stack>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveEmailTemplatesSettings} disabled={emailTemplatesSaving || emailTemplatesLoading}>
                                {emailTemplatesSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadEmailTemplatesSettings} disabled={emailTemplatesLoading}>
                                {emailTemplatesLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'operations' ? (
                <SettingsGroupHeader
                    title="Operations"
                    description="Operational policy settings for reporting, finance, exports, and issue handling."
                />
            ) : null}

            {isAdmin && activeSettingsCategory === 'operations' ? (
                <CollapsibleSection
                    id="analytics_export"
                    title="Analytics Export"
                    description="Configure scheduled exports for external reporting."
                    defaultOpen={false}
                    statusItems={analyticsExportStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {analyticsExportError ? <Alert severity="error">{analyticsExportError}</Alert> : null}
                        {analyticsExportMessage ? <Alert severity="success">{analyticsExportMessage}</Alert> : null}

                        <FormControlLabel
                            control={<Switch checked={Boolean(analyticsExport.exports_enabled)} onChange={(e) => setAnalyticsExport((s) => ({ ...s, exports_enabled: e.target.checked }))} disabled={analyticsExportLoading} />}
                            label="Enable exports"
                        />

                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    select
                                    size="small"
                                    label="Frequency"
                                    value={String(analyticsExport.exports_frequency || 'weekly')}
                                    onChange={(e) => setAnalyticsExport((s) => ({ ...s, exports_frequency: e.target.value }))}
                                    disabled={analyticsExportLoading}
                                    fullWidth
                                    sx={pillTextFieldSx}
                                >
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="weekly">Weekly</MenuItem>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Email to"
                                    value={analyticsExport.exports_email_to}
                                    onChange={(e) => setAnalyticsExport((s) => ({ ...s, exports_email_to: e.target.value }))}
                                    disabled={analyticsExportLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                        </Grid>

                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(analyticsExport.include_project_financials)} onChange={(e) => setAnalyticsExport((s) => ({ ...s, include_project_financials: e.target.checked }))} disabled={analyticsExportLoading} />}
                                label="Include project financials"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(analyticsExport.include_notifications)} onChange={(e) => setAnalyticsExport((s) => ({ ...s, include_notifications: e.target.checked }))} disabled={analyticsExportLoading} />}
                                label="Include notifications"
                            />
                        </FormGroup>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveAnalyticsExportSettings} disabled={analyticsExportSaving || analyticsExportLoading}>
                                {analyticsExportSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadAnalyticsExportSettings} disabled={analyticsExportLoading}>
                                {analyticsExportLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'operations' ? (
                <CollapsibleSection
                    id="issue_reporting"
                    title="Issue Reporting"
                    description="Configure where system issue reports are sent, and how long resolved issues stay visible before deletion."
                    defaultOpen={false}
                    statusItems={issueReportingStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 560 }}>
                        {issueReportError ? <Alert severity="error">{issueReportError}</Alert> : null}
                        {issueReportMessage ? <Alert severity="success">{issueReportMessage}</Alert> : null}

                        <TextField
                            label="Admin Email"
                            value={issueReportEmail}
                            onChange={(e) => setIssueReportEmail(e.target.value)}
                            placeholder="admin@monsurv.co.uk"
                            disabled={issueReportLoading}
                            fullWidth
                            size="small"
                            sx={pillTextFieldSx}
                        />

                        <TextField
                            label="Resolved Issues Retention (days)"
                            type="number"
                            inputProps={{ min: 1, max: 3650 }}
                            value={issueReportResolvedRetentionDays}
                            onChange={(e) => setIssueReportResolvedRetentionDays(e.target.value)}
                            disabled={issueReportLoading}
                            fullWidth
                            size="small"
                            sx={pillTextFieldSx}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Resolved issue reports stay visible in the Resolved list for this many days before they are automatically deleted.
                        </Typography>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveIssueReportSettings} disabled={issueReportSaving || issueReportLoading}>
                                {issueReportSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadIssueReportSettings} disabled={issueReportLoading}>
                                {issueReportLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'operations' ? (
                <CollapsibleSection
                    id="sla"
                    title="Service Level Agreement"
                    description="Set your organisation's SLA turnaround time policies (in calendar days). These values will be used for upcoming calendar configuration."
                    defaultOpen={false}
                    statusItems={slaStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {slaError ? <Alert severity="error">{slaError}</Alert> : null}
                        {slaMessage ? <Alert severity="success">{slaMessage}</Alert> : null}

                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Initial Report Turnaround Time (days)"
                                    type="number"
                                    inputProps={{ min: 1, max: 365 }}
                                    value={sla.initial_report_turnaround_days}
                                    onChange={(e) => setSla((s) => ({ ...s, initial_report_turnaround_days: e.target.value }))}
                                    disabled={slaLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Interim Report Turnaround Time (days)"
                                    type="number"
                                    inputProps={{ min: 1, max: 365 }}
                                    value={sla.interim_report_turnaround_days}
                                    onChange={(e) => setSla((s) => ({ ...s, interim_report_turnaround_days: e.target.value }))}
                                    disabled={slaLoading}
                                    fullWidth
                                    size="small"
                                    sx={pillTextFieldSx}
                                />
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveSlaSettings} disabled={slaSaving || slaLoading}>
                                {slaSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadSlaSettings} disabled={slaLoading}>
                                {slaLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'operations' ? (
                <CollapsibleSection
                    id="invoicing"
                    title="Invoicing"
                    description="Configure invoice placeholders/defaults and upload the invoice DOCX template."
                    defaultOpen={false}
                    statusItems={invoicingStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        {invoicingError ? <Alert severity="error">{invoicingError}</Alert> : null}
                        {invoicingMessage ? <Alert severity="success">{invoicingMessage}</Alert> : null}

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <TextField
                                label="VAT REG NO {VAT_REG_NO}"
                                size="small"
                                value={invoicingSettings.vat_reg_no}
                                onChange={(e) => updateInvoicingSetting('vat_reg_no', e.target.value)}
                                disabled={invoicingLoading}
                                fullWidth
                                sx={pillTextFieldSx}
                            />
                            <TextField
                                label="INVOICE RECIPIENT COMPANY {Invoicing_company}"
                                size="small"
                                value={invoicingSettings.invoicing_company}
                                onChange={(e) => updateInvoicingSetting('invoicing_company', e.target.value)}
                                disabled={invoicingLoading}
                                fullWidth
                                sx={pillTextFieldSx}
                            />
                        </Stack>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <TextField
                                label="Address Line 1 {Address_Line1}"
                                size="small"
                                value={invoicingSettings.address_lines?.[0] || ''}
                                onChange={(e) => updateInvoicingAddressLine(0, e.target.value)}
                                disabled={invoicingLoading}
                                fullWidth
                                sx={pillTextFieldSx}
                            />
                            <TextField
                                label="Address Line 2 {Address_Line2}"
                                size="small"
                                value={invoicingSettings.address_lines?.[1] || ''}
                                onChange={(e) => updateInvoicingAddressLine(1, e.target.value)}
                                disabled={invoicingLoading}
                                fullWidth
                                sx={pillTextFieldSx}
                            />
                        </Stack>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <TextField
                                label="Address Line 3 {Address_Line3}"
                                size="small"
                                value={invoicingSettings.address_lines?.[2] || ''}
                                onChange={(e) => updateInvoicingAddressLine(2, e.target.value)}
                                disabled={invoicingLoading}
                                fullWidth
                                sx={pillTextFieldSx}
                            />
                            <TextField
                                label="Address Line 4 {Address_Line4}"
                                size="small"
                                value={invoicingSettings.address_lines?.[3] || ''}
                                onChange={(e) => updateInvoicingAddressLine(3, e.target.value)}
                                disabled={invoicingLoading}
                                fullWidth
                                sx={pillTextFieldSx}
                            />
                        </Stack>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <TextField
                                label="PAYMENT TERMS {Pay_terms}"
                                size="small"
                                value={invoicingSettings.pay_terms}
                                onChange={(e) => updateInvoicingSetting('pay_terms', e.target.value)}
                                disabled={invoicingLoading}
                                fullWidth
                                sx={pillTextFieldSx}
                            />
                            <TextField
                                label="Next Invoice Sequence"
                                size="small"
                                type="number"
                                value={invoicingSettings.invoice_sequence_next}
                                onChange={(e) => updateInvoicingSetting('invoice_sequence_next', e.target.value)}
                                disabled={invoicingLoading}
                                sx={{ width: { md: 220 }, ...pillTextFieldSx }}
                            />
                        </Stack>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <TextField
                                label="BANK ACCOUNT NAME {Bank_acc_Name}"
                                size="small"
                                value={invoicingSettings.bank_acc_name}
                                onChange={(e) => updateInvoicingSetting('bank_acc_name', e.target.value)}
                                disabled={invoicingLoading}
                                fullWidth
                                sx={pillTextFieldSx}
                            />
                            <TextField
                                label="BANK ACCOUNT NUMBER {Bank_acc_Nr}"
                                size="small"
                                value={invoicingSettings.bank_acc_nr}
                                onChange={(e) => updateInvoicingSetting('bank_acc_nr', e.target.value)}
                                disabled={invoicingLoading}
                                fullWidth
                                sx={pillTextFieldSx}
                            />
                            <TextField
                                label="BANK SORT NUMBER {Bank_Sort}"
                                size="small"
                                value={invoicingSettings.bank_sort}
                                onChange={(e) => updateInvoicingSetting('bank_sort', e.target.value)}
                                disabled={invoicingLoading}
                                fullWidth
                                sx={pillTextFieldSx}
                            />
                        </Stack>

                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, backgroundColor: alpha('#ffffff', 0.65) }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        Invoice Word Template (S3: system/Invoice-Template)
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {invoicingSettings.template?.storage_key
                                            ? `Uploaded: ${invoicingSettings.template.original_file_name || 'template.docx'}`
                                            : 'No template uploaded yet.'}
                                    </Typography>
                                </Box>
                                <Button component="label" variant="outlined" disabled={invoicingUploading || invoicingLoading}>
                                    {invoicingUploading ? 'Uploading…' : 'Upload Template (.docx)'}
                                    <input
                                        hidden
                                        type="file"
                                        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        onChange={(e) => uploadInvoicingTemplate(e.target.files?.[0] || null)}
                                    />
                                </Button>
                            </Stack>
                        </Paper>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveInvoicingSettings} disabled={invoicingSaving || invoicingLoading || invoicingUploading}>
                                {invoicingSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadInvoicingSettings} disabled={invoicingLoading || invoicingSaving || invoicingUploading}>
                                {invoicingLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {isAdmin && activeSettingsCategory === 'operations' ? (
                <CollapsibleSection
                    id="finance"
                    title="Finance"
                    description="Configure the baseline target used on the Invoice Revenue chart."
                    defaultOpen={false}
                    statusItems={financeStatusItems}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 560 }}>
                        {financeError ? <Alert severity="error">{financeError}</Alert> : null}
                        {financeMessage ? <Alert severity="success">{financeMessage}</Alert> : null}

                        <TextField
                            label="Invoice Revenue Target (£)"
                            type="number"
                            inputProps={{ min: 0, step: 1000 }}
                            value={invoiceRevenueTarget}
                            onChange={(e) => setInvoiceRevenueTarget(e.target.value)}
                            disabled={financeLoading}
                            fullWidth
                            size="small"
                            sx={pillTextFieldSx}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Used for the red “Target” baseline line on the Invoice Revenue chart.
                        </Typography>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="contained" onClick={saveFinanceSettings} disabled={financeSaving || financeLoading}>
                                {financeSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outlined" onClick={loadFinanceSettings} disabled={financeLoading}>
                                {financeLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'security' ? (
                <CollapsibleSection
                    id="session_security"
                    title="Session & Device Security"
                    description="Review the organisation-managed session timeout policy for your account."
                    defaultOpen={false}
                >
                    <Stack spacing={1.5} sx={{ mt: 1.5, maxWidth: 900 }}>
                        <Alert severity="info">
                            Session timeouts are now managed by organisation policy. Closing the page no longer signs you out immediately.
                        </Alert>

                        <Grid container spacing={2} sx={{ maxWidth: 900 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    size="small"
                                    label="Automatic sign-out after inactivity"
                                    value={String(sessionPolicy?.idle_timeout_minutes ?? DEFAULT_SESSION_POLICY.idle_timeout_minutes)}
                                    disabled
                                    sx={pillTextFieldSx}
                                    fullWidth
                                />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Current idle timeout: {formatTimeoutMinutes(sessionPolicy?.idle_timeout_minutes)}. You receive a warning before automatic sign-out.
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    size="small"
                                    label="Maximum session duration"
                                    value={String(sessionPolicy?.session_timeout_minutes ?? DEFAULT_SESSION_POLICY.session_timeout_minutes)}
                                    disabled
                                    sx={pillTextFieldSx}
                                    fullWidth
                                />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Current session limit: {formatTimeoutMinutes(sessionPolicy?.session_timeout_minutes)}.
                                </Typography>
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button variant="outlined" onClick={() => onRefreshSessionPolicy({ silent: false })} disabled={sessionPolicyLoading}>
                                {sessionPolicyLoading ? 'Refreshing…' : 'Refresh policy'}
                            </Button>
                        </Stack>
                    </Stack>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'security' ? (
                <CollapsibleSection
                    id="mfa"
                    title="Multi-Factor Authentication (MFA)"
                    description={mfaStatus.rollout_enabled ? 'Enable time-based one-time passwords (TOTP) for your account.' : 'MFA is disabled on the backend (enable MFA_ROLLOUT_ENABLED to allow setup).'}
                    defaultOpen={false}
                >
                    {mfaError ? <Alert severity="error" sx={{ mt: 1.5 }}>{mfaError}</Alert> : null}

                    {!mfaStatus.rollout_enabled ? (
                        <Alert severity="warning" sx={{ mt: 1.5 }}>
                            MFA rollout is disabled on this server. Set <b>MFA_ROLLOUT_ENABLED=1</b> and a stable <b>MFA_ENCRYPTION_SECRET</b> on the backend, restart it, then refresh status.
                        </Alert>
                    ) : null}

                    {mfaStatus.rollout_enabled && !mfaStatus.enabled ? (
                        <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Button variant="contained" onClick={startMfaSetup} disabled={mfaSetupLoading || mfaLoading}>
                                {mfaSetupLoading ? 'Starting…' : 'Set up MFA'}
                            </Button>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                You will scan a QR code with an authenticator app, then enter a 6-digit code to confirm.
                            </Typography>
                        </div>
                    ) : null}

                    <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 13 }}>
                            Status:{' '}
                            <span style={{ fontWeight: 650, color: mfaStatus.enabled ? '#065f46' : '#6b7280' }}>
                                {mfaStatus.enabled ? 'Enabled' : 'Not enabled'}
                            </span>
                            {mfaStatus.enabled && mfaStatus.verified_at ? (
                                <span style={{ marginLeft: 10, fontSize: 12, color: '#6b7280' }}>
                                    Verified: {String(mfaStatus.verified_at)}
                                </span>
                            ) : null}
                        </div>
                        <Button variant="outlined" onClick={loadMfaStatus} disabled={mfaLoading}>
                            {mfaLoading ? 'Refreshing…' : 'Refresh'}
                        </Button>
                    </div>

                    <Dialog
                        open={mfaSetupOpen}
                        onClose={() => {
                            if (mfaSetupLoading) return;
                            setMfaSetupOpen(false);
                            setMfaSetup(null);
                            setMfaSetupCode('');
                        }}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>Set up MFA</DialogTitle>
                        <DialogContent dividers>
                            <Stack spacing={1.5}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Scan the QR code using Google Authenticator, Microsoft Authenticator, or another TOTP app.
                                </Typography>

                                {mfaSetup?.qr_data_url ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
                                        <img
                                            src={mfaSetup.qr_data_url}
                                            alt="MFA QR code"
                                            style={{ width: 220, height: 220, borderRadius: 6, border: '1px solid #e5e7eb' }}
                                        />
                                    </div>
                                ) : null}

                                {mfaSetup?.manual_entry_key ? (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Manual entry key: <b>{mfaSetup.manual_entry_key}</b>
                                    </Typography>
                                ) : null}

                                <TextField
                                    label="6-digit code"
                                    value={mfaSetupCode}
                                    onChange={(e) => setMfaSetupCode(e.target.value)}
                                    inputProps={{ inputMode: 'numeric' }}
                                    placeholder="123456"
                                    fullWidth
                                    size="small"
                                    disabled={mfaSetupLoading}
                                    sx={pillTextFieldSx}
                                />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Enter the current code shown in your authenticator app.
                                </Typography>
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    if (mfaSetupLoading) return;
                                    setMfaSetupOpen(false);
                                    setMfaSetup(null);
                                    setMfaSetupCode('');
                                }}
                                disabled={mfaSetupLoading}
                            >
                                Cancel
                            </Button>
                            <Button variant="outlined" onClick={startMfaSetup} disabled={mfaSetupLoading}>
                                New QR
                            </Button>
                            <Button
                                variant="contained"
                                onClick={verifyMfaSetup}
                                disabled={mfaSetupLoading || !String(mfaSetupCode || '').trim()}
                            >
                                {mfaSetupLoading ? 'Verifying…' : 'Verify & Enable'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </CollapsibleSection>
            ) : null}

            {activeSettingsCategory === 'integrations' ? (
                <CollapsibleSection
                    id="calendar"
                    title="Calendar Sharing"
                    description="Connect your work calendar and optionally share busy times to the Team Calendar view."
                    defaultOpen={false}
                >
                    {calendarError ? <Alert severity="error" sx={{ mt: 1.5 }}>{calendarError}</Alert> : null}
                    {calendarMessage ? <Alert severity="success" sx={{ mt: 1.5 }}>{calendarMessage}</Alert> : null}

                    <div style={{ marginTop: 12, display: 'grid', gap: 10, maxWidth: 820 }}>
                        <div style={{ fontSize: 13, color: '#111827' }}>
                            Status:{' '}
                            <span style={{ fontWeight: 650, color: calendarStatus.connected ? '#065f46' : '#6b7280' }}>
                                {calendarStatus.connected ? 'Connected' : 'Not connected'}
                            </span>
                            {calendarStatus.connected && calendarStatus.provider ? (
                                <span style={{ marginLeft: 10, fontSize: 12, color: '#6b7280' }}>
                                    ({String(calendarStatus.provider).toUpperCase()})
                                </span>
                            ) : null}
                            {calendarStatus.connected && calendarStatus.email ? (
                                <span style={{ marginLeft: 10, fontSize: 12, color: '#6b7280' }}>
                                    {calendarStatus.email}
                                </span>
                            ) : null}
                        </div>

                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Button variant="contained" onClick={connectCalendar} disabled={calendarLoading}>
                                {calendarLoading ? 'Working…' : 'Connect Microsoft 365 Calendar'}
                            </Button>
                            <Button variant="contained" onClick={connectGoogleCalendar} disabled={calendarLoading}>
                                {calendarLoading ? 'Working…' : 'Connect Google Calendar'}
                            </Button>
                            <Button variant="outlined" onClick={loadCalendarStatus} disabled={calendarLoading}>
                                {calendarLoading ? 'Refreshing…' : 'Refresh'}
                            </Button>
                            {calendarStatus.connected ? (
                                <Button variant="outlined" color="error" onClick={disconnectCalendar} disabled={calendarLoading}>
                                    Disconnect
                                </Button>
                            ) : null}
                        </div>

                        <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <input
                                type="checkbox"
                                checked={Boolean(calendarStatus.share_enabled)}
                                onChange={(e) => toggleCalendarSharing(e.target.checked)}
                                disabled={!calendarStatus.connected || calendarLoading}
                            />
                            <span>Share my busy times in the Team Calendar</span>
                        </label>

                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                            Note: by default we show “Busy” blocks (not event subjects). You must explicitly enable sharing.
                        </div>

                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                            Tip: connecting a different provider will replace your existing calendar connection.
                        </div>
                    </div>
                </CollapsibleSection>
            ) : null}

            <AnalyticsPanelSection
                eyebrow="Roadmap Notes"
                title="Recommended additions for a later phase"
            >
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {settingsRecommendations.map((item) => (
                        <Chip key={item} label={item} variant="outlined" sx={{ fontWeight: 700, backgroundColor: alpha('#ffffff', 0.5) }} />
                    ))}
                </Stack>
            </AnalyticsPanelSection>
        </Box>
    );
}
